/* ============================================================
   StatMed UI behaviors
   - Scroll reveal
   - Location picker (writes to StatMed.state.location, dispatches
     'statmed:location' event so other sections can react)
   - Booking widget
   - CNA class picker
   - Skills Only file upload (uploads to Firebase, stores path in a
     hidden cart line-item property on the product form)
   - Admin dashboard rendering
   ============================================================ */
(function () {
  const ns = (window.StatMed = window.StatMed || {});
  ns.state = ns.state || { location: null };

  /* ---------- Scroll reveal ---------- */
  function initReveal() {
    // Opt-in: turn on the "hidden at first" CSS rule. If this script never
    // loads, sections stay visible by default.
    document.documentElement.classList.add('sm-reveal-ready');
    const els = document.querySelectorAll('.sm-reveal');
    if (!('IntersectionObserver' in window) || !els.length) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    els.forEach((el) => io.observe(el));
  }

  /* ---------- Helpers ---------- */
  function readStoredLocation() {
    try { return JSON.parse(localStorage.getItem('statmed:location') || 'null'); } catch (e) { return null; }
  }
  function storeLocation(loc) {
    try { localStorage.setItem('statmed:location', JSON.stringify(loc)); } catch (e) {}
    ns.state.location = loc;
    document.dispatchEvent(new CustomEvent('statmed:location', { detail: loc }));
  }

  function fmtDateLong(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  }
  function fmtTime(iso) {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  // "Mon Sep 8 2026" — used for a ready-to-send "Appointment" order property
  // so Zapier/SMS reminders can drop it straight in. Deliberately comma-free:
  // Zapier's Shopify trigger splits line-item properties on commas, so any
  // comma in the value would shred it into pieces.
  function fmtApptDate(dateISO) {
    if (!dateISO) return '';
    const d = new Date(dateISO + 'T00:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).replace(/,/g, '');
  }

  function readUrlParam(name) {
    try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
  }

  /* ---------- Location picker ---------- */
  async function initLocationPickers() {
    const nodes = document.querySelectorAll('[data-sm-location-picker]');
    if (!nodes.length) return;
    const locations = await ns.fb.getLocations();
    const urlLoc = readUrlParam('location');
    const saved = readStoredLocation();
    let activeId = null;
    if (urlLoc && locations.find((l) => l.id === urlLoc)) activeId = urlLoc;
    else if (saved && locations.find((l) => l.id === saved.id)) activeId = saved.id;
    else activeId = locations[0].id;
    if (!ns.state.location) ns.state.location = locations.find((l) => l.id === activeId) || null;

    nodes.forEach((node) => {
      node.innerHTML = locations.map((loc) => (
        `<button type="button" class="sm-loc-chip" data-loc="${loc.id}" aria-pressed="${loc.id === activeId ? 'true' : 'false'}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
          ${loc.name}
        </button>`
      )).join('');
      node.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-loc]');
        if (!btn) return;
        node.querySelectorAll('[data-loc]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');
        const loc = locations.find((l) => l.id === btn.dataset.loc);
        if (loc) storeLocation(loc);
      });
    });

    // Ensure listeners get an initial event on load.
    storeLocation(ns.state.location);
  }

  /* ---------- Booking widget ---------- */
  /**
   * True if `iso` (slot.startsAt) falls inside at least one window.
   * Windows are `[{ start: "HH:MM", end: "HH:MM" }, ...]` in local wall-clock
   * 24h time. Half-open: start <= time < end. Empty/missing = no filter.
   */
  function isInWindows(iso, windows) {
    if (!windows || !windows.length) return true;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return true;
    const mins = d.getHours() * 60 + d.getMinutes();
    return windows.some((w) => {
      const sParts = String(w.start || '00:00').split(':');
      const eParts = String(w.end   || '23:59').split(':');
      const start = (parseInt(sParts[0], 10) || 0) * 60 + (parseInt(sParts[1], 10) || 0);
      const end   = (parseInt(eParts[0], 10) || 0) * 60 + (parseInt(eParts[1], 10) || 0);
      return mins >= start && mins < end;
    });
  }

  /**
   * Pick the windows array for a service+location pair.
   * `service.windowsByLocation[locationId]` overrides `service.availableWindows`.
   * Returns null if no constraint.
   */
  function windowsFor(service, locationId) {
    if (!service) return null;
    if (service.windowsByLocation && service.windowsByLocation[locationId]) {
      return service.windowsByLocation[locationId];
    }
    return service.availableWindows || null;
  }

  // Per-location scheduled sessions for the v2 booking flow. Each session is
  //   { date: "YYYY-MM-DD", startTime: "2:00 PM", endTime: "5:00 PM" }
  // Returns the sessions array for the (service, locationId) pair, or null
  // when no constraint is configured (in which case the location's full
  // openDays + slots list applies).
  function smPickSessions(service, locationId) {
    if (!service || !locationId) return null;
    const map = service.sessionsByLocation;
    if (!map || typeof map !== 'object') return null;
    const list = map[locationId];
    if (!Array.isArray(list) || !list.length) return null;
    return list;
  }
  // Distinct dates that have at least one session — used to gate the calendar.
  function smSessionDates(sessions) {
    if (!sessions || !sessions.length) return null;
    const out = {};
    sessions.forEach((s) => { if (s && s.date) out[s.date] = true; });
    return out;
  }
  // Sessions matching a specific date (in YYYY-MM-DD form).
  function smSessionsForDate(sessions, date) {
    if (!sessions || !sessions.length) return [];
    return sessions.filter((s) => s && s.date === date);
  }

  // Stale-tab mitigation: run `cb` when the tab becomes visible again after
  // being hidden for at least `minHiddenMs`. Booking surfaces use this to
  // re-fetch availability (or reload an untouched form) so customers act on
  // fresh data instead of whatever was on screen when they left the tab.
  function smOnWake(minHiddenMs, cb) {
    let hiddenAt = null;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        hiddenAt = Date.now();
      } else {
        const wasHidden = hiddenAt;
        hiddenAt = null;
        if (wasHidden && Date.now() - wasHidden >= minHiddenMs) cb();
      }
    });
  }

  // Returns YYYY-MM-DD for "today" in the browser's local time. Used at
  // submit time so a stale tab can't book a date that's slipped into the
  // past since the customer originally opened the page.
  function smTodayLocal() {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  // Current date + minutes-past-midnight in the shop's timezone
  // (America/New_York), independent of the visitor's own timezone. Used to
  // enforce a same-day booking cutoff.
  function smEasternNow() {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
      const g = (t) => (parts.find((p) => p.type === t) || {}).value || '';
      let hh = parseInt(g('hour'), 10); if (hh === 24) hh = 0;
      return { dateStr: g('year') + '-' + g('month') + '-' + g('day'), minutes: hh * 60 + parseInt(g('minute'), 10) };
    } catch (e) {
      const d = new Date();
      return { dateStr: smTodayLocal(), minutes: d.getHours() * 60 + d.getMinutes() };
    }
  }

  /* ---------- Certification renewal helpers (shared) ---------- */
  const SM_RENEWAL_YEARS = { bls: 2, acls: 2, pals: 2, heartsaver: 2 };
  function smCourseKey(name) {
    const s = String(name || '').toLowerCase();
    if (s.indexOf('heartsaver') > -1) return 'heartsaver';
    if (s.indexOf('acls') > -1) return 'acls';
    if (s.indexOf('pals') > -1) return 'pals';
    if (s.indexOf('bls') > -1) return 'bls';
    return '';
  }
  function smRenewalYears(name) { return SM_RENEWAL_YEARS[smCourseKey(name)] || 2; }
  function smAddYears(iso, y) {
    if (!iso) return '';
    const d = new Date(iso + 'T12:00:00'); if (isNaN(d.getTime())) return '';
    d.setFullYear(d.getFullYear() + (y || 2));
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }
  function smCertStatus(renewalIso) {
    if (!renewalIso) return { k: 'pending', label: 'No date', days: null };
    const r = new Date(renewalIso + 'T12:00:00'); if (isNaN(r.getTime())) return { k: 'pending', label: 'No date', days: null };
    const days = Math.round((r - new Date()) / 86400000);
    if (days < 0) return { k: 'exp', label: 'Expired', days };
    if (days <= 60) return { k: 'warn', label: 'Expires in ' + days + 'd', days };
    return { k: 'ok', label: 'Valid', days };
  }
  function smCertPill(st) { const cls = st.k === 'ok' ? 'sm-cert--ok' : (st.k === 'warn' ? 'sm-cert--no' : (st.k === 'exp' ? 'sm-cert--no' : '')); return '<span class="sm-cert ' + (st.k === 'ok' ? 'sm-cert--ok' : st.k === 'warn' ? 'sm-cert--pending' : 'sm-cert--no') + '">' + st.label + '</span>'; }

  async function initBookingWidgets() {
    const widgets = document.querySelectorAll('[data-sm-booking]');
    if (!widgets.length) return;

    widgets.forEach(async (widget) => {
      const serviceId = widget.dataset.serviceId || widget.dataset.smBooking || 'generic';
      const serviceName = widget.dataset.serviceName || 'service';
      const productId = widget.dataset.productId || '';
      const productVariantId = widget.dataset.variantId || '';

      const slotsEl = widget.querySelector('[data-sm-slots]');
      const dateListEl = widget.querySelector('[data-sm-dates]');
      const summaryEl = widget.querySelector('[data-sm-summary]');
      const form = widget.querySelector('form[data-sm-booking-form]');
      const statusEl = widget.querySelector('[data-sm-status]');
      const cartBtn = form && form.querySelector('[type="submit"]');

      let current = { date: null, slot: null, location: ns.state.location };
      let availability = [];
      let service = null; // cached service doc for window filtering

      async function refreshAvailability() {
        current.location = ns.state.location;
        if (!current.location) return;
        slotsEl && (slotsEl.innerHTML = '<p class="sm-field__help">Loading times…</p>');
        if (service === null && ns.fb.getService) {
          try { service = await ns.fb.getService(serviceId); } catch (e) { service = false; }
        }
        const raw = await ns.fb.getAvailability({ locationId: current.location.id, serviceId });
        const windows = windowsFor(service, current.location.id);
        availability = windows
          ? raw.map((d) => Object.assign({}, d, { slots: (d.slots || []).filter((s) => isInWindows(s.startsAt, windows)) }))
          : raw;
        renderDates();
      }

      function renderDates() {
        if (!dateListEl) return;
        if (!availability.length) {
          dateListEl.innerHTML = '<p class="sm-field__help">No upcoming classes at this location yet. Check back soon or call us to arrange a private session.</p>';
          slotsEl && (slotsEl.innerHTML = '');
          return;
        }
        dateListEl.innerHTML = availability.map((d) => {
          const has = (d.slots || []).some((s) => s.seatsLeft > 0);
          return `<button type="button" class="sm-slot" data-date="${d.date}" ${has ? '' : 'disabled'} aria-pressed="false">
            <span>${fmtDateLong(d.date + 'T00:00:00')}</span>
            <span class="sm-slot__sub">${has ? ((d.slots.reduce((n, s) => n + s.seatsLeft, 0)) + ' seats') : 'Full'}</span>
          </button>`;
        }).join('');
        // auto-select first available
        const firstBtn = dateListEl.querySelector('.sm-slot:not(:disabled)');
        if (firstBtn) { firstBtn.click(); }
      }

      function renderSlots(date) {
        const day = availability.find((d) => d.date === date);
        if (!day) { slotsEl.innerHTML = ''; return; }
        slotsEl.innerHTML = (day.slots || []).map((s) => (
          `<button type="button" class="sm-slot" data-iso="${s.startsAt}" ${s.seatsLeft > 0 ? '' : 'disabled'} aria-pressed="false">
            <span>${fmtTime(s.startsAt)}</span>
            <span class="sm-slot__sub">${s.seatsLeft > 0 ? (s.seatsLeft + ' seats left') : 'Full'}</span>
          </button>`
        )).join('');
      }

      function updateSummary() {
        if (!summaryEl) return;
        const parts = [];
        if (current.location) parts.push(current.location.name);
        if (current.date) parts.push(fmtDateLong(current.date + 'T00:00:00'));
        if (current.slot) parts.push(fmtTime(current.slot));
        summaryEl.textContent = parts.length ? parts.join(' · ') : 'Select a location, date, and time.';
      }

      dateListEl && dateListEl.addEventListener('click', (e) => {
        const b = e.target.closest('[data-date]'); if (!b || b.disabled) return;
        dateListEl.querySelectorAll('[data-date]').forEach((x) => x.setAttribute('aria-pressed', 'false'));
        b.setAttribute('aria-pressed', 'true');
        current.date = b.dataset.date;
        current.slot = null;
        renderSlots(current.date);
        updateSummary();
      });
      slotsEl && slotsEl.addEventListener('click', (e) => {
        const b = e.target.closest('[data-iso]'); if (!b || b.disabled) return;
        slotsEl.querySelectorAll('[data-iso]').forEach((x) => x.setAttribute('aria-pressed', 'false'));
        b.setAttribute('aria-pressed', 'true');
        current.slot = b.dataset.iso;
        updateSummary();
      });

      document.addEventListener('statmed:location', refreshAvailability);
      await ns.fb.onReady(() => {});
      await refreshAvailability();
      updateSummary();

      if (form) {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          if (!current.location || !current.date || !current.slot) {
            statusEl && (statusEl.textContent = 'Please pick a location, date, and time.');
            return;
          }
          cartBtn && (cartBtn.disabled = true);
          statusEl && (statusEl.textContent = 'Reserving your spot…');
          const data = {
            serviceId, serviceName,
            locationId: current.location.id,
            locationName: current.location.name,
            startsAt: current.slot,
            customer: {
              firstName: form.first_name.value.trim(),
              lastName: form.last_name.value.trim(),
              email: form.email.value.trim(),
              phone: form.phone.value.trim()
            }
          };
          try {
            const draft = await ns.fb.createBookingDraft(data);
            // Attach to the Shopify cart line item as properties, then submit.
            addPropertyInput(form, 'Location', current.location.name);
            addPropertyInput(form, 'Appointment', [fmtApptDate(current.date), fmtTime(current.slot)].filter(Boolean).join(' · '));
            addPropertyInput(form, 'Date', fmtDateLong(current.date + 'T00:00:00'));
            addPropertyInput(form, 'Time', fmtTime(current.slot));
            addPropertyInput(form, 'First Name', data.customer.firstName);
            addPropertyInput(form, 'Last Name', data.customer.lastName);
            addPropertyInput(form, 'Email', data.customer.email);
            addPropertyInput(form, 'Phone', data.customer.phone);
            addPropertyInput(form, '_Booking ID', draft.id);
            addPropertyInput(form, '_Service ID', serviceId);
            submitToCart(form, productVariantId);
          } catch (err) {
            console.error(err);
            statusEl && (statusEl.textContent = 'Something went wrong. Please try again.');
            cartBtn && (cartBtn.disabled = false);
          }
        });
      }
    });
  }

  function addPropertyInput(form, label, value) {
    if (!value) return;
    const name = 'properties[' + label + ']';
    let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  async function submitToCart(form, variantId) {
    const fd = new FormData(form);
    if (variantId && !fd.get('id')) fd.set('id', variantId);
    try {
      const res = await fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST', body: fd, headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error('Cart add failed');
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    } catch (e) {
      console.error(e);
      form.submit(); // fallback to default submit
    }
  }

  /* ---------- CNA booking ---------- */
  async function initCnaBookings() {
    const nodes = document.querySelectorAll('[data-sm-cna]');
    if (!nodes.length) return;
    nodes.forEach(async (widget) => {
      const listEl = widget.querySelector('[data-sm-cna-list]');
      const form = widget.querySelector('form[data-sm-cna-form]');
      const variantId = widget.dataset.variantId || '';
      let classes = [];
      let chosen = null;

      async function load() {
        if (!ns.state.location) return;
        listEl.innerHTML = '<p class="sm-field__help">Loading class dates…</p>';
        classes = await ns.fb.getCnaClasses({ locationId: ns.state.location.id });
        if (!classes.length) {
          listEl.innerHTML = '<p class="sm-field__help">No CNA classes scheduled at this location right now.</p>';
          return;
        }
        listEl.innerHTML = classes.map((c) => `
          <label class="sm-option" data-cna="${c.id}">
            <div class="sm-option__header">
              <h3 class="sm-option__title">${fmtDateLong(c.startDate + 'T00:00:00')} – ${fmtDateLong(c.endDate + 'T00:00:00')}</h3>
              <span class="sm-pill sm-pill--dot">${c.seatsLeft} seats</span>
            </div>
            <p class="sm-option__desc">${c.schedule} · ${c.days} class days · ${c.startTime}–${c.endTime}</p>
            <div class="sm-option__meta">
              <span>Starts ${fmtDateLong(c.startDate + 'T00:00:00')}</span>
              <span>Ends ${fmtDateLong(c.endDate + 'T00:00:00')}</span>
            </div>
          </label>
        `).join('');
      }

      listEl && listEl.addEventListener('click', (e) => {
        const el = e.target.closest('[data-cna]'); if (!el) return;
        listEl.querySelectorAll('[data-cna]').forEach((x) => x.classList.remove('sm-option--recommended'));
        el.classList.add('sm-option--recommended');
        chosen = classes.find((c) => c.id === el.dataset.cna);
      });

      document.addEventListener('statmed:location', load);
      await ns.fb.onReady(() => {});
      await load();

      if (form) {
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          if (!chosen) { alert('Please select a class date.'); return; }
          const data = {
            serviceId: 'cna', serviceName: 'CNA Training',
            locationId: ns.state.location.id,
            locationName: ns.state.location.name,
            classId: chosen.id,
            startsAt: chosen.startDate,
            customer: {
              firstName: form.first_name.value.trim(),
              lastName: form.last_name.value.trim(),
              email: form.email.value.trim(),
              phone: form.phone.value.trim(),
              dob: form.dob.value
            }
          };
          const draft = await ns.fb.createBookingDraft(data);
          addPropertyInput(form, 'Location', ns.state.location.name);
          addPropertyInput(form, 'Class', fmtDateLong(chosen.startDate + 'T00:00:00') + ' – ' + fmtDateLong(chosen.endDate + 'T00:00:00'));
          addPropertyInput(form, 'Schedule', chosen.schedule);
          addPropertyInput(form, 'First Name', data.customer.firstName);
          addPropertyInput(form, 'Last Name', data.customer.lastName);
          addPropertyInput(form, 'Email', data.customer.email);
          addPropertyInput(form, 'Phone', data.customer.phone);
          addPropertyInput(form, 'Date of Birth', data.customer.dob);
          addPropertyInput(form, '_Booking ID', draft.id);
          addPropertyInput(form, '_Service ID', 'cna');
          submitToCart(form, variantId);
        });
      }
    });
  }

  /* ---------- Skills Only upload ---------- */
  function initSkillsUploads() {
    document.querySelectorAll('[data-sm-upload]').forEach((wrap) => {
      const input = wrap.querySelector('input[type="file"]');
      const fileLabel = wrap.querySelector('[data-sm-upload-file]');
      const productForm = wrap.closest('form') || document.querySelector('form[action*="/cart/add"]');
      if (!input) return;

      const dragEvents = ['dragenter','dragover','dragleave','drop'];
      dragEvents.forEach((evt) => wrap.addEventListener(evt, (e) => { e.preventDefault(); e.stopPropagation(); }));
      ['dragenter','dragover'].forEach((evt) => wrap.addEventListener(evt, () => wrap.classList.add('is-drag')));
      ['dragleave','drop'].forEach((evt) => wrap.addEventListener(evt, () => wrap.classList.remove('is-drag')));
      wrap.addEventListener('drop', (e) => { if (e.dataTransfer.files && e.dataTransfer.files[0]) { input.files = e.dataTransfer.files; input.dispatchEvent(new Event('change')); } });

      input.addEventListener('change', async () => {
        const file = input.files && input.files[0];
        if (!file) return;
        fileLabel && (fileLabel.textContent = 'Uploading ' + file.name + '…');
        try {
          const res = await ns.fb.uploadProof(file, {
            customerEmail: (productForm && productForm.email && productForm.email.value) || '',
            productId: wrap.dataset.productId || ''
          });
          fileLabel && (fileLabel.textContent = '✓ ' + file.name + ' uploaded');
          if (productForm) {
            addPropertyInput(productForm, 'Proof of Completion', file.name);
            addPropertyInput(productForm, '_Proof File', res.path);
            if (res.url) addPropertyInput(productForm, '_Proof URL', res.url);
          }
        } catch (err) {
          console.error(err);
          fileLabel && (fileLabel.textContent = 'Upload failed. Please try again.');
        }
      });
    });
  }

  /* ---------- Admin dashboard ---------- */
  async function initAdmin() {
    const root = document.querySelector('[data-sm-admin]');
    if (!root) return;

    // If the Firebase client asset didn't load (Shopify sync lost it again),
    // ns.fb is undefined and every admin action would throw "Cannot read
    // properties of undefined". Show a clear error inline instead.
    if (!ns.fb || typeof ns.fb.signIn !== 'function') {
      console.error('[admin] statmed-firebase.js is missing or stale. window.StatMed.fb =', ns.fb);
      root.innerHTML = '<div style="padding:24px;background:#FBE4E7;border:1px solid #F5A3AD;border-radius:12px;color:#8A1220">' +
        '<strong>Firebase client not loaded.</strong><br><br>' +
        'The file <code>assets/statmed-firebase.js</code> is either missing from this theme or out of date. Shopify’s GitHub sync sometimes drops this specific file. Fix:' +
        '<ol style="margin:12px 0 0 20px;line-height:1.7">' +
          '<li>Shopify admin → <strong>Online Store → Themes → Edit code</strong>.</li>' +
          '<li>Open <strong>Assets → statmed-firebase.js</strong> (create a blank file with that name if it doesn’t exist).</li>' +
          '<li>Replace its contents with the current version from GitHub:<br>' +
            '<a href="https://raw.githubusercontent.com/Pr0jectAtlas/statmed-theme/main/assets/statmed-firebase.js" target="_blank" style="color:#0071FF">raw.githubusercontent.com/Pr0jectAtlas/statmed-theme/main/assets/statmed-firebase.js</a></li>' +
          '<li>Save, then hard-refresh this page (Cmd/Ctrl+Shift+R).</li>' +
        '</ol>' +
      '</div>';
      return;
    }

    const loginPanel = root.querySelector('[data-sm-admin-login]');
    const appPanel   = root.querySelector('[data-sm-admin-app]');
    // The user bar (email + Sign out + My time off) lives in the section
    // head ABOVE the [data-sm-admin] card, so root.querySelector misses
    // it. Use document.querySelector for these — only one admin section
    // ships per page.
    const userBar    = document.querySelector('[data-sm-admin-user]');
    const userEmail  = document.querySelector('[data-sm-admin-user-email]');
    const statusEl   = root.querySelector('[data-sm-admin-status]');

    // Tab switching
    const navBtns = root.querySelectorAll('[data-sm-admin-nav]');
    const views = {
      bookings:     root.querySelector('[data-sm-view="bookings"]'),
      availability: root.querySelector('[data-sm-view="availability"]'),
      analytics:    root.querySelector('[data-sm-view="analytics"]'),
      locations:    root.querySelector('[data-sm-view="locations"]'),
      services:     root.querySelector('[data-sm-view="services"]'),
      cnaclasses:   root.querySelector('[data-sm-view="cnaclasses"]'),
      ivclasses:    root.querySelector('[data-sm-view="ivclasses"]'),
      inquiries:    root.querySelector('[data-sm-view="inquiries"]'),
      failures:     root.querySelector('[data-sm-view="failures"]'),
      elks:         root.querySelector('[data-sm-view="elks"]'),
      assets:       root.querySelector('[data-sm-view="assets"]'),
      employees:    root.querySelector('[data-sm-view="employees"]'),
      schedule:     root.querySelector('[data-sm-view="schedule"]'),
      quotes:       root.querySelector('[data-sm-view="quotes"]'),
      oris:         root.querySelector('[data-sm-view="oris"]'),
      renewals:     root.querySelector('[data-sm-view="renewals"]'),
      baseline:     root.querySelector('[data-sm-view="baseline"]'),
      businesses:   root.querySelector('[data-sm-view="businesses"]'),
      metrics:      root.querySelector('[data-sm-view="metrics"]'),
      instructors:  root.querySelector('[data-sm-view="instructors"]'),
      classportal:  root.querySelector('[data-sm-view="classportal"]'),
      sitevars:     root.querySelector('[data-sm-view="sitevars"]'),
      sitecontent:  root.querySelector('[data-sm-view="sitecontent"]')
    };
    // Backend tab is a container for four sub-views (Services / Failures /
    // Background Checks). Sub-nav switches between them in place.
    const BACKEND_SUB_VIEWS = ['services','businesses','instructors','classportal','sitecontent','sitevars','metrics','locations','oris','renewals','baseline','assets','employees','schedule','quotes','elks','failures'];
    const BACKEND_ALLOWED_EMAIL = 'daylon@statmedtraining.com';
    const backendSubnav = root.querySelector('[data-sm-backend-subnav]');
    const backendSubBtns = root.querySelectorAll('[data-sm-backend-sub]');
    let backendActiveSub = 'services';

    function backendAllowed() {
      const u = ns.fb.currentUser && ns.fb.currentUser();
      return !!(u && u.email && u.email.toLowerCase() === BACKEND_ALLOWED_EMAIL);
    }

    navBtns.forEach((b) => b.addEventListener('click', () => {
      if (!b.dataset.smAdminNav) return;
      const target = b.dataset.smAdminNav;
      // Hard guard: even if the button is un-hidden via dev tools, the
      // Backend views won't open for a non-allowed account.
      if (target === 'backend' && !backendAllowed()) return;
      navBtns.forEach((x) => x.classList.remove('is-active'));
      b.classList.add('is-active');
      if (target === 'backend') {
        // Show the sub-nav and the currently-active sub-view.
        if (backendSubnav) backendSubnav.hidden = false;
        Object.entries(views).forEach(([k, el]) => { if (el) el.hidden = (k !== backendActiveSub); });
      } else {
        if (backendSubnav) backendSubnav.hidden = true;
        Object.entries(views).forEach(([k, el]) => { if (el) el.hidden = (k !== target); });
      }
    }));

    backendSubBtns.forEach((sb) => sb.addEventListener('click', () => {
      const target = sb.dataset.smBackendSub;
      backendActiveSub = target;
      backendSubBtns.forEach((b) => b.classList.toggle('is-active', b === sb));
      BACKEND_SUB_VIEWS.forEach((k) => { if (views[k]) views[k].hidden = (k !== target); });
      // Lazy-load each sub-tab the first time it's opened.
      if (target === 'services'  && !svcLoaded)    { svcLoaded = true;   loadServicesTable(); }
      if (target === 'businesses' && !bizLoaded)   { bizLoaded = true;   loadBusinesses(); }
      if (target === 'metrics'    && !metricsLoaded) { metricsLoaded = true; loadMetrics(); }
      if (target === 'instructors' && !instLoaded)  { instLoaded = true;   loadInstructors(); }
      if (target === 'classportal' && !classLoaded) { classLoaded = true;  loadClassData(); }
      if (target === 'sitevars'  && !siteVarsLoaded){ siteVarsLoaded = true; loadSiteVars(); }
      if (target === 'sitecontent' && !siteContentLoaded) {
        siteContentLoaded = true;
        const scRoot = root.querySelector('[data-sm-sitecontent-root]');
        if (ns.contentEditor && scRoot) ns.contentEditor.mount(scRoot);
        else if (scRoot) { siteContentLoaded = false; scRoot.innerHTML = '<p class="sm-field__error is-shown" style="padding:12px">The site content editor didn\u2019t load. Refresh the page and try again.</p>'; }
      }
      if (target === 'locations' && !locLoaded)    { locLoaded = true;   loadLocationsTable(); }
      if (target === 'oris'      && !oriLoaded)    { oriLoaded = true;   loadOriTable(); }
      if (target === 'renewals'  && !renewalsLoaded) { renewalsLoaded = true; loadRenewalsTable(); }
      if (target === 'baseline'  && !baselineLoaded) { baselineLoaded = true; loadBaseline(); }
      if (target === 'assets'    && !assetsLoaded) { assetsLoaded = true; loadAssetsTable(); }
      if (target === 'employees' && !empLoaded)    { empLoaded = true;    loadEmployeesTable(); }
      if (target === 'schedule'  && !schedLoaded)  { schedLoaded = true;  loadSchedule(); }
      if (target === 'quotes'    && !quotesLoaded) { quotesLoaded = true; loadQuotesTable(); }
      if (target === 'elks'      && !elksLoaded)   { elksLoaded = true;  loadElksTable(); }
      if (target === 'failures'  && !failLoaded)   { failLoaded = true;  loadFailuresTable(); }
    }));

    function showApp() {
      loginPanel && (loginPanel.hidden = true);
      appPanel && (appPanel.hidden = false);
      userBar && (userBar.hidden = false);
      const u = ns.fb.currentUser && ns.fb.currentUser();
      if (userEmail) userEmail.textContent = (u && u.email) || (ns.fb.isMock() ? 'mock mode' : '');
      // Gate the Backend tab to one specific staff email.
      const backendBtn = root.querySelector('[data-sm-admin-nav="backend"]');
      if (backendBtn) {
        const allowed = !!(u && u.email && u.email.toLowerCase() === BACKEND_ALLOWED_EMAIL);
        backendBtn.hidden = !allowed;
      }
    }
    function showLogin() {
      loginPanel && (loginPanel.hidden = false);
      appPanel && (appPanel.hidden = true);
      userBar && (userBar.hidden = true);
    }

    const loginForm = root.querySelector('[data-sm-admin-login-form]');
    loginForm && loginForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      statusEl && (statusEl.textContent = 'Signing in…');
      try {
        await ns.fb.signIn(loginForm.email.value.trim(), loginForm.password.value);
        statusEl && (statusEl.textContent = '');
        showApp();
        await loadLookups();
        await loadBookings();
      } catch (err) {
        statusEl && (statusEl.textContent = err.message || 'Sign-in failed.');
      }
    });
    const logoutBtn = document.querySelector('[data-sm-admin-logout]');
    logoutBtn && logoutBtn.addEventListener('click', async () => {
      await ns.fb.signOut();
      showLogin();
    });

    // ---------- "My time off" self-serve for any signed-in staff ----
    // Available to every authenticated user — not gated by the Backend
    // allowlist. Lets instructors see their assigned schedule and
    // submit time-off requests that admins approve in Backend → Schedule.
    const myTimeOffBtn = document.querySelector('[data-sm-mytimeoff]');
    function openMyTimeOffModal() {
      if (!modal || !modalBody) return;
      modalTitle && (modalTitle.textContent = 'My schedule & time off');
      modalBody.innerHTML =
        '<div data-sm-mto-events>' +
          '<h3 class="sm-h3" style="margin-top:0">Upcoming schedule</h3>' +
          '<div data-sm-mto-events-list class="sm-field__help">Loading…</div>' +
        '</div>' +
        '<div style="margin-top:18px;padding:14px;border:1px solid var(--sm-line,#e5e7eb);border-radius:12px">' +
          '<h3 class="sm-h3" style="margin-top:0">Request time off</h3>' +
          '<form data-sm-mto-form class="sm-form">' +
            '<div class="sm-form__row">' +
              '<div class="sm-field"><label>From *</label><input type="date" name="startDate" required></div>' +
              '<div class="sm-field"><label>To *</label><input type="date" name="endDate" required></div>' +
            '</div>' +
            '<label class="sm-book__chk"><input type="checkbox" name="allDay" checked><span>All day</span></label>' +
            '<div class="sm-form__row" data-sm-mto-hours hidden>' +
              '<div class="sm-field"><label>Start</label><input type="time" name="startTime" value="09:00"></div>' +
              '<div class="sm-field"><label>End</label><input type="time" name="endTime" value="17:00"></div>' +
            '</div>' +
            '<div class="sm-field"><label>Reason</label><input name="reason" placeholder="Vacation, medical, family, etc."></div>' +
            '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:8px">' +
              '<button type="submit" class="sm-btn sm-btn--primary">Submit request</button>' +
              '<span data-sm-mto-msg class="sm-field__help"></span>' +
            '</div>' +
          '</form>' +
        '</div>' +
        '<div style="margin-top:18px">' +
          '<h3 class="sm-h3" style="margin-top:0">My time-off requests</h3>' +
          '<div data-sm-mto-requests class="sm-field__help">Loading…</div>' +
        '</div>';
      modal.hidden = false;

      const eventsList = modalBody.querySelector('[data-sm-mto-events-list]');
      const requestsList = modalBody.querySelector('[data-sm-mto-requests]');
      const form = modalBody.querySelector('[data-sm-mto-form]');
      const msg  = modalBody.querySelector('[data-sm-mto-msg]');
      const allDayChk = form.querySelector('[name="allDay"]');
      const hoursRow = modalBody.querySelector('[data-sm-mto-hours]');
      allDayChk.addEventListener('change', () => { hoursRow.hidden = allDayChk.checked; });

      function statusBadge(s) {
        const map = { requested: '#f59e0b', planned: '#15803d', approved: '#15803d', denied: '#b91c1c' };
        const color = map[s] || '#6b7280';
        return '<span style="display:inline-block;padding:2px 8px;border-radius:999px;background:' + color + '22;color:' + color + ';font-size:12px;font-weight:600">' + esc((s || '—').replace(/^\w/, c => c.toUpperCase())) + '</span>';
      }
      function typeBadge(t) {
        const map = { shift: '#3b82f6', class: '#16a34a', unavailable: '#dc2626', event: '#9333ea' };
        return '<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:' + (map[t] || '#6b7280') + ';margin-right:6px"></span>';
      }
      function fmtDateTime(s) {
        if (!s) return '';
        const d = new Date(s);
        if (isNaN(d.getTime())) return s;
        return d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      }

      async function refresh() {
        try {
          const list = await ns.fb.staffListMyEvents();
          const todayISO = new Date().toISOString().slice(0, 10);
          const horizon = new Date(); horizon.setDate(horizon.getDate() + 60);
          const horizonISO = horizon.toISOString().slice(0, 10);
          const upcoming = list.filter((e) =>
            e.type !== 'unavailable' &&
            e.start && e.start.slice(0, 10) >= todayISO && e.start.slice(0, 10) <= horizonISO
          ).sort((a, b) => String(a.start).localeCompare(String(b.start)));
          eventsList.innerHTML = upcoming.length
            ? '<ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:6px">' +
              upcoming.map((e) =>
                '<li style="padding:8px 10px;border:1px solid var(--sm-line,#e5e7eb);border-radius:8px;background:#fafafa">' +
                  '<div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;align-items:flex-start">' +
                    '<div><strong>' + typeBadge(e.type) + esc(e.title || e.type) + '</strong>' + (e.location ? '<br><span class="sm-field__help">' + esc(e.location) + '</span>' : '') + '</div>' +
                    '<div class="sm-field__help">' + esc(fmtDateTime(e.start)) + '</div>' +
                  '</div>' +
                '</li>'
              ).join('') + '</ul>'
            : '<p class="sm-field__help">Nothing scheduled in the next 60 days.</p>';
          const requests = list.filter((e) => e.type === 'unavailable')
            .sort((a, b) => String(b.start || '').localeCompare(String(a.start || '')));
          requestsList.innerHTML = requests.length
            ? '<ul style="list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:6px">' +
              requests.map((r) =>
                '<li style="padding:8px 10px;border:1px solid var(--sm-line,#e5e7eb);border-radius:8px;background:#fff">' +
                  '<div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;align-items:flex-start">' +
                    '<div><strong>' + esc((r.start || '').slice(0, 10)) +
                      (r.end && r.end.slice(0, 10) !== (r.start || '').slice(0, 10) ? ' — ' + esc(r.end.slice(0, 10)) : '') +
                    '</strong>' +
                    (r.notes ? '<div class="sm-field__help">' + esc(r.notes) + '</div>' : '') +
                    (r.deniedReason ? '<div class="sm-field__help" style="color:#b91c1c">Reason: ' + esc(r.deniedReason) + '</div>' : '') +
                    '</div>' +
                    '<div>' + statusBadge(r.status) + '</div>' +
                  '</div>' +
                '</li>'
              ).join('') + '</ul>'
            : '<p class="sm-field__help">No requests yet.</p>';
        } catch (err) {
          eventsList.textContent = err.message || 'Could not load.';
          requestsList.textContent = '';
        }
      }
      refresh();

      form.addEventListener('submit', async (sub) => {
        sub.preventDefault();
        msg.textContent = 'Submitting…';
        try {
          const fd = new FormData(form);
          await ns.fb.staffSubmitTimeOffRequest({
            startDate: (fd.get('startDate') || '').toString(),
            endDate:   (fd.get('endDate') || '').toString() || (fd.get('startDate') || '').toString(),
            startTime: (fd.get('startTime') || '').toString(),
            endTime:   (fd.get('endTime') || '').toString(),
            allDay:    !!fd.get('allDay'),
            reason:    (fd.get('reason') || '').toString().trim()
          });
          form.reset();
          allDayChk.checked = true;
          hoursRow.hidden = true;
          msg.textContent = '✓ Submitted — admin will review.';
          refresh();
        } catch (err) { msg.textContent = err.message || 'Submission failed.'; }
      });
    }
    myTimeOffBtn && myTimeOffBtn.addEventListener('click', openMyTimeOffModal);

    // Shared location + service lookups (populated once after login)
    const cache = { locations: [], services: [] };

    const SM_STYLE_OPTIONS = [
      { value: 'in-person',    label: 'In-person' },
      { value: 'blended',      label: 'Blended' },
      { value: 'self-guided',  label: 'Self-guided' },
      { value: 'skills-only',  label: 'Skills only' },
      { value: 'exam-only',    label: 'Exam only' },
      { value: 'online',       label: 'Online' },
      { value: 'renewal',      label: 'Renewal' },
      { value: 'group-onsite', label: 'Group / Onsite' }
    ];
    const SM_STYLE_LABEL = SM_STYLE_OPTIONS.reduce((m, o) => { m[o.value] = o.label; return m; }, {});
    function smSlug(v) {
      return String(v || '').toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }
    async function loadLookups() {
      try {
        cache.locations = await ns.fb.adminListLocationsAll();
        cache.services  = await ns.fb.adminListServicesAll();
        if (typeof ns.fb.adminListResourceGroups === 'function') {
          cache.resourceGroups = await ns.fb.adminListResourceGroups();
        }
        if (typeof ns.fb.getScheduleSettings === 'function') {
          try { cache.schedule = await ns.fb.getScheduleSettings(); } catch (e) {}
        }
      } catch (e) {
        console.error('[admin] lookup load failed', e);
      }
      // Populate select dropdowns across all tabs
      root.querySelectorAll('select[data-sm-admin-location], [data-sm-view] select[name="location"]').forEach((sel) => {
        const current = sel.value;
        const includeAll = sel.hasAttribute('data-sm-admin-location');
        sel.innerHTML = (includeAll ? '<option value="">All locations</option>' : '') +
          cache.locations.map((l) => '<option value="' + l.id + '">' + (l.name || l.id) + '</option>').join('');
        if (current) sel.value = current;
      });
      const svcSelects = root.querySelectorAll('[data-sm-admin-service], [data-sm-view="availability"] select[name="service"]');
      svcSelects.forEach((sel) => {
        const current = sel.value;
        const isFilter = sel.hasAttribute('data-sm-admin-service');
        sel.innerHTML = (isFilter ? '<option value="">All services</option>' : '') +
          cache.services.map((s) => '<option value="' + s.id + '">' + (s.name || s.id) + '</option>').join('');
        if (current) sel.value = current;
      });

      // Availability blocking — service & resource-group pickers (readable names, value = id).
      // Merge the built-in catalog (ns.SEED_DATA) with live Firestore so a
      // service/group that was never seeded (e.g. background-check) is still
      // selectable here; live docs win on name. The stored value is the id.
      const catalogFromSeed = (prefix) => {
        const seed = (ns && ns.SEED_DATA) || {};
        return Object.keys(seed)
          .filter((k) => k.indexOf(prefix + '/') === 0)
          .map((k) => ({ id: k.slice(prefix.length + 1), name: (seed[k] && seed[k].name) || k, active: !(seed[k] && seed[k].active === false) }));
      };
      const mergeCatalog = (live, prefix) => {
        const map = {};
        catalogFromSeed(prefix).forEach((x) => { map[x.id] = x; });
        (live || []).forEach((x) => { if (x && x.id) map[x.id] = { id: x.id, name: x.name || x.id, active: x.active !== false }; });
        return Object.keys(map).map((id) => map[id])
          .filter((x) => x.active !== false)
          .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
      };
      const svcCatalog = mergeCatalog(cache.services, 'services');
      const grpCatalog = mergeCatalog(cache.resourceGroups, 'resource_groups');
      root.querySelectorAll('[data-sm-service-select]').forEach((sel) => {
        const current = sel.value;
        sel.innerHTML = '<option value="">Select a service…</option>' +
          svcCatalog.map((s) => '<option value="' + s.id + '">' + (s.name || s.id) + '</option>').join('');
        if (current) sel.value = current;
      });
      root.querySelectorAll('[data-sm-group-select]').forEach((sel) => {
        const current = sel.value;
        sel.innerHTML = '<option value="">Select a group…</option>' +
          grpCatalog.map((g) => '<option value="' + g.id + '">' + (g.name || g.id) + '</option>').join('');
        if (current) sel.value = current;
      });

      // Bookings tab — product + style filters (unique values across services)
      const productFilter = root.querySelector('[data-sm-admin-product]');
      if (productFilter) {
        const current = productFilter.value;
        const products = Array.from(new Set(cache.services.map((s) => s.productKey).filter(Boolean))).sort();
        productFilter.innerHTML = '<option value="">All products</option>' +
          products.map((p) => '<option value="' + p + '">' + p + '</option>').join('');
        if (current) productFilter.value = current;
      }
      const styleFilter = root.querySelector('[data-sm-admin-style]');
      if (styleFilter) {
        const current = styleFilter.value;
        const used = new Set(cache.services.map((s) => s.styleKey).filter(Boolean));
        styleFilter.innerHTML = '<option value="">All styles</option>' +
          SM_STYLE_OPTIONS.filter((o) => used.has(o.value)).map((o) => '<option value="' + o.value + '">' + o.label + '</option>').join('');
        if (current) styleFilter.value = current;
      }
    }

    /* ---------- BOOKINGS TAB ---------- */
    const rowsEl = root.querySelector('[data-sm-admin-rows]');
    const locationFilter = root.querySelector('[data-sm-admin-location]');
    const serviceFilter  = root.querySelector('[data-sm-admin-service]');
    const productFilter  = root.querySelector('[data-sm-admin-product]');
    const styleFilter    = root.querySelector('[data-sm-admin-style]');
    const statusFilter   = root.querySelector('[data-sm-admin-status]');
    const refreshBtn     = root.querySelector('[data-sm-admin-refresh]');

    const listWrap     = root.querySelector('[data-sm-bookings-list]');
    const calWrap      = root.querySelector('[data-sm-bookings-calendar]');
    const sortBtn      = root.querySelector('[data-sm-bookings-sort]');
    const viewBtns     = root.querySelectorAll('[data-sm-bookings-view]');
    const calGrid      = root.querySelector('[data-sm-cal-grid]');
    const calTitle     = root.querySelector('[data-sm-cal-title]');
    const calPrev      = root.querySelector('[data-sm-cal-prev]');
    const calNext      = root.querySelector('[data-sm-cal-next]');
    const calTodayBtn  = root.querySelector('[data-sm-cal-today]');

    const searchInput  = root.querySelector('[data-sm-bookings-search]');

    // Bookings tab state
    let bookingsCache = [];
    let bookingsView  = 'list';
    let sortNewestFirst = true;
    let bookingsSearch = '';
    const calCursor = new Date(); calCursor.setDate(1);

    [locationFilter, serviceFilter, productFilter, styleFilter, statusFilter].forEach((el) => el && el.addEventListener('change', loadBookings));
    refreshBtn && refreshBtn.addEventListener('click', loadBookings);
    // Live text search — filters the loaded bookings by name / email / order #
    // (and phone) as you type. Purely client-side, so it's instant.
    searchInput && searchInput.addEventListener('input', () => {
      bookingsSearch = searchInput.value || '';
      renderBookings();
    });
    // Manually add a booking (e.g. phone-in registrations).
    const addBookingBtn = root.querySelector('[data-sm-booking-add]');
    addBookingBtn && addBookingBtn.addEventListener('click', () => openBookingModal(null, null));

    viewBtns.forEach((b) => b.addEventListener('click', () => {
      bookingsView = b.dataset.smBookingsView;
      viewBtns.forEach((x) => x.classList.toggle('is-active', x === b));
      if (listWrap) listWrap.hidden = (bookingsView !== 'list');
      if (calWrap)  calWrap.hidden  = (bookingsView !== 'calendar');
      renderBookings();
    }));
    sortBtn && sortBtn.addEventListener('click', () => {
      sortNewestFirst = !sortNewestFirst;
      sortBtn.textContent = sortNewestFirst ? 'Date ↓ (newest)' : 'Date ↑ (oldest)';
      renderBookings();
    });
    calPrev && calPrev.addEventListener('click', () => { calCursor.setMonth(calCursor.getMonth() - 1); renderCalendar(); });
    calNext && calNext.addEventListener('click', () => { calCursor.setMonth(calCursor.getMonth() + 1); renderCalendar(); });
    calTodayBtn && calTodayBtn.addEventListener('click', () => {
      const t = new Date();
      calCursor.setFullYear(t.getFullYear());
      calCursor.setMonth(t.getMonth());
      calCursor.setDate(1);
      renderCalendar();
    });

    function fmtWhen(iso) {
      if (!iso) return '—';
      try { const d = new Date(iso); return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) + '<br><span style="color:var(--sm-ink-muted);font-size:12px">' + d.toLocaleTimeString(undefined, { hour:'numeric', minute:'2-digit' }) + '</span>'; } catch(e) { return iso; }
    }
    function fmtDate(isoOrDate) {
      if (!isoOrDate) return '—';
      try { const d = new Date(isoOrDate); return d.toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' }); } catch(e) { return isoOrDate; }
    }

    /* ----- Booking field accessors + helpers ----- */
    function bkName(r)  {
      const c = (r.customer && typeof r.customer === 'object') ? r.customer : {};
      const nested = ((c.firstName || '') + ' ' + (c.lastName || '')).trim();
      const top    = ((r.firstName || '') + ' ' + (r.lastName || '')).trim();
      return nested || top || (typeof r.customer === 'string' ? r.customer : '') || r.name || '—';
    }
    function bkEmail(r) { return (r.customer && r.customer.email) || r.email || ''; }
    function bkPhone(r) { return (r.customer && r.customer.phone) || r.phone || ''; }
    function bkProduct(r) { return r.serviceName || r.service || r.serviceId || '—'; }
    function bkProductShort(r) {
      const p = bkProduct(r);
      if (!p || p === '—') return '';
      return String(p).split('—')[0].trim();
    }
    // The course (BLS / ACLS / PALS / bundle), parsed from the service id
    // (e.g. "bls-self-guided-stat-lake-city" → "BLS"). Falls back to the
    // service name when the id doesn't match a known course prefix.
    function bkCourse(r) {
      const id = String(r.service || r.serviceId || '').toLowerCase().replace(/^stat-/, '');
      const s = (p) => id.indexOf(p) === 0;
      if (s('bls-acls-pals')) return 'BLS + ACLS + PALS';
      if (s('bls-acls')) return 'BLS + ACLS';
      if (s('bls-pals')) return 'BLS + PALS';
      if (s('acls-pals')) return 'ACLS + PALS';
      if (s('bls')) return 'BLS';
      if (s('acls')) return 'ACLS';
      if (s('pals')) return 'PALS';
      if (s('heartsaver-pediatric')) return 'Heartsaver Pediatric';
      if (s('heartsaver')) return 'Heartsaver';
      if (s('cna')) return 'CNA';
      if (s('iv')) return 'IV Certification';
      if (s('background') || s('fingerprint')) return 'Background Check';
      if (s('apd')) return 'APD Medication';
      return r.serviceName || r.service || '—';
    }
    function bkHasProof(r) { return !!((r.proofFiles && r.proofFiles.length) || r.proofPath || r.proofUrl); }
    // Small at-a-glance badge for whether a booking's part-1 certificate has
    // been reviewed and is valid. Blank when the booking has no proof.
    function proofBadge(r) {
      if (!bkHasProof(r)) return '';
      if (r.proofValid === true)  return '<span class="sm-cert sm-cert--ok" title="Part 1 certificate marked valid">Cert ✓</span>';
      if (r.proofValid === false) return '<span class="sm-cert sm-cert--no" title="Part 1 certificate marked NOT valid">Cert ✗</span>';
      return '<span class="sm-cert sm-cert--pending" title="Part 1 certificate not yet reviewed">Cert ?</span>';
    }
    function bkLocation(r){ return r.locationName || r.location || '—'; }
    function bkOrder(r)  { return r.shopifyOrderId || r.orderName || '—'; }
    function bkStartTime(r) {
      if (r.time) return String(r.time).split(/[–-]/)[0].trim();
      if (r.startsAt) { try { return new Date(r.startsAt).toLocaleTimeString(undefined, { hour:'numeric', minute:'2-digit' }); } catch(e){} }
      return '';
    }
    function bkDateStr(r) {
      if (r.date) return r.date;
      if (r.startsAt) { const d = new Date(r.startsAt); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'); }
      return '';
    }
    function bkTimestamp(r) {
      const ds = bkDateStr(r);
      if (!ds) return r.startsAt ? new Date(r.startsAt).getTime() : 0;
      const m = String(r.time || '').match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      let mins = 0;
      if (m) { let h = +m[1] % 12; if (m[3] && /pm/i.test(m[3])) h += 12; mins = h * 60 + (+m[2]); }
      return new Date(ds + 'T00:00:00').getTime() + mins * 60000;
    }
    function isCprBooking(r) {
      const hay = ((bkProduct(r)) + ' ' + (r.service || '') + ' ' + (r.serviceId || '')).toLowerCase();
      return /\b(bls|acls|pals|cpr|heartsaver|heart\s*saver)\b/.test(hay);
    }
    function fmtBkDate(r) {
      const ds = bkDateStr(r);
      if (!ds) return '—';
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(ds) ? (ds + 'T12:00:00') : ds;
      try { return new Date(iso).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' }); }
      catch (e) { return ds; }
    }

    async function loadBookings() {
      if (!rowsEl) return;
      rowsEl.innerHTML = '<tr><td colspan="12" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      let rows;
      try {
        rows = await ns.fb.adminListBookings({});
      } catch (e) {
        console.error('[admin] bookings failed', e);
        rowsEl.innerHTML = '<tr><td colspan="12" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load bookings. ' + (e.message || '') + '</td></tr>';
        return;
      }
      // Location filter — permissive matching so we tolerate older bookings
      // that stored the display name in `location` instead of the doc id.
      const locVal = locationFilter && locationFilter.value;
      if (locVal) {
        const locDoc = (cache.locations || []).find((l) => l.id === locVal) || {};
        const locNameLc = String(locDoc.name || '').toLowerCase().trim();
        rows = rows.filter((r) => {
          const rLoc     = String(r.location     || '').toLowerCase().trim();
          const rLocId   = String(r.locationId   || '').toLowerCase().trim();
          const rLocName = String(r.locationName || '').toLowerCase().trim();
          const target   = locVal.toLowerCase().trim();
          return rLoc === target
              || rLocId === target
              || (!!locNameLc && (rLoc === locNameLc || rLocName === locNameLc));
        });
      }
      // Service filter — same permissive matching.
      const svcVal = serviceFilter && serviceFilter.value;
      if (svcVal) {
        const svcLc = svcVal.toLowerCase().trim();
        rows = rows.filter((r) => {
          const rSvc  = String(r.service   || '').toLowerCase().trim();
          const rSvcId = String(r.serviceId || '').toLowerCase().trim();
          return rSvc === svcLc || rSvcId === svcLc;
        });
      }
      const statusVal = statusFilter && statusFilter.value;
      if (statusVal) rows = rows.filter((r) => r.status === statusVal);

      // Product / style filters — look up each booking's service to read those fields.
      const productVal = productFilter && productFilter.value;
      const styleVal   = styleFilter && styleFilter.value;
      if (productVal || styleVal) {
        const svcById = cache.services.reduce((m, s) => { m[s.id] = s; return m; }, {});
        rows = rows.filter((r) => {
          const svc = svcById[r.service || r.serviceId] || {};
          if (productVal && svc.productKey !== productVal) return false;
          if (styleVal && svc.styleKey !== styleVal) return false;
          return true;
        });
      }
      bookingsCache = rows;
      renderBookings();
    }

    // Bookings passing the current text search (name / email / order # / phone).
    // Every whitespace-separated term must appear, so "john gmail" narrows further.
    function bookingMatchesSearch(r, terms) {
      if (!terms.length) return true;
      const hay = (bkName(r) + ' ' + bkEmail(r) + ' ' + bkPhone(r) + ' ' + bkOrder(r)).toLowerCase();
      return terms.every((t) => hay.indexOf(t) !== -1);
    }
    function visibleBookings() {
      const terms = bookingsSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
      return terms.length ? bookingsCache.filter((r) => bookingMatchesSearch(r, terms)) : bookingsCache;
    }

    function renderBookings() {
      if (bookingsView === 'calendar') renderCalendar();
      else renderList();
    }

    function renderList() {
      if (!rowsEl) return;
      const rows = visibleBookings().slice().sort((a, b) => sortNewestFirst ? (bkTimestamp(b) - bkTimestamp(a)) : (bkTimestamp(a) - bkTimestamp(b)));
      if (!rows.length) {
        rowsEl.innerHTML = '<tr><td colspan="12" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No bookings match.</td></tr>';
        return;
      }
      rowsEl.innerHTML = rows.map((r) => {
        const status = r.status || 'pending';
        const cpr = isCprBooking(r);
        const rqiCell = '<input type="checkbox" data-sm-rqi="' + r.id + '"' + (r.addedToRqi ? ' checked' : '') + '>';
        const kioskCell = cpr
          ? '<input type="checkbox" data-sm-cprkiosk="' + r.id + '"' + (r.addedToCprKiosk ? ' checked' : '') + '>'
          : '<span style="color:var(--sm-ink-muted)">—</span>';
        return '<tr data-booking-id="' + r.id + '">' +
          '<td><strong>' + bkName(r) + '</strong></td>' +
          '<td>' + (bkEmail(r) || '—') + '</td>' +
          '<td style="white-space:nowrap">' + (bkPhone(r) || '—') + '</td>' +
          '<td><strong>' + esc(bkCourse(r)) + '</strong>' + (r.serviceName ? '<br><span style="color:var(--sm-ink-muted);font-size:12px">' + esc(r.serviceName) + '</span>' : '') + (proofBadge(r) ? '<br>' + proofBadge(r) : '') + '</td>' +
          '<td>' + bkLocation(r) + '</td>' +
          '<td style="white-space:nowrap">' + fmtBkDate(r) + '</td>' +
          '<td style="white-space:nowrap">' + (bkStartTime(r) || '—') + '</td>' +
          '<td>' + bkOrder(r) + '</td>' +
          '<td style="text-align:center">' + rqiCell + '</td>' +
          '<td style="text-align:center">' + kioskCell + '</td>' +
          '<td><span class="sm-table__status sm-status--' + status + '">' + status + '</span></td>' +
          '<td style="white-space:nowrap"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-booking-edit="' + r.id + '">Edit</button></td>' +
        '</tr>';
      }).join('');
    }

    function renderCalendar() {
      if (!calGrid) return;
      const year = calCursor.getFullYear();
      const month = calCursor.getMonth();
      if (calTitle) calTitle.textContent = calCursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      const byDay = {};
      visibleBookings().forEach((r) => {
        const k = bkDateStr(r);
        if (!k) return;
        (byDay[k] = byDay[k] || []).push(r);
      });
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      // Weekday header row, columns 1–7 placed explicitly so it can never
      // be shifted by an off-by-one elsewhere.
      let cells = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
        .map((w, i) => '<div class="sm-cal__cell sm-cal__cell--head" style="grid-row:1;grid-column:' + (i + 1) + '">' + w + '</div>')
        .join('');
      // Each day cell anchors itself to its OWN day-of-week column. Even if
      // the first-day calculation drifted we'd still land in the right column.
      // Show every booking on a day inline — the cell grows to fit. The CSS
      // uses min-height so empty days stay compact while busy days expand.
      for (let day = 1; day <= daysInMonth; day++) {
        const dayDate = new Date(year, month, day);
        const dow = dayDate.getDay(); // 0=Sun .. 6=Sat
        const col = dow + 1; // 1-indexed grid column
        const key = year + '-' + String(month + 1).padStart(2, '0') + '-' + String(day).padStart(2, '0');
        const events = (byDay[key] || []).sort((a, b) => bkTimestamp(a) - bkTimestamp(b));
        const evHtml = events.map((r) => {
          const st = bkStartTime(r);
          const loc = bkLocation(r); const prod = bkCourse(r);
          return '<button type="button" class="sm-cal__event sm-cal__event--' + (r.status || 'pending') + '" data-sm-cal-event="' + r.id + '">' +
            '<span class="sm-cal__event-time">' + (st || '—') + '</span> ' + bkName(r) +
            (loc && loc !== '—' ? '<span class="sm-cal__event-sub">' + loc + '</span>' : '') +
            (prod ? '<span class="sm-cal__event-sub">' + prod + (proofBadge(r) ? ' ' + proofBadge(r) : '') + '</span>' : '') +
          '</button>';
        }).join('');
        cells += '<div class="sm-cal__cell" data-date="' + key + '" style="grid-column:' + col + '"><span class="sm-cal__daynum">' + day + '</span>' + evHtml + '</div>';
      }
      calGrid.innerHTML = cells;
    }

    // Inline RQI / CPR-Kiosk toggles
    rowsEl && rowsEl.addEventListener('change', async (e) => {
      const rqi = e.target.closest('[data-sm-rqi]');
      const kiosk = e.target.closest('[data-sm-cprkiosk]');
      const el = rqi || kiosk;
      if (!el) return;
      const id = el.dataset.smRqi || el.dataset.smCprkiosk;
      const field = rqi ? 'addedToRqi' : 'addedToCprKiosk';
      el.disabled = true;
      try {
        await ns.fb.adminUpdateBooking(id, { [field]: el.checked });
        const b = bookingsCache.find((x) => x.id === id);
        if (b) b[field] = el.checked;
      } catch (err) {
        console.error('[admin] toggle ' + field + ' failed', err);
        el.checked = !el.checked;
        alert('Could not save. ' + (err.message || ''));
      } finally {
        el.disabled = false;
      }
    });

    rowsEl && rowsEl.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-sm-booking-edit]');
      if (!btn) return;
      openBookingModal(btn.dataset.smBookingEdit, btn.closest('tr'));
    });
    calGrid && calGrid.addEventListener('click', (e) => {
      const moreBtn = e.target.closest('[data-sm-cal-more]');
      if (moreBtn) {
        openDayBookingsModal(moreBtn.dataset.smCalMore);
        return;
      }
      const ev = e.target.closest('[data-sm-cal-event]');
      if (!ev) return;
      openBookingModal(ev.dataset.smCalEvent, null);
    });

    // Lists every booking on a given day. Opened from the "+N more" cells.
    function openDayBookingsModal(dateKey) {
      if (!modal || !modalBody) return;
      const rows = bookingsCache
        .filter((r) => bkDateStr(r) === dateKey)
        .sort((a, b) => bkTimestamp(a) - bkTimestamp(b));
      const dateLabel = (() => {
        const iso = /^\d{4}-\d{2}-\d{2}$/.test(dateKey) ? dateKey + 'T12:00:00' : dateKey;
        try { return new Date(iso).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); }
        catch (e) { return dateKey; }
      })();
      modalTitle && (modalTitle.textContent = dateLabel);
      if (!rows.length) {
        modalBody.innerHTML = '<p>No bookings on this day.</p>';
        modal.hidden = false;
        return;
      }
      modalBody.innerHTML = '<div style="overflow:auto;max-height:60vh"><table class="sm-table" style="font-size:13px">' +
        '<thead><tr><th>Time</th><th>Customer</th><th>Product</th><th>Location</th><th>Status</th></tr></thead>' +
        '<tbody>' + rows.map((r) => {
          const st = bkStartTime(r);
          return '<tr style="cursor:pointer" data-sm-day-row="' + r.id + '">' +
            '<td style="white-space:nowrap;font-weight:600">' + esc(st || '—') + '</td>' +
            '<td>' + esc(bkName(r)) + '</td>' +
            '<td><strong>' + esc(bkCourse(r)) + '</strong>' + (r.serviceName ? '<br><span style="color:var(--sm-ink-muted);font-size:12px">' + esc(r.serviceName) + '</span>' : '') + (proofBadge(r) ? '<br>' + proofBadge(r) : '') + '</td>' +
            '<td>' + esc(bkLocation(r)) + '</td>' +
            '<td><span class="sm-table__status sm-status--' + (r.status || 'pending') + '">' + (r.status || 'pending') + '</span></td>' +
          '</tr>';
        }).join('') + '</tbody></table></div>';
      modal.hidden = false;
      // Click any row to open the booking edit modal.
      modalBody.querySelectorAll('[data-sm-day-row]').forEach((tr) => {
        tr.addEventListener('click', () => openBookingModal(tr.dataset.smDayRow, null));
      });
    }

    /* ---------- BOOKING EDIT MODAL ---------- */
    const modal     = document.querySelector('[data-sm-admin-modal]');
    const modalBody = document.querySelector('[data-sm-admin-modal-body]');
    const modalTitle= document.querySelector('[data-sm-admin-modal-title]');
    const modalClose= document.querySelector('[data-sm-admin-modal-close]');
    function closeModal() { if (modal) modal.hidden = true; }
    modalClose && modalClose.addEventListener('click', closeModal);
    modal && modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

    function esc(v) { return String(v == null ? '' : v).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;'); }

    function openBookingModal(id, tr) {
      if (!modal || !modalBody) return;
      const isNew = !id;
      const rec = isNew ? {} : (bookingsCache.find((x) => x.id === id) || {});
      modalTitle && (modalTitle.textContent = isNew ? 'Add appointment' : 'Edit appointment');
      const fn = (rec.customer && rec.customer.firstName) || rec.firstName || '';
      const ln = (rec.customer && rec.customer.lastName) || rec.lastName || '';
      const statusOpts = ['pending','confirmed','needs_review','cancelled','completed','no-show'];
      const cpr = isCprBooking(rec);
      const rgOpts = '<option value="">— none —</option>' +
        (cache.resourceGroups || []).map((g) =>
          '<option value="' + g.id + '"' + ((rec.resourceGroup || '') === g.id ? ' selected' : '') + '>' + esc(g.name || g.id) + '</option>'
        ).join('');
      // Location + course/format dropdowns built from the real lookup lists.
      // A value that doesn't match any option (legacy/custom) is kept as a
      // "(current)" option so editing never silently drops it.
      const curLocName = rec.locationName || rec.location || '';
      const locKnown = (cache.locations || []).some((l) => (l.name || l.id) === curLocName);
      const locOpts = '<option value="">— select location —</option>' +
        (cache.locations || []).map((l) => { const nm = l.name || l.id;
          return '<option value="' + esc(nm) + '" data-loc-id="' + esc(l.id) + '"' + (nm === curLocName ? ' selected' : '') + '>' + esc(nm) + '</option>'; }).join('') +
        ((curLocName && !locKnown) ? '<option value="' + esc(curLocName) + '" selected>' + esc(curLocName) + ' (current)</option>' : '');
      const curSvcName = rec.serviceName || rec.service || '';
      const svcKnown = (cache.services || []).some((s) => (s.name || s.id) === curSvcName);
      const svcOpts = '<option value="">— select course/format —</option>' +
        (cache.services || []).map((s) => { const nm = s.name || s.id;
          return '<option value="' + esc(nm) + '" data-svc-id="' + esc(s.id) + '"' + (nm === curSvcName ? ' selected' : '') + '>' + esc(nm) + '</option>'; }).join('') +
        ((curSvcName && !svcKnown) ? '<option value="' + esc(curSvcName) + '" selected>' + esc(curSvcName) + ' (current)</option>' : '');
      // Published slot times for a (service, location) — mirrors the public
      // booking form's priority so admin times match exactly and block right.
      function slotTimesFor(svcId, locId) {
        const svc = (cache.services || []).find((s) => s.id === svcId) || null;
        const loc = (cache.locations || []).find((l) => l.id === locId) || null;
        const byLoc = svc && svc.slotsByLocation && locId && svc.slotsByLocation[locId];
        if (Array.isArray(byLoc) && byLoc.length) return byLoc;
        if (loc && Array.isArray(loc.slots) && loc.slots.length) return loc.slots;
        if (cache.schedule && Array.isArray(cache.schedule.defaultSlots) && cache.schedule.defaultSlots.length) return cache.schedule.defaultSlots;
        return ['9:00 AM','11:00 AM','1:00 PM','3:00 PM'];
      }
      function timeOptsHtml(svcId, locId, current) {
        const times = slotTimesFor(svcId, locId);
        let html = '<option value="">— select time —</option>' +
          times.map((t) => '<option value="' + esc(t) + '"' + (t === current ? ' selected' : '') + '>' + esc(t) + '</option>').join('');
        if (current && times.indexOf(current) === -1) html += '<option value="' + esc(current) + '" selected>' + esc(current) + ' (current)</option>';
        return html;
      }
      const svcIdInit = rec.service || (((cache.services || []).find((s) => (s.name || s.id) === curSvcName) || {}).id) || '';
      const locIdInit = rec.location || (((cache.locations || []).find((l) => (l.name || l.id) === curLocName) || {}).id) || '';
      const timeOpts = timeOptsHtml(svcIdInit, locIdInit, rec.time || '');
      // Proof(s): new bookings store a proofFiles array; older ones a single
      // proofPath/proofUrl — normalize to a list.
      const proofList = (rec.proofFiles && rec.proofFiles.length) ? rec.proofFiles
        : ((rec.proofPath || rec.proofUrl) ? [{ name: rec.proofFileName || '', path: rec.proofPath || '', url: rec.proofUrl || '' }] : []);
      modalBody.innerHTML =
        '<form class="sm-form" data-sm-booking-form>' +
          '<div class="sm-form__row">' +
            '<div class="sm-field"><label>First name</label><input type="text" name="firstName" value="' + esc(fn) + '"></div>' +
            '<div class="sm-field"><label>Last name</label><input type="text" name="lastName" value="' + esc(ln) + '"></div>' +
          '</div>' +
          '<div class="sm-form__row">' +
            '<div class="sm-field"><label>Email</label><input type="email" name="email" value="' + esc(bkEmail(rec)) + '"></div>' +
            '<div class="sm-field"><label>Phone</label><input type="tel" name="phone" value="' + esc(bkPhone(rec)) + '"></div>' +
          '</div>' +
          (isNew ? '' : '<div class="sm-field"><label>Course</label><div class="sm-book__note" style="margin:0"><strong>' + esc(bkCourse(rec)) + '</strong>' + (rec.serviceName ? ' &middot; ' + esc(rec.serviceName) + ' format' : '') + '</div></div>') +
          '<div class="sm-form__row">' +
            '<div class="sm-field"><label>Course / format</label><select name="serviceName">' + svcOpts + '</select></div>' +
            '<div class="sm-field"><label>Location</label><select name="locationName">' + locOpts + '</select></div>' +
          '</div>' +
          '<div class="sm-form__row">' +
            '<div class="sm-field"><label>Date</label><input type="date" name="date" value="' + esc(bkDateStr(rec)) + '"></div>' +
            '<div class="sm-field"><label>Start time</label><select name="time" data-sm-booking-time>' + timeOpts + '</select></div>' +
          '</div>' +
          '<div class="sm-form__row">' +
            '<div class="sm-field"><label>Status</label><select name="status">' +
              statusOpts.map((s) => '<option value="' + s + '"' + ((rec.status||'pending')===s?' selected':'') + '>' + s + '</option>').join('') +
            '</select></div>' +
            '<div class="sm-field"><label>Shopify order #</label><input type="text" name="shopifyOrderId" value="' + esc(rec.shopifyOrderId || rec.orderName || '') + '"></div>' +
          '</div>' +
          '<div class="sm-form__row">' +
            '<div class="sm-field"><label>Resource group</label><select name="resourceGroup">' + rgOpts + '</select><div class="sm-field__help">Which instructor/room this booking uses (controls slot capacity sharing).</div></div>' +
            '<div class="sm-field"></div>' +
          '</div>' +
          '<div class="sm-flex sm-gap-16" style="flex-wrap:wrap;margin:4px 0">' +
            '<label class="sm-book__chk"><input type="checkbox" name="addedToRqi"' + (rec.addedToRqi ? ' checked' : '') + '><span>Added to RQI</span></label>' +
            (cpr ? '<label class="sm-book__chk"><input type="checkbox" name="addedToCprKiosk"' + (rec.addedToCprKiosk ? ' checked' : '') + '><span>Added to CPR Kiosk</span></label>' : '') +
          '</div>' +
          (proofList.length ?
            '<div class="sm-field"><label>Proof of completion' + (proofList.length > 1 ? ' (' + proofList.length + ' files)' : '') + '</label>' +
              proofList.map((f) => '<div data-sm-proof-view data-proof-url="' + esc(f.url || '') + '" data-proof-path="' + esc(f.path || '') + '" data-proof-name="' + esc(f.name || '') + '" style="margin-bottom:10px">' +
                '<span class="sm-field__help">Loading proof…</span></div>').join('') +
              '<label style="margin-top:6px">Is the part one certificate valid?</label>' +
              '<div class="sm-flex sm-gap-12" data-proof-valid-wrap style="margin-top:2px">' +
                '<button type="button" class="sm-btn sm-btn--sm ' + (rec.proofValid === true ? 'sm-btn--primary' : 'sm-btn--light') + '" data-proof-valid="yes">✓ Yes, valid</button>' +
                '<button type="button" class="sm-btn sm-btn--sm ' + (rec.proofValid === false ? 'sm-btn--primary' : 'sm-btn--light') + '" data-proof-valid="no">✗ No</button>' +
              '</div>' +
              '<input type="hidden" name="proofValid" value="' + (rec.proofValid === true ? 'yes' : (rec.proofValid === false ? 'no' : '')) + '">' +
            '</div>'
          : '') +
          '<div class="sm-form__row">' +
            '<div class="sm-field"><label>IV subscription ID</label><input name="subscriptionId" value="' + esc(rec.subscriptionId || '') + '" placeholder="e.g. SUB-10482"></div>' +
            '<div class="sm-field"><label>Access valid through</label><input type="date" name="accessValidThrough" value="' + esc(rec.accessValidThrough || '') + '"></div>' +
          '</div>' +
          '<div class="sm-field"><label>Internal notes</label><textarea name="notes" rows="3">' + esc(rec.notes || '') + '</textarea></div>' +
          '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:8px">' +
            '<button type="submit" class="sm-btn sm-btn--primary">' + (isNew ? 'Add appointment' : 'Save changes') + '</button>' +
            (isNew ? '' : '<button type="button" class="sm-btn sm-btn--light" data-sm-send-iv-email>✉ Send IV access email</button>') +
            (isNew ? '' : '<button type="button" class="sm-btn sm-btn--light" data-sm-booking-cancel>Cancel booking</button>') +
            (isNew ? '' : '<button type="button" class="sm-btn sm-btn--light" data-sm-booking-delete style="color:var(--sm-danger)">Delete</button>') +
            '<span class="sm-field__help" data-sm-booking-msg></span>' +
          '</div>' +
        '</form>';
      modal.hidden = false;
      const form = modalBody.querySelector('[data-sm-booking-form]');
      const msg  = modalBody.querySelector('[data-sm-booking-msg]');

      // Keep the Start-time options in sync with the chosen service+location
      // so they match the customer calendar exactly (and block the right slot).
      const timeSel = form.querySelector('[data-sm-booking-time]');
      function refreshTimeOptions() {
        if (!timeSel) return;
        const so = form.serviceName.options[form.serviceName.selectedIndex];
        const lo = form.locationName.options[form.locationName.selectedIndex];
        timeSel.innerHTML = timeOptsHtml((so && so.dataset.svcId) || '', (lo && lo.dataset.locId) || '', timeSel.value);
      }
      form.serviceName && form.serviceName.addEventListener('change', refreshTimeOptions);
      form.locationName && form.locationName.addEventListener('change', refreshTimeOptions);

      // Resolve the proof image. Customer uploads store only the path (they
      // can't read proofs/), so staff generate the download URL here.
      modalBody.querySelectorAll('[data-sm-proof-view]').forEach((proofView) => {
        (async () => {
          let url = proofView.dataset.proofUrl;
          const path = proofView.dataset.proofPath;
          const name = proofView.dataset.proofName;
          if (!url && path && ns.fb.getProofUrl) { url = await ns.fb.getProofUrl(path); }
          if (!url) { proofView.innerHTML = '<span class="sm-field__help">Proof was uploaded' + (name ? ' (' + esc(name) + ')' : '') + ', but the preview could not load.</span>'; return; }
          const isPdf = /\.pdf(\?|$)/i.test(name || url);
          proofView.innerHTML = isPdf
            ? '<a href="' + esc(url) + '" target="_blank" rel="noopener" class="sm-btn sm-btn--light sm-btn--sm" style="align-self:flex-start">Open PDF' + (name ? ' (' + esc(name) + ')' : '') + ' ↗</a>'
            : '<a href="' + esc(url) + '" target="_blank" rel="noopener" style="display:inline-block;text-decoration:none">' +
                '<img src="' + esc(url) + '" alt="Proof of completion" loading="lazy" style="max-width:280px;max-height:230px;border-radius:10px;border:1px solid var(--sm-border-soft);display:block">' +
                '<span class="sm-field__help" style="display:block;margin-top:4px">Click to open full size' + (name ? ' · ' + esc(name) : '') + ' ↗</span>' +
              '</a>';
        })();
      });

      // Yes/No toggle for "is the part one certificate valid?".
      const validWrap = modalBody.querySelector('[data-proof-valid-wrap]');
      if (validWrap) {
        validWrap.addEventListener('click', (e) => {
          const b = e.target.closest('[data-proof-valid]');
          if (!b) return;
          if (form.proofValid) form.proofValid.value = b.dataset.proofValid;
          validWrap.querySelectorAll('[data-proof-valid]').forEach((x) => {
            const on = x === b;
            x.classList.toggle('sm-btn--primary', on);
            x.classList.toggle('sm-btn--light', !on);
          });
          // Marking the cert invalid nudges the appointment to Needs review
          // (staff can still change it before saving).
          if (b.dataset.proofValid === 'no' && form.status && form.status.value !== 'cancelled' && form.status.value !== 'completed') {
            form.status.value = 'needs_review';
          }
        });
      }

      // Recount the related cna/iv class's seatsBooked after any status
      // change so the customer-facing "X seats open" stays accurate.
      // No-op if the booking isn't class-linked.
      async function recountIfClassLinked() {
        const cid = rec.classId;
        if (!cid || !ns.fb || typeof ns.fb.adminRecountClassSeats !== 'function') return;
        try { await ns.fb.adminRecountClassSeats(cid); } catch (e) { console.warn('[admin] recount failed', e); }
      }
      // Free the calendar seat this booking holds (if any). Guarded so a seat
      // is only released once — skips already-cancelled / already-released.
      async function releaseSeatIfHeld() {
        const cid = rec.counterId;
        const t = rec.counterTime || rec.time;
        if (!cid || !t || rec.slotReleased || rec.status === 'cancelled') return;
        if (ns.fb && typeof ns.fb.adminReleaseSlotSeat === 'function') {
          // Unpaid holds (CPR/fingerprint 'held', B2B 'pending' with a hold) only
          // own a holds{} entry — release that, never a paid confirmed seat.
          const isHold = rec.status === 'held' || (rec.status === 'pending' && rec.holdExpiresAt) || rec.seatTaken === 'hold';
          const arg = isHold ? { counterId: cid, time: t, bookingId: rec.id, status: 'held' } : { counterId: cid, time: t };
          try { await ns.fb.adminReleaseSlotSeat(arg); } catch (e) { console.warn('[admin] slot release failed', e); }
        }
      }

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const email = form.email.value.trim();
          const phone = form.phone.value.trim();
          const update = {
            customer: Object.assign({}, (rec.customer && typeof rec.customer === 'object') ? rec.customer : {}, {
              firstName: form.firstName.value.trim(),
              lastName: form.lastName.value.trim(),
              email: email,
              phone: phone
            }),
            email: email,
            phone: phone,
            serviceName: form.serviceName.value.trim(),
            locationName: form.locationName.value.trim(),
            date: form.date.value,
            time: form.time.value.trim(),
            status: form.status.value,
            shopifyOrderId: form.shopifyOrderId.value.trim(),
            notes: form.notes.value.trim(),
            subscriptionId: form.subscriptionId ? form.subscriptionId.value.trim() : (rec.subscriptionId || ''),
            accessValidThrough: form.accessValidThrough ? form.accessValidThrough.value : (rec.accessValidThrough || ''),
            addedToRqi: !!(form.addedToRqi && form.addedToRqi.checked)
          };
          if (form.resourceGroup) update.resourceGroup = form.resourceGroup.value;
          if (form.addedToCprKiosk) update.addedToCprKiosk = !!form.addedToCprKiosk.checked;
          if (form.proofValid) update.proofValid = form.proofValid.value === 'yes' ? true : (form.proofValid.value === 'no' ? false : null);
          // Carry the underlying location/service ids from the dropdowns so
          // manual bookings match the filters (only when a real option is picked).
          const locOpt = form.locationName && form.locationName.options[form.locationName.selectedIndex];
          if (locOpt && locOpt.dataset.locId) update.location = locOpt.dataset.locId;
          const svcOpt = form.serviceName && form.serviceName.options[form.serviceName.selectedIndex];
          if (svcOpt && svcOpt.dataset.svcId) update.service = svcOpt.dataset.svcId;
          if (isNew) {
            if (!update.customer.firstName && !update.customer.lastName) { msg.textContent = 'Enter at least a first or last name.'; return; }
            // Block a seat on the customer calendar for timed slot services.
            let counter = null;
            try {
              const svcId = (svcOpt && svcOpt.dataset.svcId) || '';
              const svc = (cache.services || []).find((s) => s.id === svcId) || null;
              if (svc && svc.blockMode === 'slot' && update.date && update.time && update.status !== 'cancelled'
                  && ns.fb && typeof ns.fb.adminReserveSlotSeat === 'function') {
                const grp = svc.resourceGroup || null;
                const grpDoc = grp ? (cache.resourceGroups || []).find((g) => g.id === grp) : null;
                counter = await ns.fb.adminReserveSlotSeat({
                  locationId: update.location || '', serviceId: svcId, resourceGroup: grp,
                  crossLocations: !!(grpDoc && grpDoc.sharedAcrossLocations), date: update.date, time: update.time
                });
              }
            } catch (e) { console.warn('[admin] slot reserve failed', e); }
            if (counter && counter.counterId) { update.counterId = counter.counterId; update.counterTime = counter.time; }
            await ns.fb.adminCreateBooking(update);
            msg.textContent = counter ? '✓ Booking added — seat blocked on the calendar.' : '✓ Booking added.';
          } else {
            // If staff just cancelled the booking, free its held seat.
            if (update.status === 'cancelled' && rec.status !== 'cancelled') {
              await releaseSeatIfHeld();
              update.slotReleased = true;
            }
            // If the appointment date/time changed (reschedule), clear the
            // reminder/review flags so the messenger re-sends for the new time.
            if ((update.date || '') !== (bkDateStr(rec) || '') || (update.time || '') !== (rec.time || '')) {
              update.reminderSentAt = null;
              update.reviewSentAt = null;
            }
            await ns.fb.adminUpdateBooking(id, update);
            await recountIfClassLinked();
            msg.textContent = '✓ Saved.';
          }
          await loadBookings();
          setTimeout(closeModal, 600);
        } catch (err) { msg.textContent = err.message || 'Save failed.'; }
      });
      // Send the IV access email (subscription ID + valid-through date) to this
      // student. Saves the two fields first so they persist on the booking.
      const sendIvBtn = modalBody.querySelector('[data-sm-send-iv-email]');
      sendIvBtn && sendIvBtn.addEventListener('click', async () => {
        const to = form.email.value.trim();
        if (!to) { msg.textContent = 'Add the student’s email address first.'; return; }
        const subId = form.subscriptionId ? form.subscriptionId.value.trim() : '';
        const validThrough = form.accessValidThrough ? form.accessValidThrough.value : '';
        if (!confirm('Send the IV access email to ' + to + '?')) return;
        const prev = sendIvBtn.textContent;
        sendIvBtn.disabled = true; sendIvBtn.textContent = 'Sending…'; msg.textContent = '';
        try {
          if (!isNew) await ns.fb.adminUpdateBooking(id, { subscriptionId: subId, accessValidThrough: validThrough });
          await ns.fb.adminSendStudentEmail({
            type: 'iv_access', bookingId: id || '', to: to,
            firstName: form.firstName.value.trim(), lastName: form.lastName.value.trim(),
            subscriptionId: subId, accessValidThrough: validThrough, service: form.serviceName.value.trim()
          });
          msg.textContent = '✓ IV access email sent to ' + to + '.';
        } catch (e) { msg.textContent = e.message || 'Send failed.'; }
        finally { sendIvBtn.disabled = false; sendIvBtn.textContent = prev; }
      });
      const cancelBtn = modalBody.querySelector('[data-sm-booking-cancel]');
      cancelBtn && cancelBtn.addEventListener('click', async () => {
        if (!confirm('Mark this booking as cancelled?')) return;
        msg.textContent = 'Cancelling…';
        try {
          await releaseSeatIfHeld();
          await ns.fb.adminUpdateBooking(id, { status: 'cancelled', slotReleased: true });
          await recountIfClassLinked();
          msg.textContent = '✓ Cancelled.';
          await loadBookings();
          setTimeout(closeModal, 600);
        } catch (err) { msg.textContent = err.message || 'Cancel failed.'; }
      });
      const deleteBtn = modalBody.querySelector('[data-sm-booking-delete]');
      deleteBtn && deleteBtn.addEventListener('click', async () => {
        if (!confirm('Permanently delete this booking? This cannot be undone.')) return;
        msg.textContent = 'Deleting…';
        try {
          await releaseSeatIfHeld();
          await ns.fb.adminDeleteBooking(id);
          await recountIfClassLinked();
          msg.textContent = '✓ Deleted.';
          await loadBookings();
          setTimeout(closeModal, 400);
        } catch (err) { msg.textContent = err.message || 'Delete failed.'; }
      });
    }

    /* ---------- AVAILABILITY TAB ---------- */
    const blockDateForm  = root.querySelector('[data-sm-blockdate-form]');
    const blockTimeForm  = root.querySelector('[data-sm-blocktime-form]');
    const addClassForm   = root.querySelector('[data-sm-addclass-form]');
    const blockDateMsg   = root.querySelector('[data-sm-blockdate-msg]');
    const blockTimeMsg   = root.querySelector('[data-sm-blocktime-msg]');
    const addClassMsg    = root.querySelector('[data-sm-addclass-msg]');
    const blockDateRows  = root.querySelector('[data-sm-blockdate-rows]');
    const scopeSelect    = blockDateForm && blockDateForm.querySelector('[name="scope"]');
    const scopeServices  = root.querySelector('[data-sm-blockdate-services]');
    const scopeGroups    = root.querySelector('[data-sm-blockdate-groups]');

    function splitCsv(v) {
      return (v || '').split(',').map((s) => s.trim()).filter(Boolean);
    }
    // Resolve a service / resource-group id to its readable name for display.
    // Falls back to the built-in catalog (ns.SEED_DATA) for ids that have no
    // live Firestore doc yet (e.g. background-check), so tables never show raw ids.
    function seedName(prefix, id) {
      const seed = (ns && ns.SEED_DATA) || {};
      const e = seed[prefix + '/' + id];
      return (e && e.name) || id;
    }
    function svcName(id) { return ((cache.services || []).find((s) => s.id === id) || {}).name || seedName('services', id); }
    function grpName(id) { return ((cache.resourceGroups || []).find((g) => g.id === id) || {}).name || seedName('resource_groups', id); }

    // The "which service / which group" fields stay visible; the one that
    // doesn't apply to the chosen scope is grayed out (disabled).
    function setFieldOn(wrap, on) {
      if (!wrap) return;
      wrap.hidden = false;
      wrap.classList.toggle('is-disabled', !on);
      wrap.querySelectorAll('select, input').forEach((el) => { el.disabled = !on; });
    }
    function syncDateScope() {
      const v = scopeSelect ? scopeSelect.value : 'all';
      setFieldOn(scopeServices, v === 'services');
      setFieldOn(scopeGroups, v === 'groups');
    }
    scopeSelect && scopeSelect.addEventListener('change', syncDateScope);
    syncDateScope();

    // Block a date range
    blockDateForm && blockDateForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!blockDateMsg) return;
      blockDateMsg.textContent = 'Saving…';
      try {
        const fd = new FormData(blockDateForm);
        const scope = fd.get('scope');
        await ns.fb.adminAddBlockedDate({
          locationId: fd.get('location'),
          startDate:  fd.get('startDate'),
          endDate:    fd.get('endDate') || fd.get('startDate'),
          services:       scope === 'services' ? splitCsv(fd.get('services'))       : [],
          resourceGroups: scope === 'groups'   ? splitCsv(fd.get('resourceGroups')) : [],
          adminOnly: !!fd.get('adminOnly'),
          reason: fd.get('reason') || ''
        });
        blockDateMsg.textContent = '✓ Blocked date added.';
        blockDateForm.reset();
        syncDateScope();
        await loadBlockedDates();
      } catch (err) {
        console.error('[admin] block date failed', err);
        blockDateMsg.textContent = err.message || 'Save failed.';
      }
    });

    // Block a single time slot
    // Parse a native time input value ("HH:MM", 24-hour) to minutes past midnight.
    function hmToMin(hm) {
      const m = String(hm || '').match(/^(\d{1,2}):(\d{2})$/);
      if (!m) return null;
      return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
    }
    // Minutes past midnight → "11:30 AM".
    function minTo12(min) {
      if (min == null) return '';
      let h = Math.floor(min / 60), mm = min % 60;
      const ap = h >= 12 ? 'PM' : 'AM';
      h = h % 12; if (h === 0) h = 12;
      return h + ':' + String(mm).padStart(2, '0') + ' ' + ap;
    }

    // Time-block scope select toggles the services field
    const timeScopeSelect   = blockTimeForm && blockTimeForm.querySelector('[name="scope"]');
    const timeScopeServices = root.querySelector('[data-sm-blocktime-services]');
    function syncTimeScope() { setFieldOn(timeScopeServices, !!timeScopeSelect && timeScopeSelect.value === 'services'); }
    timeScopeSelect && timeScopeSelect.addEventListener('change', syncTimeScope);
    syncTimeScope();

    const blockTimeRows = root.querySelector('[data-sm-blocktime-rows]');
    async function loadBlockedTimes() {
      if (!blockTimeRows) return;
      let rows;
      try { rows = await ns.fb.adminListBlockedTimes(); }
      catch (e) { blockTimeRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:16px;color:var(--sm-danger)">Unable to load.</td></tr>'; return; }
      if (!rows.length) {
        blockTimeRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:16px;color:var(--sm-ink-muted)">No time ranges blocked.</td></tr>';
        return;
      }
      const locName = (id) => (cache.locations.find((l) => l.id === id) || {}).name || id || '—';
      blockTimeRows.innerHTML = rows.map((r) => {
        const label = (r.startLabel || minTo12(r.startMinutes)) + ' – ' + (r.endLabel || minTo12(r.endMinutes));
        const svc = (r.services && r.services.length) ? r.services.map(svcName).join(', ') : 'All services';
        return '<tr><td>' + locName(r.location) + '</td><td>' + (r.date || '—') + '</td><td style="white-space:nowrap">' + label + '</td>' +
          '<td>' + svc + '</td>' +
          '<td>' + (r.reason || '') + '</td>' +
          '<td><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-blocktime-del="' + r.id + '" style="color:var(--sm-danger)">Remove</button></td></tr>';
      }).join('');
    }
    blockTimeRows && blockTimeRows.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-sm-blocktime-del]');
      if (!btn) return;
      if (!confirm('Remove this blocked time range?')) return;
      btn.disabled = true;
      try { await ns.fb.adminDeleteBlockedTime(btn.dataset.smBlocktimeDel); await loadBlockedTimes(); }
      catch (err) { btn.disabled = false; alert(err.message || 'Delete failed.'); }
    });

    blockTimeForm && blockTimeForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!blockTimeMsg) return;
      const fd = new FormData(blockTimeForm);
      const startMin = hmToMin(fd.get('startTime'));
      const endMin = hmToMin(fd.get('endTime'));
      if (startMin == null || endMin == null) { blockTimeMsg.textContent = 'Enter a start and end time.'; return; }
      if (endMin <= startMin) { blockTimeMsg.textContent = 'End time must be after start time.'; return; }
      blockTimeMsg.textContent = 'Saving…';
      try {
        const timeScope = fd.get('scope');
        await ns.fb.adminAddBlockedTime({
          locationId: fd.get('location'),
          date: fd.get('date'),
          startMinutes: startMin,
          endMinutes: endMin,
          startLabel: minTo12(startMin),
          endLabel: minTo12(endMin),
          services: timeScope === 'services' ? splitCsv(fd.get('services')) : [],
          reason: fd.get('reason') || ''
        });
        blockTimeMsg.textContent = '✓ Time blocked (' + minTo12(startMin) + ' – ' + minTo12(endMin) + ').';
        blockTimeForm.reset();
        syncTimeScope();
        await loadBlockedTimes();
      } catch (err) {
        console.error('[admin] block time failed', err);
        blockTimeMsg.textContent = err.message || 'Save failed.';
      }
    });

    // Schedule a staff-created class
    addClassForm && addClassForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!addClassMsg) return;
      addClassMsg.textContent = 'Saving…';
      try {
        const fd = new FormData(addClassForm);
        const serviceId = fd.get('service');
        const svc = cache.services.find((s) => s.id === serviceId) || {};
        const date = fd.get('date');
        await ns.fb.adminCreateBooking({
          location: fd.get('location'),
          service:  serviceId,
          serviceName: svc.name || serviceId,
          resourceGroup: svc.resourceGroup || '',
          blockMode: svc.blockMode || 'slot',
          date,
          endDate: fd.get('endDate') || date,
          time: fd.get('time'),
          durationMinutes: svc.durationMinutes || null,
          maxPerSlot: parseInt(fd.get('maxAttendees'), 10) || (svc.maxPerSlot || 1),
          customer: { firstName: 'Staff', lastName: 'Scheduled', email: '', phone: '' },
          notes: fd.get('notes') || '',
          status: 'confirmed',
          source: 'admin'
        });
        addClassMsg.textContent = '✓ Added to the staff schedule (see CPR Bookings).';
        addClassForm.reset();
      } catch (err) {
        console.error('[admin] add class failed', err);
        addClassMsg.textContent = err.message || 'Save failed.';
      }
    });

    // Load + render the existing blocked dates table
    async function loadBlockedDates() {
      if (!blockDateRows) return;
      blockDateRows.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      let rows;
      try { rows = await ns.fb.adminListBlockedDates(); }
      catch (err) {
        console.error('[admin] list blocked dates failed', err);
        blockDateRows.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      if (!rows.length) {
        blockDateRows.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No blocked dates.</td></tr>';
        return;
      }
      blockDateRows.innerHTML = rows.map((r) => {
        const loc = cache.locations.find((l) => l.id === r.location);
        const locName = (loc && loc.name) || r.location;
        const dateRange = r.startDate === r.endDate ? fmtDate(r.startDate) : (fmtDate(r.startDate) + ' → ' + fmtDate(r.endDate));
        let scope;
        if ((r.services && r.services.length) || (r.resourceGroups && r.resourceGroups.length)) {
          const svcNames = (r.services || []).map(svcName);
          const grpNames = (r.resourceGroups || []).map(grpName);
          scope = svcNames.concat(grpNames).join(', ');
        } else {
          scope = 'All services';
        }
        if (r.adminOnly) scope += ' · admin only';
        return '<tr>' +
          '<td>' + locName + '</td>' +
          '<td>' + dateRange + '</td>' +
          '<td>' + scope + '</td>' +
          '<td>' + (r.reason || '—') + '</td>' +
          '<td><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-blockdate-del="' + r.id + '">Remove</button></td>' +
        '</tr>';
      }).join('');
    }

    blockDateRows && blockDateRows.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-sm-blockdate-del]');
      if (!btn) return;
      if (!confirm('Remove this blocked date?')) return;
      btn.disabled = true;
      try { await ns.fb.adminDeleteBlockedDate(btn.dataset.smBlockdateDel); await loadBlockedDates(); }
      catch (err) { btn.disabled = false; alert(err.message || 'Delete failed.'); }
    });

    // Lazy-load blocked dates the first time the Availability tab is opened
    let availLoaded = false;

    /* ---------- LOCATIONS TAB ---------- */
    const locRows = root.querySelector('[data-sm-loc-rows]');
    const locNewBtn = root.querySelector('[data-sm-loc-new]');
    const DAYS_FULL = [['sun','Sun',0],['mon','Mon',1],['tue','Tue',2],['wed','Wed',3],['thu','Thu',4],['fri','Fri',5],['sat','Sat',6]];

    async function loadLocationsTable() {
      if (!locRows) return;
      locRows.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try { cache.locations = await ns.fb.adminListLocationsAll(); }
      catch (err) {
        console.error('[admin] list locations failed', err);
        locRows.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      if (!cache.locations.length) {
        locRows.innerHTML = '<tr><td colspan="5" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No locations yet. Click + Add location.</td></tr>';
        return;
      }
      locRows.innerHTML = cache.locations.map((l) => (
        '<tr>' +
          '<td><strong>' + (l.name || l.id) + '</strong><br><span style="color:var(--sm-ink-muted);font-size:12px">' + l.id + '</span></td>' +
          '<td>' + (l.city || '—') + '</td>' +
          '<td>' + ((l.services || []).length + ' services') + '</td>' +
          '<td><span class="sm-table__status sm-status--' + (l.active ? 'confirmed' : 'cancelled') + '">' + (l.active ? 'Active' : 'Inactive') + '</span></td>' +
          '<td style="white-space:nowrap">' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-loc-edit="' + l.id + '">Edit</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-loc-del="' + l.id + '" style="color:var(--sm-danger)">Delete</button>' +
          '</td>' +
        '</tr>'
      )).join('');
    }

    function locationModalHtml(l, isNew) {
      const hours = l.hours || {};
      const openDays = l.openDays || [];
      // Each row gets a few data attrs so we can filter without re-rendering.
      const serviceOpts = cache.services.map((s) => {
        const on = (l.services || []).indexOf(s.id) > -1;
        const styleLabel = SM_STYLE_LABEL[s.styleKey] || s.styleKey || '';
        const composition = [s.productKey, styleLabel, s.locationKey].filter(Boolean).join(' · ');
        const searchKey = ((s.name || '') + ' ' + s.id + ' ' + composition).toLowerCase();
        return '<label class="sm-book__chk sm-svc-pick-row" style="margin-top:6px"' +
          ' data-sm-svc-loc="' + (s.locationKey || '') + '"' +
          ' data-sm-svc-product="' + (s.productKey || '') + '"' +
          ' data-sm-svc-style="' + (s.styleKey || '') + '"' +
          ' data-sm-svc-search="' + searchKey.replace(/"/g, '&quot;') + '"' +
        '>' +
          '<input type="checkbox" name="svc_' + s.id + '"' + (on ? ' checked' : '') + ' data-sm-svc-pick>' +
          '<span>' + (s.name || s.id) +
            (composition ? '<br><span style="color:var(--sm-ink);font-size:11px">' + composition + '</span>' : '') +
            '<br><span style="color:var(--sm-ink-muted);font-size:11px">' + s.id + '</span>' +
          '</span>' +
        '</label>';
      }).join('');

      // Filter dropdown options drawn from the catalog so empty filters
      // never appear in the menu.
      const allProducts = Array.from(new Set(cache.services.map((s) => s.productKey).filter(Boolean))).sort();
      const productOpts = '<option value="">All products</option>' + allProducts.map((p) => '<option value="' + p + '">' + p + '</option>').join('');
      const usedStyles  = new Set(cache.services.map((s) => s.styleKey).filter(Boolean));
      const styleOpts   = '<option value="">All styles</option>' + SM_STYLE_OPTIONS.filter((o) => usedStyles.has(o.value)).map((o) => '<option value="' + o.value + '">' + o.label + '</option>').join('');
      // Default "match this location" ON when editing an existing location
      // that already has the locationKey field in the service catalog.
      const defaultMatchOn = !isNew && l.id ? ' checked' : '';

      const hoursRows = DAYS_FULL.map(([k, label, n]) => {
        const h = hours[k];
        const on = openDays.indexOf(n) > -1;
        const openV = (h && h.open) || '';
        const closeV = (h && h.close) || '';
        return '<div class="sm-form__row" style="grid-template-columns:80px 1fr 1fr 1fr;align-items:center;gap:10px">' +
          '<label style="font-weight:600;font-size:13px">' + label + '</label>' +
          '<label class="sm-flex" style="gap:6px;align-items:center;font-size:13px"><input type="checkbox" name="day_' + n + '"' + (on ? ' checked' : '') + '> Open</label>' +
          '<input type="time" name="open_' + k + '" value="' + openV + '">' +
          '<input type="time" name="close_' + k + '" value="' + closeV + '">' +
        '</div>';
      }).join('');
      return '<form class="sm-form" data-sm-loc-form>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>ID' + (isNew ? ' (lowercase, no spaces)' : '') + '</label><input type="text" name="id" value="' + (l.id || '') + '" ' + (isNew ? 'required' : 'readonly style="background:var(--sm-surface-alt)"') + '></div>' +
          '<div class="sm-field"><label>Name</label><input type="text" name="name" value="' + (l.name || '') + '" required></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>City / state</label><input type="text" name="city" value="' + (l.city || '') + '"></div>' +
          '<div class="sm-field"><label>Phone</label><input type="tel" name="phone" value="' + (l.phone || '') + '"></div>' +
        '</div>' +
        '<div class="sm-field"><label>Address</label><input type="text" name="address" value="' + ((l.address || '').replace(/"/g, '&quot;')) + '"></div>' +
        '<div class="sm-field"><label>Image URL</label><input type="url" name="imageUrl" value="' + (l.imageUrl || '') + '"></div>' +
        '<div class="sm-field"><label>Time slots offered (comma-separated)</label><input type="text" name="slots" value="' + ((l.slots || []).join(', ')) + '"></div>' +

        '<h4 class="sm-h4" style="margin-top:20px;margin-bottom:8px">Hours</h4>' +
        hoursRows +

        '<h4 class="sm-h4" style="margin-top:20px;margin-bottom:8px">Services offered</h4>' +

        // Filter toolbar above the checkbox list.
        '<div class="sm-flex sm-gap-12" style="flex-wrap:wrap;align-items:center;margin-bottom:8px">' +
          '<input type="search" data-sm-loc-svc-search placeholder="Search services…" style="flex:1;min-width:160px">' +
          '<select data-sm-loc-svc-product class="sm-admin__select">' + productOpts + '</select>' +
          '<select data-sm-loc-svc-style class="sm-admin__select">' + styleOpts + '</select>' +
          '<label class="sm-flex" style="gap:6px;align-items:center;font-size:12px;white-space:nowrap"><input type="checkbox" data-sm-loc-svc-match' + defaultMatchOn + '><span>Match this location only</span></label>' +
        '</div>' +
        '<div class="sm-flex sm-gap-12" style="flex-wrap:wrap;align-items:center;margin-bottom:6px;font-size:12px;color:var(--sm-ink-muted)">' +
          '<span data-sm-loc-svc-count>Showing 0 of 0 · 0 selected</span>' +
          '<span style="flex:1"></span>' +
          '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-loc-svc-select-all>Select all in view</button>' +
          '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-loc-svc-clear-all>Clear all in view</button>' +
        '</div>' +
        '<div data-sm-loc-svc-list style="max-height:260px;overflow:auto;padding:10px;border:1px solid var(--sm-border-soft);border-radius:10px">' +
          (serviceOpts || '<p class="sm-field__help">No services defined yet — add some in Backend → Services.</p>') +
        '</div>' +

        '<label class="sm-book__chk sm-mt-16"><input type="checkbox" name="active"' + (l.active === false ? '' : ' checked') + '><span>Active (visible to customers)</span></label>' +

        '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
          '<button type="submit" class="sm-btn sm-btn--primary">Save location</button>' +
          '<span class="sm-field__help" data-sm-loc-msg></span>' +
        '</div>' +
      '</form>';
    }

    function openLocationModal(id) {
      if (!modal || !modalBody) return;
      const isNew = !id;
      const l = isNew ? { active: true, services: [], slots: ['9:00 AM','11:00 AM','1:00 PM','3:00 PM'], openDays: [1,2,3,4,5], hours: {} } : (cache.locations.find((x) => x.id === id) || {});
      modalTitle && (modalTitle.textContent = isNew ? 'Add location' : ('Edit location — ' + (l.name || l.id)));
      modalBody.innerHTML = locationModalHtml(l, isNew);
      modal.hidden = false;
      const form = modalBody.querySelector('[data-sm-loc-form]');
      const msg  = modalBody.querySelector('[data-sm-loc-msg]');

      // Services-offered filter toolbar. Search + product + style + match-
      // this-location toggle re-runs the filter; counts update too.
      (function wireServiceFilter() {
        const listEl    = form.querySelector('[data-sm-loc-svc-list]');
        if (!listEl) return;
        const searchEl  = form.querySelector('[data-sm-loc-svc-search]');
        const prodEl    = form.querySelector('[data-sm-loc-svc-product]');
        const styleEl   = form.querySelector('[data-sm-loc-svc-style]');
        const matchEl   = form.querySelector('[data-sm-loc-svc-match]');
        const countEl   = form.querySelector('[data-sm-loc-svc-count]');
        const selectAll = form.querySelector('[data-sm-loc-svc-select-all]');
        const clearAll  = form.querySelector('[data-sm-loc-svc-clear-all]');
        function visibleRows() { return Array.from(listEl.querySelectorAll('.sm-svc-pick-row:not([hidden])')); }
        function applyFilter() {
          const q = (searchEl.value || '').trim().toLowerCase();
          const p = prodEl.value;
          const st = styleEl.value;
          const matchOnly = matchEl.checked;
          const targetLoc = (form.querySelector('[name="id"]').value || '').trim();
          let shown = 0, total = 0;
          listEl.querySelectorAll('.sm-svc-pick-row').forEach((row) => {
            total++;
            let visible = true;
            if (q && row.dataset.smSvcSearch.indexOf(q) === -1) visible = false;
            if (visible && p && row.dataset.smSvcProduct !== p) visible = false;
            if (visible && st && row.dataset.smSvcStyle !== st) visible = false;
            if (visible && matchOnly && targetLoc && row.dataset.smSvcLoc !== targetLoc) visible = false;
            row.hidden = !visible;
            if (visible) shown++;
          });
          const selected = listEl.querySelectorAll('input[data-sm-svc-pick]:checked').length;
          if (countEl) countEl.textContent = 'Showing ' + shown + ' of ' + total + ' · ' + selected + ' selected';
        }
        [searchEl, prodEl, styleEl, matchEl].forEach((el) => el && el.addEventListener('input', applyFilter));
        [searchEl, prodEl, styleEl, matchEl].forEach((el) => el && el.addEventListener('change', applyFilter));
        listEl.addEventListener('change', (e) => { if (e.target.matches('[data-sm-svc-pick]')) applyFilter(); });
        selectAll && selectAll.addEventListener('click', () => {
          visibleRows().forEach((row) => { const cb = row.querySelector('[data-sm-svc-pick]'); if (cb) cb.checked = true; });
          applyFilter();
        });
        clearAll && clearAll.addEventListener('click', () => {
          visibleRows().forEach((row) => { const cb = row.querySelector('[data-sm-svc-pick]'); if (cb) cb.checked = false; });
          applyFilter();
        });
        applyFilter();
      })();

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const fd = new FormData(form);
          const newId = (fd.get('id') || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
          if (!newId) throw new Error('ID is required.');
          // openDays + hours from checkboxes + inputs
          const openDays = [];
          const hours = {};
          DAYS_FULL.forEach(([k, _, n]) => {
            if (fd.get('day_' + n)) openDays.push(n);
            const open = fd.get('open_' + k);
            const close = fd.get('close_' + k);
            hours[k] = (open && close) ? { open, close } : null;
          });
          // services from checkboxes
          const services = cache.services.filter((s) => !!fd.get('svc_' + s.id)).map((s) => s.id);
          // slots from CSV
          const slots = (fd.get('slots') || '').toString().split(',').map((s) => s.trim()).filter(Boolean);

          const data = {
            active: !!fd.get('active'),
            name: fd.get('name') || newId,
            city: fd.get('city') || '',
            phone: fd.get('phone') || '',
            address: fd.get('address') || '',
            imageUrl: fd.get('imageUrl') || '',
            slots, openDays, hours, services
          };
          await ns.fb.adminSaveLocation(newId, data);
          msg.textContent = '✓ Saved.';
          await loadLocationsTable();
          await loadLookups();
          setTimeout(closeModal, 500);
        } catch (err) {
          console.error('[admin] save location failed', err);
          msg.textContent = err.message || 'Save failed.';
        }
      });
    }

    locRows && locRows.addEventListener('click', async (e) => {
      const editBtn = e.target.closest('[data-sm-loc-edit]');
      const delBtn  = e.target.closest('[data-sm-loc-del]');
      if (editBtn) openLocationModal(editBtn.dataset.smLocEdit);
      else if (delBtn) {
        if (!confirm('Delete location "' + delBtn.dataset.smLocDel + '"? Existing bookings reference this ID and will become orphaned.')) return;
        delBtn.disabled = true;
        try { await ns.fb.adminDeleteLocation(delBtn.dataset.smLocDel); await loadLocationsTable(); await loadLookups(); }
        catch (err) { delBtn.disabled = false; alert(err.message || 'Delete failed.'); }
      }
    });
    locNewBtn && locNewBtn.addEventListener('click', () => openLocationModal(null));

    let locLoaded = false;

    /* ---------- SERVICES TAB ---------- */
    const svcRows = root.querySelector('[data-sm-svc-rows]');
    const svcNewBtn = root.querySelector('[data-sm-svc-new]');
    const svcFilterProduct  = root.querySelector('[data-sm-svc-filter-product]');
    const svcFilterStyle    = root.querySelector('[data-sm-svc-filter-style]');
    const svcFilterLocation = root.querySelector('[data-sm-svc-filter-location]');
    const svcBulkToggle = root.querySelector('[data-sm-svc-bulk-toggle]');
    const svcBulkBar    = root.querySelector('[data-sm-svc-bulkbar]');
    const svcGrid       = root.querySelector('[data-sm-svc-grid]');
    const svcNormal     = root.querySelector('[data-sm-svc-normal]');
    cache.resourceGroups = cache.resourceGroups || [];

    function refreshSvcFilterDropdowns() {
      // Product filter — unique productKey values across services.
      if (svcFilterProduct) {
        const current = svcFilterProduct.value;
        const products = Array.from(new Set(cache.services.map((x) => x.productKey).filter(Boolean))).sort();
        svcFilterProduct.innerHTML = '<option value="">All products</option>' +
          products.map((p) => '<option value="' + p + '">' + p + '</option>').join('');
        if (current) svcFilterProduct.value = current;
      }
      // Style filter — only show styles actually used.
      if (svcFilterStyle) {
        const current = svcFilterStyle.value;
        const used = new Set(cache.services.map((x) => x.styleKey).filter(Boolean));
        svcFilterStyle.innerHTML = '<option value="">All styles</option>' +
          SM_STYLE_OPTIONS.filter((o) => used.has(o.value)).map((o) => '<option value="' + o.value + '">' + o.label + '</option>').join('');
        if (current) svcFilterStyle.value = current;
      }
      // Location filter — pull from the cached locations list.
      if (svcFilterLocation) {
        const current = svcFilterLocation.value;
        svcFilterLocation.innerHTML = '<option value="">All locations</option>' +
          (cache.locations || []).map((l) => '<option value="' + l.id + '">' + (l.name || l.id) + '</option>').join('');
        if (current) svcFilterLocation.value = current;
      }
    }

    /* ================= BUSINESSES (B2B) ================= */
    const BIZ_TIERS = [5, 10, 15, 20, 30, 50];
    const BIZ_COURSES = [
      { key: 'bls', name: 'BLS' },
      { key: 'acls', name: 'ACLS' },
      { key: 'pals', name: 'PALS' },
      { key: 'heartsaver', name: 'Heartsaver CPR/AED/FA' }
    ];
    const bizRoot = root.querySelector('[data-sm-biz-root]');
    const bizMoney = (n) => (n || n === 0) ? '$' + Number(n).toLocaleString() : '—';

    async function loadBusinesses() {
      if (!bizRoot) return;
      bizRoot.innerHTML = '<p class="sm-field__help" style="padding:16px">Loading businesses…</p>';
      let list = [];
      try { list = await ns.fb.adminListBusinesses(); }
      catch (e) { bizRoot.innerHTML = '<p class="sm-field__error is-shown" style="padding:16px">Could not load businesses: ' + esc(e.message || e) + '</p>'; return; }
      renderBizList(list);
    }

    function renderBizList(list) {
      bizRoot.innerHTML =
        '<div class="sm-admin__toolbar"><div>' +
          '<h2 class="sm-h3" style="margin:0">Businesses</h2>' +
          '<p class="sm-field__help">B2B accounts — company info, negotiated pricing, and their employees.</p>' +
        '</div><div class="sm-admin__filter"><button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-biz-new>+ New business</button></div></div>' +
        '<div style="overflow-x:auto"><table class="sm-table">' +
          '<thead><tr><th>Business</th><th>Location</th><th>Account admin</th><th>Status</th><th></th></tr></thead><tbody>' +
          (list.length ? list.map((b) =>
            '<tr><td><strong>' + esc(b.name || '(unnamed)') + '</strong></td>' +
            '<td>' + esc([b.city, b.state].filter(Boolean).join(', ') || '—') + '</td>' +
            '<td class="sm-field__help">' + esc(b.adminEmail || '—') + '</td>' +
            '<td><span class="sm-table__status sm-status--' + (b.active === false ? 'cancelled' : 'confirmed') + '">' + (b.active === false ? 'inactive' : 'active') + '</span></td>' +
            '<td style="text-align:right"><button type="button" class="ico-btn" data-biz-open="' + esc(b.id) + '" style="border:1px solid var(--sm-border-soft);background:var(--sm-surface);border-radius:8px;padding:4px 10px;font-size:12px;font-weight:600;cursor:pointer">Open</button></td></tr>'
          ).join('') : '<tr><td colspan="5" style="text-align:center;padding:22px;color:var(--sm-ink-muted)">No businesses yet. Click “New business” to add one.</td></tr>') +
        '</tbody></table></div>';
      bizRoot.querySelector('[data-biz-new]').addEventListener('click', () => openBizEditor(null));
      bizRoot.querySelectorAll('[data-biz-open]').forEach((btn) =>
        btn.addEventListener('click', () => openBizEditor(list.find((x) => x.id === btn.dataset.bizOpen))));
    }

    function openBizEditor(biz) {
      const b = biz || { officeRates: {}, onsiteRates: {}, active: true };
      const office = b.officeRates || {};
      const onsite = b.onsiteRates || {};
      const inp = (label, id, val, type) => '<div class="sm-field"><label>' + label + '</label><input type="' + (type || 'text') + '" data-bf="' + id + '" value="' + esc(val || '') + '"></div>';

      const pricingRows = BIZ_COURSES.map((c) => {
        const tiers = (onsite[c.key]) || {};
        return '<tr><td><strong>' + c.name + '</strong></td>' +
          '<td><input type="number" min="0" step="5" data-office="' + c.key + '" value="' + esc(office[c.key] || '') + '" placeholder="—" style="width:78px"></td>' +
          BIZ_TIERS.map((t) => '<td><input type="number" min="0" step="5" data-onsite="' + c.key + ':' + t + '" value="' + esc(tiers[t] || '') + '" placeholder="—" style="width:64px"></td>').join('') +
        '</tr>';
      }).join('');

      bizRoot.innerHTML =
        '<div class="sm-flex" style="justify-content:space-between;align-items:center;margin-bottom:12px">' +
          '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-biz-back>← All businesses</button>' +
          '<span class="sm-field__help" data-biz-msg></span>' +
        '</div>' +
        '<h2 class="sm-h3" style="margin:0 0 4px">' + (b.id ? esc(b.name || 'Business') : 'New business') + '</h2>' +
        '<div class="sm-card" style="padding:18px;margin-top:12px" data-biz-form>' +
          '<h3 class="sm-h4" style="margin:0 0 10px">Company</h3>' +
          '<div class="sm-form__row">' + inp('Business name', 'name', b.name) + inp('Account admin email', 'adminEmail', b.adminEmail, 'email') + '</div>' +
          '<div class="sm-form__row" style="margin-top:12px">' + inp('Street address', 'address', b.address) + inp('City', 'city', b.city) + '</div>' +
          '<div class="sm-form__row" style="margin-top:12px">' + inp('State', 'state', b.state) + inp('Contact name', 'contactName', b.contactName) + '</div>' +
          '<div class="sm-form__row" style="margin-top:12px">' + inp('Contact email', 'contactEmail', b.contactEmail, 'email') + inp('Contact phone', 'contactPhone', b.contactPhone, 'tel') + '</div>' +
          '<label class="sm-book__chk" style="margin-top:12px"><input type="checkbox" data-bf="active"' + (b.active === false ? '' : ' checked') + '><span>Account active</span></label>' +

          '<h3 class="sm-h4" style="margin:22px 0 4px">Negotiated pricing</h3>' +
          '<p class="sm-field__help" style="margin:0 0 8px">Office = per seat. On-site = per person at each group-size tier. Leave blank if not offered.</p>' +
          '<div style="overflow-x:auto"><table class="sm-table" style="font-size:12.5px"><thead><tr><th>Course</th><th>Office /seat</th>' +
            BIZ_TIERS.map((t) => '<th>' + t + '+ /pp</th>').join('') + '</tr></thead><tbody>' + pricingRows + '</tbody></table></div>' +

          '<div class="sm-flex sm-gap-12" style="align-items:center;margin-top:18px;flex-wrap:wrap">' +
            '<button type="button" class="sm-btn sm-btn--primary" data-biz-save>Save business</button>' +
            (b.id ? '<button type="button" class="sm-btn sm-btn--light" data-biz-del style="color:var(--sm-danger)">Delete</button>' : '') +
          '</div>' +
        '</div>' +
        (b.id ? '<div class="sm-card" style="padding:18px;margin-top:16px" data-biz-emps></div>' +
                '<div class="sm-card" style="padding:18px;margin-top:16px" data-biz-regs></div>' +
                '<div class="sm-card" style="padding:18px;margin-top:16px" data-biz-certs></div>'
              : '<p class="sm-field__help" style="margin-top:12px">Save the business first, then you can add employees.</p>');

      bizRoot.querySelector('[data-biz-back]').addEventListener('click', loadBusinesses);
      const msg = bizRoot.querySelector('[data-biz-msg]');

      bizRoot.querySelector('[data-biz-save]').addEventListener('click', async () => {
        const form = bizRoot.querySelector('[data-biz-form]');
        const get = (id) => { const el = form.querySelector('[data-bf="' + id + '"]'); return el ? el.value.trim() : ''; };
        const officeRates = {}, onsiteRates = {};
        form.querySelectorAll('[data-office]').forEach((el) => { const v = parseFloat(el.value); if (v > 0) officeRates[el.dataset.office] = v; });
        form.querySelectorAll('[data-onsite]').forEach((el) => { const v = parseFloat(el.value); if (v > 0) { const [k, t] = el.dataset.onsite.split(':'); (onsiteRates[k] = onsiteRates[k] || {})[t] = v; } });
        const data = {
          name: get('name'), adminEmail: get('adminEmail').toLowerCase(), address: get('address'), city: get('city'), state: get('state'),
          contactName: get('contactName'), contactEmail: get('contactEmail'), contactPhone: get('contactPhone'),
          active: form.querySelector('[data-bf="active"]').checked, officeRates, onsiteRates
        };
        if (!data.name) { msg.textContent = 'Business name is required.'; msg.style.color = 'var(--sm-danger)'; return; }
        msg.textContent = 'Saving…'; msg.style.color = 'var(--sm-ink-muted)';
        try {
          const res = await ns.fb.adminSaveBusiness(b.id || null, data);
          msg.textContent = '✓ Saved.';
          openBizEditor(Object.assign({ id: res.id }, data)); // re-open with id so employees section appears
        } catch (e) { msg.textContent = e.message || 'Save failed.'; msg.style.color = 'var(--sm-danger)'; }
      });

      const delBtn = bizRoot.querySelector('[data-biz-del]');
      if (delBtn) delBtn.addEventListener('click', async () => {
        if (!confirm('Delete this business and its employees? This cannot be undone.')) return;
        try { await ns.fb.adminDeleteBusiness(b.id); loadBusinesses(); }
        catch (e) { msg.textContent = e.message || 'Delete failed.'; msg.style.color = 'var(--sm-danger)'; }
      });

      if (b.id) { renderBizEmployees(b.id); renderBizRegs(b.id); renderBizCerts(b.id); }
    }

    async function renderBizCerts(bizId) {
      const wrap = bizRoot.querySelector('[data-biz-certs]');
      if (!wrap) return;
      let certs = [], emps = [];
      try { const r = await Promise.all([ns.fb.adminListCertifications(bizId), ns.fb.adminListBizEmployees(bizId)]); certs = r[0] || []; emps = r[1] || []; } catch (e) {}
      const rows = certs.length ? certs.map((c) => { const s = smCertStatus(c.renewalDate);
        return '<tr><td><strong>' + esc(c.employeeName || '') + '</strong></td><td>' + esc(c.course || '') + '</td><td class="tnum">' + esc(c.completedDate || '—') + '</td><td class="tnum">' + esc(c.renewalDate || '—') + '</td><td>' + smCertPill(s) + '</td>' +
          '<td style="text-align:right"><button type="button" class="ico-btn" data-c-del="' + esc(c.id) + '" style="border:1px solid var(--sm-border-soft);background:var(--sm-surface);border-radius:8px;padding:3px 9px;font-size:12px;cursor:pointer;color:var(--sm-danger)">✕</button></td></tr>';
      }).join('') : '<tr><td colspan="6" style="text-align:center;padding:14px;color:var(--sm-ink-muted)">No certifications yet.</td></tr>';
      const empOpts = emps.map((e) => '<option value="' + esc(e.id) + '">' + esc((e.firstName || '') + ' ' + (e.lastName || '')) + '</option>').join('');
      const courseOpts = BIZ_COURSES.map((c) => '<option>' + c.name + '</option>').join('');
      wrap.innerHTML = '<h3 class="sm-h4" style="margin:0 0 10px">Certifications <span class="sm-field__help">(' + certs.length + ')</span></h3>' +
        '<div style="overflow-x:auto"><table class="sm-table"><thead><tr><th>Employee</th><th>Course</th><th>Completed</th><th>Renews</th><th>Status</th><th></th></tr></thead><tbody>' + rows + '</tbody></table></div>' +
        '<div class="sm-card" style="padding:14px;margin-top:12px;background:var(--sm-surface-alt,#f6f9fd)" data-c-form>' +
          '<div class="sm-form__row"><div class="sm-field" style="margin:0"><label>Employee</label><select data-cf="employeeId">' + empOpts + '</select></div><div class="sm-field" style="margin:0"><label>Course</label><select data-cf="course">' + courseOpts + '</select></div></div>' +
          '<div class="sm-form__row" style="margin-top:10px"><div class="sm-field" style="margin:0"><label>Completed</label><input type="date" data-cf="completedDate"></div><div class="sm-field" style="margin:0"><label>Renews (auto)</label><input type="date" data-cf="renewalDate"></div></div>' +
          '<div class="sm-flex sm-gap-12" style="align-items:center;margin-top:12px"><button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-c-save>Add certification</button><span class="sm-field__help" data-c-msg></span></div></div>';
      const form = wrap.querySelector('[data-c-form]');
      const comp = form.querySelector('[data-cf="completedDate"]'), ren = form.querySelector('[data-cf="renewalDate"]'), crs = form.querySelector('[data-cf="course"]');
      const auto = () => { if (comp.value) ren.value = smAddYears(comp.value, smRenewalYears(crs.value)); };
      comp.addEventListener('change', auto); crs.addEventListener('change', auto);
      const cmsg = form.querySelector('[data-c-msg]');
      form.querySelector('[data-c-save]').addEventListener('click', async () => {
        const empId = form.querySelector('[data-cf="employeeId"]').value; const e = emps.find((x) => x.id === empId);
        if (!e) { cmsg.textContent = 'Pick an employee.'; return; }
        if (!comp.value) { cmsg.textContent = 'Enter the completed date.'; return; }
        const data = { employeeId: empId, employeeName: ((e.firstName || '') + ' ' + (e.lastName || '')).trim(), course: crs.value, completedDate: comp.value, renewalDate: ren.value || smAddYears(comp.value, smRenewalYears(crs.value)), source: 'manual' };
        cmsg.textContent = 'Saving…';
        try { await ns.fb.adminSaveCertification(bizId, null, data); renderBizCerts(bizId); } catch (err) { cmsg.textContent = err.message || 'Failed.'; }
      });
      wrap.querySelectorAll('[data-c-del]').forEach((b) => b.addEventListener('click', async () => { if (!confirm('Delete this certification?')) return; try { await ns.fb.adminDeleteCertification(bizId, b.dataset.cDel); renderBizCerts(bizId); } catch (e) {} }));
    }

    async function renderBizRegs(bizId) {
      const wrap = bizRoot.querySelector('[data-biz-regs]');
      if (!wrap) return;
      let regs = [];
      try { regs = await ns.fb.adminListRegistrations(bizId); } catch (e) {}
      if (!regs.length) { wrap.innerHTML = '<h3 class="sm-h4" style="margin:0 0 6px">Training registrations</h3><p class="sm-field__help">No registrations yet. They appear here after a business checks out a session.</p>'; return; }
      wrap.innerHTML = '<h3 class="sm-h4" style="margin:0 0 10px">Training registrations <span class="sm-field__help">(' + regs.length + ')</span></h3>' +
        '<div style="overflow-x:auto"><table class="sm-table"><thead><tr><th>Date created</th><th>Course</th><th>Mode</th><th>Attendees</th><th>Total</th><th>Status</th><th></th></tr></thead><tbody>' +
        regs.map((r) => '<tr><td class="tnum">' + esc((r.createdAt || '').slice(0, 10)) + '</td><td>' + esc(r.courseName || r.course || '') + '</td><td>' + (r.mode === 'onsite' ? 'On-site' : 'Office') + '</td>' +
          '<td class="tnum">' + esc(r.count || (r.employees || []).length) + '</td><td class="tnum">' + (r.total != null ? '$' + Number(r.total).toLocaleString() : '—') + '</td>' +
          '<td><span class="sm-table__status sm-status--' + (r.status === 'completed' ? 'completed' : 'pending') + '">' + esc(r.status || 'invoiced') + '</span></td>' +
          '<td style="text-align:right">' + (r.status === 'completed' ? '<span class="sm-field__help">certs issued</span>' : r.status === 'paid' ? '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-reg-issue="' + esc(r.id) + '">Issue certs</button>' : '<span class="sm-field__help">' + (r.status === 'cancelled' ? 'cancelled' : 'awaiting payment') + '</span>') + '</td></tr>').join('') +
        '</tbody></table></div>';
      wrap.querySelectorAll('[data-reg-issue]').forEach((btn) => btn.addEventListener('click', async () => {
        const reg = regs.find((x) => x.id === btn.dataset.regIssue); if (!reg) return;
        const dateStr = (reg.schedule && reg.schedule.date) || new Date().toISOString().slice(0, 10);
        if (!confirm('Issue ' + (reg.employees || []).length + ' ' + (reg.courseName || reg.course) + ' certificate(s), completed ' + dateStr + '?')) return;
        btn.disabled = true; btn.textContent = 'Issuing…';
        try {
          for (const e of (reg.employees || [])) {
            await ns.fb.adminSaveCertification(bizId, null, { employeeId: e.id || '', employeeName: e.name || '', course: reg.courseName || reg.course || '', completedDate: dateStr, renewalDate: smAddYears(dateStr, smRenewalYears(reg.courseName || reg.course)), source: 'registration', registrationId: reg.id });
          }
          await ns.fb.adminUpdateRegistration(bizId, reg.id, { status: 'completed', completedAt: new Date().toISOString() });
          renderBizRegs(bizId); renderBizCerts(bizId);
        } catch (err) { btn.disabled = false; btn.textContent = 'Issue certs'; alert(err.message || 'Failed to issue certs.'); }
      }));
    }

    async function renderBizEmployees(bizId) {
      const wrap = bizRoot.querySelector('[data-biz-emps]');
      if (!wrap) return;
      let emps = [];
      try { emps = await ns.fb.adminListBizEmployees(bizId); } catch (e) {}
      wrap.dataset.editing = '';
      const rows = emps.length ? emps.map((e) =>
        '<tr><td><strong>' + esc((e.firstName || '') + ' ' + (e.lastName || '')) + '</strong></td>' +
        '<td>' + esc(e.position || '—') + '</td><td class="tnum">' + esc(e.employeeId || '—') + '</td>' +
        '<td class="sm-field__help">' + esc(e.email || '—') + '<br>' + esc(e.phone || '') + '</td>' +
        '<td style="text-align:right;white-space:nowrap">' +
          '<button type="button" class="ico-btn" data-emp-edit="' + esc(e.id) + '" style="border:1px solid var(--sm-border-soft);background:var(--sm-surface);border-radius:8px;padding:3px 9px;font-size:12px;cursor:pointer">Edit</button> ' +
          '<button type="button" class="ico-btn" data-emp-del="' + esc(e.id) + '" style="border:1px solid var(--sm-border-soft);background:var(--sm-surface);border-radius:8px;padding:3px 9px;font-size:12px;cursor:pointer;color:var(--sm-danger)">✕</button>' +
        '</td></tr>'
      ).join('') : '<tr><td colspan="5" style="text-align:center;padding:16px;color:var(--sm-ink-muted)">No employees yet.</td></tr>';

      const ef = (ph, id) => '<input type="text" data-ef="' + id + '" placeholder="' + ph + '" style="padding:8px 10px;border:1px solid var(--sm-border-soft);border-radius:8px;font:inherit;font-size:13px;width:100%">';
      wrap.innerHTML =
        '<h3 class="sm-h4" style="margin:0 0 10px">Employees <span class="sm-field__help">(' + emps.length + ')</span></h3>' +
        '<div style="overflow-x:auto"><table class="sm-table"><thead><tr><th>Name</th><th>Position</th><th>Employee ID</th><th>Contact</th><th></th></tr></thead><tbody data-emp-rows>' + rows + '</tbody></table></div>' +
        '<div class="sm-card" style="padding:14px;margin-top:12px;background:var(--sm-surface-alt,#f6f9fd)" data-emp-form>' +
          '<strong style="font-size:13px" data-emp-form-title>Add employee</strong>' +
          '<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-top:10px">' + ef('First name', 'firstName') + ef('Last name', 'lastName') + ef('Position (RN, CNA…)', 'position') + '</div>' +
          '<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-top:8px">' + ef('Employee ID', 'employeeId') + ef('Email', 'email') + ef('Phone', 'phone') + '</div>' +
          '<div class="sm-flex sm-gap-12" style="align-items:center;margin-top:12px"><button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-emp-save>Add employee</button><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-emp-cancel hidden>Cancel</button><span class="sm-field__help" data-emp-msg></span></div>' +
        '</div>';

      const form = wrap.querySelector('[data-emp-form]');
      const efGet = (id) => { const el = form.querySelector('[data-ef="' + id + '"]'); return el ? el.value.trim() : ''; };
      const emsg = form.querySelector('[data-emp-msg]');
      const resetForm = () => { form.querySelectorAll('[data-ef]').forEach((el) => el.value = ''); wrap.dataset.editing = ''; form.querySelector('[data-emp-form-title]').textContent = 'Add employee'; form.querySelector('[data-emp-save]').textContent = 'Add employee'; form.querySelector('[data-emp-cancel]').hidden = true; };

      form.querySelector('[data-emp-save]').addEventListener('click', async () => {
        const data = { firstName: efGet('firstName'), lastName: efGet('lastName'), position: efGet('position'), employeeId: efGet('employeeId'), email: efGet('email'), phone: efGet('phone') };
        if (!data.firstName && !data.lastName) { emsg.textContent = 'Enter a name.'; emsg.style.color = 'var(--sm-danger)'; return; }
        emsg.textContent = 'Saving…'; emsg.style.color = 'var(--sm-ink-muted)';
        try { await ns.fb.adminSaveBizEmployee(bizId, wrap.dataset.editing || null, data); renderBizEmployees(bizId); }
        catch (e) { emsg.textContent = e.message || 'Save failed.'; emsg.style.color = 'var(--sm-danger)'; }
      });
      form.querySelector('[data-emp-cancel]').addEventListener('click', resetForm);

      wrap.querySelectorAll('[data-emp-del]').forEach((btn) => btn.addEventListener('click', async () => {
        if (!confirm('Remove this employee?')) return;
        try { await ns.fb.adminDeleteBizEmployee(bizId, btn.dataset.empDel); renderBizEmployees(bizId); } catch (e) {}
      }));
      wrap.querySelectorAll('[data-emp-edit]').forEach((btn) => btn.addEventListener('click', () => {
        const e = emps.find((x) => x.id === btn.dataset.empEdit); if (!e) return;
        wrap.dataset.editing = e.id;
        ['firstName', 'lastName', 'position', 'employeeId', 'email', 'phone'].forEach((k) => { const el = form.querySelector('[data-ef="' + k + '"]'); if (el) el.value = e[k] || ''; });
        form.querySelector('[data-emp-form-title]').textContent = 'Edit employee';
        form.querySelector('[data-emp-save]').textContent = 'Save changes';
        form.querySelector('[data-emp-cancel]').hidden = false;
        form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }));
    }

    /* ================= MANUAL MARKETING METRICS ================= */
    const FUNNEL_METRICS = [
      { k: 'sessions', label: 'Sessions' },
      { k: 'addToCartRate', label: 'Add-to-cart rate' },
      { k: 'checkoutStartRate', label: 'Checkout start rate' },
      { k: 'completedCheckoutRate', label: 'Completed checkout rate' },
      { k: 'cartToCheckout', label: 'Cart to checkout' },
      { k: 'checkoutCompletion', label: 'Checkout completion' }
    ];
    const AD_METRICS = [
      { k: 'adSpend', label: 'Ad spend' },
      { k: 'impressions', label: 'Impressions' },
      { k: 'clicks', label: 'Clicks' },
      { k: 'ctr', label: 'Click-through rate' },
      { k: 'cpc', label: 'Cost per click' },
      { k: 'googleConversions', label: 'Google Ads conversions' },
      { k: 'cpConversion', label: 'Cost per Google conversion' },
      { k: 'conversionValue', label: 'Google Ads conversion value' },
      { k: 'roas', label: 'Google Ads ROAS' },
      { k: 'adSpendPctGross', label: 'Ad spend as % of gross sales' }
    ];
    const SUMMARY_METRICS = [
      { k: 'bizOrders', label: 'Orders' },
      { k: 'grossSales', label: 'Gross sales' },
      { k: 'aov', label: 'Average order value' },
      { k: 'webConversion', label: 'Website conversion rate' }
    ];
    const METRICS_DEFAULT = {
      months: ['October 2025', 'March 2026', 'April 2026'],
      targets: { addToCartRate: '6% to 8%', checkoutStartRate: '3.5% to 4.5%', completedCheckoutRate: '4% to 5.5%', cartToCheckout: '65%+', checkoutCompletion: '90%+' },
      values: {
        'October 2025': { sessions: '3,140', bizOrders: '124', grossSales: '$14,437', aov: '$123', webConversion: '3.9%', addToCartRate: '5.9%', checkoutStartRate: '3.8%', completedCheckoutRate: '3.7%', cartToCheckout: '64.1%', checkoutCompletion: '98.3%', adSpend: '$6,470', impressions: '18,800', clicks: '1,680', ctr: '8.9%', cpc: '$3.85', googleConversions: '57', cpConversion: '$113.51', conversionValue: 'NA', roas: 'NA', adSpendPctGross: '44.8%' },
        'March 2026': { sessions: '1,918', bizOrders: '53', grossSales: '$5,857', aov: '$108', webConversion: '2.5%', addToCartRate: '4.6%', checkoutStartRate: '2.7%', completedCheckoutRate: '2.5%', cartToCheckout: '57.3%', checkoutCompletion: '92.2%', adSpend: '$1,060', impressions: '2,410', clicks: '287', ctr: '11.9%', cpc: '$3.69', googleConversions: '27', cpConversion: '$39.26', conversionValue: '$4,871', roas: '4.6x', adSpendPctGross: '18.1%' },
        'April 2026': { sessions: '2,046', bizOrders: '59', grossSales: '$10,073.47', aov: '$152', webConversion: '2.9%', addToCartRate: '4.2%', checkoutStartRate: '3.0%', completedCheckoutRate: '2.6%', cartToCheckout: '71.8%', checkoutCompletion: '88.5%', adSpend: '$1,064', impressions: '2,075', clicks: '266', ctr: '12.8%', cpc: '$4', googleConversions: '18', cpConversion: '$59.11', conversionValue: '$3,294', roas: '3.1x', adSpendPctGross: '10.6%' }
      }
    };
    const metricsRoot = root.querySelector('[data-sm-metrics-root]');
    let metricsData = null;

    const METRICS_ALL_SECTIONS = [
      { title: 'Business summary', metrics: SUMMARY_METRICS },
      { title: 'Sales funnel', metrics: FUNNEL_METRICS },
      { title: 'Advertising performance', metrics: AD_METRICS }
    ];
    // Load the shared manual-metrics doc once (used by the Metrics tab and the
    // Analytics → Marketing sub-tab).
    async function ensureMetrics() {
      if (metricsData) return;
      let d = null; try { d = await ns.fb.adminGetManualMetrics(); } catch (e) {}
      metricsData = (d && d.months && d.months.length) ? d : JSON.parse(JSON.stringify(METRICS_DEFAULT));
      metricsData.months = metricsData.months || [];
      metricsData.targets = metricsData.targets || {};
      metricsData.values = metricsData.values || {};
    }
    async function loadMetrics() {
      if (!metricsRoot) return;
      metricsRoot.innerHTML = '<p class="sm-field__help" style="padding:16px">Loading…</p>';
      await ensureMetrics();
      renderMetricsInto(metricsRoot, METRICS_ALL_SECTIONS, 'Marketing metrics', 'Manually-entered summary, funnel &amp; ad performance by month. Add a column each month and fill it in.');
    }

    // Read the visible inputs in `el` back into metricsData (other sections'
    // values already in metricsData are preserved).
    function metricsGather(el) {
      el.querySelectorAll('[data-tg]').forEach((i) => { metricsData.targets[i.dataset.tg] = i.value; });
      el.querySelectorAll('[data-mv]').forEach((i) => { const parts = i.dataset.mv.split('|||'); (metricsData.values[parts[0]] = metricsData.values[parts[0]] || {})[parts[1]] = i.value; });
    }

    // Render an editable month-grid of the given metric sections into `el`.
    function renderMetricsInto(el, sections, title, subtitle) {
      const months = metricsData.months;
      const cell = 'padding:6px 8px;border:1px solid var(--sm-border-soft);border-radius:7px;font:inherit;font-size:13px;width:100%;min-width:88px';
      const tcell = 'padding:6px 8px;border:1px solid var(--sm-border-soft);border-radius:7px;font:inherit;font-size:13px;width:100%;min-width:210px;resize:vertical;line-height:1.4';
      const rmBtn = 'width:26px;height:26px;border-radius:50%;border:0;background:var(--sm-danger);color:#fff;cursor:pointer;font-weight:700;line-height:1;font-size:12px';
      const section = (t, metrics) =>
        '<h3 class="sm-h4" style="margin:18px 0 8px">' + t + '</h3>' +
        '<div style="overflow-x:auto"><table class="sm-table sm-table--sticky" style="font-size:13px"><thead><tr>' +
          '<th class="sm-metrics-sticky" style="min-width:150px">Metric</th><th style="min-width:210px">Target / note</th>' +
          months.map((m) => '<th style="white-space:nowrap;text-align:center">' + esc(m) + '</th>').join('') +
        '</tr></thead><tbody>' +
        metrics.map((mt) => {
          const tgt = (metricsData.targets[mt.k]) || '';
          return '<tr><td class="sm-metrics-sticky"><strong>' + mt.label + '</strong></td>' +
            '<td><textarea data-tg="' + mt.k + '" rows="2" style="' + tcell + '">' + esc(tgt) + '</textarea></td>' +
            months.map((m) => { const v = (metricsData.values[m] && metricsData.values[m][mt.k]) || ''; return '<td><input data-mv="' + esc(m) + '|||' + mt.k + '" value="' + esc(v) + '" style="' + cell + '"></td>'; }).join('') +
          '</tr>';
        }).join('') + '</tbody>' +
        '<tfoot><tr><td class="sm-metrics-sticky"></td><td style="text-align:right;color:var(--sm-ink-muted);font-size:11px;vertical-align:middle">remove month →</td>' +
          months.map((m) => '<td style="text-align:center;padding-top:10px"><button type="button" data-rm-month="' + esc(m) + '" title="Remove ' + esc(m) + '" style="' + rmBtn + '">✕</button></td>').join('') +
        '</tr></tfoot></table></div>';

      el.innerHTML =
        '<div class="sm-admin__toolbar"><div><h2 class="sm-h3" style="margin:0">' + title + '</h2>' +
          '<p class="sm-field__help">' + subtitle + '</p></div>' +
          '<div class="sm-admin__filter"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-metrics-addmonth>+ Add month</button>' +
          '<button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-metrics-save>Save</button></div></div>' +
        '<span class="sm-field__help" data-metrics-status></span>' +
        sections.map((s) => section(s.title, s.metrics)).join('');

      // Live-sync every keystroke into metricsData so targets AND values
      // survive re-renders / sub-tab switches (Save persists to Firestore).
      el.querySelectorAll('[data-tg]').forEach((i) => i.addEventListener('input', () => { metricsData.targets[i.dataset.tg] = i.value; }));
      el.querySelectorAll('[data-mv]').forEach((i) => i.addEventListener('input', () => { const p = i.dataset.mv.split('|||'); (metricsData.values[p[0]] = metricsData.values[p[0]] || {})[p[1]] = i.value; }));

      const redraw = () => renderMetricsInto(el, sections, title, subtitle);
      el.querySelector('[data-metrics-addmonth]').addEventListener('click', () => {
        const label = prompt('New month label (e.g. "May 2026"):');
        if (!label) return;
        metricsGather(el);
        if (metricsData.months.indexOf(label) === -1) metricsData.months.push(label);
        metricsData.values[label] = metricsData.values[label] || {};
        redraw();
      });
      el.querySelectorAll('[data-rm-month]').forEach((b) => b.addEventListener('click', () => {
        if (!confirm('Remove the "' + b.dataset.rmMonth + '" column?')) return;
        metricsGather(el);
        metricsData.months = metricsData.months.filter((m) => m !== b.dataset.rmMonth);
        delete metricsData.values[b.dataset.rmMonth];
        redraw();
      }));
      el.querySelector('[data-metrics-save]').addEventListener('click', async () => {
        metricsGather(el);
        const status = el.querySelector('[data-metrics-status]');
        status.textContent = 'Saving…'; status.style.color = 'var(--sm-ink-muted)';
        try { await ns.fb.adminSaveManualMetrics(metricsData); status.textContent = '✓ Saved.'; status.style.color = 'var(--sm-success,#0f9d6b)'; }
        catch (e) { status.textContent = e.message || 'Save failed.'; status.style.color = 'var(--sm-danger)'; }
      });
    }

    /* ================= INSTRUCTORS (materials portal) ================= */
    const instRoot = root.querySelector('[data-sm-inst-root]');
    let instMaterials = [], instList = [];

    async function loadInstructors() {
      if (!instRoot) return;
      instRoot.innerHTML = '<p class="sm-field__help" style="padding:16px">Loading…</p>';
      try { const r = await Promise.all([ns.fb.adminListMaterials(), ns.fb.adminListInstructors()]); instMaterials = r[0] || []; instList = r[1] || []; }
      catch (e) { instRoot.innerHTML = '<p class="sm-field__error is-shown" style="padding:16px">Could not load: ' + esc(e.message || e) + '</p>'; return; }
      renderInstHome();
    }

    function renderInstHome() {
      const matRows = instMaterials.length ? instMaterials.map((m) =>
        '<tr><td><strong>' + esc(m.name || '(unnamed)') + '</strong>' + (m.sku ? ' <span class="sm-field__help">' + esc(m.sku) + '</span>' : '') + '</td>' +
        '<td class="tnum">' + bizMoney(m.standardPrice) + '</td>' +
        '<td><span class="sm-table__status sm-status--' + (m.active === false ? 'cancelled' : 'confirmed') + '">' + (m.active === false ? 'inactive' : 'active') + '</span></td>' +
        '<td style="text-align:right;white-space:nowrap"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-mat-edit="' + m.id + '">Edit</button> <button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-mat-del="' + m.id + '" style="color:var(--sm-danger)">✕</button></td></tr>'
      ).join('') : '<tr><td colspan="4" style="text-align:center;padding:16px;color:var(--sm-ink-muted)">No materials yet — add your catalog below.</td></tr>';
      const instRows = instList.length ? instList.map((i) =>
        '<tr><td><strong>' + esc(i.name || '(unnamed)') + '</strong></td><td class="sm-field__help">' + esc(i.email || '—') + '</td>' +
        '<td class="tnum">' + Object.keys(i.pricing || {}).length + '</td>' +
        '<td><span class="sm-table__status sm-status--' + (i.active === false ? 'cancelled' : 'confirmed') + '">' + (i.active === false ? 'inactive' : 'active') + '</span></td>' +
        '<td style="text-align:right"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-inst-open="' + i.id + '">Open</button></td></tr>'
      ).join('') : '<tr><td colspan="5" style="text-align:center;padding:16px;color:var(--sm-ink-muted)">No instructors yet.</td></tr>';
      instRoot.innerHTML =
        '<div class="sm-admin__toolbar"><div><h2 class="sm-h3" style="margin:0">Instructors</h2><p class="sm-field__help">Materials catalog + instructor accounts. Each instructor buys only the items you price for them.</p></div></div>' +
        '<div class="sm-card" style="padding:16px"><h3 class="sm-h4" style="margin:0 0 10px">Materials catalog</h3>' +
          '<div style="overflow-x:auto"><table class="sm-table"><thead><tr><th>Material</th><th>Standard price</th><th>Status</th><th></th></tr></thead><tbody>' + matRows + '</tbody></table></div>' +
          '<div class="sm-card" style="padding:12px;margin-top:12px;background:var(--sm-surface-alt,#f6f9fd)" data-mat-form><strong style="font-size:13px" data-mat-title>Add material</strong>' +
            '<div class="sm-form__row" style="margin-top:10px"><div class="sm-field" style="margin:0"><label>Name</label><input data-mf="name"></div><div class="sm-field" style="margin:0"><label>Standard price</label><input type="number" min="0" step="1" data-mf="standardPrice"></div><div class="sm-field" style="margin:0"><label>SKU (optional)</label><input data-mf="sku"></div></div>' +
            '<div class="sm-flex sm-gap-12" style="align-items:center;margin-top:10px"><button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-mat-save>Add material</button><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-mat-cancel hidden>Cancel</button><span class="sm-field__help" data-mat-msg></span></div></div>' +
        '</div>' +
        '<div class="sm-card" style="padding:16px;margin-top:16px"><div class="sm-flex" style="justify-content:space-between;align-items:center;margin-bottom:8px"><h3 class="sm-h4" style="margin:0">Instructor accounts</h3><button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-inst-new>+ New instructor</button></div>' +
          '<div style="overflow-x:auto"><table class="sm-table"><thead><tr><th>Instructor</th><th>Login email</th><th>Items</th><th>Status</th><th></th></tr></thead><tbody>' + instRows + '</tbody></table></div></div>';

      // Material form
      const mf = instRoot.querySelector('[data-mat-form]'); let matEditing = '';
      const mget = (k) => { const x = mf.querySelector('[data-mf="' + k + '"]'); return x ? x.value.trim() : ''; };
      const mmsg = mf.querySelector('[data-mat-msg]');
      const matReset = () => { mf.querySelectorAll('[data-mf]').forEach((x) => x.value = ''); matEditing = ''; mf.querySelector('[data-mat-title]').textContent = 'Add material'; mf.querySelector('[data-mat-save]').textContent = 'Add material'; mf.querySelector('[data-mat-cancel]').hidden = true; };
      mf.querySelector('[data-mat-save]').addEventListener('click', async () => {
        if (!mget('name')) { mmsg.textContent = 'Enter a name.'; return; }
        mmsg.textContent = 'Saving…';
        try { await ns.fb.adminSaveMaterial(matEditing || null, { name: mget('name'), standardPrice: parseFloat(mget('standardPrice')) || 0, sku: mget('sku'), active: true }); loadInstructors(); }
        catch (e) { mmsg.textContent = e.message || 'Failed.'; }
      });
      mf.querySelector('[data-mat-cancel]').addEventListener('click', matReset);
      instRoot.querySelectorAll('[data-mat-edit]').forEach((b) => b.addEventListener('click', () => { const m = instMaterials.find((x) => x.id === b.dataset.matEdit); if (!m) return; matEditing = m.id; mf.querySelector('[data-mf="name"]').value = m.name || ''; mf.querySelector('[data-mf="standardPrice"]').value = m.standardPrice || ''; mf.querySelector('[data-mf="sku"]').value = m.sku || ''; mf.querySelector('[data-mat-title]').textContent = 'Edit material'; mf.querySelector('[data-mat-save]').textContent = 'Save material'; mf.querySelector('[data-mat-cancel]').hidden = false; }));
      instRoot.querySelectorAll('[data-mat-del]').forEach((b) => b.addEventListener('click', async () => { if (!confirm('Delete this material?')) return; try { await ns.fb.adminDeleteMaterial(b.dataset.matDel); loadInstructors(); } catch (e) {} }));

      instRoot.querySelector('[data-inst-new]').addEventListener('click', () => openInstructorEditor(null));
      instRoot.querySelectorAll('[data-inst-open]').forEach((b) => b.addEventListener('click', () => openInstructorEditor(instList.find((x) => x.id === b.dataset.instOpen))));
    }

    function openInstructorEditor(inst) {
      const it = inst || { pricing: {}, active: true };
      const pricing = it.pricing || {};
      const inp = (label, id, val, type) => '<div class="sm-field"><label>' + label + '</label><input type="' + (type || 'text') + '" data-if="' + id + '" value="' + esc(val || '') + '"></div>';
      const priceRows = instMaterials.map((m) => {
        const has = (m.id in pricing);
        return '<tr><td><label class="sm-book__chk" style="margin:0"><input type="checkbox" data-allow="' + m.id + '"' + (has ? ' checked' : '') + '><span>' + esc(m.name) + '</span></label></td>' +
          '<td class="sm-field__help tnum">' + bizMoney(m.standardPrice) + '</td>' +
          '<td><input type="number" min="0" step="1" data-price="' + m.id + '" value="' + esc(has ? pricing[m.id] : '') + '" placeholder="' + (m.standardPrice || 0) + '" style="width:110px"></td></tr>';
      }).join('') || '<tr><td colspan="3" class="sm-field__help">Add materials to the catalog first.</td></tr>';
      instRoot.innerHTML =
        '<div class="sm-flex" style="justify-content:space-between;align-items:center;margin-bottom:12px"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-inst-back>← All instructors</button><span class="sm-field__help" data-inst-msg></span></div>' +
        '<h2 class="sm-h3" style="margin:0 0 4px">' + (it.id ? esc(it.name || 'Instructor') : 'New instructor') + '</h2>' +
        '<div class="sm-card" style="padding:18px;margin-top:12px" data-inst-form>' +
          '<div class="sm-form__row">' + inp('Name', 'name', it.name) + inp('Login email', 'email', it.email, 'email') + '</div>' +
          '<div class="sm-form__row" style="margin-top:12px">' + inp('Phone', 'phone', it.phone, 'tel') + '<div class="sm-field"><label>&nbsp;</label><label class="sm-book__chk"><input type="checkbox" data-if="active"' + (it.active === false ? '' : ' checked') + '><span>Account active</span></label></div></div>' +
          '<h3 class="sm-h4" style="margin:22px 0 4px">Materials this instructor can buy</h3>' +
          '<p class="sm-field__help" style="margin:0 0 8px">Check an item to allow it; set their price (blank uses the standard price). Unchecked items are hidden from that instructor.</p>' +
          '<div style="overflow-x:auto"><table class="sm-table" style="font-size:13px"><thead><tr><th>Material</th><th>Standard</th><th>Their price</th></tr></thead><tbody>' + priceRows + '</tbody></table></div>' +
          '<div class="sm-flex sm-gap-12" style="align-items:center;margin-top:18px"><button type="button" class="sm-btn sm-btn--primary" data-inst-save>Save instructor</button>' + (it.id ? '<button type="button" class="sm-btn sm-btn--light" data-inst-del style="color:var(--sm-danger)">Delete</button>' : '') + '</div>' +
        '</div>';
      instRoot.querySelector('[data-inst-back]').addEventListener('click', loadInstructors);
      const msg = instRoot.querySelector('[data-inst-msg]');
      instRoot.querySelector('[data-inst-save]').addEventListener('click', async () => {
        const form = instRoot.querySelector('[data-inst-form]');
        const g = (id) => { const x = form.querySelector('[data-if="' + id + '"]'); return x ? x.value.trim() : ''; };
        const pricingOut = {};
        form.querySelectorAll('[data-allow]').forEach((cb) => { if (cb.checked) { const mid = cb.dataset.allow; const priceEl = form.querySelector('[data-price="' + mid + '"]'); const mat = instMaterials.find((x) => x.id === mid); const p = parseFloat(priceEl && priceEl.value); pricingOut[mid] = (p >= 0 && priceEl.value !== '') ? p : (mat ? (mat.standardPrice || 0) : 0); } });
        const data = { name: g('name'), email: g('email').toLowerCase(), phone: g('phone'), active: form.querySelector('[data-if="active"]').checked, pricing: pricingOut };
        if (!data.name) { msg.textContent = 'Name required.'; msg.style.color = 'var(--sm-danger)'; return; }
        msg.textContent = 'Saving…'; msg.style.color = 'var(--sm-ink-muted)';
        try { const res = await ns.fb.adminSaveInstructor(it.id || null, data); msg.textContent = '✓ Saved.'; openInstructorEditor(Object.assign({ id: res.id }, data)); }
        catch (e) { msg.textContent = e.message || 'Save failed.'; msg.style.color = 'var(--sm-danger)'; }
      });
      const del = instRoot.querySelector('[data-inst-del]');
      if (del) del.addEventListener('click', async () => { if (!confirm('Delete this instructor account?')) return; try { await ns.fb.adminDeleteInstructor(it.id); loadInstructors(); } catch (e) {} });
    }

    /* ================= CLASS PORTAL (check-ins + quiz results) ================= */
    const classRoot = root.querySelector('[data-sm-class-root]');
    async function loadClassData() {
      if (!classRoot) return;
      classRoot.innerHTML = '<p class="sm-field__help" style="padding:16px">Loading…</p>';
      let regs = [], quiz = [];
      try { const r = await Promise.all([ns.fb.adminListClassRegistrations(), ns.fb.adminListClassQuiz()]); regs = r[0] || []; quiz = r[1] || []; }
      catch (e) { classRoot.innerHTML = '<p class="sm-field__error is-shown" style="padding:16px">Could not load: ' + esc(e.message || e) + '</p>'; return; }
      const dt = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (e) { return s; } };
      const csv = (rows) => rows.map((r) => r.map((c) => '"' + String(c == null ? '' : c).replace(/"/g, '""') + '"').join(',')).join('\n');
      const regRows = regs.length ? regs.map((r) =>
        '<tr><td><strong>' + esc((r.firstName || '') + ' ' + (r.lastName || '')) + '</strong></td><td class="sm-field__help">' + esc(r.email || '') + '<br>' + esc(r.phone || '') + '</td>' +
        '<td class="sm-field__help">' + esc(r.address || '') + '</td><td>' + esc(r.className || '') + '</td><td class="tnum" style="white-space:nowrap">' + dt(r.createdAt) + '</td></tr>'
      ).join('') : '<tr><td colspan="5" style="text-align:center;padding:16px;color:var(--sm-ink-muted)">No check-ins yet.</td></tr>';
      const quizRows = quiz.length ? quiz.map((q) => { const pct = q.percent != null ? q.percent : (q.total ? Math.round(q.score / q.total * 100) : 0);
        return '<tr><td><strong>' + esc((q.firstName || '') + ' ' + (q.lastName || '')) + '</strong></td><td class="sm-field__help">' + esc(q.email || '') + '</td><td>' + esc(q.className || '') + '</td>' +
          '<td class="tnum">' + (q.score != null ? q.score : '—') + ' / ' + (q.total || '—') + '</td>' +
          '<td><span class="sm-cert ' + (pct >= 80 ? 'sm-cert--ok' : 'sm-cert--no') + '">' + pct + '%</span></td><td class="tnum" style="white-space:nowrap">' + dt(q.createdAt) + '</td></tr>';
      }).join('') : '<tr><td colspan="6" style="text-align:center;padding:16px;color:var(--sm-ink-muted)">No quiz submissions yet.</td></tr>';
      classRoot.innerHTML =
        '<div class="sm-admin__toolbar"><div><h2 class="sm-h3" style="margin:0">Class portal</h2><p class="sm-field__help">Student check-ins and quiz results from the class page.</p></div><div class="sm-admin__filter"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-class-refresh>Refresh</button><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-class-csv>Export CSV</button></div></div>' +
        '<div class="sm-card" style="padding:16px"><h3 class="sm-h4" style="margin:0 0 10px">Check-ins <span class="sm-field__help">(' + regs.length + ')</span></h3>' +
          '<div style="overflow-x:auto"><table class="sm-table"><thead><tr><th>Name</th><th>Contact</th><th>Address</th><th>Class</th><th>When</th></tr></thead><tbody>' + regRows + '</tbody></table></div></div>' +
        '<div class="sm-card" style="padding:16px;margin-top:16px"><h3 class="sm-h4" style="margin:0 0 10px">Quiz results <span class="sm-field__help">(' + quiz.length + ')</span></h3>' +
          '<div style="overflow-x:auto"><table class="sm-table"><thead><tr><th>Name</th><th>Email</th><th>Class</th><th>Score</th><th>%</th><th>When</th></tr></thead><tbody>' + quizRows + '</tbody></table></div></div>';
      classRoot.querySelector('[data-class-refresh]').addEventListener('click', loadClassData);
      classRoot.querySelector('[data-class-csv]').addEventListener('click', () => {
        const header = ['Type', 'First', 'Last', 'Email', 'Phone', 'Address', 'Class', 'Score', 'Total', 'Percent', 'When'];
        const lines = [header];
        regs.forEach((r) => lines.push(['check-in', r.firstName, r.lastName, r.email, r.phone, r.address, r.className, '', '', '', r.createdAt]));
        quiz.forEach((q) => lines.push(['quiz', q.firstName, q.lastName, q.email, '', '', q.className, q.score, q.total, q.percent, q.createdAt]));
        const blob = new Blob([csv(lines)], { type: 'text/csv' });
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'class-portal.csv'; a.click();
      });
    }

    async function loadServicesTable() {
      if (!svcRows) return;
      svcRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        cache.services = await ns.fb.adminListServicesAll();
        cache.resourceGroups = await ns.fb.adminListResourceGroups();
        if (!cache.locations || !cache.locations.length) {
          cache.locations = await ns.fb.adminListLocationsAll();
        }
      } catch (err) {
        console.error('[admin] list services failed', err);
        svcRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      refreshSvcFilterDropdowns();
      if (svcBulkMode) { renderServicesGrid(); return; }
      if (!cache.services.length) {
        svcRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No services yet. Click + Add service.</td></tr>';
        return;
      }

      // Apply filter selections.
      const filterProduct  = svcFilterProduct  ? svcFilterProduct.value  : '';
      const filterStyle    = svcFilterStyle    ? svcFilterStyle.value    : '';
      const filterLocation = svcFilterLocation ? svcFilterLocation.value : '';
      const filtered = cache.services.filter((s) => {
        if (filterProduct  && s.productKey  !== filterProduct)  return false;
        if (filterStyle    && s.styleKey    !== filterStyle)    return false;
        if (filterLocation && s.locationKey !== filterLocation) return false;
        return true;
      });

      if (!filtered.length) {
        svcRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No services match the current filters.</td></tr>';
        return;
      }

      svcRows.innerHTML = filtered.map((s) => {
        let cap;
        if (s.blockMode === 'day') cap = (s.durationDays || 1) + ' day' + ((s.durationDays || 1) !== 1 ? 's' : '') + ' · 1 class';
        else cap = (s.durationMinutes || '—') + ' min · max ' + (s.maxPerSlot || 1);
        const locLabel = (cache.locations.find((l) => l.id === s.locationKey) || {}).name || s.locationKey || '';
        const composition = [s.productKey, SM_STYLE_LABEL[s.styleKey] || s.styleKey, locLabel].filter(Boolean).join(' · ');
        return '<tr>' +
          '<td><strong>' + (s.name || s.id) + '</strong>' +
            (composition ? '<br><span style="color:var(--sm-ink);font-size:12px">' + composition + '</span>' : '') +
            '<br><span style="color:var(--sm-ink-muted);font-size:12px">' + s.id + (s.adminOnly ? ' · admin-only' : '') + '</span>' +
          '</td>' +
          '<td>' + (s.resourceGroup || '—') + '</td>' +
          '<td>' + (s.blockMode || 'slot') + '</td>' +
          '<td>' + cap + '</td>' +
          '<td><span class="sm-table__status sm-status--' + (s.active ? 'confirmed' : 'cancelled') + '">' + (s.active ? 'Active' : 'Inactive') + '</span></td>' +
          '<td style="white-space:nowrap">' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-svc-edit="' + s.id + '">Edit</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-svc-dupe="' + s.id + '">Duplicate</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-svc-del="' + s.id + '" style="color:var(--sm-danger)">Delete</button>' +
          '</td>' +
        '</tr>';
      }).join('');
    }

    // Re-run filter on dropdown change. Doesn't refetch — just re-applies.
    [svcFilterProduct, svcFilterStyle, svcFilterLocation].forEach((el) => {
      el && el.addEventListener('change', loadServicesTable);
    });

    function sessionRowHtml(sess) {
      const date  = (sess && sess.date) || '';
      const start = (sess && sess.startTime) || '';
      const end   = (sess && sess.endTime) || '';
      return '<div class="sm-svc-session-row" style="display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:6px;margin-top:6px;align-items:center">' +
        '<input type="date" data-sm-sess-date value="' + date + '" style="font-size:13px;padding:6px">' +
        '<input type="text" data-sm-sess-start placeholder="2:00 PM" value="' + start.replace(/"/g, '&quot;') + '" style="font-size:13px;padding:6px">' +
        '<input type="text" data-sm-sess-end placeholder="5:00 PM" value="' + end.replace(/"/g, '&quot;') + '" style="font-size:13px;padding:6px">' +
        '<button type="button" data-sm-sess-del aria-label="Remove" style="background:transparent;border:none;color:var(--sm-danger);cursor:pointer;font-size:18px;padding:0 8px">×</button>' +
      '</div>';
    }

    function serviceModalHtml(s, isNew) {
      const groupOpts = cache.resourceGroups.length
        ? cache.resourceGroups.map((g) => '<option value="' + g.id + '"' + (s.resourceGroup === g.id ? ' selected' : '') + '>' + (g.name || g.id) + '</option>').join('')
        : '<option value="">(none defined — add in Backend → Locations)</option>';
      const locationOpts = '<option value="">— none —</option>' +
        cache.locations.map((l) => '<option value="' + l.id + '"' + (s.locationKey === l.id ? ' selected' : '') + '>' + (l.name || l.id) + '</option>').join('');
      const styleOpts = '<option value="">— none —</option>' +
        SM_STYLE_OPTIONS.map((o) => '<option value="' + o.value + '"' + (s.styleKey === o.value ? ' selected' : '') + '>' + o.label + '</option>').join('');
      const productSuggestions = Array.from(new Set(cache.services.map((x) => x.productKey).filter(Boolean))).sort();
      const productDatalist = '<datalist id="sm-svc-products">' + productSuggestions.map((p) => '<option value="' + p + '">').join('') + '</datalist>';
      return '<form class="sm-form" data-sm-svc-form>' +
        productDatalist +
        '<fieldset class="sm-field" style="border:1px dashed var(--sm-border);padding:12px;border-radius:8px;margin-bottom:14px">' +
          '<legend style="font-size:12px;color:var(--sm-ink-muted);padding:0 6px">Service composition (used to build the ID and filter bookings)</legend>' +
          '<div class="sm-form__row">' +
            '<div class="sm-field"><label>Product</label><input type="text" name="productKey" list="sm-svc-products" value="' + (s.productKey || '') + '" placeholder="bls, acls, pals, cna, …"></div>' +
            '<div class="sm-field"><label>Style</label><select name="styleKey">' + styleOpts + '</select></div>' +
            '<div class="sm-field"><label>Location</label><select name="locationKey">' + locationOpts + '</select></div>' +
          '</div>' +
          (isNew ? '<div class="sm-field__help">Suggested ID: <code data-sm-svc-id-preview>—</code></div>' : '') +
        '</fieldset>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>ID' + (isNew ? ' (lowercase, no spaces)' : '') + '</label><input type="text" name="id" value="' + (s.id || '') + '" data-sm-svc-id ' + (isNew ? 'required' : 'readonly style="background:var(--sm-surface-alt)"') + '></div>' +
          '<div class="sm-field"><label>Name</label><input type="text" name="name" value="' + ((s.name || '').replace(/"/g, '&quot;')) + '" required></div>' +
        '</div>' +
        '<div class="sm-field"><label>Description</label><textarea name="description" rows="2">' + (s.description || '') + '</textarea></div>' +

        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Resource group</label><select name="resourceGroup">' + groupOpts + '</select></div>' +
          '<div class="sm-field"><label>Block mode</label>' +
            '<select name="blockMode">' +
              '<option value="slot"' + (s.blockMode !== 'day' ? ' selected' : '') + '>Slot (time-based)</option>' +
              '<option value="day"' + (s.blockMode === 'day' ? ' selected' : '') + '>Day (full-day for multi-day programs)</option>' +
            '</select>' +
          '</div>' +
        '</div>' +

        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Duration (minutes)</label><input type="number" name="durationMinutes" min="5" step="5" value="' + (s.durationMinutes || '') + '"><div class="sm-field__help">Used when block mode = slot.</div></div>' +
          '<div class="sm-field"><label>Duration (days)</label><input type="number" name="durationDays" min="1" value="' + (s.durationDays || '') + '"><div class="sm-field__help">Used when block mode = day.</div></div>' +
        '</div>' +

        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Max attendees per slot</label><input type="number" name="maxPerSlot" min="1" value="' + (s.maxPerSlot || 1) + '" required></div>' +
          '<div class="sm-field"><label>Price display</label><input type="text" name="priceDisplay" value="' + ((s.priceDisplay || '').replace(/"/g, '&quot;')) + '" placeholder="$95"></div>' +
        '</div>' +

        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Shopify product ID</label><input type="text" name="shopifyProductId" value="' + (s.shopifyProductId || '') + '"></div>' +
          '<div class="sm-field"><label>Shopify variant ID</label><input type="text" name="shopifyVariantId" value="' + (s.shopifyVariantId || '') + '"></div>' +
        '</div>' +

        '<label class="sm-book__chk sm-mt-16"><input type="checkbox" name="active"' + (s.active === false ? '' : ' checked') + '><span>Active</span></label>' +
        '<label class="sm-book__chk"><input type="checkbox" name="adminOnly"' + (s.adminOnly ? ' checked' : '') + '><span>Admin-only (not shown on customer booking form)</span></label>' +
        '<label class="sm-book__chk"><input type="checkbox" name="multiDay"' + (s.multiDay ? ' checked' : '') + '><span>Multi-day service</span></label>' +
        '<label class="sm-book__chk"><input type="checkbox" name="requiresDob"' + (s.requiresDob ? ' checked' : '') + '><span>Requires date of birth (e.g. CNA state registration)</span></label>' +
        '<label class="sm-book__chk"><input type="checkbox" name="requiresProofUpload"' + (s.requiresProofUpload ? ' checked' : '') + '><span>Requires proof-of-completion upload (Skills Only)</span></label>' +

        (function buildSessionsBlock() {
          const map = s.sessionsByLocation || {};
          const hasAny = Object.keys(map).some((k) => Array.isArray(map[k]) && map[k].length);
          if (!cache.locations || !cache.locations.length) {
            return '<details class="sm-field sm-mt-16" style="border:1px dashed var(--sm-border);padding:12px;border-radius:8px">' +
              '<summary style="cursor:pointer;font-weight:600;font-size:13px">Per-location scheduled sessions</summary>' +
              '<p class="sm-field__help" style="margin-top:8px">Add locations first, then come back to schedule sessions.</p>' +
              '</details>';
          }
          let html = '<details class="sm-field sm-mt-16" data-sm-svc-sessions style="border:1px dashed var(--sm-border);padding:12px;border-radius:8px"' + (hasAny ? ' open' : '') + '>' +
            '<summary style="cursor:pointer;font-weight:600;font-size:13px">Per-location scheduled sessions (optional)</summary>' +
            '<p class="sm-field__help" style="margin-top:8px">Lock this service to specific dates with a custom start/end time. When sessions are listed for a location, the booking calendar shows only those dates and the time picker shows only those time ranges. Leave empty for a location to use its default openDays + slots.</p>';
          cache.locations.forEach((l) => {
            const rows = (map[l.id] || []);
            html += '<div class="sm-svc-loc-sessions" data-sm-svc-loc="' + l.id + '" style="border:1px solid var(--sm-border-soft);padding:10px;border-radius:6px;margin-top:10px">' +
              '<div style="font-weight:600;font-size:13px;margin-bottom:6px">' + (l.name || l.id) + ' <span style="color:var(--sm-ink-muted);font-weight:400;font-size:11px">(' + l.id + ')</span></div>' +
              '<div data-sm-svc-sessions-list>';
            rows.forEach((sess) => {
              html += sessionRowHtml(sess);
            });
            html += '</div>' +
              '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-svc-add-session style="margin-top:8px">+ Add session</button>' +
            '</div>';
          });
          html += '</details>';
          return html;
        })() +

        (function buildSlotsByLocationBlock() {
          const map = s.slotsByLocation || {};
          const hasAny = Object.keys(map).some((k) => Array.isArray(map[k]) && map[k].length);
          if (!cache.locations || !cache.locations.length) {
            return '';
          }
          let html = '<details class="sm-field sm-mt-16" style="border:1px dashed var(--sm-border);padding:12px;border-radius:8px"' + (hasAny ? ' open' : '') + '>' +
            '<summary style="cursor:pointer;font-weight:600;font-size:13px">Per-location time-of-day overrides (optional)</summary>' +
            '<p class="sm-field__help" style="margin-top:8px">Tighten the booking time slots <strong>for this service</strong> at a specific office. Useful when one course (e.g. Heartsaver) is only run during certain hours, even though the office is open longer. Comma-separated; leave blank to use the location\'s default slots.</p>';
          cache.locations.forEach((l) => {
            const list = Array.isArray(map[l.id]) ? map[l.id].join(', ') : '';
            html += '<div class="sm-field" style="margin-top:10px"><label>' + (l.name || l.id) + ' <span style="color:var(--sm-ink-muted);font-size:11px;font-weight:400">(' + l.id + ')</span></label>' +
              '<input type="text" data-sm-svc-slots-loc="' + l.id + '" value="' + String(list).replace(/"/g,'&quot;') + '" placeholder="9:00 AM, 10:00 AM, 2:00 PM, 3:00 PM">' +
            '</div>';
          });
          html += '</details>';
          return html;
        })() +

        (function buildOpenDaysByLocationBlock() {
          const map = s.openDaysByLocation || {};
          const hasAny = Object.keys(map).some((k) => Array.isArray(map[k]) && map[k].length);
          if (!cache.locations || !cache.locations.length) {
            return '';
          }
          const DAY_LABELS = [[0,'Sun'],[1,'Mon'],[2,'Tue'],[3,'Wed'],[4,'Thu'],[5,'Fri'],[6,'Sat']];
          let html = '<details class="sm-field sm-mt-16" style="border:1px dashed var(--sm-border);padding:12px;border-radius:8px"' + (hasAny ? ' open' : '') + '>' +
            '<summary style="cursor:pointer;font-weight:600;font-size:13px">Per-location day-of-week overrides (optional)</summary>' +
            '<p class="sm-field__help" style="margin-top:8px">Restrict <strong>which days</strong> this service is bookable at a specific office. Example: Heartsaver only runs Mon/Wed/Fri at Lake City. Leave all unchecked for a location to use that location\'s default open days.</p>';
          cache.locations.forEach((l) => {
            const days = Array.isArray(map[l.id]) ? map[l.id] : [];
            html += '<div style="margin-top:10px;padding:8px;border:1px solid var(--sm-border-soft);border-radius:6px"' +
              ' data-sm-svc-days-loc="' + l.id + '">' +
              '<div style="font-weight:600;font-size:13px;margin-bottom:6px">' + (l.name || l.id) + ' <span style="color:var(--sm-ink-muted);font-weight:400;font-size:11px">(' + l.id + ')</span></div>' +
              '<div style="display:flex;gap:10px;flex-wrap:wrap">' +
              DAY_LABELS.map(([n, lbl]) => '<label class="sm-book__chk" style="font-size:13px"><input type="checkbox" data-sm-svc-day="' + n + '"' + (days.indexOf(n) > -1 ? ' checked' : '') + '><span>' + lbl + '</span></label>').join('') +
              '</div></div>';
          });
          html += '</details>';
          return html;
        })() +

        '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
          '<button type="submit" class="sm-btn sm-btn--primary">Save service</button>' +
          '<span class="sm-field__help" data-sm-svc-msg></span>' +
        '</div>' +
      '</form>';
    }

    async function openServiceModal(id, prefill) {
      if (!modal || !modalBody) return;
      if (!cache.locations || !cache.locations.length) {
        try { cache.locations = await ns.fb.adminListLocationsAll(); } catch (e) {}
      }
      const isNew = !id;
      // prefill (used by Duplicate) populates the new-service form with an
      // existing service's settings so the merchant only changes what
      // differs (typically locationKey + the auto-composed ID).
      const s = isNew
        ? (prefill || { active: true, blockMode: 'slot', maxPerSlot: 6, durationMinutes: 60 })
        : (cache.services.find((x) => x.id === id) || {});
      modalTitle && (modalTitle.textContent = isNew ? (prefill ? 'Duplicate service' : 'Add service') : ('Edit service — ' + (s.name || s.id)));
      modalBody.innerHTML = serviceModalHtml(s, isNew);
      modal.hidden = false;
      const form = modalBody.querySelector('[data-sm-svc-form]');
      const msg  = modalBody.querySelector('[data-sm-svc-msg]');

      // Sessions add/remove — delegated clicks within the modal.
      form.addEventListener('click', (ev) => {
        const addBtn = ev.target.closest('[data-sm-svc-add-session]');
        if (addBtn) {
          ev.preventDefault();
          const list = addBtn.closest('[data-sm-svc-loc]').querySelector('[data-sm-svc-sessions-list]');
          if (list) {
            const tmp = document.createElement('div');
            tmp.innerHTML = sessionRowHtml({});
            list.appendChild(tmp.firstChild);
          }
          return;
        }
        const delBtn = ev.target.closest('[data-sm-sess-del]');
        if (delBtn) {
          ev.preventDefault();
          const row = delBtn.closest('.sm-svc-session-row');
          if (row) row.remove();
        }
      });

      // Live composed-ID preview + auto-fill (new services only).
      if (isNew) {
        const idField  = form.querySelector('[data-sm-svc-id]');
        const preview  = form.querySelector('[data-sm-svc-id-preview]');
        const prodEl   = form.querySelector('[name="productKey"]');
        const styleEl  = form.querySelector('[name="styleKey"]');
        const locEl    = form.querySelector('[name="locationKey"]');
        let idTouched = false;
        idField && idField.addEventListener('input', () => { idTouched = true; });
        function recomposeId() {
          const parts = [smSlug(prodEl && prodEl.value), styleEl && styleEl.value, locEl && locEl.value].filter(Boolean);
          const composed = parts.join('-');
          if (preview) preview.textContent = composed || '—';
          if (idField && !idTouched && composed) idField.value = composed;
        }
        [prodEl, styleEl, locEl].forEach((el) => el && el.addEventListener('input', recomposeId));
        recomposeId();
      }

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const fd = new FormData(form);
          const newId = (fd.get('id') || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
          if (!newId) throw new Error('ID is required.');
          const toInt = (v) => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };
          // Build sessionsByLocation from the structured rows. Only locations
          // that have at least one fully-filled session are included; rows
          // missing a date are skipped.
          const sessionsByLocation = {};
          form.querySelectorAll('[data-sm-svc-loc]').forEach((block) => {
            const locId = block.dataset.smSvcLoc;
            const rows = [];
            block.querySelectorAll('.sm-svc-session-row').forEach((row) => {
              const date  = (row.querySelector('[data-sm-sess-date]')  || {}).value || '';
              const start = (row.querySelector('[data-sm-sess-start]') || {}).value || '';
              const end   = (row.querySelector('[data-sm-sess-end]')   || {}).value || '';
              if (date && start) rows.push({ date: date.trim(), startTime: start.trim(), endTime: end.trim() });
            });
            if (rows.length) sessionsByLocation[locId] = rows;
          });
          // Per-location time-of-day overrides: parse the comma-separated
          // textarea per location and keep only locations with non-empty lists.
          const slotsByLocation = {};
          form.querySelectorAll('[data-sm-svc-slots-loc]').forEach((inp) => {
            const locId = inp.dataset.smSvcSlotsLoc;
            const list = (inp.value || '').split(',').map((s) => s.trim()).filter(Boolean);
            if (list.length) slotsByLocation[locId] = list;
          });
          // Per-location day-of-week overrides: collect checked days per loc.
          const openDaysByLocation = {};
          form.querySelectorAll('[data-sm-svc-days-loc]').forEach((box) => {
            const locId = box.dataset.smSvcDaysLoc;
            const days = Array.from(box.querySelectorAll('[data-sm-svc-day]:checked'))
              .map((cb) => parseInt(cb.dataset.smSvcDay, 10))
              .filter((n) => !isNaN(n))
              .sort((a, b) => a - b);
            if (days.length) openDaysByLocation[locId] = days;
          });
          const data = {
            active: !!fd.get('active'),
            name: fd.get('name') || newId,
            description: fd.get('description') || '',
            resourceGroup: fd.get('resourceGroup') || '',
            productKey: smSlug(fd.get('productKey')) || '',
            styleKey: (fd.get('styleKey') || '').toString(),
            locationKey: (fd.get('locationKey') || '').toString(),
            blockMode: fd.get('blockMode') || 'slot',
            durationMinutes: toInt(fd.get('durationMinutes')),
            durationDays: toInt(fd.get('durationDays')),
            maxPerSlot: toInt(fd.get('maxPerSlot')) || 1,
            priceDisplay: fd.get('priceDisplay') || '',
            shopifyProductId: fd.get('shopifyProductId') || null,
            shopifyVariantId: fd.get('shopifyVariantId') || null,
            adminOnly: !!fd.get('adminOnly'),
            multiDay: !!fd.get('multiDay'),
            requiresDob: !!fd.get('requiresDob'),
            requiresProofUpload: !!fd.get('requiresProofUpload'),
            sessionsByLocation: Object.keys(sessionsByLocation).length ? sessionsByLocation : null,
            slotsByLocation: Object.keys(slotsByLocation).length ? slotsByLocation : null,
            openDaysByLocation: Object.keys(openDaysByLocation).length ? openDaysByLocation : null
          };
          await ns.fb.adminSaveService(newId, data);
          msg.textContent = '✓ Saved.';
          await loadServicesTable();
          await loadLookups();
          setTimeout(closeModal, 500);
        } catch (err) {
          console.error('[admin] save service failed', err);
          msg.textContent = err.message || 'Save failed.';
        }
      });
    }

    svcRows && svcRows.addEventListener('click', async (e) => {
      const editBtn = e.target.closest('[data-sm-svc-edit]');
      const dupeBtn = e.target.closest('[data-sm-svc-dupe]');
      const delBtn  = e.target.closest('[data-sm-svc-del]');
      if (editBtn) openServiceModal(editBtn.dataset.smSvcEdit);
      else if (dupeBtn) {
        // Open the create-service modal pre-filled from the source row.
        // The ID is blanked so the merchant picks a fresh composition
        // (typically same product + style, different location).
        const src = cache.services.find((x) => x.id === dupeBtn.dataset.smSvcDupe);
        if (!src) return;
        const clone = Object.assign({}, src, { id: '', name: (src.name || '') + ' (copy)' });
        openServiceModal(null, clone);
      }
      else if (delBtn) {
        if (!confirm('Delete service "' + delBtn.dataset.smSvcDel + '"? Existing bookings reference this ID.')) return;
        delBtn.disabled = true;
        try { await ns.fb.adminDeleteService(delBtn.dataset.smSvcDel); await loadServicesTable(); await loadLookups(); }
        catch (err) { delBtn.disabled = false; alert(err.message || 'Delete failed.'); }
      }
    });
    svcNewBtn && svcNewBtn.addEventListener('click', () => openServiceModal(null));

    // Restore built-in services/groups (e.g. Background Check) that were never
    // written to Firestore. Safe: creates only missing docs, never overwrites.
    const svcRestoreBtn = root.querySelector('[data-sm-svc-restore]');
    const svcRestoreMsg = root.querySelector('[data-sm-svc-restore-msg]');
    svcRestoreBtn && svcRestoreBtn.addEventListener('click', async () => {
      if (typeof ns.fb.adminSeedCatalog !== 'function') { alert('Update unavailable — reload the page.'); return; }
      if (!confirm('Create any built-in services and resource groups that are missing from the database? Existing ones are left unchanged.')) return;
      svcRestoreBtn.disabled = true;
      if (svcRestoreMsg) { svcRestoreMsg.hidden = false; svcRestoreMsg.textContent = 'Checking catalog…'; }
      try {
        const res = await ns.fb.adminSeedCatalog();
        const made = (res.created || []).map((p) => p.split('/')[1]);
        await loadServicesTable();
        await loadLookups();
        if (svcRestoreMsg) {
          svcRestoreMsg.textContent = made.length
            ? '✓ Added ' + made.length + ' missing: ' + made.join(', ') + '.'
            : '✓ Nothing missing — all built-in services already exist.';
        }
      } catch (err) {
        console.error('[admin] restore built-ins failed', err);
        if (svcRestoreMsg) svcRestoreMsg.textContent = err.message || 'Failed.';
      } finally {
        svcRestoreBtn.disabled = false;
      }
    });

    /* ---------- Services BULK EDIT grid ---------- */
    let svcBulkMode = false;
    let svcDrafts = [];   // new rows being added: [{ id, source }]

    function svcFilteredList() {
      const fp = svcFilterProduct ? svcFilterProduct.value : '';
      const fs = svcFilterStyle ? svcFilterStyle.value : '';
      const fl = svcFilterLocation ? svcFilterLocation.value : '';
      return (cache.services || []).filter((s) =>
        (!fp || s.productKey === fp) && (!fs || s.styleKey === fs) && (!fl || s.locationKey === fl));
    }
    // The flat, grid-editable fields of a service (normalized for comparison).
    function svcGridFields(s) {
      return {
        active: !!s.active,
        name: s.name || '',
        productKey: smSlug(s.productKey || ''),
        priceDisplay: s.priceDisplay || '',
        styleKey: s.styleKey || '',
        locationKey: s.locationKey || '',
        resourceGroup: s.resourceGroup || '',
        blockMode: s.blockMode || 'slot',
        durationMinutes: (s.durationMinutes == null || s.durationMinutes === '') ? '' : (parseInt(s.durationMinutes, 10) || ''),
        maxPerSlot: (s.maxPerSlot == null || s.maxPerSlot === '') ? 1 : (parseInt(s.maxPerSlot, 10) || 1),
        adminOnly: !!s.adminOnly,
        shopifyProductId: s.shopifyProductId || '',
        shopifyVariantId: s.shopifyVariantId || ''
      };
    }
    function svcGridFieldsFromRaw(raw) {
      return svcGridFields({
        active: raw.active, name: raw.name, productKey: raw.productKey, priceDisplay: raw.priceDisplay, styleKey: raw.styleKey,
        locationKey: raw.locationKey, resourceGroup: raw.resourceGroup, blockMode: raw.blockMode,
        durationMinutes: raw.durationMinutes, maxPerSlot: raw.maxPerSlot, adminOnly: raw.adminOnly,
        shopifyProductId: raw.shopifyProductId, shopifyVariantId: raw.shopifyVariantId
      });
    }
    function svcSelOptions(list, val, blank) {
      return (blank != null ? '<option value="">' + blank + '</option>' : '') +
        list.map((o) => '<option value="' + esc(o.value) + '"' + (String(o.value) === String(val) ? ' selected' : '') + '>' + esc(o.label) + '</option>').join('');
    }
    function svcGridRow(s, draftIdx) {
      const f = svcGridFields(s);
      const isDraft = draftIdx != null;
      const styleList = SM_STYLE_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
      const locList = (cache.locations || []).map((l) => ({ value: l.id, label: l.name || l.id }));
      const grpList = (cache.resourceGroups || []).map((g) => ({ value: g.id, label: g.name || g.id }));
      const modeList = [{ value: 'slot', label: 'slot' }, { value: 'day', label: 'day' }];
      const idCell = '<input data-f="id" value="' + esc(s.id || '') + '"' + (isDraft ? ' placeholder="new-service-id"' : '') + ' style="width:170px">';
      return '<tr data-svc-row' + (isDraft ? ' data-draft="' + draftIdx + '"' : ' data-id="' + esc(s.id) + '"') + '>' +
        '<td><input type="checkbox" data-svc-check></td>' +
        '<td style="text-align:center"><input type="checkbox" data-f="active"' + (f.active ? ' checked' : '') + '></td>' +
        '<td><input data-f="name" value="' + esc(f.name) + '" style="width:150px"></td>' +
        '<td>' + idCell + '</td>' +
        '<td><input data-f="productKey" value="' + esc(f.productKey) + '" list="sm-svc-grid-products" placeholder="acls" style="width:90px"></td>' +
        '<td><input data-f="priceDisplay" value="' + esc(f.priceDisplay) + '" style="width:70px"></td>' +
        '<td><select data-f="styleKey">' + svcSelOptions(styleList, f.styleKey, '—') + '</select></td>' +
        '<td><select data-f="locationKey">' + svcSelOptions(locList, f.locationKey, '—') + '</select></td>' +
        '<td><select data-f="resourceGroup">' + svcSelOptions(grpList, f.resourceGroup, '—') + '</select></td>' +
        '<td><select data-f="blockMode">' + svcSelOptions(modeList, f.blockMode, null) + '</select></td>' +
        '<td><input type="number" data-f="durationMinutes" value="' + esc(f.durationMinutes) + '" style="width:60px"></td>' +
        '<td><input type="number" data-f="maxPerSlot" value="' + esc(f.maxPerSlot) + '" style="width:52px"></td>' +
        '<td style="text-align:center"><input type="checkbox" data-f="adminOnly"' + (f.adminOnly ? ' checked' : '') + '></td>' +
        '<td><input data-f="shopifyProductId" value="' + esc(f.shopifyProductId) + '" style="width:120px"></td>' +
        '<td><input data-f="shopifyVariantId" value="' + esc(f.shopifyVariantId) + '" style="width:120px"></td>' +
        '<td style="white-space:nowrap">' +
          (isDraft
            ? '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-draft-del="' + draftIdx + '" style="color:var(--sm-danger)">Remove</button>'
            : '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-grid-dupe="' + esc(s.id) + '">Duplicate</button>') +
        '</td>' +
      '</tr>';
    }
    function renderServicesGrid() {
      if (!svcGrid) return;
      const rows = svcFilteredList();
      const prodSuggest = Array.from(new Set((cache.services || []).map((s) => s.productKey).filter(Boolean))).sort();
      const dl = '<datalist id="sm-svc-grid-products">' + prodSuggest.map((p) => '<option value="' + esc(p) + '">').join('') + '</datalist>';
      const head = '<tr><th><input type="checkbox" data-svc-checkall></th><th>On</th><th>Name</th><th>ID</th><th>Product</th><th>Price</th><th>Style</th><th>Location</th><th>Group</th><th>Mode</th><th>Min</th><th>Max</th><th>Admin</th><th>Shopify product</th><th>Shopify variant</th><th></th></tr>';
      const body = rows.map((s) => svcGridRow(s, null)).join('') +
        svcDrafts.map((d, i) => svcGridRow(Object.assign({}, d.source, { id: d.id }), i)).join('');
      svcGrid.innerHTML = dl + '<table class="sm-table" style="font-size:12px;min-width:1300px"><thead>' + head + '</thead><tbody>' +
        (body || '<tr><td colspan="16" style="text-align:center;padding:16px;color:var(--sm-ink-muted)">No services. Use “+ Blank row” or Duplicate.</td></tr>') +
        '</tbody></table>';
      updateSvcSelCount();
    }
    function svcCheckedRows() {
      return Array.from(svcGrid.querySelectorAll('tr[data-svc-row]')).filter((tr) => {
        const c = tr.querySelector('[data-svc-check]'); return c && c.checked;
      });
    }
    function updateSvcSelCount() { const el = svcBulkBar && svcBulkBar.querySelector('[data-svc-selcount]'); if (el) el.textContent = svcCheckedRows().length; }
    function renderServicesBulkBar() {
      if (!svcBulkBar) return;
      const grpOpts = '<option value="">group…</option>' + (cache.resourceGroups || []).map((g) => '<option value="' + esc(g.id) + '">' + esc(g.name || g.id) + '</option>').join('');
      svcBulkBar.innerHTML =
        '<strong style="font-size:13px"><span data-svc-selcount>0</span> selected</strong>' +
        '<span style="font-size:12px;color:var(--sm-ink-muted)">Apply to selected →</span>' +
        '<input data-svc-apply-product placeholder="acls" list="sm-svc-grid-products" style="width:70px"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-apply="product">Set product</button>' +
        '<input data-svc-apply-price placeholder="$95" style="width:64px"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-apply="price">Set price</button>' +
        '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-apply="activate">Activate</button>' +
        '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-apply="deactivate">Deactivate</button>' +
        '<select data-svc-apply-group>' + grpOpts + '</select><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-apply="group">Set group</button>' +
        '<input type="number" data-svc-apply-max placeholder="max" style="width:56px"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-apply="max">Set max</button>' +
        '<span style="flex:1;min-width:12px"></span>' +
        '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-bulk-addrow>+ Blank row</button>' +
        '<button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-svc-bulk-save>Save all</button>' +
        '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-svc-bulk-done>Done</button>' +
        '<div data-svc-bulk-msg style="width:100%;font-size:12px;color:var(--sm-ink-muted);margin-top:2px"></div>';
    }
    function enterSvcBulk() {
      svcBulkMode = true; svcDrafts = [];
      if (svcNormal) svcNormal.hidden = true;
      if (svcGrid) svcGrid.hidden = false;
      if (svcBulkBar) svcBulkBar.hidden = false;
      if (svcBulkToggle) svcBulkToggle.textContent = 'Exit bulk edit';
      renderServicesBulkBar();
      loadServicesTable();
    }
    function exitSvcBulk() {
      svcBulkMode = false; svcDrafts = [];
      if (svcGrid) { svcGrid.hidden = true; svcGrid.innerHTML = ''; }
      if (svcBulkBar) { svcBulkBar.hidden = true; svcBulkBar.innerHTML = ''; }
      if (svcNormal) svcNormal.hidden = false;
      if (svcBulkToggle) svcBulkToggle.textContent = 'Bulk edit';
      loadServicesTable();
    }
    function svcReadRow(tr) {
      const out = {};
      tr.querySelectorAll('[data-f]').forEach((el) => {
        out[el.dataset.f] = (el.type === 'checkbox') ? !!el.checked : el.value;
      });
      return out;
    }
    async function saveSvcBulk() {
      const setMsg = (t) => { const m = svcBulkBar.querySelector('[data-svc-bulk-msg]'); if (m) m.textContent = t; };
      const trs = Array.from(svcGrid.querySelectorAll('tr[data-svc-row]'));
      // Warn once if any existing service's ID is being changed (it's a
      // create-under-new-id + delete-old, and bookings keep the old id).
      const willRename = trs.some((tr) => !tr.hasAttribute('data-draft') && smSlug(svcReadRow(tr).id || '') && smSlug(svcReadRow(tr).id || '') !== tr.dataset.id);
      if (willRename && !confirm('You changed the ID of one or more services. This re-creates each under its new ID and deletes the old one — existing bookings still reference the old ID. Continue?')) { setMsg('Cancelled — no changes saved.'); return; }
      setMsg('Saving…');
      let saved = 0; const errs = []; const renames = [];
      for (const tr of trs) {
        const raw = svcReadRow(tr);
        const fields = svcGridFieldsFromRaw(raw);
        const saveData = Object.assign({}, fields);
        if (saveData.durationMinutes === '') saveData.durationMinutes = null;
        if (saveData.shopifyVariantId === '') saveData.shopifyVariantId = null;
        if (saveData.shopifyProductId === '') saveData.shopifyProductId = null;
        if (tr.hasAttribute('data-draft')) {
          const id = smSlug(raw.id || '');
          if (!id) { if ((fields.name || '').trim()) errs.push('A new row needs an ID.'); continue; }
          const src = (svcDrafts[parseInt(tr.dataset.draft, 10)] || {}).source || {};
          const data = Object.assign({}, src, saveData); delete data.id;
          try { await ns.fb.adminSaveService(id, data); saved++; } catch (e) { errs.push(id + ': ' + (e.message || e)); }
        } else {
          const origId = tr.dataset.id;
          const newId = smSlug(raw.id || '') || origId;
          const orig = (cache.services || []).find((x) => x.id === origId) || {};
          const flatSame = JSON.stringify(fields) === JSON.stringify(svcGridFields(orig));
          if (flatSame && newId === origId) continue; // nothing changed
          if (newId !== origId) {
            // Rename: create the new doc carrying ALL of the original's fields
            // (incl. nested per-location config), then delete the old one.
            const full = Object.assign({}, orig, saveData); delete full.id;
            try { await ns.fb.adminSaveService(newId, full); await ns.fb.adminDeleteService(origId); saved++; renames.push(origId + ' → ' + newId); }
            catch (e) { errs.push(origId + ': ' + (e.message || e)); }
          } else {
            try { await ns.fb.adminSaveService(origId, saveData); saved++; } catch (e) { errs.push(origId + ': ' + (e.message || e)); }
          }
        }
      }
      try { cache.services = await ns.fb.adminListServicesAll(); await loadLookups(); } catch (e) {}
      svcDrafts = [];
      renderServicesBulkBar();
      renderServicesGrid();
      setMsg('✓ Saved ' + saved + ' change(s).' + (renames.length ? ' Renamed: ' + renames.join(', ') + '.' : '') + (errs.length ? ' ⚠ ' + errs.join('; ') : ''));
    }

    svcBulkToggle && svcBulkToggle.addEventListener('click', () => { if (svcBulkMode) exitSvcBulk(); else enterSvcBulk(); });
    svcGrid && svcGrid.addEventListener('change', (e) => {
      if (e.target.matches('[data-svc-checkall]')) {
        const on = e.target.checked;
        svcGrid.querySelectorAll('[data-svc-check]').forEach((c) => { c.checked = on; });
      }
      updateSvcSelCount();
    });
    svcGrid && svcGrid.addEventListener('click', (e) => {
      const del = e.target.closest('[data-svc-draft-del]');
      const dup = e.target.closest('[data-svc-grid-dupe]');
      if (del) { svcDrafts.splice(parseInt(del.dataset.svcDraftDel, 10), 1); renderServicesGrid(); }
      else if (dup) {
        const src = (cache.services || []).find((x) => x.id === dup.dataset.svcGridDupe);
        if (src) { svcDrafts.push({ id: src.id + '-copy', source: Object.assign({}, src) }); renderServicesGrid(); }
      }
    });
    svcBulkBar && svcBulkBar.addEventListener('click', (e) => {
      const apply = e.target.closest('[data-svc-apply]');
      if (apply) {
        const rows = svcCheckedRows();
        const m = svcBulkBar.querySelector('[data-svc-bulk-msg]');
        if (!rows.length) { if (m) m.textContent = 'Tick some rows first (left checkboxes).'; return; }
        const kind = apply.dataset.svcApply;
        const price = (svcBulkBar.querySelector('[data-svc-apply-price]') || {}).value;
        const product = (svcBulkBar.querySelector('[data-svc-apply-product]') || {}).value;
        const group = (svcBulkBar.querySelector('[data-svc-apply-group]') || {}).value;
        const max   = (svcBulkBar.querySelector('[data-svc-apply-max]') || {}).value;
        rows.forEach((tr) => {
          const set = (f, v, chk) => { const el = tr.querySelector('[data-f="' + f + '"]'); if (!el) return; if (chk != null) el.checked = chk; else el.value = v; };
          if (kind === 'product') set('productKey', product);
          else if (kind === 'price') set('priceDisplay', price);
          else if (kind === 'activate') set('active', null, true);
          else if (kind === 'deactivate') set('active', null, false);
          else if (kind === 'group') set('resourceGroup', group);
          else if (kind === 'max') set('maxPerSlot', max);
        });
        if (m) m.textContent = 'Applied to ' + rows.length + ' row(s). Review, then Save all.';
        return;
      }
      if (e.target.closest('[data-svc-bulk-addrow]')) { svcDrafts.push({ id: '', source: { active: true, blockMode: 'slot', maxPerSlot: 1 } }); renderServicesGrid(); return; }
      if (e.target.closest('[data-svc-bulk-save]')) { saveSvcBulk(); return; }
      if (e.target.closest('[data-svc-bulk-done]')) { exitSvcBulk(); return; }
    });

    let svcLoaded = false;
    let bizLoaded = false;
    let metricsLoaded = false;
    let instLoaded = false;
    let classLoaded = false;
    let siteVarsLoaded = false;
    let siteContentLoaded = false;

    /* ================= SITE VARIABLES ================= */
    const siteVarsRoot = root.querySelector('[data-sm-sitevars-root]');
    function siteVarRow(k, v) {
      return '<tr data-sv-row>' +
        '<td><input data-sv-key value="' + esc(k) + '" placeholder="fl_locations" style="width:100%"></td>' +
        '<td><textarea data-sv-val rows="2" placeholder="4 North Florida locations" style="width:100%;min-height:44px;resize:vertical;font:inherit;line-height:1.4;overflow:hidden">' + esc(v) + '</textarea></td>' +
        '<td style="white-space:nowrap"><code style="font-size:12px" data-sv-token>[[' + esc(k) + ']]</code></td>' +
        '<td><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sv-del style="color:var(--sm-danger)">Remove</button></td>' +
      '</tr>';
    }
    function renderSiteVars(vars) {
      const all = Object.keys(vars || {});
      const gen = all.filter((k) => k.indexOf('desc_') !== 0).sort();
      const desc = all.filter((k) => k.indexOf('desc_') === 0).sort();
      const hdr = (label) => '<tr><td colspan="4" style="background:var(--sm-surface-alt,#f1f5fa);font-weight:700;font-size:12px;padding:8px 10px;color:var(--sm-ink)">' + label + '</td></tr>';
      let rows = '';
      if (gen.length) rows += hdr('General') + gen.map((k) => siteVarRow(k, vars[k])).join('');
      if (desc.length) rows += hdr('Course-option descriptions (format × certification)') + desc.map((k) => siteVarRow(k, vars[k])).join('');
      const keys = all;
      siteVarsRoot.innerHTML =
        '<div class="sm-admin__toolbar"><div><h2 class="sm-h3" style="margin:0">Site variables</h2>' +
          '<p class="sm-field__help">Reusable text you set once and use anywhere. Paste the <strong>token</strong> (e.g. <code>[[fl_locations]]</code>) into any page — including PageFly — and it shows the value. Change it here → every page updates within a minute.</p></div>' +
          '<div class="sm-admin__filter"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sv-add>+ Add variable</button>' +
          '<button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-sv-save>Save all</button></div></div>' +
        '<div style="overflow-x:auto"><table class="sm-table"><thead><tr><th>Key</th><th>Value (what shows on the page)</th><th>Token to paste</th><th></th></tr></thead>' +
          '<tbody data-sv-rows>' + rows + '</tbody></table></div>' +
        (keys.length ? '' : '<p class="sm-field__help" style="padding:10px">No variables yet. Click “+ Add variable”.</p>') +
        '<div class="sm-field__help" data-sv-msg style="margin-top:10px"></div>';
      wireSiteVars();
    }
    function wireSiteVars() {
      const autoGrow = (ta) => { if (!ta) return; ta.style.height = 'auto'; ta.style.height = (ta.scrollHeight + 2) + 'px'; };
      const growAll = () => siteVarsRoot.querySelectorAll('[data-sv-val]').forEach(autoGrow);
      const add = siteVarsRoot.querySelector('[data-sv-add]');
      add && add.addEventListener('click', () => { const tb = siteVarsRoot.querySelector('[data-sv-rows]'); tb.insertAdjacentHTML('beforeend', siteVarRow('', '')); autoGrow(tb.querySelector('[data-sv-row]:last-child [data-sv-val]')); });
      siteVarsRoot.querySelectorAll('[data-sv-del]').forEach((b) => b.addEventListener('click', () => { const tr = b.closest('[data-sv-row]'); if (tr) tr.remove(); }));
      siteVarsRoot.addEventListener('input', (e) => {
        if (e.target.matches('[data-sv-key]')) { const tr = e.target.closest('[data-sv-row]'); const t = tr && tr.querySelector('[data-sv-token]'); if (t) t.textContent = '[[' + (e.target.value || '') + ']]'; }
        if (e.target.matches('[data-sv-val]')) autoGrow(e.target);
      });
      growAll();
      const save = siteVarsRoot.querySelector('[data-sv-save]');
      save && save.addEventListener('click', async () => {
        const msg = siteVarsRoot.querySelector('[data-sv-msg]');
        const vars = {}; let bad = '';
        siteVarsRoot.querySelectorAll('[data-sv-row]').forEach((tr) => {
          const k = (tr.querySelector('[data-sv-key]').value || '').trim();
          const v = tr.querySelector('[data-sv-val]').value;
          if (!k) return;
          if (!/^[a-zA-Z0-9_.\-]+$/.test(k)) { bad = k; return; }
          vars[k] = v;
        });
        if (bad) { msg.style.color = 'var(--sm-danger)'; msg.textContent = 'Key “' + bad + '” is invalid — use only letters, numbers, _ . - (no spaces).'; return; }
        msg.style.color = ''; msg.textContent = 'Saving…';
        try { await ns.fb.adminSaveSiteVariables(vars); msg.textContent = '✓ Saved. Live on all pages within about a minute.'; }
        catch (e) { msg.style.color = 'var(--sm-danger)'; msg.textContent = 'Save failed: ' + (e.message || e); }
      });
    }
    async function loadSiteVars() {
      if (!siteVarsRoot) return;
      siteVarsRoot.innerHTML = '<p class="sm-field__help" style="padding:12px">Loading…</p>';
      let vars = {};
      try { vars = await ns.fb.getSiteVariables(); }
      catch (e) { siteVarsRoot.innerHTML = '<p class="sm-field__error is-shown" style="padding:12px">Could not load: ' + esc(e.message || e) + '</p>'; return; }
      // Always show the full list: the built-in defaults, with any saved
      // overrides applied on top. That way every variable is visible/editable.
      const defaults = (typeof SM_VAR_DEFAULTS === 'object' && SM_VAR_DEFAULTS) ? SM_VAR_DEFAULTS : {};
      renderSiteVars(Object.assign({}, defaults, vars));
    }

    navBtns.forEach((b) => b.addEventListener('click', async () => {
      if (b.dataset.smAdminNav === 'availability' && !availLoaded) {
        availLoaded = true;
        await loadBlockedDates();
        await loadBlockedTimes();
      }
      if (b.dataset.smAdminNav === 'cnaclasses' && !cnaLoaded) {
        cnaLoaded = true;
        await loadCnaClassesTable();
      }
      if (b.dataset.smAdminNav === 'ivclasses' && !ivLoaded) {
        ivLoaded = true;
        await loadIvClassesTable();
      }
      if (b.dataset.smAdminNav === 'inquiries' && !inqLoaded) {
        inqLoaded = true;
        await loadInquiriesTable();
      }
      if (b.dataset.smAdminNav === 'analytics' && !analyticsLoaded) {
        analyticsLoaded = true;
        await loadAnalytics();
      }
      // Backend tab: lazy-load whichever sub-tab is currently active.
      if (b.dataset.smAdminNav === 'backend') {
        if (backendActiveSub === 'services'  && !svcLoaded)    { svcLoaded = true;    await loadServicesTable(); }
        if (backendActiveSub === 'businesses' && !bizLoaded)   { bizLoaded = true;    await loadBusinesses(); }
        if (backendActiveSub === 'metrics'    && !metricsLoaded) { metricsLoaded = true; await loadMetrics(); }
        if (backendActiveSub === 'instructors' && !instLoaded)  { instLoaded = true;   await loadInstructors(); }
        if (backendActiveSub === 'classportal' && !classLoaded) { classLoaded = true;  await loadClassData(); }
        if (backendActiveSub === 'sitevars'  && !siteVarsLoaded){ siteVarsLoaded = true; await loadSiteVars(); }
        if (backendActiveSub === 'locations' && !locLoaded)    { locLoaded = true;    await loadLocationsTable(); }
        if (backendActiveSub === 'oris'      && !oriLoaded)    { oriLoaded = true;    await loadOriTable(); }
        if (backendActiveSub === 'assets'    && !assetsLoaded) { assetsLoaded = true; await loadAssetsTable(); }
        if (backendActiveSub === 'employees' && !empLoaded)    { empLoaded = true;    await loadEmployeesTable(); }
        if (backendActiveSub === 'schedule'  && !schedLoaded)  { schedLoaded = true;  await loadSchedule(); }
        if (backendActiveSub === 'quotes'    && !quotesLoaded) { quotesLoaded = true; await loadQuotesTable(); }
        if (backendActiveSub === 'elks'      && !elksLoaded)   { elksLoaded = true;   await loadElksTable(); }
        if (backendActiveSub === 'failures'  && !failLoaded)   { failLoaded = true;   await loadFailuresTable(); }
      }
    }));

    /* ---------- CNA CLASSES TAB ---------- */
    let cnaLoaded = false;
    const cnaRows = root.querySelector('[data-sm-cna-rows]');
    const cnaNewBtn = root.querySelector('[data-sm-cna-new]');
    cache.cnaClasses = [];

    function cnaLocLabel(locId) {
      return (cache.locations.find((l) => l.id === locId) || {}).name || locId || '—';
    }
    // A class's "number of days" = how many session days it has. This is
    // the value matched against the Shopify product's 2/3/4-day variant.
    function cnaNumDays(c) { return (c.sessions && c.sessions.length) || 0; }
    function cnaDateRange(c) {
      const s = (c.sessions || []).map((x) => x.date).filter(Boolean).sort();
      if (!s.length) return '—';
      return s.length === 1 ? s[0] : (s[0] + ' → ' + s[s.length - 1]);
    }

    let cnaShowPast = false;
    const cnaShowPastBtn = root.querySelector('[data-sm-cna-show-past]');
    function classIsCurrentOrFuture(c) {
      const today = new Date().toISOString().slice(0, 10);
      const last = c.endDate || c.startDate || '';
      return !last || last >= today;
    }

    async function loadCnaClassesTable() {
      if (!cnaRows) return;
      cnaRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        cache.cnaClasses = await ns.fb.adminListCnaClasses();
        if (!cache.locations || !cache.locations.length) cache.locations = await ns.fb.adminListLocationsAll();
      } catch (err) {
        console.error('[admin] list CNA classes failed', err);
        cnaRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      // Filter to current+future by default; toggle button reveals past classes.
      let rows = cnaShowPast ? cache.cnaClasses : cache.cnaClasses.filter(classIsCurrentOrFuture);
      rows = rows.slice().sort((a, b) => String(a.startDate || '').localeCompare(String(b.startDate || '')));
      if (!rows.length) {
        const empty = cnaShowPast ? 'No CNA classes yet. Click + Add class.' : 'No current or upcoming classes. Click "Show past classes" to view older ones, or + Add class.';
        cnaRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">' + empty + '</td></tr>';
        return;
      }
      cnaRows.innerHTML = rows.map((c) => {
        const nd = cnaNumDays(c);
        // Show each day's time compactly: "6/1 8:00 AM–4:00 PM · 6/2 …"
        const timeSummary = (c.sessions || []).map((s) => {
          const md = (s.date || '').slice(5).replace('-', '/');
          return md + ' ' + (s.startTime || '?') + '–' + (s.endTime || '?');
        }).join(' · ') || '—';
        const seats = (typeof c.seatsBooked === 'number' ? c.seatsBooked : 0) + ' / ' + (c.seatsTotal || '—');
        const past = !classIsCurrentOrFuture(c);
        return '<tr' + (past ? ' style="opacity:0.55"' : '') + '>' +
          '<td><strong>' + (c.label || 'CNA Class') + '</strong></td>' +
          '<td>' + cnaLocLabel(c.location) + '</td>' +
          '<td>' + cnaDateRange(c) + '</td>' +
          '<td style="font-size:12px">' + timeSummary + '</td>' +
          '<td>' + (nd ? nd + '-day' : '—') + '</td>' +
          '<td>' + seats + '</td>' +
          '<td><span class="sm-table__status sm-status--' + (c.active === false ? 'cancelled' : 'confirmed') + '">' + (c.active === false ? 'Inactive' : 'Active') + '</span></td>' +
          '<td style="white-space:nowrap">' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-cna-students="' + c.id + '">View students</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-cna-edit="' + c.id + '">Edit</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-cna-del="' + c.id + '" style="color:var(--sm-danger)">Delete</button>' +
          '</td>' +
        '</tr>';
      }).join('');
    }
    cnaShowPastBtn && cnaShowPastBtn.addEventListener('click', () => {
      cnaShowPast = !cnaShowPast;
      cnaShowPastBtn.textContent = cnaShowPast ? 'Hide past classes' : 'Show past classes';
      loadCnaClassesTable();
    });

    function cnaSessionRowHtml(s) {
      s = s || {};
      return '<div class="sm-cna-session-row" style="display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:6px;margin-top:6px;align-items:center">' +
        '<input type="date" data-sm-cna-sess-date value="' + (s.date || '') + '" style="font-size:13px;padding:6px">' +
        '<input type="text" data-sm-cna-sess-start value="' + ((s.startTime || '').replace(/"/g, '&quot;')) + '" placeholder="8:00 AM" style="font-size:13px;padding:6px">' +
        '<input type="text" data-sm-cna-sess-end value="' + ((s.endTime || '').replace(/"/g, '&quot;')) + '" placeholder="4:00 PM" style="font-size:13px;padding:6px">' +
        '<button type="button" data-sm-cna-sess-del aria-label="Remove" style="background:transparent;border:none;color:var(--sm-danger);cursor:pointer;font-size:18px;padding:0 8px">×</button>' +
      '</div>';
    }

    function cnaModalHtml(c) {
      const locOpts = '<option value="">— pick a location —</option>' +
        (cache.locations || []).map((l) => '<option value="' + l.id + '"' + (c.location === l.id ? ' selected' : '') + '>' + (l.name || l.id) + '</option>').join('');
      const sessions = (c.sessions && c.sessions.length) ? c.sessions : [{}];
      const sessionRows = sessions.map(cnaSessionRowHtml).join('');
      const productHandle = c.shopifyProductHandle || 'cna-in-person-class';
      return '<form class="sm-form" data-sm-cna-form>' +
        '<div class="sm-field"><label>Class label</label><input type="text" name="label" value="' + ((c.label || '').replace(/"/g, '&quot;')) + '" placeholder="June Day Class" required></div>' +
        '<div class="sm-field"><label>Location</label><select name="location" required>' + locOpts + '</select></div>' +
        '<div class="sm-field"><label>Class days <span class="sm-field__help">(each day has its own date + start/end time)</span></label>' +
          '<div data-sm-cna-sessions-list>' + sessionRows + '</div>' +
          '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-cna-add-session style="margin-top:8px">+ Add day</button>' +
          '<div class="sm-field__help" style="margin-top:6px">Number of days = how many rows here.</div>' +
        '</div>' +
        '<div class="sm-field"><label>Total seats</label><input type="number" name="seatsTotal" min="1" value="' + (c.seatsTotal || 12) + '"></div>' +

        // Product handle is hidden by default — staff almost never need to
        // change it from the default cna-in-person-class. Click "Loading
        // from a different product?" to reveal it for renamed products.
        '<input type="hidden" name="shopifyProductHandle" value="' + productHandle.replace(/"/g, '&quot;') + '" data-sm-cna-product-handle>' +
        '<div class="sm-field"><label>Class offering (Shopify variant)</label>' +
          '<select name="shopifyVariantId" data-sm-cna-variant-select required>' +
            '<option value="">Loading variants…</option>' +
            (c.shopifyVariantId ? '<option value="' + String(c.shopifyVariantId).replace(/"/g,'&quot;') + '" selected>Currently saved: ' + String(c.shopifyVariantId).replace(/</g,'&lt;') + ' — keep, or pick a new one below once loaded</option>' : '') +
          '</select>' +
          '<div class="sm-field__help">Pick the matching location + price tier — the customer pays this variant\'s price. <a href="#" data-sm-cna-show-handle style="color:var(--sm-brand)">Loading from a different product?</a></div>' +
        '</div>' +

        '<label class="sm-book__chk sm-mt-16"><input type="checkbox" name="active"' + (c.active === false ? '' : ' checked') + '><span>Active (shown to customers)</span></label>' +
        '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
          '<button type="submit" class="sm-btn sm-btn--primary">Save class</button>' +
          '<span class="sm-field__help" data-sm-cna-msg></span>' +
        '</div>' +
      '</form>';
    }

    async function openCnaModal(id) {
      if (!modal || !modalBody) return;
      if (!cache.locations || !cache.locations.length) {
        try { cache.locations = await ns.fb.adminListLocationsAll(); } catch (e) {}
      }
      const c = id ? (cache.cnaClasses.find((x) => x.id === id) || {}) : { active: true, seatsTotal: 12, sessions: [{}] };
      modalTitle && (modalTitle.textContent = id ? ('Edit class — ' + (c.label || '')) : 'Add CNA class');
      modalBody.innerHTML = cnaModalHtml(c);
      modal.hidden = false;
      const form = modalBody.querySelector('[data-sm-cna-form]');
      const msg  = modalBody.querySelector('[data-sm-cna-msg]');
      const handleInput = form.querySelector('[data-sm-cna-product-handle]');
      const variantSelect = form.querySelector('[data-sm-cna-variant-select]');

      // Populate the variant dropdown from /products/<handle>.js — each
      // option shows the variant's option values plus its price so staff
      // can pick by location/price tier instead of pasting an ID.
      async function loadVariants(handle, currentId) {
        if (!variantSelect) return;
        variantSelect.innerHTML = '<option value="">Loading variants…</option>';
        if (!handle) {
          variantSelect.innerHTML = '<option value="">Enter a product handle above</option>';
          return;
        }
        try {
          const r = await fetch('/products/' + encodeURIComponent(handle) + '.js');
          if (!r.ok) throw new Error('Product not found (' + r.status + ')');
          const prod = await r.json();
          const variants = (prod.variants || []).filter((v) => v.available !== false);
          if (!variants.length) {
            variantSelect.innerHTML = '<option value="">No active variants on this product</option>';
            return;
          }
          const moneyOf = (cents) => {
            if (cents == null) return '';
            const n = Number(cents) / 100;
            return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
          };
          variantSelect.innerHTML = '<option value="">— pick a variant —</option>' +
            variants.map((v) => {
              const opts = (v.options || []).filter(Boolean).join(' / ');
              const price = moneyOf(v.price);
              const label = (opts || v.title || ('Variant ' + v.id)) + (price ? '  —  ' + price : '');
              const sel = String(v.id) === String(currentId) ? ' selected' : '';
              return '<option value="' + v.id + '"' + sel + '>' + label.replace(/</g,'&lt;') + '</option>';
            }).join('');
        } catch (err) {
          variantSelect.innerHTML = '<option value="">Could not load: ' + (err.message || 'error') + '</option>';
        }
      }
      loadVariants(handleInput && handleInput.value, c.shopifyVariantId);
      handleInput && handleInput.addEventListener('change', () => loadVariants(handleInput.value, null));

      // Reveal the product handle field on demand.
      const showHandleLink = form.querySelector('[data-sm-cna-show-handle]');
      showHandleLink && showHandleLink.addEventListener('click', (ev) => {
        ev.preventDefault();
        if (!handleInput) return;
        const wrap = document.createElement('div');
        wrap.className = 'sm-field';
        wrap.style.marginTop = '10px';
        wrap.innerHTML = '<label>Shopify product handle</label>' +
          '<input type="text" value="' + handleInput.value.replace(/"/g, '&quot;') + '" data-sm-cna-product-handle-visible>' +
          '<div class="sm-field__help">Press Enter or click outside the field to reload variants.</div>';
        showHandleLink.closest('.sm-field').appendChild(wrap);
        showHandleLink.remove();
        const visible = wrap.querySelector('[data-sm-cna-product-handle-visible]');
        visible.addEventListener('change', () => {
          handleInput.value = visible.value;
          loadVariants(handleInput.value, null);
        });
        visible.focus();
      });

      // Add / remove session-day rows.
      form.addEventListener('click', (ev) => {
        const addBtn = ev.target.closest('[data-sm-cna-add-session]');
        if (addBtn) {
          ev.preventDefault();
          const list = form.querySelector('[data-sm-cna-sessions-list]');
          const tmp = document.createElement('div');
          tmp.innerHTML = cnaSessionRowHtml({});
          list.appendChild(tmp.firstChild);
          return;
        }
        const delBtn = ev.target.closest('[data-sm-cna-sess-del]');
        if (delBtn) {
          ev.preventDefault();
          const row = delBtn.closest('.sm-cna-session-row');
          if (row) row.remove();
        }
      });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const fd = new FormData(form);
          const toInt = (v) => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };
          const sessions = [];
          form.querySelectorAll('.sm-cna-session-row').forEach((row) => {
            const date  = (row.querySelector('[data-sm-cna-sess-date]')  || {}).value || '';
            const start = (row.querySelector('[data-sm-cna-sess-start]') || {}).value || '';
            const end   = (row.querySelector('[data-sm-cna-sess-end]')   || {}).value || '';
            if (date && start && end) sessions.push({ date: date.trim(), startTime: start.trim(), endTime: end.trim() });
          });
          if (!fd.get('location')) throw new Error('Pick a location.');
          if (!sessions.length) throw new Error('Add at least one class day with a date + start + end time.');
          sessions.sort((a, b) => a.date.localeCompare(b.date));
          const data = {
            label: fd.get('label') || 'CNA Class',
            location: fd.get('location') || '',
            sessions: sessions,
            numDays: sessions.length,
            startDate: sessions[0].date,
            endDate: sessions[sessions.length - 1].date,
            seatsTotal: toInt(fd.get('seatsTotal')) || 12,
            shopifyVariantId: (fd.get('shopifyVariantId') || '').toString().trim(),
            shopifyProductHandle: (fd.get('shopifyProductHandle') || '').toString().trim() || null,
            active: !!fd.get('active')
          };
          await ns.fb.adminSaveCnaClass(id || null, data);
          msg.textContent = '✓ Saved.';
          await loadCnaClassesTable();
          setTimeout(closeModal, 500);
        } catch (err) {
          console.error('[admin] save CNA class failed', err);
          msg.textContent = err.message || 'Save failed.';
        }
      });
    }

    cnaRows && cnaRows.addEventListener('click', async (e) => {
      const editBtn = e.target.closest('[data-sm-cna-edit]');
      const delBtn  = e.target.closest('[data-sm-cna-del]');
      const studentsBtn = e.target.closest('[data-sm-cna-students]');
      if (studentsBtn) {
        openClassStudentsModal('cna', studentsBtn.dataset.smCnaStudents);
        return;
      }
      if (editBtn) openCnaModal(editBtn.dataset.smCnaEdit);
      else if (delBtn) {
        if (!confirm('Delete this CNA class?')) return;
        delBtn.disabled = true;
        try { await ns.fb.adminDeleteCnaClass(delBtn.dataset.smCnaDel); await loadCnaClassesTable(); }
        catch (err) { delBtn.disabled = false; alert(err.message || 'Delete failed.'); }
      }
    });
    cnaNewBtn && cnaNewBtn.addEventListener('click', () => openCnaModal(null));

    // Shared roster modal: lists students registered for a CNA or IV class
    // (queried by classId on the bookings collection). Used by the "View
    // students" button in both class tabs.
    async function openClassStudentsModal(kind, classId) {
      if (!modal || !modalBody) return;
      const isIv = kind === 'iv';
      const classCollection = isIv ? 'iv_classes' : 'cna_classes';
      const kindLabel = isIv ? 'IV' : 'CNA';
      const extraLabel = isIv ? 'LPN License #' : 'Date of birth';
      const extraKey   = isIv ? 'lpnLicense'    : 'dob';
      const extraType  = isIv ? 'text'          : 'date';

      const STATUSES = ['pending','confirmed','cancelled','completed','no-show'];
      let editingId = null;       // booking id currently in edit mode, or 'NEW' for the add-student row
      let students = [];
      let classObj = {};
      let recounted = false;       // freshen seatsBooked once per modal open

      const csvCell = (v) => {
        const s = (v == null) ? '' : String(v);
        return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const fmtReg = (ts) => {
        if (!ts) return '';
        try {
          const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
          return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        } catch (e) { return ''; }
      };
      const rowFields = (s) => ({
        firstName: (s.customer && s.customer.firstName) || '',
        lastName:  (s.customer && s.customer.lastName)  || '',
        name: ((s.customer && ((s.customer.firstName || '') + ' ' + (s.customer.lastName || '')).trim())) || s.name || '',
        email: (s.customer && s.customer.email) || s.email || '',
        phone: (s.customer && s.customer.phone) || s.phone || '',
        extra: isIv
          ? ((s.customer && s.customer.lpnLicense) || s.lpnLicense || '')
          : ((s.customer && s.customer.dob) || s.dob || ''),
        registered: fmtReg(s.createdAt),
        status: s.status || 'pending'
      });

      async function loadStudents() {
        try {
          students = await ns.fb.adminListBookingsByClass(classId);
          // Refresh classObj from cache to get the latest seatsBooked
          classObj = (isIv ? (cache.ivClasses || []) : (cache.cnaClasses || [])).find((x) => x.id === classId) || classObj;
          if (!recounted && ns.fb && typeof ns.fb.adminRecountClassSeats === 'function') {
            recounted = true;
            const fresh = await ns.fb.adminRecountClassSeats(classId);
            if (typeof fresh === 'number') classObj.seatsBooked = fresh;
          }
        } catch (err) {
          modalBody.innerHTML = '<p style="color:var(--sm-danger)">Unable to load students. ' + (err.message || '') + '</p>';
          throw err;
        }
      }

      const moneyC = (c) => '$' + (Number(c || 0) / 100).toFixed(2);
      // Payment status cell + whether a balance is still owed.
      function payInfo(s) {
        const p = s.payment || {};
        if (!p.choice) return { label: '—', owed: false, invoiced: false };
        if (p.choice === 'full') return { label: 'Paid in full', owed: false, invoiced: false };
        const bal = Number(p.balanceCents || 0);
        const st = p.balanceStatus || 'due';
        const url = (p.balanceInvoice && p.balanceInvoice.invoiceUrl) || '';
        let label;
        if (st === 'paid') label = 'Deposit + balance paid';
        else if (st === 'invoiced') label = 'Balance invoiced' + (bal ? ' (' + moneyC(bal) + ')' : '') + (url ? ' · <a href="' + esc(url) + '" target="_blank" rel="noopener">link</a>' : '');
        else label = 'Deposit paid · ' + (bal ? moneyC(bal) + ' due' : 'balance due');
        return { label: label, owed: st !== 'paid', invoiced: st === 'invoiced' };
      }
      function rowViewHtml(s) {
        const f = rowFields(s);
        const pi = payInfo(s);
        return '<tr data-row="' + s.id + '">' +
          '<td><strong>' + esc(f.name || '—') + '</strong></td>' +
          '<td>' + esc(f.email || '—') + '</td>' +
          '<td style="white-space:nowrap">' + esc(f.phone || '—') + '</td>' +
          '<td>' + esc(f.extra || '—') + '</td>' +
          '<td style="white-space:nowrap">' + esc(f.registered) + '</td>' +
          '<td><span class="sm-table__status sm-status--' + f.status + '">' + f.status + '</span></td>' +
          '<td style="font-size:12px">' + pi.label + '</td>' +
          '<td style="white-space:nowrap">' +
            (isIv ? '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-stu-email="' + s.id + '">✉ Send email</button> ' : '') +
            (pi.owed ? '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-stu-balance="' + s.id + '">' + (pi.invoiced ? 'Resend balance' : 'Send balance') + '</button> ' : '') +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-stu-edit="' + s.id + '">Edit</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-stu-del="' + s.id + '" style="color:var(--sm-danger)">Remove</button>' +
          '</td>' +
        '</tr>';
      }

      function rowEditHtml(s) {
        const f = rowFields(s);
        const isNew = s.id === 'NEW';
        return '<tr data-row-edit="' + s.id + '" style="background:var(--sm-surface-alt)">' +
          '<td colspan="8" style="padding:12px">' +
            '<form data-sm-stu-form data-id="' + s.id + '" style="display:grid;grid-template-columns:repeat(6, minmax(0,1fr));gap:8px;align-items:end">' +
              '<label style="grid-column:span 1"><span style="display:block;font-size:11px;color:var(--sm-ink-muted)">First name</span><input name="firstName" type="text" value="' + esc(f.firstName) + '" style="width:100%"></label>' +
              '<label style="grid-column:span 1"><span style="display:block;font-size:11px;color:var(--sm-ink-muted)">Last name</span><input name="lastName" type="text" value="' + esc(f.lastName) + '" style="width:100%"></label>' +
              '<label style="grid-column:span 2"><span style="display:block;font-size:11px;color:var(--sm-ink-muted)">Email</span><input name="email" type="email" value="' + esc(f.email) + '" style="width:100%"></label>' +
              '<label style="grid-column:span 1"><span style="display:block;font-size:11px;color:var(--sm-ink-muted)">Phone</span><input name="phone" type="tel" value="' + esc(f.phone) + '" style="width:100%"></label>' +
              '<label style="grid-column:span 1"><span style="display:block;font-size:11px;color:var(--sm-ink-muted)">' + extraLabel + '</span><input name="extra" type="' + extraType + '" value="' + esc(f.extra) + '" style="width:100%"></label>' +
              (isIv ? '<label style="grid-column:span 2"><span style="display:block;font-size:11px;color:var(--sm-ink-muted)">Subscription ID</span><input name="subscriptionId" type="text" value="' + esc(s.subscriptionId || '') + '" placeholder="e.g. SUB-10482" style="width:100%"></label>' +
                      '<label style="grid-column:span 2"><span style="display:block;font-size:11px;color:var(--sm-ink-muted)">Access valid through</span><input name="accessValidThrough" type="date" value="' + esc(s.accessValidThrough || '') + '" style="width:100%"></label>' : '') +
              '<label style="grid-column:span 2"><span style="display:block;font-size:11px;color:var(--sm-ink-muted)">Status</span><select name="status" style="width:100%">' +
                STATUSES.map((st) => '<option value="' + st + '"' + (f.status === st ? ' selected' : '') + '>' + st + '</option>').join('') +
              '</select></label>' +
              '<div style="grid-column:span 4;display:flex;gap:8px;align-items:center;justify-content:flex-end">' +
                '<span data-sm-stu-msg style="font-size:12px;color:var(--sm-ink-muted)"></span>' +
                '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-stu-cancel>Cancel</button>' +
                '<button type="submit" class="sm-btn sm-btn--primary sm-btn--sm">' + (isNew ? 'Add student' : 'Save') + '</button>' +
              '</div>' +
            '</form>' +
          '</td>' +
        '</tr>';
      }

      function blankStudent() {
        return { id: 'NEW', customer: { firstName: '', lastName: '', email: '', phone: '', dob: '', lpnLicense: '' }, status: 'pending' };
      }

      function render() {
        const seatsBooked = typeof classObj.seatsBooked === 'number' ? classObj.seatsBooked : 0;
        const seatsTotal = classObj.seatsTotal || '—';
        const seatHdr = 'Confirmed seats: ' + seatsBooked + ' / ' + seatsTotal +
          ' · Total registrations (incl. pending): ' + students.length;

        const headerHtml = '<div style="margin-bottom:14px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap">' +
          '<div style="color:var(--sm-ink-muted);font-size:13px">' + esc(seatHdr) + '</div>' +
          '<div style="display:flex;gap:8px">' +
            (editingId === 'NEW' ? '' : '<button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-sm-stu-new>+ Add student</button>') +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-students-csv>Download CSV</button>' +
          '</div>' +
          '</div>';

        const rows = [];
        if (editingId === 'NEW') rows.push(rowEditHtml(blankStudent()));
        students.forEach((s) => {
          rows.push(editingId === s.id ? rowEditHtml(s) : rowViewHtml(s));
        });

        const emptyState = !students.length && editingId !== 'NEW'
          ? '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No students registered yet. Click "+ Add student" to add one manually.</td></tr>'
          : '';

        modalBody.innerHTML = headerHtml +
          '<div style="overflow:auto;max-height:60vh"><table class="sm-table" style="font-size:13px">' +
          '<thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>' + extraLabel + '</th><th>Registered</th><th>Status</th><th>Payment</th><th></th></tr></thead>' +
          '<tbody>' + rows.join('') + emptyState + '</tbody></table></div>';

        wireHandlers();
      }

      async function refresh() {
        editingId = null;
        await loadStudents();
        render();
      }

      function wireHandlers() {
        const newBtn = modalBody.querySelector('[data-sm-stu-new]');
        newBtn && newBtn.addEventListener('click', () => { editingId = 'NEW'; render(); });

        modalBody.querySelectorAll('[data-sm-stu-edit]').forEach((b) => {
          b.addEventListener('click', () => { editingId = b.dataset.smStuEdit; render(); });
        });

        // Send the CEUFast IV access email to this student (IV roster only).
        modalBody.querySelectorAll('[data-sm-stu-email]').forEach((b) => {
          b.addEventListener('click', async () => {
            const bid = b.dataset.smStuEmail;
            const s = students.find((x) => x.id === bid);
            if (!s) return;
            const f = rowFields(s);
            if (!f.email) { alert('This student has no email address on file. Add one via Edit first.'); return; }
            if (typeof ns.fb.adminSendStudentEmail !== 'function') { alert('Email sending is not available.'); return; }
            if (!confirm('Send the CEUFast IV access email to ' + f.email + '?')) return;
            const orig = b.textContent; b.disabled = true; b.textContent = 'Sending…';
            try {
              await ns.fb.adminSendStudentEmail({
                type: 'iv_access', bookingId: bid, to: f.email,
                firstName: f.firstName, lastName: f.lastName,
                subscriptionId: s.subscriptionId || '', accessValidThrough: s.accessValidThrough || '',
                service: s.serviceName || 'LPN IV Certification'
              });
              b.textContent = '✓ Sent';
              setTimeout(() => { b.disabled = false; b.textContent = orig; }, 2500);
            } catch (err) {
              b.disabled = false; b.textContent = orig;
              alert('Could not send the email: ' + (err.message || err));
            }
          });
        });

        modalBody.querySelectorAll('[data-sm-stu-balance]').forEach((b) => {
          b.addEventListener('click', async () => {
            const bid = b.dataset.smStuBalance;
            const s = students.find((x) => x.id === bid);
            const who = s ? rowFields(s).name : 'this student';
            if (!ns.fb || typeof ns.fb.classBalanceCheckout !== 'function') { alert('Balance invoicing is not available.'); return; }
            if (!confirm('Create and email a tuition balance invoice to ' + who + '?')) return;
            const orig = b.textContent; b.disabled = true; b.textContent = 'Sending…';
            try {
              const res = await ns.fb.classBalanceCheckout({ bookingId: bid });
              await refresh();
              alert('Balance invoice for ' + who + ': $' + (res.balance != null ? res.balance : '?') +
                (res.emailed ? ' — emailed to the student.' : '\nCreated — share this link:\n' + (res.invoiceUrl || '')));
            } catch (err) {
              b.disabled = false; b.textContent = orig;
              alert('Could not create the balance invoice: ' + (err.message || err));
            }
          });
        });

        modalBody.querySelectorAll('[data-sm-stu-del]').forEach((b) => {
          b.addEventListener('click', async () => {
            const bid = b.dataset.smStuDel;
            const s = students.find((x) => x.id === bid);
            const who = s ? rowFields(s).name : 'this student';
            if (!confirm('Remove ' + who + ' from this class? This deletes their booking.')) return;
            try {
              await ns.fb.adminDeleteBooking(bid);
              if (ns.fb && typeof ns.fb.adminRecountClassSeats === 'function') await ns.fb.adminRecountClassSeats(classId);
              await refresh();
            } catch (err) {
              alert('Delete failed: ' + (err.message || err));
            }
          });
        });

        const cancelBtn = modalBody.querySelector('[data-sm-stu-cancel]');
        cancelBtn && cancelBtn.addEventListener('click', () => { editingId = null; render(); });

        const form = modalBody.querySelector('[data-sm-stu-form]');
        form && form.addEventListener('submit', async (e) => {
          e.preventDefault();
          const msgEl = form.querySelector('[data-sm-stu-msg]');
          if (msgEl) msgEl.textContent = 'Saving…';
          const data = {
            firstName: form.firstName.value.trim(),
            lastName: form.lastName.value.trim(),
            email: form.email.value.trim(),
            phone: form.phone.value.trim()
          };
          data[extraKey] = form.extra.value.trim();
          const status = form.status.value;
          const submittingId = form.dataset.id;
          // IV students carry a CEUFast subscription ID + access-valid-through date.
          const ivExtra = {};
          if (isIv && form.subscriptionId) ivExtra.subscriptionId = form.subscriptionId.value.trim();
          if (isIv && form.accessValidThrough) ivExtra.accessValidThrough = form.accessValidThrough.value;

          try {
            if (submittingId === 'NEW') {
              await ns.fb.adminCreateClassBooking(Object.assign({
                classId: classId,
                classCollection: classCollection,
                location: classObj.location || '',
                locationName: (cache.locations.find((l) => l.id === classObj.location) || {}).name || '',
                customer: data,
                status: status,
                source: 'admin-roster'
              }, ivExtra));
            } else {
              const existing = students.find((x) => x.id === submittingId);
              const customer = Object.assign({}, (existing && existing.customer && typeof existing.customer === 'object') ? existing.customer : {}, data);
              await ns.fb.adminUpdateBooking(submittingId, Object.assign({
                customer: customer,
                email: data.email,
                phone: data.phone,
                status: status
              }, ivExtra));
            }
            if (ns.fb && typeof ns.fb.adminRecountClassSeats === 'function') await ns.fb.adminRecountClassSeats(classId);
            await refresh();
          } catch (err) {
            if (msgEl) msgEl.textContent = 'Save failed: ' + (err.message || err);
          }
        });

        const csvBtn = modalBody.querySelector('[data-sm-students-csv]');
        csvBtn && csvBtn.addEventListener('click', () => {
          const header = ['Name', 'Email', 'Phone', extraLabel, 'Registered', 'Status'];
          const rows = students.map((s) => {
            const f = rowFields(s);
            return [f.name, f.email, f.phone, f.extra, f.registered, f.status];
          });
          const lines = [header, ...rows].map((row) => row.map(csvCell).join(','));
          const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          const safeLabel = (classObj.label || (kindLabel + '-class')).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
          a.href = url;
          a.download = (isIv ? 'iv-' : 'cna-') + 'students-' + safeLabel + '.csv';
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          setTimeout(() => URL.revokeObjectURL(url), 5000);
        });
      }

      classObj = (isIv ? (cache.ivClasses || []) : (cache.cnaClasses || [])).find((x) => x.id === classId) || {};
      modalTitle && (modalTitle.textContent = 'Students — ' + (classObj.label || kindLabel + ' Class'));
      modalBody.innerHTML = '<p class="sm-field__help">Loading…</p>';
      modal.hidden = false;
      try {
        await loadStudents();
      } catch (e) { return; }
      render();
    }

    /* ---------- IV CLASSES TAB ---------- */
    let ivLoaded = false;
    const ivRows = root.querySelector('[data-sm-iv-rows]');
    const ivNewBtn = root.querySelector('[data-sm-iv-new]');
    cache.ivClasses = [];

    function ivLocLabel(locId) {
      return (cache.locations.find((l) => l.id === locId) || {}).name || locId || '—';
    }
    function ivNumDays(c) { return (c.sessions && c.sessions.length) || 0; }
    function ivDateRange(c) {
      const s = (c.sessions || []).map((x) => x.date).filter(Boolean).sort();
      if (!s.length) return '—';
      return s.length === 1 ? s[0] : (s[0] + ' → ' + s[s.length - 1]);
    }

    let ivShowPast = false;
    const ivShowPastBtn = root.querySelector('[data-sm-iv-show-past]');

    async function loadIvClassesTable() {
      if (!ivRows) return;
      ivRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        cache.ivClasses = await ns.fb.adminListIvClasses();
        if (!cache.locations || !cache.locations.length) cache.locations = await ns.fb.adminListLocationsAll();
      } catch (err) {
        console.error('[admin] list IV classes failed', err);
        ivRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      let rows = ivShowPast ? cache.ivClasses : cache.ivClasses.filter(classIsCurrentOrFuture);
      rows = rows.slice().sort((a, b) => String(a.startDate || '').localeCompare(String(b.startDate || '')));
      if (!rows.length) {
        const empty = ivShowPast ? 'No IV classes yet. Click + Add class.' : 'No current or upcoming classes. Click "Show past classes" to view older ones, or + Add class.';
        ivRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">' + empty + '</td></tr>';
        return;
      }
      ivRows.innerHTML = rows.map((c) => {
        const nd = ivNumDays(c);
        const timeSummary = (c.sessions || []).map((s) => {
          const md = (s.date || '').slice(5).replace('-', '/');
          return md + ' ' + (s.startTime || '?') + '–' + (s.endTime || '?');
        }).join(' · ') || '—';
        const seats = (typeof c.seatsBooked === 'number' ? c.seatsBooked : 0) + ' / ' + (c.seatsTotal || '—');
        const past = !classIsCurrentOrFuture(c);
        return '<tr' + (past ? ' style="opacity:0.55"' : '') + '>' +
          '<td><strong>' + (c.label || 'IV Class') + '</strong></td>' +
          '<td>' + ivLocLabel(c.location) + '</td>' +
          '<td>' + ivDateRange(c) + '</td>' +
          '<td style="font-size:12px">' + timeSummary + '</td>' +
          '<td>' + (nd ? nd + '-day' : '—') + '</td>' +
          '<td>' + seats + '</td>' +
          '<td><span class="sm-table__status sm-status--' + (c.active === false ? 'cancelled' : 'confirmed') + '">' + (c.active === false ? 'Inactive' : 'Active') + '</span></td>' +
          '<td style="white-space:nowrap">' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-iv-students="' + c.id + '">View students</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-iv-edit="' + c.id + '">Edit</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-iv-del="' + c.id + '" style="color:var(--sm-danger)">Delete</button>' +
          '</td>' +
        '</tr>';
      }).join('');
    }

    function ivSessionRowHtml(s) {
      s = s || {};
      return '<div class="sm-iv-session-row" style="display:grid;grid-template-columns:1fr 1fr 1fr auto;gap:6px;margin-top:6px;align-items:center">' +
        '<input type="date" data-sm-iv-sess-date value="' + (s.date || '') + '" style="font-size:13px;padding:6px">' +
        '<input type="text" data-sm-iv-sess-start value="' + ((s.startTime || '').replace(/"/g, '&quot;')) + '" placeholder="9:00 AM" style="font-size:13px;padding:6px">' +
        '<input type="text" data-sm-iv-sess-end value="' + ((s.endTime || '').replace(/"/g, '&quot;')) + '" placeholder="1:00 PM" style="font-size:13px;padding:6px">' +
        '<button type="button" data-sm-iv-sess-del aria-label="Remove" style="background:transparent;border:none;color:var(--sm-danger);cursor:pointer;font-size:18px;padding:0 8px">×</button>' +
      '</div>';
    }

    function ivModalHtml(c) {
      const locOpts = '<option value="">— pick a location —</option>' +
        (cache.locations || []).map((l) => '<option value="' + l.id + '"' + (c.location === l.id ? ' selected' : '') + '>' + (l.name || l.id) + '</option>').join('');
      const sessions = (c.sessions && c.sessions.length) ? c.sessions : [{}];
      const sessionRows = sessions.map(ivSessionRowHtml).join('');
      const productHandle = c.shopifyProductHandle || 'lpn-iv-certification';
      return '<form class="sm-form" data-sm-iv-form>' +
        '<div class="sm-field"><label>Class label</label><input type="text" name="label" value="' + ((c.label || '').replace(/"/g, '&quot;')) + '" placeholder="June IV Cert Class" required></div>' +
        '<div class="sm-field"><label>Location</label><select name="location" required>' + locOpts + '</select></div>' +
        '<div class="sm-field"><label>Class days <span class="sm-field__help">(each day has its own date + start/end time)</span></label>' +
          '<div data-sm-iv-sessions-list>' + sessionRows + '</div>' +
          '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-iv-add-session style="margin-top:8px">+ Add day</button>' +
          '<div class="sm-field__help" style="margin-top:6px">Number of days = how many rows here.</div>' +
        '</div>' +
        '<div class="sm-field"><label>Total seats</label><input type="number" name="seatsTotal" min="1" value="' + (c.seatsTotal || 12) + '"></div>' +

        // Product handle is hidden by default — staff almost never need to
        // change it from the default lpn-iv-certification. Click "Change"
        // to reveal the field for the rare case the product is renamed.
        '<input type="hidden" name="shopifyProductHandle" value="' + productHandle.replace(/"/g, '&quot;') + '" data-sm-iv-product-handle>' +
        '<div class="sm-field"><label>Class offering (Shopify variant)</label>' +
          '<select name="shopifyVariantId" data-sm-iv-variant-select required>' +
            '<option value="">Loading variants…</option>' +
            (c.shopifyVariantId ? '<option value="' + String(c.shopifyVariantId).replace(/"/g,'&quot;') + '" selected>Currently saved: ' + String(c.shopifyVariantId).replace(/</g,'&lt;') + ' — keep, or pick a new one below once loaded</option>' : '') +
          '</select>' +
          '<div class="sm-field__help">Pick the matching location + price tier — the customer pays this variant\'s price. <a href="#" data-sm-iv-show-handle style="color:var(--sm-brand)">Loading from a different product?</a></div>' +
        '</div>' +

        '<label class="sm-book__chk sm-mt-16"><input type="checkbox" name="active"' + (c.active === false ? '' : ' checked') + '><span>Active (shown to customers)</span></label>' +
        '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
          '<button type="submit" class="sm-btn sm-btn--primary">Save class</button>' +
          '<span class="sm-field__help" data-sm-iv-msg></span>' +
        '</div>' +
      '</form>';
    }

    async function openIvModal(id) {
      if (!modal || !modalBody) return;
      if (!cache.locations || !cache.locations.length) {
        try { cache.locations = await ns.fb.adminListLocationsAll(); } catch (e) {}
      }
      const c = id ? (cache.ivClasses.find((x) => x.id === id) || {}) : { active: true, seatsTotal: 12, sessions: [{}] };
      modalTitle && (modalTitle.textContent = id ? ('Edit class — ' + (c.label || '')) : 'Add IV class');
      modalBody.innerHTML = ivModalHtml(c);
      modal.hidden = false;
      const form = modalBody.querySelector('[data-sm-iv-form]');
      const msg  = modalBody.querySelector('[data-sm-iv-msg]');
      const handleInput = form.querySelector('[data-sm-iv-product-handle]');
      const variantSelect = form.querySelector('[data-sm-iv-variant-select]');

      // Populate the variant dropdown from /products/<handle>.js — each
      // option shows the variant's option values plus its price so staff
      // can pick by location/days/price instead of pasting an ID.
      async function loadVariants(handle, currentId) {
        if (!variantSelect) return;
        variantSelect.innerHTML = '<option value="">Loading variants…</option>';
        if (!handle) {
          variantSelect.innerHTML = '<option value="">Enter a product handle above</option>';
          return;
        }
        try {
          const r = await fetch('/products/' + encodeURIComponent(handle) + '.js');
          if (!r.ok) throw new Error('Product not found (' + r.status + ')');
          const prod = await r.json();
          const variants = (prod.variants || []).filter((v) => v.available !== false);
          if (!variants.length) {
            variantSelect.innerHTML = '<option value="">No active variants on this product</option>';
            return;
          }
          const moneyOf = (cents) => {
            if (cents == null) return '';
            const n = Number(cents) / 100;
            return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
          };
          variantSelect.innerHTML = '<option value="">— pick a variant —</option>' +
            variants.map((v) => {
              const opts = (v.options || []).filter(Boolean).join(' / ');
              const price = moneyOf(v.price);
              const label = (opts || v.title || ('Variant ' + v.id)) + (price ? '  —  ' + price : '');
              const sel = String(v.id) === String(currentId) ? ' selected' : '';
              return '<option value="' + v.id + '"' + sel + '>' + label.replace(/</g,'&lt;') + '</option>';
            }).join('');
        } catch (err) {
          variantSelect.innerHTML = '<option value="">Could not load: ' + (err.message || 'error') + '</option>';
        }
      }
      loadVariants(handleInput && handleInput.value, c.shopifyVariantId);
      handleInput && handleInput.addEventListener('change', () => loadVariants(handleInput.value, null));

      // Reveal the product handle field on demand (rare case: staff need to
      // load variants from a non-default Shopify product).
      const showHandleLink = form.querySelector('[data-sm-iv-show-handle]');
      showHandleLink && showHandleLink.addEventListener('click', (ev) => {
        ev.preventDefault();
        if (!handleInput) return;
        const wrap = document.createElement('div');
        wrap.className = 'sm-field';
        wrap.style.marginTop = '10px';
        wrap.innerHTML = '<label>Shopify product handle</label>' +
          '<input type="text" value="' + handleInput.value.replace(/"/g, '&quot;') + '" data-sm-iv-product-handle-visible>' +
          '<div class="sm-field__help">Press Enter or click outside the field to reload variants.</div>';
        showHandleLink.closest('.sm-field').appendChild(wrap);
        showHandleLink.remove();
        const visible = wrap.querySelector('[data-sm-iv-product-handle-visible]');
        visible.addEventListener('change', () => {
          handleInput.value = visible.value;
          loadVariants(handleInput.value, null);
        });
        visible.focus();
      });

      form.addEventListener('click', (ev) => {
        const addBtn = ev.target.closest('[data-sm-iv-add-session]');
        if (addBtn) {
          ev.preventDefault();
          const list = form.querySelector('[data-sm-iv-sessions-list]');
          const tmp = document.createElement('div');
          tmp.innerHTML = ivSessionRowHtml({});
          list.appendChild(tmp.firstChild);
          return;
        }
        const delBtn = ev.target.closest('[data-sm-iv-sess-del]');
        if (delBtn) {
          ev.preventDefault();
          const row = delBtn.closest('.sm-iv-session-row');
          if (row) row.remove();
        }
      });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const fd = new FormData(form);
          const toInt = (v) => { const n = parseInt(v, 10); return isNaN(n) ? null : n; };
          const sessions = [];
          form.querySelectorAll('.sm-iv-session-row').forEach((row) => {
            const date  = (row.querySelector('[data-sm-iv-sess-date]')  || {}).value || '';
            const start = (row.querySelector('[data-sm-iv-sess-start]') || {}).value || '';
            const end   = (row.querySelector('[data-sm-iv-sess-end]')   || {}).value || '';
            if (date && start && end) sessions.push({ date: date.trim(), startTime: start.trim(), endTime: end.trim() });
          });
          if (!fd.get('location')) throw new Error('Pick a location.');
          if (!sessions.length) throw new Error('Add at least one class day with a date + start + end time.');
          sessions.sort((a, b) => a.date.localeCompare(b.date));
          const data = {
            label: fd.get('label') || 'IV Class',
            location: fd.get('location') || '',
            sessions: sessions,
            numDays: sessions.length,
            startDate: sessions[0].date,
            endDate: sessions[sessions.length - 1].date,
            seatsTotal: toInt(fd.get('seatsTotal')) || 12,
            shopifyVariantId: (fd.get('shopifyVariantId') || '').toString().trim(),
            shopifyProductHandle: (fd.get('shopifyProductHandle') || '').toString().trim() || null,
            active: !!fd.get('active')
          };
          await ns.fb.adminSaveIvClass(id || null, data);
          msg.textContent = '✓ Saved.';
          await loadIvClassesTable();
          setTimeout(closeModal, 500);
        } catch (err) {
          console.error('[admin] save IV class failed', err);
          msg.textContent = err.message || 'Save failed.';
        }
      });
    }

    ivRows && ivRows.addEventListener('click', async (e) => {
      const editBtn = e.target.closest('[data-sm-iv-edit]');
      const delBtn  = e.target.closest('[data-sm-iv-del]');
      const studentsBtn = e.target.closest('[data-sm-iv-students]');
      if (studentsBtn) {
        openClassStudentsModal('iv', studentsBtn.dataset.smIvStudents);
        return;
      }
      if (editBtn) openIvModal(editBtn.dataset.smIvEdit);
      else if (delBtn) {
        if (!confirm('Delete this IV class?')) return;
        delBtn.disabled = true;
        try { await ns.fb.adminDeleteIvClass(delBtn.dataset.smIvDel); await loadIvClassesTable(); }
        catch (err) { delBtn.disabled = false; alert(err.message || 'Delete failed.'); }
      }
    });
    ivNewBtn && ivNewBtn.addEventListener('click', () => openIvModal(null));
    ivShowPastBtn && ivShowPastBtn.addEventListener('click', () => {
      ivShowPast = !ivShowPast;
      ivShowPastBtn.textContent = ivShowPast ? 'Hide past classes' : 'Show past classes';
      loadIvClassesTable();
    });

    /* ---------- INQUIRIES TAB (onsite / group form leads) ---------- */
    let inqLoaded = false;
    const inqRows = root.querySelector('[data-sm-inq-rows]');
    const inqStatusFilter = root.querySelector('[data-sm-inq-filter-status]');
    const inqRefresh = root.querySelector('[data-sm-inq-refresh]');
    cache.inquiries = [];

    function fmtInqDate(ts) {
      if (!ts) return '—';
      try {
        const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
        return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) +
          '<br><span style="color:var(--sm-ink-muted);font-size:11px">' +
          d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) + '</span>';
      } catch (e) { return '—'; }
    }

    async function loadInquiriesTable() {
      if (!inqRows) return;
      inqRows.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        cache.inquiries = await ns.fb.adminListOnsiteInquiries();
      } catch (err) {
        console.error('[admin] list inquiries failed', err);
        inqRows.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      const status = inqStatusFilter && inqStatusFilter.value;
      const rows = cache.inquiries.filter((r) => !status || (r.status || 'new') === status);
      if (!rows.length) {
        inqRows.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No inquiries yet.</td></tr>';
        return;
      }
      inqRows.innerHTML = rows.map((r) => {
        const st = r.status || 'new';
        const pillClass = (st === 'won' || st === 'quoted') ? 'confirmed' : (st === 'lost' ? 'cancelled' : (st === 'contacted' ? 'completed' : 'pending'));
        return '<tr>' +
          '<td>' + fmtInqDate(r.createdAt) + '</td>' +
          '<td><strong>' + (r.name || '—') + '</strong><br><span style="color:var(--sm-ink-muted);font-size:12px">' + (r.company || '') + (r.jobTitle ? ' · ' + r.jobTitle : '') + '</span></td>' +
          '<td>' + (r.email || '—') + (r.phone ? '<br><span style="color:var(--sm-ink-muted);font-size:12px">' + r.phone + '</span>' : '') + '</td>' +
          '<td style="font-size:12px;max-width:280px">' + (r.courses || '—') + '</td>' +
          '<td>' + (r.headcount || '—') + '</td>' +
          '<td><span class="sm-table__status sm-status--' + pillClass + '">' + st + '</span></td>' +
          '<td style="white-space:nowrap">' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-inq-view="' + r.id + '">View</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-inq-del="' + r.id + '" style="color:var(--sm-danger)">Delete</button>' +
          '</td>' +
        '</tr>';
      }).join('');
    }

    function openInquiryModal(id) {
      if (!modal || !modalBody) return;
      const r = cache.inquiries.find((x) => x.id === id) || {};
      modalTitle && (modalTitle.textContent = 'Inquiry — ' + (r.name || ''));
      const row = (label, val) => val ? ('<div style="display:flex;gap:12px;padding:8px 0;border-bottom:1px solid var(--sm-border-soft)"><div style="min-width:140px;color:var(--sm-ink-muted);font-size:12px;text-transform:uppercase;letter-spacing:.04em">' + label + '</div><div style="flex:1">' + esc(val) + '</div></div>') : '';
      modalBody.innerHTML =
        '<form class="sm-form" data-sm-inq-form>' +
          row('Received', r.createdAt && r.createdAt.toDate ? r.createdAt.toDate().toLocaleString() : '') +
          row('Name', r.name) +
          row('Job title', r.jobTitle) +
          row('Email', r.email) +
          row('Phone', r.phone) +
          row('Company', r.company) +
          row('Industry', r.industry) +
          row('Courses', r.courses) +
          row('Headcount', r.headcount) +
          row('Where', r.where) +
          row('Preferred date', r.preferredDate) +
          row('Backup date', r.backupDate) +
          row('Message', r.message) +
          row('From page', r.page) +
          '<div class="sm-form__row" style="margin-top:16px">' +
            '<div class="sm-field"><label>Status</label>' +
              '<select name="status">' +
                ['new','contacted','quoted','won','lost'].map((s) => '<option value="' + s + '"' + ((r.status||'new')===s?' selected':'') + '>' + s + '</option>').join('') +
              '</select></div>' +
          '</div>' +
          '<div class="sm-field"><label>Internal notes</label><textarea name="notes" rows="3">' + esc(r.notes || '') + '</textarea></div>' +
          '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:8px">' +
            '<button type="submit" class="sm-btn sm-btn--primary">Save</button>' +
            '<span class="sm-field__help" data-sm-inq-msg></span>' +
          '</div>' +
        '</form>';
      modal.hidden = false;
      const form = modalBody.querySelector('[data-sm-inq-form]');
      const msg = modalBody.querySelector('[data-sm-inq-msg]');
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        msg.textContent = 'Saving…';
        try {
          await ns.fb.adminUpdateOnsiteInquiry(id, { status: form.status.value, notes: form.notes.value.trim() });
          const local = cache.inquiries.find((x) => x.id === id);
          if (local) { local.status = form.status.value; local.notes = form.notes.value.trim(); }
          msg.textContent = '✓ Saved.';
          await loadInquiriesTable();
          setTimeout(closeModal, 500);
        } catch (err) { msg.textContent = err.message || 'Save failed.'; }
      });
    }

    inqRows && inqRows.addEventListener('click', async (e) => {
      const viewBtn = e.target.closest('[data-sm-inq-view]');
      const delBtn = e.target.closest('[data-sm-inq-del]');
      if (viewBtn) openInquiryModal(viewBtn.dataset.smInqView);
      else if (delBtn) {
        if (!confirm('Delete this inquiry?')) return;
        delBtn.disabled = true;
        try { await ns.fb.adminDeleteOnsiteInquiry(delBtn.dataset.smInqDel); await loadInquiriesTable(); }
        catch (err) { delBtn.disabled = false; alert(err.message || 'Delete failed.'); }
      }
    });
    inqStatusFilter && inqStatusFilter.addEventListener('change', loadInquiriesTable);
    inqRefresh && inqRefresh.addEventListener('click', loadInquiriesTable);

    /* ---------- BOOKING FAILURES TAB (diagnostic log) ---------- */
    let failLoaded = false;
    const failRows = root.querySelector('[data-sm-fail-rows]');
    const failRefresh = root.querySelector('[data-sm-fail-refresh]');
    cache.failures = [];

    async function loadFailuresTable() {
      if (!failRows) return;
      failRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        cache.failures = await ns.fb.adminListBookingFailures();
      } catch (err) {
        console.error('[admin] list booking failures failed', err);
        failRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      if (!cache.failures.length) {
        failRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No failures logged. 🎉</td></tr>';
        return;
      }
      failRows.innerHTML = cache.failures.map((r) => {
        const when = (r.createdAt && r.createdAt.toDate) ? r.createdAt.toDate().toLocaleString(undefined, { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' }) : '—';
        return '<tr>' +
          '<td style="white-space:nowrap">' + when + '</td>' +
          '<td>' + (r.matchValue || r.serviceName || r.serviceId || '—') + '</td>' +
          '<td>' + (r.locationName || r.locationId || '—') + '</td>' +
          '<td>' + esc(r.customerName || '—') + (r.customerEmail ? '<br><span style="color:var(--sm-ink-muted);font-size:12px">' + esc(r.customerEmail) + '</span>' : '') + '</td>' +
          '<td style="font-family:monospace;font-size:12px">' + (r.productHandle || '—') + '</td>' +
          '<td style="white-space:nowrap">' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-fail-view="' + r.id + '">View</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-fail-del="' + r.id + '" style="color:var(--sm-danger)">Delete</button>' +
          '</td>' +
        '</tr>';
      }).join('');
    }

    function openFailureModal(id) {
      if (!modal || !modalBody) return;
      const r = cache.failures.find((x) => x.id === id) || {};
      modalTitle && (modalTitle.textContent = 'Booking failure');
      const row = (label, val) => (val !== undefined && val !== '' && val !== null) ? ('<div style="display:flex;gap:12px;padding:8px 0;border-bottom:1px solid var(--sm-border-soft)"><div style="min-width:160px;color:var(--sm-ink-muted);font-size:12px;text-transform:uppercase;letter-spacing:.04em">' + label + '</div><div style="flex:1;word-break:break-word">' + esc(typeof val === 'object' ? JSON.stringify(val) : String(val)) + '</div></div>') : '';
      let variantBlock = '';
      if (r.variants && r.variants.length) {
        variantBlock = '<div style="margin-top:14px"><div style="font-size:12px;color:var(--sm-ink-muted);text-transform:uppercase;letter-spacing:.04em;margin-bottom:6px">What the browser saw on the product</div>' +
          '<div style="overflow:auto;max-height:300px"><table class="sm-table" style="font-size:12px"><thead><tr><th>Variant ID</th><th>Available</th><th>Options (raw)</th><th>Normalized</th></tr></thead><tbody>' +
          r.variants.map((v) => '<tr><td style="font-family:monospace">' + esc(v.id) + '</td><td>' + (v.available === false ? 'no' : 'yes') + '</td><td>' + esc((v.opts||[]).join(' / ')) + '</td><td style="font-family:monospace;color:var(--sm-ink-muted)">' + esc((v.normOpts||[]).join(' / ')) + '</td></tr>').join('') +
          '</tbody></table></div></div>';
      }
      modalBody.innerHTML =
        '<div>' +
          row('When', r.createdAt && r.createdAt.toDate ? r.createdAt.toDate().toLocaleString() : '') +
          row('Reason', r.reason) +
          row('Course (tile match_value)', r.matchValue) +
          row('Service ID', r.serviceId) +
          row('Service name', r.serviceName) +
          row('Location name', r.locationName) +
          row('Location ID', r.locationId) +
          row('Product handle', r.productHandle) +
          row('Needles searched', (r.needles || []).join(' AND ')) +
          row('Needles (normalized)', (r.needlesNormalized || []).join(' AND ')) +
          row('Customer', r.customerName) +
          row('Customer email', r.customerEmail) +
          row('Page', r.url) +
          row('User agent', r.userAgent) +
          variantBlock +
        '</div>';
      modal.hidden = false;
    }

    failRows && failRows.addEventListener('click', async (e) => {
      const viewBtn = e.target.closest('[data-sm-fail-view]');
      const delBtn = e.target.closest('[data-sm-fail-del]');
      if (viewBtn) openFailureModal(viewBtn.dataset.smFailView);
      else if (delBtn) {
        if (!confirm('Delete this failure log entry?')) return;
        delBtn.disabled = true;
        try { await ns.fb.adminDeleteBookingFailure(delBtn.dataset.smFailDel); await loadFailuresTable(); }
        catch (err) { delBtn.disabled = false; alert(err.message || 'Delete failed.'); }
      }
    });
    failRefresh && failRefresh.addEventListener('click', loadFailuresTable);

    /* ---------- ELKS EVENT REGISTRATIONS TAB ---------- */
    let elksLoaded = false;
    const elksRows = root.querySelector('[data-sm-elks-rows]');
    const elksRefresh = root.querySelector('[data-sm-elks-refresh]');
    const elksDownload = root.querySelector('[data-sm-elks-download]');
    cache.elks = [];

    function elksDate(ts) {
      if (!ts) return '';
      try {
        const d = ts.toDate ? ts.toDate() : (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts));
        return d.toLocaleString();
      } catch (e) { return ''; }
    }

    async function loadElksTable() {
      if (!elksRows) return;
      elksRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        cache.elks = await ns.fb.adminListElksRegistrations();
      } catch (err) {
        console.error('[admin] list elks failed', err);
        elksRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      if (!cache.elks.length) {
        elksRows.innerHTML = '<tr><td colspan="6" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No registrations yet.</td></tr>';
        return;
      }
      elksRows.innerHTML = cache.elks.map((r) => {
        const when = r.createdAt && r.createdAt.toDate ? r.createdAt.toDate().toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' }) : '—';
        const name = ((r.firstName || '') + ' ' + (r.lastName || '')).trim() || '—';
        return '<tr>' +
          '<td style="white-space:nowrap">' + when + '</td>' +
          '<td><strong>' + esc(name) + '</strong></td>' +
          '<td>' + esc(r.lodge || '—') + '</td>' +
          '<td>' + esc(r.memberNumber || '—') + '</td>' +
          '<td>' + esc(r.email || '—') + '</td>' +
          '<td style="white-space:nowrap"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-elks-del="' + r.id + '" style="color:var(--sm-danger)">Delete</button></td>' +
        '</tr>';
      }).join('');
    }

    // CSV-safe a single cell: wrap in quotes, escape embedded quotes.
    function csvCell(v) {
      const s = (v == null) ? '' : String(v);
      if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    }
    function downloadElksCsv() {
      if (!cache.elks || !cache.elks.length) { alert('No registrations to export.'); return; }
      const header = ['Received', 'First name', 'Last name', 'Lodge #', 'Member #', 'Email', 'Event', 'Source'];
      const rows = cache.elks.map((r) => [
        elksDate(r.createdAt),
        r.firstName || '',
        r.lastName || '',
        r.lodge || '',
        r.memberNumber || '',
        r.email || '',
        r.event || '',
        r.source || ''
      ]);
      const lines = [header, ...rows].map((row) => row.map(csvCell).join(','));
      // Prepend UTF-8 BOM so Excel opens it with correct encoding.
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = 'elks-registrations-' + stamp + '.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }

    elksRows && elksRows.addEventListener('click', async (e) => {
      const delBtn = e.target.closest('[data-sm-elks-del]');
      if (!delBtn) return;
      if (!confirm('Delete this Elks registration?')) return;
      delBtn.disabled = true;
      try { await ns.fb.adminDeleteElksRegistration(delBtn.dataset.smElksDel); await loadElksTable(); }
      catch (err) { delBtn.disabled = false; alert(err.message || 'Delete failed.'); }
    });
    elksRefresh && elksRefresh.addEventListener('click', loadElksTable);
    elksDownload && elksDownload.addEventListener('click', downloadElksCsv);

    /* ---------- INVENTORY / ASSET TRACKER TAB ---------- */
    let assetsLoaded = false;
    const assetRows = root.querySelector('[data-sm-asset-rows]');
    const assetNewBtn = root.querySelector('[data-sm-asset-new]');
    const assetSeedBtn = root.querySelector('[data-sm-asset-seed]');
    const assetDownloadBtn = root.querySelector('[data-sm-asset-download]');
    const assetCatFilter = root.querySelector('[data-sm-asset-filter-category]');
    const assetLocFilter = root.querySelector('[data-sm-asset-filter-location]');
    const assetPrintLabelsBtn = root.querySelector('[data-sm-asset-print-labels]');
    const checkoutStartBtn = root.querySelector('[data-sm-checkout-start]');
    const checkoutReturnBtn = root.querySelector('[data-sm-checkout-return]');
    const checkoutListBtn = root.querySelector('[data-sm-checkout-list]');
    const checkoutBadge = root.querySelector('[data-sm-checkout-badge]');
    cache.assets = [];
    cache.assetCheckouts = [];

    // The list the user supplied at setup time. Used once via "Seed starter
    // list" so the team isn't pasting 36 rows manually.
    const ASSET_SEED = [
      { name: 'Yellow Mini AED Trainers',            category: 'CPR',         qty: 14, manufacturer: 'Shenzhen XFT Medical Limited', location: 'Lake City' },
      { name: 'X-Force Tourniquet',                  category: 'CPR',         qty: 2,  manufacturer: 'S.T.A.T Medical Devices',      location: 'Lake City' },
      { name: 'Blue Prestan AED Ultratrainer',       category: 'CPR',         qty: 6,  manufacturer: 'MCR-Medical AED Trainer Sales & Service', location: 'Lake City' },
      { name: 'Nasal Med Trainer (Narcan)',          category: 'CPR',         qty: 11, location: 'Lake City' },
      { name: 'Epi Pen Trainers',                    category: 'CPR',         qty: 10, location: 'Lake City' },
      { name: 'One-way valves',                      category: 'CPR',         qty: 19, location: 'Lake City' },
      { name: 'CPR Mask (Pocket Mask)',              category: 'CPR',         qty: 1,  manufacturer: 'Moore Medical Corp',           location: 'Lake City' },
      { name: 'Blue Bag Prestan Compact Mannequin',  category: 'CPR',         qty: 4,  manufacturer: 'Prestan CPR Feedback Piston',  location: 'Lake City' },
      { name: 'Prestan Click/Button Mannequin',      category: 'CPR',         qty: 2,  manufacturer: 'Prestan Products LLC',         location: 'Lake City' },
      { name: 'Little Anne Mannequin',               category: 'CPR',         qty: 6,  manufacturer: 'Laerdal Medical',              location: 'Lake City' },
      { name: 'Infant Mannequin',                    category: 'CPR',         qty: 2,  manufacturer: 'Prestan Products LLC',         location: 'Lake City' },
      { name: 'Infant Doll',                         category: 'CPR',         qty: 1,  location: 'Lake City' },
      { name: 'RQI (Go-Bag)',                        category: 'CPR',         qty: 1,  manufacturer: 'AHA & Laerdal',                location: 'Lake City' },
      { name: 'Bag Valve Mask (Adult)',              category: 'CPR',         qty: 18, location: 'Lake City' },
      { name: 'Bag Valve Mask (Infant)',             category: 'CPR',         qty: 1,  location: 'Lake City' },
      { name: 'RQI-Station - LC',                    category: 'CPR',         qty: 1,  location: 'Lake City' },
      { name: 'RQI-Station - GV',                    category: 'CPR',         qty: 1,  location: 'Gainesville' },
      { name: 'RQI-Station - JX',                    category: 'CPR',         qty: 1,  location: 'Jacksonville' },
      { name: 'SMT-LT1',  category: 'Electronics', qty: 1, manufacturer: 'Lenovo', location: 'Lake City', serial: 'PF5S1N4Y' },
      { name: 'SMT-LT2',  category: 'Electronics', qty: 1, manufacturer: 'Lenovo', location: 'Lake City', serial: 'PF5RQ0VY' },
      { name: 'SMT-LT3',  category: 'Electronics', qty: 1, manufacturer: 'Lenovo', location: 'Lake City', serial: 'PF5S62X0' },
      { name: 'SMT-LT4',  category: 'Electronics', qty: 1, manufacturer: 'Lenovo', location: 'Lake City', serial: 'PF5TZ171' },
      { name: 'SMT-LT5',  category: 'Electronics', qty: 1, manufacturer: 'Lenovo', location: 'Lake City', serial: 'PF5S9BN4' },
      { name: 'SMT-LT6',  category: 'Electronics', qty: 1, manufacturer: 'Lenovo', location: 'Lake City', serial: 'PF5S6WAK' },
      { name: 'SMT-LT7',  category: 'Electronics', qty: 1, manufacturer: 'Lenovo', location: 'Lake City', serial: 'PF5S1HXW' },
      { name: 'SMT-LT8',  category: 'Electronics', qty: 1, manufacturer: 'Lenovo', location: 'Lake City', serial: 'PF5TVJLZ' },
      { name: 'SMT-LT9',  category: 'Electronics', qty: 1, manufacturer: 'Lenovo', location: 'Lake City', serial: 'PF5TZ430' },
      { name: 'SMT-LT10', category: 'Electronics', qty: 1, manufacturer: 'HP',     location: 'Lake City', serial: '5CG438379J' },
      { name: 'SMT-LT11', category: 'Electronics', qty: 1, manufacturer: 'HP',     location: 'Lake City', serial: '1H953606LS' },
      { name: 'SMT-LT12', category: 'Electronics', qty: 1, manufacturer: 'HP',     location: 'Lake City', serial: '1H95360940' },
      { name: 'PS-RS-1',   category: 'Electronics', qty: 1, manufacturer: 'Xperix Inc', model: 'RealScan-G10', location: 'Lake City', serial: 'RSG10(V02D)' },
      { name: 'STAT-RS-1', category: 'Electronics', qty: 1, manufacturer: 'Xperix Inc', model: 'RealScan-G10', location: 'Lake City', serial: 'RSG10(V01A)' },
      { name: 'iPad Mini - Stat Med',  category: 'Electronics', qty: 1, manufacturer: 'Apple', model: 'MXPN3LL/A', location: 'Lake City', serial: 'C2LVWC4W5F' },
      { name: 'iPad Mini - Ofc Mngr',  category: 'Electronics', qty: 1, manufacturer: 'Apple', model: 'MXPN3LL/A', location: 'Lake City', serial: 'FYK7F79Q1N' },
      { name: 'iPad Mini - CEO',       category: 'Electronics', qty: 1, manufacturer: 'Apple', model: 'MXPN3LL/A', location: 'Lake City', serial: 'JR73LW2WVG' }
    ];

    function refreshAssetFilters() {
      const cats = Array.from(new Set(cache.assets.map((a) => a.category).filter(Boolean))).sort();
      const locs = Array.from(new Set(cache.assets.map((a) => a.location).filter(Boolean))).sort();
      if (assetCatFilter) {
        const cur = assetCatFilter.value;
        assetCatFilter.innerHTML = '<option value="">All categories</option>' +
          cats.map((c) => '<option value="' + esc(c) + '">' + esc(c) + '</option>').join('');
        if (cur) assetCatFilter.value = cur;
      }
      if (assetLocFilter) {
        const cur = assetLocFilter.value;
        assetLocFilter.innerHTML = '<option value="">All locations</option>' +
          locs.map((l) => '<option value="' + esc(l) + '">' + esc(l) + '</option>').join('');
        if (cur) assetLocFilter.value = cur;
      }
    }

    // Build a map of assetId -> total units currently checked out across
    // every active checkout. Used to render "X out of Y" in the table
    // and to gate scanner quantities during checkout.
    function assetsOutMap() {
      const m = Object.create(null);
      (cache.assetCheckouts || []).forEach((co) => {
        if (co.status !== 'active') return;
        (co.items || []).forEach((it) => {
          const out = Math.max(0, (it.qty || 0) - (it.qtyReturned || 0));
          if (out > 0) m[it.assetId] = (m[it.assetId] || 0) + out;
        });
      });
      return m;
    }

    function refreshCheckoutBadge() {
      if (!checkoutBadge) return;
      const active = (cache.assetCheckouts || []).filter((c) => c.status === 'active').length;
      if (active > 0) { checkoutBadge.textContent = String(active); checkoutBadge.style.display = ''; }
      else { checkoutBadge.style.display = 'none'; }
    }

    async function loadAssetsTable() {
      if (!assetRows) return;
      assetRows.innerHTML = '<tr><td colspan="9" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        const [assets, checkouts] = await Promise.all([
          ns.fb.adminListAssets(),
          ns.fb.adminListAssetCheckouts({ activeOnly: false }).catch(() => [])
        ]);
        cache.assets = assets;
        cache.assetCheckouts = checkouts;
      } catch (err) {
        console.error('[admin] list assets failed', err);
        assetRows.innerHTML = '<tr><td colspan="9" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      refreshAssetFilters();
      refreshCheckoutBadge();
      const outMap = assetsOutMap();
      const catVal = assetCatFilter && assetCatFilter.value;
      const locVal = assetLocFilter && assetLocFilter.value;
      const rows = cache.assets.filter((a) => (!catVal || a.category === catVal) && (!locVal || a.location === locVal))
        .sort((a, b) => String(a.category || '').localeCompare(String(b.category || '')) || String(a.name || '').localeCompare(String(b.name || '')));
      if (!rows.length) {
        const empty = cache.assets.length
          ? 'No items match the current filters.'
          : 'No items yet. Click <strong>Seed starter list</strong> to load the 36-item inventory we set up for you, or <strong>+ Add item</strong> to enter one manually.';
        assetRows.innerHTML = '<tr><td colspan="9" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">' + empty + '</td></tr>';
        return;
      }
      assetRows.innerHTML = rows.map((a) => {
        const out = outMap[a.id] || 0;
        const outCell = out > 0
          ? '<span style="color:var(--sm-danger,#b91c1c);font-weight:600">' + out + '</span>'
          : '<span style="color:var(--sm-ink-muted)">0</span>';
        return '<tr>' +
          '<td><strong>' + esc(a.name || '—') + '</strong></td>' +
          '<td>' + esc(a.category || '—') + '</td>' +
          '<td>' + (a.qty != null ? esc(String(a.qty)) : '—') + '</td>' +
          '<td>' + outCell + '</td>' +
          '<td>' + esc(a.manufacturer || '—') + '</td>' +
          '<td>' + esc(a.model || '—') + '</td>' +
          '<td>' + esc(a.location || '—') + '</td>' +
          '<td style="font-family:monospace;font-size:12px">' + esc(a.serial || '—') + '</td>' +
          '<td style="white-space:nowrap">' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-asset-edit="' + a.id + '">Edit</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-asset-del="' + a.id + '" style="color:var(--sm-danger)">Delete</button>' +
          '</td>' +
        '</tr>';
      }).join('');
    }

    function openAssetModal(id) {
      if (!modal || !modalBody) return;
      const a = id ? (cache.assets.find((x) => x.id === id) || {}) : {};
      modalTitle && (modalTitle.textContent = id ? ('Edit — ' + (a.name || 'asset')) : 'Add inventory item');
      const v = (s) => esc(s == null ? '' : String(s));
      modalBody.innerHTML = '<form class="sm-form" data-sm-asset-form>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Asset name</label><input name="name" value="' + v(a.name) + '" required></div>' +
          '<div class="sm-field"><label>Category</label><input name="category" value="' + v(a.category) + '" placeholder="CPR, Electronics, Office, …"></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Quantity</label><input type="number" name="qty" value="' + (a.qty != null ? v(a.qty) : '') + '" min="0"></div>' +
          '<div class="sm-field"><label>Location</label><input name="location" value="' + v(a.location) + '" placeholder="Lake City / Gainesville / Jacksonville"></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Manufacturer</label><input name="manufacturer" value="' + v(a.manufacturer) + '"></div>' +
          '<div class="sm-field"><label>Model</label><input name="model" value="' + v(a.model) + '"></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Serial number</label><input name="serial" value="' + v(a.serial) + '"></div>' +
          '<div class="sm-field"><label>Manufacture date</label><input type="date" name="manufactureDate" value="' + v(a.manufactureDate) + '"></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Install date</label><input type="date" name="installDate" value="' + v(a.installDate) + '"></div>' +
          '<div class="sm-field"><label>Notes</label><input name="notes" value="' + v(a.notes) + '"></div>' +
        '</div>' +
        '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
          '<button type="submit" class="sm-btn sm-btn--primary">Save</button>' +
          (id ? '<button type="button" class="sm-btn sm-btn--light" data-sm-asset-modal-del style="color:var(--sm-danger)">Delete</button>' : '') +
          '<span class="sm-field__help" data-sm-asset-msg></span>' +
        '</div>' +
      '</form>';
      modal.hidden = false;
      const form = modalBody.querySelector('[data-sm-asset-form]');
      const msg  = modalBody.querySelector('[data-sm-asset-msg]');
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const fd = new FormData(form);
          const qtyRaw = (fd.get('qty') || '').toString().trim();
          const data = {
            name: (fd.get('name') || '').toString().trim(),
            category: (fd.get('category') || '').toString().trim(),
            qty: qtyRaw === '' ? null : parseInt(qtyRaw, 10),
            location: (fd.get('location') || '').toString().trim(),
            manufacturer: (fd.get('manufacturer') || '').toString().trim(),
            model: (fd.get('model') || '').toString().trim(),
            serial: (fd.get('serial') || '').toString().trim(),
            manufactureDate: (fd.get('manufactureDate') || '').toString().trim(),
            installDate: (fd.get('installDate') || '').toString().trim(),
            notes: (fd.get('notes') || '').toString().trim()
          };
          if (!data.name) throw new Error('Asset name is required.');
          await ns.fb.adminSaveAsset(id || null, data);
          msg.textContent = '✓ Saved.';
          await loadAssetsTable();
          setTimeout(closeModal, 400);
        } catch (err) { msg.textContent = err.message || 'Save failed.'; }
      });
      const modalDelBtn = modalBody.querySelector('[data-sm-asset-modal-del]');
      modalDelBtn && modalDelBtn.addEventListener('click', async () => {
        if (!confirm('Delete this item permanently?')) return;
        try { await ns.fb.adminDeleteAsset(id); await loadAssetsTable(); closeModal(); }
        catch (err) { alert(err.message || 'Delete failed.'); }
      });
    }

    function csvCellAsset(v) {
      const s = (v == null) ? '' : String(v);
      return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    }
    function downloadAssetsCsv() {
      if (!cache.assets || !cache.assets.length) { alert('No inventory to export.'); return; }
      const header = ['Asset Name','Category','Quantity','Manufacturer','Model','Location','Serial Number','Manufacture Date','Install Date','Notes'];
      const rows = cache.assets.map((a) => [
        a.name || '', a.category || '', a.qty != null ? a.qty : '',
        a.manufacturer || '', a.model || '', a.location || '',
        a.serial || '', a.manufactureDate || '', a.installDate || '', a.notes || ''
      ]);
      const lines = [header, ...rows].map((row) => row.map(csvCellAsset).join(','));
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      a.href = url; a.download = 'inventory-' + stamp + '.csv';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }
    async function seedAssetsList() {
      if (cache.assets && cache.assets.length) {
        if (!confirm('Inventory already has ' + cache.assets.length + ' items. Add ' + ASSET_SEED.length + ' more from the starter list? (Duplicates by name will be skipped.)')) return;
      } else {
        if (!confirm('Load the ' + ASSET_SEED.length + '-item starter inventory? Safe to run later again — duplicates by name are skipped.')) return;
      }
      assetSeedBtn && (assetSeedBtn.disabled = true);
      const existingNames = new Set((cache.assets || []).map((a) => (a.name || '').toLowerCase()));
      let added = 0, skipped = 0;
      for (const item of ASSET_SEED) {
        if (existingNames.has((item.name || '').toLowerCase())) { skipped++; continue; }
        try { await ns.fb.adminSaveAsset(null, item); added++; }
        catch (err) { console.error('[asset seed] failed for', item.name, err); }
      }
      assetSeedBtn && (assetSeedBtn.disabled = false);
      alert('Seed done. Added ' + added + ', skipped ' + skipped + ' (already existed).');
      await loadAssetsTable();
    }

    assetRows && assetRows.addEventListener('click', async (e) => {
      const editBtn = e.target.closest('[data-sm-asset-edit]');
      const delBtn  = e.target.closest('[data-sm-asset-del]');
      if (editBtn) openAssetModal(editBtn.dataset.smAssetEdit);
      else if (delBtn) {
        if (!confirm('Delete this item?')) return;
        delBtn.disabled = true;
        try { await ns.fb.adminDeleteAsset(delBtn.dataset.smAssetDel); await loadAssetsTable(); }
        catch (err) { delBtn.disabled = false; alert(err.message || 'Delete failed.'); }
      }
    });
    assetNewBtn && assetNewBtn.addEventListener('click', () => openAssetModal(null));
    assetSeedBtn && assetSeedBtn.addEventListener('click', seedAssetsList);
    assetDownloadBtn && assetDownloadBtn.addEventListener('click', downloadAssetsCsv);
    assetCatFilter && assetCatFilter.addEventListener('change', loadAssetsTable);
    assetLocFilter && assetLocFilter.addEventListener('change', loadAssetsTable);

    /* ---------- ASSET CHECKOUT / RETURN (QR FLOW) ---------- */
    // QR payload format used on every label we print:
    //   STATMED:ASSET:<firestoreId>
    // Anything else scanned is ignored so a random QR (e.g. a Wi-Fi
    // sticker) can't accidentally inject an item.
    const QR_PREFIX = 'STATMED:ASSET:';
    let qrScannerLib = null;
    let activeScanner = null;

    function loadQrScannerLib() {
      if (qrScannerLib) return Promise.resolve(qrScannerLib);
      if (window.Html5Qrcode) { qrScannerLib = window.Html5Qrcode; return Promise.resolve(qrScannerLib); }
      return new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/html5-qrcode@2.3.8/html5-qrcode.min.js';
        s.async = true;
        s.onload = () => { qrScannerLib = window.Html5Qrcode; qrScannerLib ? resolve(qrScannerLib) : reject(new Error('Scanner library failed to expose Html5Qrcode')); };
        s.onerror = () => reject(new Error('Could not load QR scanner library (check your internet connection).'));
        document.head.appendChild(s);
      });
    }

    function parseAssetQr(text) {
      if (!text) return null;
      const t = String(text).trim();
      if (!t.toUpperCase().startsWith(QR_PREFIX)) return null;
      const id = t.slice(QR_PREFIX.length).trim();
      return id || null;
    }

    // Re-entrancy guards. html5-qrcode throws "cannot transition to a
    // new state, already under transition" if start/stop is called while
    // the internal state machine is mid-transition (often caused by a
    // double-tap on the toggle button on mobile).
    let scannerStarting = false;
    let scannerStopping = false;
    const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

    async function startScanner(elementId, onDecoded) {
      if (scannerStarting) throw new Error('Camera is already starting — wait a moment and try again.');
      scannerStarting = true;
      try {
        const Html5Qr = await loadQrScannerLib();
        // Hard stop and let the previous instance's state machine settle.
        await stopScanner();
        await sleep(120);
        const box = document.getElementById(elementId);
        if (box) {
          // iOS Safari won't paint the video unless the container has a
          // real pixel size before getUserMedia. Force a 1:1 square.
          box.style.width = '100%';
          box.style.maxWidth = '340px';
          box.style.aspectRatio = '1 / 1';
          box.style.background = '#000';
          box.style.borderRadius = '8px';
          box.style.overflow = 'hidden';
          box.style.position = 'relative';
          box.innerHTML = '';  // wipe anything the previous attempt left behind
        }
        let last = '';
        let lastAt = 0;
        const onScan = (decoded) => {
          const now = Date.now();
          if (decoded === last && (now - lastAt) < 1500) return;
          last = decoded; lastAt = now;
          try { onDecoded(decoded); } catch (e) { console.error('[qr] handler threw', e); }
        };
        // No qrbox = scan the full camera frame, so the user doesn't have
        // to center the sticker perfectly. fps:15 is a small CPU win on
        // newer phones with no downside on iOS.
        const cfg = { fps: 15, aspectRatio: 1.0 };

        const friendly = (err) => {
          if (!err) return 'Unknown camera error.';
          const name = err.name || '';
          const msg  = err.message || String(err);
          if (/already under transition/i.test(msg))
            return 'Scanner is busy. Wait a couple of seconds and tap Start camera again.';
          if (/NotAllowedError|Permission/i.test(name + ' ' + msg))
            return 'Camera permission was denied. On iPhone: tap "AA" in the Safari URL bar → Website Settings → Camera → Allow, then reload.';
          if (/NotFoundError|DevicesNotFound/i.test(name + ' ' + msg))
            return 'No camera found on this device.';
          if (/NotReadableError|TrackStartError/i.test(name + ' ' + msg))
            return 'The camera is in use by another app. Close any other apps using the camera (FaceTime, Zoom, the Camera app) and try again.';
          if (/OverconstrainedError|ConstraintNotSatisfiedError/i.test(name + ' ' + msg))
            return 'This device camera does not match the requested settings.';
          if (location.protocol !== 'https:')
            return 'Camera access only works on HTTPS. The page must be loaded over https://.';
          return msg || 'Could not open the camera.';
        };

        // Fresh Html5Qrcode instance per attempt — once an instance hits
        // an error mid-start it stays in a half-broken state and any
        // subsequent start() throws "already under transition". So if
        // an attempt fails we tear it all the way down before retrying.
        // Order: hard-force rear cam, then enumerate and pick a back-
        // labeled cam, then soft preference for environment, then any
        // cam, then user, then totally permissive. The first one that
        // works wins, so rear-facing is always preferred when present.
        const attempts = [
          (i) => i.start({ facingMode: { exact: 'environment' } }, cfg, onScan, () => {}),
          async (i) => {
            const devs = await navigator.mediaDevices.enumerateDevices();
            const cams = (devs || []).filter((d) => d.kind === 'videoinput');
            const back = cams.find((c) => /back|rear|environment/i.test(c.label || ''))
                      || cams[cams.length - 1];
            if (!back) throw new Error('No video input device available.');
            return i.start({ deviceId: { exact: back.deviceId } }, cfg, onScan, () => {});
          },
          (i) => i.start({ facingMode: { ideal: 'environment' } }, cfg, onScan, () => {}),
          (i) => i.start({ facingMode: 'environment' }, cfg, onScan, () => {}),
          (i) => i.start({ facingMode: 'user' }, cfg, onScan, () => {}),
          (i) => i.start(true, cfg, onScan, () => {})
        ];

        let lastErr = null;
        for (let n = 0; n < attempts.length; n++) {
          const inst = new Html5Qr(elementId, { verbose: false });
          try {
            await attempts[n](inst);
            activeScanner = inst;
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
            console.warn('[qr] camera attempt', n, 'failed', e);
            try { if (inst.isScanning) await inst.stop(); } catch (_) {}
            try { await inst.clear(); } catch (_) {}
            await sleep(150);
          }
        }
        if (!activeScanner) throw new Error(friendly(lastErr));

        // Force the injected <video> to play inline and fill the box.
        // Some iOS Safari builds leave it paused or sized 0×0 without this.
        setTimeout(() => {
          if (!box) return;
          const vid = box.querySelector('video');
          if (!vid) return;
          vid.setAttribute('playsinline', '');
          vid.setAttribute('webkit-playsinline', '');
          vid.muted = true;
          vid.style.width = '100%';
          vid.style.height = '100%';
          vid.style.objectFit = 'cover';
          vid.style.display = 'block';
          if (vid.paused) { vid.play().catch(() => {}); }
        }, 250);
      } finally {
        scannerStarting = false;
      }
    }

    async function stopScanner() {
      if (scannerStopping) return;
      if (!activeScanner) return;
      scannerStopping = true;
      const s = activeScanner;
      activeScanner = null;
      try { if (s.isScanning) await s.stop(); } catch (_) {}
      try { await s.clear(); } catch (_) {}
      scannerStopping = false;
    }

    // ---------- Check-out modal ----------
    function openCheckoutModal() {
      if (!modal || !modalBody) return;
      modalTitle && (modalTitle.textContent = 'Check out items');
      const today = smTodayLocal ? smTodayLocal() : new Date().toISOString().slice(0, 10);
      modalBody.innerHTML =
        '<form class="sm-form" data-sm-checkout-form>' +
          '<div class="sm-form__row">' +
            '<div class="sm-field"><label>Expected return date *</label><input type="date" name="expectedReturn" min="' + today + '" value="' + today + '" required></div>' +
            '<div class="sm-field"><label>Going to / In possession of *</label><input name="destination" placeholder="Person, school, agency, etc." required></div>' +
          '</div>' +
          '<div class="sm-field"><label>Reason *</label><input name="reason" placeholder="On-site class at Suwannee County HS, etc." required></div>' +

          '<div style="margin-top:18px;padding:14px;border:1px solid var(--sm-line,#e5e7eb);border-radius:12px;background:#fafafa">' +
            '<div class="sm-flex sm-gap-12" style="align-items:center;justify-content:space-between;flex-wrap:wrap;margin-bottom:8px">' +
              '<strong>Scan item QR codes</strong>' +
              '<div class="sm-flex sm-gap-12" style="align-items:center">' +
                '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-co-toggle>Start camera</button>' +
                '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-co-add-manual>+ Add manually</button>' +
              '</div>' +
            '</div>' +
            '<div id="sm-co-scanner" data-sm-scanner-box style="display:none;width:100%;max-width:340px;margin:0 auto 8px"></div>' +
            '<div data-sm-co-scan-msg class="sm-field__help" style="text-align:center;min-height:18px"></div>' +
          '</div>' +

          '<div style="margin-top:14px">' +
            '<strong>Items on this checkout</strong>' +
            '<div data-sm-co-items style="margin-top:8px"></div>' +
          '</div>' +

          '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
            '<button type="submit" class="sm-btn sm-btn--primary" data-sm-co-submit disabled>Confirm checkout</button>' +
            '<button type="button" class="sm-btn sm-btn--light" data-sm-co-cancel>Cancel</button>' +
            '<span class="sm-field__help" data-sm-co-msg></span>' +
          '</div>' +
        '</form>';
      modal.hidden = false;

      const form = modalBody.querySelector('[data-sm-checkout-form]');
      const itemsBox = modalBody.querySelector('[data-sm-co-items]');
      const submitBtn = modalBody.querySelector('[data-sm-co-submit]');
      const cancelBtn = modalBody.querySelector('[data-sm-co-cancel]');
      const msg = modalBody.querySelector('[data-sm-co-msg]');
      const scanMsg = modalBody.querySelector('[data-sm-co-scan-msg]');
      const scannerBox = modalBody.querySelector('#sm-co-scanner');
      const toggleBtn = modalBody.querySelector('[data-sm-co-toggle]');
      const addManualBtn = modalBody.querySelector('[data-sm-co-add-manual]');

      const picked = []; // [{ assetId, qty }]
      const outMap = assetsOutMap();

      function availableFor(assetId) {
        const a = (cache.assets || []).find((x) => x.id === assetId);
        if (!a) return 0;
        const total = parseInt(a.qty, 10) || 0;
        const alreadyOut = outMap[assetId] || 0;
        const inThis = (picked.find((p) => p.assetId === assetId) || { qty: 0 }).qty;
        return Math.max(0, total - alreadyOut - inThis);
      }

      function renderItems() {
        if (!picked.length) {
          itemsBox.innerHTML = '<p class="sm-field__help">No items yet. Scan a QR code or add an item manually.</p>';
          submitBtn.disabled = true;
          return;
        }
        itemsBox.innerHTML = '<table class="sm-table" style="margin-top:0"><thead><tr><th>Item</th><th style="width:120px">Qty</th><th style="width:80px"></th></tr></thead><tbody>' +
          picked.map((p, i) => {
            const a = (cache.assets || []).find((x) => x.id === p.assetId) || {};
            const total = parseInt(a.qty, 10) || 0;
            const alreadyOut = outMap[p.assetId] || 0;
            const maxQty = Math.max(1, total - alreadyOut);
            return '<tr>' +
              '<td><strong>' + esc(a.name || p.assetId) + '</strong>' +
                (a.category ? '<br><span class="sm-field__help">' + esc(a.category) + '</span>' : '') +
              '</td>' +
              '<td>' + (maxQty > 1
                ? '<input type="number" min="1" max="' + maxQty + '" value="' + p.qty + '" data-sm-co-qty="' + i + '" style="width:80px">'
                : '<span>1</span>') +
              '</td>' +
              '<td><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-co-remove="' + i + '">Remove</button></td>' +
            '</tr>';
          }).join('') + '</tbody></table>';
        submitBtn.disabled = false;
      }

      function addAsset(assetId, qty) {
        const a = (cache.assets || []).find((x) => x.id === assetId);
        if (!a) { scanMsg.textContent = 'Unknown asset (' + assetId + '). Scan a STAT Med label or refresh inventory.'; return false; }
        const total = parseInt(a.qty, 10) || 0;
        const alreadyOut = outMap[assetId] || 0;
        const existing = picked.find((p) => p.assetId === assetId);
        const inThis = existing ? existing.qty : 0;
        const remaining = total - alreadyOut - inThis;
        if (remaining <= 0) {
          scanMsg.textContent = (a.name || 'Item') + ': nothing available to check out (all units already accounted for).';
          return false;
        }
        let askQty = qty;
        if (askQty == null) {
          if (total > 1) {
            const promptMax = remaining;
            const ans = prompt((a.name || 'Item') + ' — how many? (1–' + promptMax + ')', '1');
            if (ans == null) return false;
            askQty = parseInt(ans, 10);
            if (!askQty || askQty < 1) { scanMsg.textContent = 'Invalid quantity.'; return false; }
          } else {
            askQty = 1;
          }
        }
        if (askQty > remaining) { scanMsg.textContent = 'Only ' + remaining + ' available for ' + (a.name || 'this item') + '.'; askQty = remaining; }
        if (existing) existing.qty += askQty;
        else picked.push({ assetId, qty: askQty });
        scanMsg.textContent = 'Added ' + askQty + ' × ' + (a.name || 'item');
        renderItems();
        return true;
      }

      itemsBox.addEventListener('click', (e) => {
        const rm = e.target.closest('[data-sm-co-remove]');
        if (rm) { picked.splice(parseInt(rm.dataset.smCoRemove, 10), 1); renderItems(); }
      });
      itemsBox.addEventListener('input', (e) => {
        const q = e.target.closest('[data-sm-co-qty]');
        if (!q) return;
        const i = parseInt(q.dataset.smCoQty, 10);
        const n = parseInt(q.value, 10);
        const max = parseInt(q.max, 10) || 1;
        picked[i].qty = Math.min(Math.max(1, n || 1), max);
      });

      toggleBtn.addEventListener('click', async () => {
        if (toggleBtn.disabled) return;
        if (activeScanner) {
          toggleBtn.disabled = true;
          await stopScanner();
          scannerBox.style.display = 'none';
          toggleBtn.textContent = 'Start camera';
          toggleBtn.disabled = false;
          return;
        }
        toggleBtn.disabled = true;
        toggleBtn.textContent = 'Starting…';
        scannerBox.style.display = '';
        scanMsg.textContent = 'Point the camera at a STAT Med QR sticker.';
        try {
          await startScanner('sm-co-scanner', (text) => {
            const id = parseAssetQr(text);
            if (!id) { scanMsg.textContent = 'That is not a STAT Med asset QR.'; return; }
            addAsset(id, null);
          });
          toggleBtn.textContent = 'Stop camera';
        } catch (err) {
          scannerBox.style.display = 'none';
          toggleBtn.textContent = 'Start camera';
          scanMsg.textContent = err.message || 'Could not start the camera.';
        } finally {
          toggleBtn.disabled = false;
        }
      });

      addManualBtn.addEventListener('click', () => {
        const opts = (cache.assets || []).slice().sort((a, b) =>
          String(a.category || '').localeCompare(String(b.category || '')) ||
          String(a.name || '').localeCompare(String(b.name || ''))
        );
        const picker = document.createElement('div');
        picker.style.cssText = 'margin-top:10px;display:flex;gap:8px;align-items:center;flex-wrap:wrap';
        picker.innerHTML =
          '<select data-sm-co-manual class="sm-admin__select" style="min-width:240px"><option value="">Pick an item…</option>' +
          opts.map((a) => '<option value="' + a.id + '">' + esc((a.category || '?') + ' — ' + (a.name || a.id)) + '</option>').join('') +
          '</select>' +
          '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-co-manual-add>Add</button>';
        scannerBox.parentNode.insertBefore(picker, scannerBox.nextSibling);
        picker.querySelector('[data-sm-co-manual-add]').addEventListener('click', () => {
          const sel = picker.querySelector('[data-sm-co-manual]');
          if (sel.value) { addAsset(sel.value, null); sel.value = ''; }
        });
      });

      cancelBtn.addEventListener('click', async () => { await stopScanner(); closeModal(); });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!picked.length) return;
        msg.textContent = 'Saving…';
        submitBtn.disabled = true;
        try {
          const fd = new FormData(form);
          const items = picked.map((p) => {
            const a = (cache.assets || []).find((x) => x.id === p.assetId) || {};
            return { assetId: p.assetId, name: a.name || '', category: a.category || '', qty: p.qty };
          });
          await ns.fb.adminCreateAssetCheckout({
            expectedReturn: (fd.get('expectedReturn') || '').toString(),
            destination:    (fd.get('destination') || '').toString(),
            reason:         (fd.get('reason') || '').toString(),
            items: items
          });
          msg.textContent = '✓ Checked out.';
          await stopScanner();
          await loadAssetsTable();
          setTimeout(closeModal, 500);
        } catch (err) {
          submitBtn.disabled = false;
          msg.textContent = err.message || 'Save failed.';
        }
      });

      renderItems();
    }

    // ---------- Return modal ----------
    function openReturnModal() {
      if (!modal || !modalBody) return;
      modalTitle && (modalTitle.textContent = 'Return items');
      modalBody.innerHTML =
        '<div data-sm-rt-root>' +
          '<p class="sm-field__help">Scan the QR sticker on each item as it comes back. Multi-unit items will ask how many.</p>' +
          '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-bottom:8px">' +
            '<button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-sm-rt-toggle>Start camera</button>' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-rt-manual>Return manually</button>' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-rt-done>Done</button>' +
          '</div>' +
          '<div id="sm-rt-scanner" data-sm-scanner-box style="display:none;width:100%;max-width:340px;margin:0 auto 8px"></div>' +
          '<div data-sm-rt-msg class="sm-field__help" style="min-height:18px;text-align:center"></div>' +
          '<div data-sm-rt-log style="margin-top:10px;max-height:240px;overflow:auto"></div>' +
        '</div>';
      modal.hidden = false;

      const toggleBtn = modalBody.querySelector('[data-sm-rt-toggle]');
      const manualBtn = modalBody.querySelector('[data-sm-rt-manual]');
      const doneBtn = modalBody.querySelector('[data-sm-rt-done]');
      const scannerBox = modalBody.querySelector('#sm-rt-scanner');
      const msgEl = modalBody.querySelector('[data-sm-rt-msg]');
      const logEl = modalBody.querySelector('[data-sm-rt-log]');
      const log = [];

      function renderLog() {
        if (!log.length) { logEl.innerHTML = ''; return; }
        logEl.innerHTML = '<table class="sm-table" style="margin-top:0"><thead><tr><th>Item</th><th>Returned</th><th>Still out (this checkout)</th></tr></thead><tbody>' +
          log.map((r) => '<tr>' +
            '<td><strong>' + esc(r.name) + '</strong></td>' +
            '<td>' + r.returnedQty + '</td>' +
            '<td>' + r.remaining + (r.completed ? ' <span style="color:var(--sm-accent,#15803d)">✓ checkout complete</span>' : '') + '</td>' +
          '</tr>').join('') + '</tbody></table>';
      }

      // For a scanned assetId, find every active checkout that still has
      // outstanding units of it. Returns [{ checkout, item, outstanding }].
      function activeForAsset(assetId) {
        const out = [];
        (cache.assetCheckouts || []).forEach((co) => {
          if (co.status !== 'active') return;
          (co.items || []).forEach((it) => {
            if (it.assetId !== assetId) return;
            const outstanding = (it.qty || 0) - (it.qtyReturned || 0);
            if (outstanding > 0) out.push({ checkout: co, item: it, outstanding });
          });
        });
        return out;
      }

      async function handleAsset(assetId) {
        const matches = activeForAsset(assetId);
        if (!matches.length) {
          const a = (cache.assets || []).find((x) => x.id === assetId);
          msgEl.textContent = (a ? (a.name || 'That item') : 'That item') + ' is not on any active checkout.';
          return;
        }
        let target = matches[0];
        if (matches.length > 1) {
          const choices = matches.map((m, i) => (i + 1) + ') ' + (m.checkout.destination || '?') + ' — out ' + m.outstanding).join('\n');
          const ans = prompt('That item is out on multiple checkouts. Pick one (1-' + matches.length + '):\n' + choices, '1');
          if (ans == null) return;
          const k = parseInt(ans, 10);
          if (!k || k < 1 || k > matches.length) { msgEl.textContent = 'Cancelled.'; return; }
          target = matches[k - 1];
        }
        let qty = 1;
        if (target.outstanding > 1) {
          const ans = prompt((target.item.name || 'Item') + ' — how many returning? (1–' + target.outstanding + ')', String(target.outstanding));
          if (ans == null) return;
          qty = parseInt(ans, 10);
          if (!qty || qty < 1) { msgEl.textContent = 'Invalid quantity.'; return; }
          if (qty > target.outstanding) qty = target.outstanding;
        }
        try {
          const res = await ns.fb.adminReturnAssetItem(target.checkout.id, assetId, qty);
          // Patch local cache so subsequent scans see the new remaining.
          const co = cache.assetCheckouts.find((c) => c.id === target.checkout.id);
          if (co) {
            const it = (co.items || []).find((x) => x.assetId === assetId && ((x.qty || 0) - (x.qtyReturned || 0)) > 0);
            if (it) it.qtyReturned = (it.qtyReturned || 0) + qty;
            co.remainingCount = res.remaining;
            if (res.completed) co.status = 'completed';
          }
          log.unshift({ name: target.item.name || assetId, returnedQty: qty, remaining: res.remaining, completed: res.completed });
          renderLog();
          msgEl.textContent = '✓ Returned ' + qty + ' × ' + (target.item.name || 'item') + (res.completed ? ' — checkout complete.' : '.');
        } catch (err) {
          msgEl.textContent = err.message || 'Return failed.';
        }
      }

      toggleBtn.addEventListener('click', async () => {
        if (toggleBtn.disabled) return;
        if (activeScanner) {
          toggleBtn.disabled = true;
          await stopScanner();
          scannerBox.style.display = 'none';
          toggleBtn.textContent = 'Start camera';
          toggleBtn.disabled = false;
          return;
        }
        toggleBtn.disabled = true;
        toggleBtn.textContent = 'Starting…';
        scannerBox.style.display = '';
        msgEl.textContent = 'Point the camera at a STAT Med QR sticker.';
        try {
          await startScanner('sm-rt-scanner', (text) => {
            const id = parseAssetQr(text);
            if (!id) { msgEl.textContent = 'That is not a STAT Med asset QR.'; return; }
            handleAsset(id);
          });
          toggleBtn.textContent = 'Stop camera';
        } catch (err) {
          scannerBox.style.display = 'none';
          toggleBtn.textContent = 'Start camera';
          msgEl.textContent = err.message || 'Could not start the camera.';
        } finally {
          toggleBtn.disabled = false;
        }
      });

      manualBtn.addEventListener('click', () => {
        const outMap = assetsOutMap();
        const opts = (cache.assets || []).filter((a) => (outMap[a.id] || 0) > 0)
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        if (!opts.length) { msgEl.textContent = 'Nothing is currently checked out.'; return; }
        const sel = document.createElement('div');
        sel.style.cssText = 'margin:8px 0;display:flex;gap:8px;flex-wrap:wrap;justify-content:center';
        sel.innerHTML = '<select data-sm-rt-pick class="sm-admin__select"><option value="">Pick an item that is out…</option>' +
          opts.map((a) => '<option value="' + a.id + '">' + esc((a.name || a.id) + ' (out: ' + (outMap[a.id] || 0) + ')') + '</option>').join('') +
          '</select><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-rt-pick-go>Return</button>';
        msgEl.parentNode.insertBefore(sel, msgEl);
        sel.querySelector('[data-sm-rt-pick-go]').addEventListener('click', () => {
          const v = sel.querySelector('[data-sm-rt-pick]').value;
          if (v) handleAsset(v);
        });
      });

      doneBtn.addEventListener('click', async () => {
        await stopScanner();
        await loadAssetsTable();
        closeModal();
      });
    }

    // ---------- Active checkouts list ----------
    function openCheckoutsListModal() {
      if (!modal || !modalBody) return;
      modalTitle && (modalTitle.textContent = 'Active checkouts');
      const list = (cache.assetCheckouts || []).filter((c) => c.status === 'active');
      const fmt = (d) => {
        try {
          if (!d) return '—';
          if (d.toDate) return d.toDate().toLocaleString();
          if (d.seconds) return new Date(d.seconds * 1000).toLocaleString();
          return new Date(d).toLocaleString();
        } catch { return '—'; }
      };
      if (!list.length) {
        modalBody.innerHTML = '<p class="sm-field__help">No active checkouts. Everything is on the shelf.</p>';
        modal.hidden = false;
        return;
      }
      modalBody.innerHTML = '<div style="display:flex;flex-direction:column;gap:12px">' +
        list.map((co) => {
          const out = (co.items || []).filter((it) => ((it.qty || 0) - (it.qtyReturned || 0)) > 0);
          const rows = out.map((it) =>
            '<li><strong>' + esc(it.name || it.assetId) + '</strong> — ' +
            ((it.qty || 0) - (it.qtyReturned || 0)) + ' of ' + (it.qty || 0) + ' still out' +
            '</li>'
          ).join('');
          return '<div style="border:1px solid var(--sm-line,#e5e7eb);border-radius:12px;padding:12px">' +
            '<div class="sm-flex sm-gap-12" style="justify-content:space-between;align-items:flex-start;flex-wrap:wrap">' +
              '<div>' +
                '<strong>' + esc(co.destination || '—') + '</strong>' +
                '<div class="sm-field__help">' + esc(co.reason || '') + '</div>' +
                '<div class="sm-field__help">Expected back: ' + esc(co.expectedReturn || '—') + ' · Checked out: ' + esc(fmt(co.createdAt)) + '</div>' +
              '</div>' +
              '<div style="text-align:right">' +
                '<div><strong>' + (co.remainingCount || 0) + '</strong> units out</div>' +
              '</div>' +
            '</div>' +
            (rows ? '<ul style="margin:8px 0 0 18px;padding:0">' + rows + '</ul>' : '') +
          '</div>';
        }).join('') +
      '</div>';
      modal.hidden = false;
    }

    // ---------- QR label printing ----------
    function printAssetQrLabels() {
      const items = (cache.assets || []).slice().sort((a, b) =>
        String(a.category || '').localeCompare(String(b.category || '')) ||
        String(a.name || '').localeCompare(String(b.name || ''))
      );
      if (!items.length) { alert('No items in inventory yet.'); return; }
      const ok = confirm('Print QR stickers for all ' + items.length + ' items? You can choose which printer in the print dialog.');
      if (!ok) return;
      const w = window.open('', '_blank', 'width=900,height=700');
      if (!w) { alert('Pop-up blocked. Allow pop-ups to print labels.'); return; }
      const labels = items.map((a) => {
        const payload = QR_PREFIX + a.id;
        // margin=4 = 4-module quiet zone around the QR (required by spec
        // for reliable scanning). ecc=H = highest error correction so a
        // smudged or partly-covered sticker still reads. 400x400 gives
        // the printer high source resolution.
        const url = 'https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=4&ecc=H&data=' + encodeURIComponent(payload);
        return '<div class="lbl">' +
          '<img src="' + url + '" alt="">' +
          '<div class="meta">' +
            '<div class="nm">' + esc(a.name || '—') + '</div>' +
            '<div class="sub">' + esc(a.category || '') + (a.serial ? ' · ' + esc(a.serial) : '') + '</div>' +
            '<div class="sub">STAT Med Training</div>' +
          '</div>' +
        '</div>';
      }).join('');
      w.document.open();
      w.document.write(
        '<!doctype html><html><head><title>STAT Med inventory labels</title><style>' +
          '*{box-sizing:border-box} body{margin:0;font-family:system-ui,sans-serif}' +
          '.sheet{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;padding:12px}' +
          '.lbl{border:1px dashed #999;border-radius:8px;padding:10px;display:flex;gap:12px;align-items:center;page-break-inside:avoid;break-inside:avoid;min-height:180px}' +
          '.lbl img{width:170px;height:170px;display:block;background:#fff}' +
          '.meta{flex:1;min-width:0}' +
          '.nm{font-weight:700;font-size:14px;line-height:1.2;margin-bottom:4px;word-wrap:break-word}' +
          '.sub{font-size:11px;color:#555;line-height:1.3}' +
          '@media print { .lbl{border:1px solid #bbb} }' +
        '</style></head><body><div class="sheet">' + labels + '</div>' +
        '<script>window.addEventListener("load", function(){ setTimeout(function(){ window.print(); }, 500); });<\/script>' +
        '</body></html>'
      );
      w.document.close();
    }

    checkoutStartBtn  && checkoutStartBtn.addEventListener('click', openCheckoutModal);
    checkoutReturnBtn && checkoutReturnBtn.addEventListener('click', openReturnModal);
    checkoutListBtn   && checkoutListBtn.addEventListener('click', openCheckoutsListModal);
    assetPrintLabelsBtn && assetPrintLabelsBtn.addEventListener('click', printAssetQrLabels);

    // Make sure the camera releases when the modal is closed by the X
    // button or backdrop click (handled by closeModal elsewhere).
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal || e.target.closest('[data-sm-admin-modal-close]')) stopScanner();
      });
    }

    /* ---------- EMPLOYEES TAB ---------- */
    let empLoaded = false;
    let empView = 'table';
    const empRows = root.querySelector('[data-sm-emp-rows]');
    const empTableWrap = root.querySelector('[data-sm-emp-table-wrap]');
    const empChartWrap = root.querySelector('[data-sm-emp-chart-wrap]');
    const empNewBtn = root.querySelector('[data-sm-emp-new]');
    const empDownloadBtn = root.querySelector('[data-sm-emp-download]');
    const empSearchInput = root.querySelector('[data-sm-emp-search]');
    const empStatusFilter = root.querySelector('[data-sm-emp-filter-status]');
    const empLocFilter = root.querySelector('[data-sm-emp-filter-loc]');
    const empCertFilter = root.querySelector('[data-sm-emp-filter-cert]');
    const empViewToggleBtn = root.querySelector('[data-sm-emp-view-toggle]');
    cache.employees = [];

    const EMP_LOCATIONS = ['Lake City', 'Gainesville', 'Jacksonville', 'Remote'];
    const EMP_STATUSES  = ['active', 'inactive', 'terminated'];
    const EMP_TYPES     = ['W2 full-time', 'W2 part-time', 'Contractor', 'Per-diem'];
    const EMP_DEPTS     = ['Training', 'Admin', 'Support', 'Management'];
    const EMP_PAY_TYPES = ['Hourly', 'Per class', 'Salary'];
    const EMP_PAY_METHODS = ['Direct deposit', 'Check', 'Contractor invoice'];
    const EMP_TAX_FORMS = ['W-4 on file', 'W-9 on file', 'Missing'];
    const EMP_BG_STATUS = ['Pending', 'In progress', 'Completed', 'Not required'];
    const EMP_USER_ROLES = ['Admin', 'Instructor', 'Viewer'];
    const EMP_ACCOUNT_STATUSES = ['Active', 'Disabled'];
    const EMP_DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

    function empFullName(e) {
      return [(e.preferredName || e.firstName || ''), e.lastName].filter(Boolean).join(' ').trim() || '—';
    }
    function empInitials(e) {
      const a = (e.firstName || e.preferredName || '').trim()[0] || '';
      const b = (e.lastName || '').trim()[0] || '';
      return (a + b).toUpperCase() || '??';
    }
    function empAvatar(e, size) {
      const px = size || 36;
      if (e.photoUrl) {
        return '<img src="' + esc(e.photoUrl) + '" alt="" style="width:' + px + 'px;height:' + px + 'px;border-radius:50%;object-fit:cover;display:block;background:#e5e7eb">';
      }
      const fs = Math.max(10, Math.round(px * 0.4));
      return '<div style="width:' + px + 'px;height:' + px + 'px;border-radius:50%;background:#e0e7ff;color:#3730a3;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:' + fs + 'px">' + esc(empInitials(e)) + '</div>';
    }
    function empStatusBadge(s) {
      const map = { active: '#15803d', inactive: '#6b7280', terminated: '#b91c1c' };
      const color = map[s] || '#6b7280';
      const label = (s || 'unknown').replace(/^\w/, (c) => c.toUpperCase());
      return '<span style="display:inline-block;padding:2px 8px;border-radius:999px;background:' + color + '22;color:' + color + ';font-size:12px;font-weight:600">' + label + '</span>';
    }

    // ---------- certification helpers ----------
    function daysUntil(dateStr) {
      if (!dateStr) return null;
      const d = new Date(dateStr + 'T00:00:00');
      if (isNaN(d.getTime())) return null;
      const today = new Date(); today.setHours(0, 0, 0, 0);
      return Math.round((d.getTime() - today.getTime()) / 86400000);
    }
    function empNextCert(e) {
      const list = (e.certifications || []).filter((c) => c && c.expiresOn);
      if (!list.length) return null;
      const sorted = list.slice().sort((a, b) => String(a.expiresOn).localeCompare(String(b.expiresOn)));
      return sorted[0];
    }
    function empMatchesCertFilter(e, filterVal) {
      if (!filterVal) return true;
      const list = (e.certifications || []).filter((c) => c && c.expiresOn);
      if (!list.length) return false;
      if (filterVal === 'expired') {
        return list.some((c) => (daysUntil(c.expiresOn) || 0) < 0);
      }
      const within = parseInt(filterVal, 10);
      return list.some((c) => {
        const d = daysUntil(c.expiresOn);
        return d != null && d >= 0 && d <= within;
      });
    }
    function empCertStatusCell(e) {
      const next = empNextCert(e);
      if (!next) return '<span class="sm-field__help">—</span>';
      const d = daysUntil(next.expiresOn);
      let color = '#6b7280', txt = next.expiresOn;
      if (d != null) {
        if (d < 0) { color = '#b91c1c'; txt = 'Expired ' + Math.abs(d) + 'd ago'; }
        else if (d <= 30) { color = '#b91c1c'; txt = 'in ' + d + 'd'; }
        else if (d <= 60) { color = '#b45309'; txt = 'in ' + d + 'd'; }
        else if (d <= 90) { color = '#a16207'; txt = 'in ' + d + 'd'; }
        else { color = '#15803d'; txt = 'in ' + d + 'd'; }
      }
      return '<span style="font-size:12px"><strong style="color:' + color + '">' + esc(next.name || 'Cert') + '</strong><br><span style="color:' + color + '">' + esc(txt) + '</span></span>';
    }

    function refreshEmpFilters() {
      const locs = Array.from(new Set([
        ...EMP_LOCATIONS,
        ...cache.employees.map((e) => e.workLocation).filter(Boolean)
      ])).sort();
      if (empLocFilter) {
        const cur = empLocFilter.value;
        empLocFilter.innerHTML = '<option value="">All locations</option>' +
          locs.map((l) => '<option value="' + esc(l) + '">' + esc(l) + '</option>').join('');
        if (cur) empLocFilter.value = cur;
      }
    }

    function filteredEmployees() {
      const q = (empSearchInput && empSearchInput.value || '').trim().toLowerCase();
      const status = empStatusFilter && empStatusFilter.value;
      const loc = empLocFilter && empLocFilter.value;
      const cert = empCertFilter && empCertFilter.value;
      return cache.employees.filter((e) => {
        if (status && (e.status || 'active') !== status) return false;
        if (loc && (e.workLocation || '') !== loc) return false;
        if (cert && !empMatchesCertFilter(e, cert)) return false;
        if (q) {
          const hay = [e.firstName, e.lastName, e.preferredName, e.email, e.jobTitle, e.employeeId]
            .filter(Boolean).join(' ').toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      }).sort((a, b) => String(a.lastName || '').localeCompare(String(b.lastName || '')) || String(a.firstName || '').localeCompare(String(b.firstName || '')));
    }

    async function loadEmployeesTable() {
      if (!empRows) return;
      empRows.innerHTML = '<tr><td colspan="9" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        cache.employees = await ns.fb.adminListEmployees();
      } catch (err) {
        console.error('[admin] list employees failed', err);
        empRows.innerHTML = '<tr><td colspan="9" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      refreshEmpFilters();
      renderEmployeesView();
    }

    function renderEmployeesView() {
      if (empView === 'chart') {
        if (empTableWrap) empTableWrap.hidden = true;
        if (empChartWrap) { empChartWrap.hidden = false; renderEmployeesChart(); }
        return;
      }
      if (empTableWrap) empTableWrap.hidden = false;
      if (empChartWrap) empChartWrap.hidden = true;
      const rows = filteredEmployees();
      if (!rows.length) {
        empRows.innerHTML = '<tr><td colspan="9" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">' +
          (cache.employees.length ? 'No employees match the filters.' : 'No employees yet. Click <strong>+ Add employee</strong> to create one.') +
          '</td></tr>';
        return;
      }
      empRows.innerHTML = rows.map((e) => '<tr>' +
        '<td style="width:48px">' + empAvatar(e, 36) + '</td>' +
        '<td><strong>' + esc(empFullName(e)) + '</strong>' + (e.employeeId ? '<br><span class="sm-field__help" style="font-family:monospace">' + esc(e.employeeId) + '</span>' : '') + '</td>' +
        '<td>' + esc(e.jobTitle || '—') + (e.department ? '<br><span class="sm-field__help">' + esc(e.department) + '</span>' : '') + '</td>' +
        '<td>' + esc(e.workLocation || '—') + '</td>' +
        '<td>' + empStatusBadge(e.status || 'active') + '</td>' +
        '<td>' + esc(e.employmentType || '—') + '</td>' +
        '<td>' + esc(e.startDate || '—') + '</td>' +
        '<td>' + empCertStatusCell(e) + '</td>' +
        '<td style="white-space:nowrap">' +
          '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-emp-edit="' + e.id + '">Open</button>' +
        '</td>' +
      '</tr>').join('');
    }

    function renderEmployeesChart() {
      if (!empChartWrap) return;
      const rows = filteredEmployees();
      if (!rows.length) {
        empChartWrap.innerHTML = '<p class="sm-field__help" style="text-align:center;padding:20px">No employees to chart.</p>';
        return;
      }
      // Build manager → reports map. managerId is the source of truth;
      // managerName is just display. Employees without a known manager
      // become roots (CEO, contractors with no chain of command, etc.).
      const byId = Object.create(null);
      rows.forEach((e) => { byId[e.id] = Object.assign({}, e, { _reports: [] }); });
      const roots = [];
      rows.forEach((e) => {
        const mgrId = e.managerId && byId[e.managerId] ? e.managerId : null;
        if (mgrId) byId[mgrId]._reports.push(byId[e.id]);
        else roots.push(byId[e.id]);
      });
      const card = (e) => {
        const next = empNextCert(e);
        const certLine = next
          ? '<div style="font-size:11px;color:#6b7280;margin-top:2px">Next cert: ' + esc(next.name || '?') + ' · ' + esc(next.expiresOn || '?') + '</div>'
          : '';
        return '<div class="sm-emp-card" data-sm-emp-edit="' + e.id + '" style="display:inline-flex;gap:10px;align-items:center;padding:10px 14px;border:1px solid var(--sm-line,#e5e7eb);border-radius:12px;background:#fff;cursor:pointer;min-width:240px;box-shadow:0 1px 2px rgba(0,0,0,.04)">' +
          empAvatar(e, 40) +
          '<div style="min-width:0">' +
            '<div style="font-weight:700">' + esc(empFullName(e)) + '</div>' +
            '<div style="font-size:12px;color:#6b7280">' + esc(e.jobTitle || '—') + (e.workLocation ? ' · ' + esc(e.workLocation) : '') + '</div>' +
            certLine +
          '</div>' +
        '</div>';
      };
      const renderNode = (n, depth) => {
        const indent = depth === 0 ? '' : 'margin-left:24px;padding-left:18px;border-left:2px solid var(--sm-line,#e5e7eb)';
        const childRows = n._reports.length
          ? '<div style="margin-top:8px;display:flex;flex-direction:column;gap:8px">' +
            n._reports.map((r) => renderNode(r, depth + 1)).join('') +
            '</div>'
          : '';
        return '<div style="' + indent + ';margin-top:' + (depth === 0 ? '0' : '8px') + '">' + card(n) + childRows + '</div>';
      };
      empChartWrap.innerHTML = '<div style="display:flex;flex-direction:column;gap:18px">' +
        roots.map((r) => renderNode(r, 0)).join('') +
      '</div>';
    }

    function selOptions(values, current) {
      return values.map((v) => '<option value="' + esc(v) + '"' + (String(v).toLowerCase() === String(current || '').toLowerCase() ? ' selected' : '') + '>' + esc(v) + '</option>').join('');
    }
    function dayCheckboxes(selected) {
      const set = new Set((selected || []).map((d) => String(d).toLowerCase()));
      return EMP_DAYS.map((d) => '<label style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;border:1px solid var(--sm-line,#e5e7eb);border-radius:999px;cursor:pointer;font-size:13px">' +
        '<input type="checkbox" name="availability" value="' + d + '"' + (set.has(d.toLowerCase()) ? ' checked' : '') + '> ' + d +
      '</label>').join(' ');
    }
    function fmtBytes(n) {
      if (!n && n !== 0) return '';
      if (n < 1024) return n + ' B';
      if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
      return (n / 1024 / 1024).toFixed(1) + ' MB';
    }

    function openEmployeeModal(id) {
      if (!modal || !modalBody) return;
      const e = id ? (cache.employees.find((x) => x.id === id) || {}) : {};
      modalTitle && (modalTitle.textContent = id ? ('Employee — ' + empFullName(e)) : 'Add employee');
      const v = (s) => esc(s == null ? '' : String(s));

      const section = (title, body) =>
        '<details open style="margin-top:12px;border:1px solid var(--sm-line,#e5e7eb);border-radius:12px;padding:0 14px 4px">' +
          '<summary style="padding:12px 0;cursor:pointer;font-weight:700;font-size:15px">' + esc(title) + '</summary>' +
          '<div style="padding:6px 0 12px">' + body + '</div>' +
        '</details>';

      // Profile photo banner at the top of the modal. The avatar acts
      // as the upload button when there's no photo yet; when there is,
      // a "Change" / "Remove" pair appears next to it.
      const photoBanner = id
        ? '<div style="display:flex;gap:16px;align-items:center;padding:14px;border:1px solid var(--sm-line,#e5e7eb);border-radius:12px;background:#fafafa">' +
            '<div data-sm-emp-avatar>' + empAvatar(e, 72) + '</div>' +
            '<div style="flex:1">' +
              '<div style="font-weight:700;font-size:16px">' + esc(empFullName(e)) + '</div>' +
              '<div class="sm-field__help">' + esc(e.jobTitle || '') + (e.workLocation ? ' · ' + esc(e.workLocation) : '') + '</div>' +
              '<div style="margin-top:8px;display:flex;gap:8px;flex-wrap:wrap;align-items:center">' +
                '<input type="file" accept="image/*" data-sm-emp-photo-file style="display:none">' +
                '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-emp-photo-pick>' + (e.photoUrl ? 'Change photo' : 'Upload photo') + '</button>' +
                (e.photoUrl ? '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-emp-photo-remove style="color:var(--sm-danger)">Remove</button>' : '') +
                '<span data-sm-emp-photo-msg class="sm-field__help"></span>' +
              '</div>' +
            '</div>' +
          '</div>'
        : '<div class="sm-field__help" style="padding:10px;background:#fff7ed;border:1px solid #fed7aa;border-radius:8px">Save the employee first, then a profile photo + documents can be uploaded.</div>';

      const basic = section('Basic information',
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Employee ID</label><input name="employeeId" value="' + v(e.employeeId) + '" placeholder="EMP-001"></div>' +
          '<div class="sm-field"><label>Status</label><select name="status">' + selOptions(EMP_STATUSES, e.status || 'active') + '</select></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>First name *</label><input name="firstName" value="' + v(e.firstName) + '" required></div>' +
          '<div class="sm-field"><label>Last name *</label><input name="lastName" value="' + v(e.lastName) + '" required></div>' +
          '<div class="sm-field"><label>Preferred name</label><input name="preferredName" value="' + v(e.preferredName) + '"></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Email</label><input type="email" name="email" value="' + v(e.email) + '"></div>' +
          '<div class="sm-field"><label>Phone</label><input name="phone" value="' + v(e.phone) + '" placeholder="386-000-0000"></div>' +
        '</div>' +
        '<div class="sm-field"><label>Address</label><input name="address" value="' + v(e.address) + '" placeholder="Optional"></div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Emergency contact name</label><input name="emergencyName" value="' + v(e.emergencyName) + '"></div>' +
          '<div class="sm-field"><label>Emergency contact phone</label><input name="emergencyPhone" value="' + v(e.emergencyPhone) + '"></div>' +
        '</div>'
      );

      const employment = section('Employment',
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Job title</label><input name="jobTitle" value="' + v(e.jobTitle) + '" placeholder="Instructor"></div>' +
          '<div class="sm-field"><label>Department / role type</label><select name="department"><option value="">—</option>' + selOptions(EMP_DEPTS, e.department) + '</select></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Employment type</label><select name="employmentType"><option value="">—</option>' + selOptions(EMP_TYPES, e.employmentType) + '</select></div>' +
          '<div class="sm-field"><label>Work location</label><select name="workLocation"><option value="">—</option>' + selOptions(EMP_LOCATIONS, e.workLocation) + '</select></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Start date</label><input type="date" name="startDate" value="' + v(e.startDate) + '"></div>' +
          '<div class="sm-field"><label>End date</label><input type="date" name="endDate" value="' + v(e.endDate) + '" placeholder="Optional"></div>' +
          '<div class="sm-field"><label>Manager</label><select name="managerId">' +
            '<option value="">— None —</option>' +
            (cache.employees || [])
              .filter((mgr) => mgr.id !== id)
              .sort((a, b) => String(a.lastName || '').localeCompare(String(b.lastName || '')))
              .map((mgr) => '<option value="' + esc(mgr.id) + '"' + (e.managerId === mgr.id ? ' selected' : '') + '>' + esc(empFullName(mgr)) + (mgr.jobTitle ? ' — ' + esc(mgr.jobTitle) : '') + '</option>')
              .join('') +
          '</select></div>' +
        '</div>'
      );

      const certRowsHtml = (() => {
        const list = (e.certifications && e.certifications.length) ? e.certifications : [];
        const row = (c, idx) =>
          '<div class="sm-form__row" data-sm-cert-row="' + idx + '" style="align-items:end">' +
            '<div class="sm-field"><label>Certification</label><input data-sm-cert-name value="' + v(c.name) + '" placeholder="BLS Instructor"></div>' +
            '<div class="sm-field"><label>Expires on</label><input type="date" data-sm-cert-expires value="' + v(c.expiresOn) + '"></div>' +
            '<div class="sm-field"><label>&nbsp;</label><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-cert-remove style="color:var(--sm-danger)">Remove</button></div>' +
          '</div>';
        if (!list.length) return '<p class="sm-field__help" data-sm-cert-empty>No certifications tracked. Add one below to enable "Expires soon" filtering.</p>';
        return list.map((c, i) => row(c, i)).join('');
      })();

      const skills = section('Skills & qualifications',
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Can teach</label><input name="canTeach" value="' + v(e.canTeach) + '" placeholder="BLS, ACLS, PALS, CNA"></div>' +
          '<div class="sm-field"><label>Background check</label><select name="backgroundStatus"><option value="">—</option>' + selOptions(EMP_BG_STATUS, e.backgroundStatus) + '</select></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Driver / license info</label><input name="driverLicense" value="' + v(e.driverLicense) + '" placeholder="Optional"></div>' +
          '<div class="sm-field"><label>Notes</label><input name="skillNotes" value="' + v(e.skillNotes) + '" placeholder="Can cover Jacksonville weekends"></div>' +
        '</div>' +
        '<div style="margin-top:8px;padding:10px;border:1px dashed var(--sm-line,#e5e7eb);border-radius:10px">' +
          '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">' +
            '<strong style="font-size:14px">Certifications (with expiration dates)</strong>' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-cert-add>+ Add certification</button>' +
          '</div>' +
          '<div data-sm-cert-list>' + certRowsHtml + '</div>' +
        '</div>'
      );

      const scheduling = section('Scheduling',
        '<div class="sm-field"><label>Availability (days of week)</label><div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:4px">' + dayCheckboxes(e.availability) + '</div></div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Preferred location</label><select name="preferredLocation"><option value="">—</option>' + selOptions(EMP_LOCATIONS, e.preferredLocation) + '</select></div>' +
          '<div class="sm-field"><label>Max hours / week</label><input type="number" name="maxHours" value="' + v(e.maxHours) + '" min="0" placeholder="20"></div>' +
          '<div class="sm-field"><label>Can travel?</label><select name="canTravel"><option value="">—</option><option value="yes"' + (e.canTravel === 'yes' ? ' selected' : '') + '>Yes</option><option value="no"' + (e.canTravel === 'no' ? ' selected' : '') + '>No</option></select></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Blackout dates</label><input name="blackoutDates" value="' + v(e.blackoutDates) + '" placeholder="07/04, 12/24-12/26"></div>' +
          '<div class="sm-field"><label>Side gig availability</label><input name="sideGig" value="' + v(e.sideGig) + '" placeholder="Elder care, private care"></div>' +
        '</div>'
      );

      const payroll = section('Payroll & admin',
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Pay type</label><select name="payType"><option value="">—</option>' + selOptions(EMP_PAY_TYPES, e.payType) + '</select></div>' +
          '<div class="sm-field"><label>Pay rate</label><input name="payRate" value="' + v(e.payRate) + '" placeholder="$25/hr or $75/class"></div>' +
          '<div class="sm-field"><label>Payroll ID</label><input name="payrollId" value="' + v(e.payrollId) + '" placeholder="Optional"></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Payment method</label><select name="paymentMethod"><option value="">—</option>' + selOptions(EMP_PAY_METHODS, e.paymentMethod) + '</select></div>' +
          '<div class="sm-field"><label>Tax form status</label><select name="taxFormStatus"><option value="">—</option>' + selOptions(EMP_TAX_FORMS, e.taxFormStatus) + '</select></div>' +
        '</div>' +
        '<div class="sm-field"><label>Reimbursement notes</label><input name="reimbursementNotes" value="' + v(e.reimbursementNotes) + '" placeholder="Mileage, supplies, etc."></div>'
      );

      const access = section('System access',
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>User role</label><select name="userRole"><option value="">—</option>' + selOptions(EMP_USER_ROLES, e.userRole) + '</select></div>' +
          '<div class="sm-field"><label>Login email</label><input type="email" name="loginEmail" value="' + v(e.loginEmail) + '" placeholder="employee@email.com"></div>' +
          '<div class="sm-field"><label>Account status</label><select name="accountStatus"><option value="">—</option>' + selOptions(EMP_ACCOUNT_STATUSES, e.accountStatus) + '</select></div>' +
        '</div>' +
        '<div class="sm-field"><label>Permissions / notes</label><input name="permissions" value="' + v(e.permissions) + '" placeholder="Can edit classes, view bookings"></div>'
      );

      // Documents section — rendered separately because uploads need the
      // employee ID and a save round-trip first if the record is new.
      const docs = (e.documents || []);
      const docsList = docs.length
        ? '<div style="display:grid;grid-template-columns:1fr;gap:6px;margin-top:8px">' +
          docs.map((d, i) =>
            '<div style="display:flex;gap:10px;align-items:center;justify-content:space-between;padding:8px 12px;border:1px solid var(--sm-line,#e5e7eb);border-radius:10px;background:#fafafa">' +
              '<div style="min-width:0;flex:1">' +
                '<div style="font-weight:600">' + esc(d.label || d.name) + '</div>' +
                '<div class="sm-field__help" style="word-break:break-all">' + esc(d.name) + (d.size ? ' · ' + fmtBytes(d.size) : '') + (d.uploadedAt ? ' · ' + esc(String(d.uploadedAt).slice(0, 10)) : '') + '</div>' +
              '</div>' +
              '<div style="display:flex;gap:6px">' +
                (d.url ? '<a class="sm-btn sm-btn--light sm-btn--sm" href="' + esc(d.url) + '" target="_blank" rel="noopener">View</a>' : '') +
                '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-doc-del="' + i + '" style="color:var(--sm-danger)">Remove</button>' +
              '</div>' +
            '</div>'
          ).join('') +
        '</div>'
        : '<p class="sm-field__help">No documents uploaded yet.</p>';

      const documents = section('Documents',
        (id
          ? '<div class="sm-form__row">' +
              '<div class="sm-field"><label>Document label</label><input data-sm-doc-label placeholder="I-9 Form, Drug policy, Photo ID, etc."></div>' +
              '<div class="sm-field"><label>File</label><input type="file" data-sm-doc-file accept="image/*,application/pdf,.doc,.docx"></div>' +
              '<div class="sm-field"><label>&nbsp;</label><button type="button" class="sm-btn sm-btn--primary" data-sm-doc-upload>Upload</button></div>' +
            '</div>' +
            '<div data-sm-doc-msg class="sm-field__help" style="min-height:18px"></div>' +
            '<div data-sm-doc-list>' + docsList + '</div>'
          : '<p class="sm-field__help">Save the employee first, then re-open to upload documents.</p>'
        )
      );

      modalBody.innerHTML = photoBanner +
        '<form class="sm-form" data-sm-emp-form>' +
        basic + employment + skills + scheduling + payroll + access + documents +
        '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
          '<button type="submit" class="sm-btn sm-btn--primary">Save</button>' +
          '<button type="button" class="sm-btn sm-btn--light" data-sm-emp-cancel>Cancel</button>' +
          (id ? '<button type="button" class="sm-btn sm-btn--light" data-sm-emp-modal-del style="color:var(--sm-danger);margin-left:auto">Delete employee</button>' : '') +
          '<span class="sm-field__help" data-sm-emp-msg></span>' +
        '</div>' +
      '</form>';
      modal.hidden = false;

      // Certifications inline editor — add / remove rows. The save
      // handler reads whichever rows are present at submit time.
      const certList = modalBody.querySelector('[data-sm-cert-list]');
      const certAddBtn = modalBody.querySelector('[data-sm-cert-add]');
      function makeCertRow() {
        const idx = certList.querySelectorAll('[data-sm-cert-row]').length;
        const div = document.createElement('div');
        div.className = 'sm-form__row';
        div.setAttribute('data-sm-cert-row', String(idx));
        div.style.alignItems = 'end';
        div.innerHTML =
          '<div class="sm-field"><label>Certification</label><input data-sm-cert-name placeholder="BLS Instructor"></div>' +
          '<div class="sm-field"><label>Expires on</label><input type="date" data-sm-cert-expires></div>' +
          '<div class="sm-field"><label>&nbsp;</label><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-cert-remove style="color:var(--sm-danger)">Remove</button></div>';
        return div;
      }
      certAddBtn && certAddBtn.addEventListener('click', () => {
        const empty = certList.querySelector('[data-sm-cert-empty]');
        if (empty) empty.remove();
        certList.appendChild(makeCertRow());
      });
      certList && certList.addEventListener('click', (ev) => {
        const rm = ev.target.closest('[data-sm-cert-remove]');
        if (!rm) return;
        const row = rm.closest('[data-sm-cert-row]');
        if (row) row.remove();
      });

      // Photo upload — opens the hidden file picker, uploads to Storage,
      // updates the Firestore doc, then refreshes both the avatar in
      // the banner and the row in the employees list behind us.
      if (id) {
        const photoFile = modalBody.querySelector('[data-sm-emp-photo-file]');
        const photoPick = modalBody.querySelector('[data-sm-emp-photo-pick]');
        const photoRemove = modalBody.querySelector('[data-sm-emp-photo-remove]');
        const photoMsg = modalBody.querySelector('[data-sm-emp-photo-msg]');
        const avatarBox = modalBody.querySelector('[data-sm-emp-avatar]');
        photoPick && photoPick.addEventListener('click', () => photoFile && photoFile.click());
        photoFile && photoFile.addEventListener('change', async () => {
          const file = photoFile.files && photoFile.files[0];
          if (!file) return;
          if (file.size > 8 * 1024 * 1024) { photoMsg.textContent = 'Photo is over 8 MB.'; return; }
          photoMsg.textContent = 'Uploading…';
          photoPick.disabled = true;
          try {
            const res = await ns.fb.adminUploadEmployeePhoto(id, file, e.photoPath || '');
            e.photoUrl = res.photoUrl; e.photoPath = res.photoPath;
            avatarBox.innerHTML = empAvatar(e, 72);
            photoPick.textContent = 'Change photo';
            // Inject the Remove button if it wasn't there.
            if (!modalBody.querySelector('[data-sm-emp-photo-remove]')) {
              const rm = document.createElement('button');
              rm.type = 'button'; rm.className = 'sm-btn sm-btn--light sm-btn--sm';
              rm.style.color = 'var(--sm-danger)';
              rm.setAttribute('data-sm-emp-photo-remove', '');
              rm.textContent = 'Remove';
              rm.addEventListener('click', removePhoto);
              photoPick.insertAdjacentElement('afterend', rm);
            }
            photoMsg.textContent = '✓ Updated.';
            // Refresh the list rendering behind the modal.
            const cached = cache.employees.find((x) => x.id === id);
            if (cached) { cached.photoUrl = res.photoUrl; cached.photoPath = res.photoPath; }
            renderEmployeesView();
          } catch (err) {
            photoMsg.textContent = (err && err.code === 'storage/unauthorized')
              ? 'Permission denied. Make sure Firebase Storage is enabled and storage.rules has been published.'
              : (err.message || 'Upload failed.');
          } finally {
            photoPick.disabled = false;
            photoFile.value = '';
          }
        });
        async function removePhoto() {
          if (!confirm('Remove the profile photo?')) return;
          try {
            await ns.fb.adminDeleteEmployeePhoto(id, e.photoPath || '');
            e.photoUrl = ''; e.photoPath = '';
            avatarBox.innerHTML = empAvatar(e, 72);
            photoPick.textContent = 'Upload photo';
            const rmBtn = modalBody.querySelector('[data-sm-emp-photo-remove]');
            if (rmBtn) rmBtn.remove();
            const cached = cache.employees.find((x) => x.id === id);
            if (cached) { delete cached.photoUrl; delete cached.photoPath; }
            renderEmployeesView();
          } catch (err) {
            alert(err.message || 'Remove failed.');
          }
        }
        photoRemove && photoRemove.addEventListener('click', removePhoto);
      }

      const form = modalBody.querySelector('[data-sm-emp-form]');
      const msg  = modalBody.querySelector('[data-sm-emp-msg]');

      form.addEventListener('submit', async (ev) => {
        ev.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const fd = new FormData(form);
          const get = (k) => (fd.get(k) || '').toString().trim();
          const availability = fd.getAll('availability').map(String);
          const managerId = get('managerId');
          const mgr = managerId ? (cache.employees.find((x) => x.id === managerId) || null) : null;
          const certifications = Array.from(modalBody.querySelectorAll('[data-sm-cert-row]'))
            .map((r) => ({
              name: (r.querySelector('[data-sm-cert-name]').value || '').trim(),
              expiresOn: (r.querySelector('[data-sm-cert-expires]').value || '').trim()
            }))
            .filter((c) => c.name || c.expiresOn);
          const data = {
            employeeId: get('employeeId'),
            status: get('status') || 'active',
            firstName: get('firstName'),
            lastName: get('lastName'),
            preferredName: get('preferredName'),
            email: get('email').toLowerCase(),
            phone: get('phone'),
            address: get('address'),
            emergencyName: get('emergencyName'),
            emergencyPhone: get('emergencyPhone'),
            jobTitle: get('jobTitle'),
            department: get('department'),
            employmentType: get('employmentType'),
            workLocation: get('workLocation'),
            startDate: get('startDate'),
            endDate: get('endDate'),
            managerId: managerId || '',
            managerName: mgr ? empFullName(mgr) : '',
            canTeach: get('canTeach'),
            certifications: certifications,
            backgroundStatus: get('backgroundStatus'),
            driverLicense: get('driverLicense'),
            skillNotes: get('skillNotes'),
            availability: availability,
            preferredLocation: get('preferredLocation'),
            maxHours: get('maxHours') ? parseInt(get('maxHours'), 10) : null,
            canTravel: get('canTravel'),
            blackoutDates: get('blackoutDates'),
            sideGig: get('sideGig'),
            payType: get('payType'),
            payRate: get('payRate'),
            payrollId: get('payrollId'),
            paymentMethod: get('paymentMethod'),
            taxFormStatus: get('taxFormStatus'),
            reimbursementNotes: get('reimbursementNotes'),
            userRole: get('userRole'),
            loginEmail: get('loginEmail').toLowerCase(),
            accountStatus: get('accountStatus'),
            permissions: get('permissions'),
            // Preserve existing documents[] / photo refs — those mutate
            // out-of-band via the upload helpers, not the main form.
            documents: (id && e.documents) ? e.documents : (e.documents || [])
          };
          if (!data.firstName || !data.lastName) throw new Error('First and last name are required.');
          const saved = await ns.fb.adminSaveEmployee(id || null, data);
          msg.textContent = '✓ Saved.';
          await loadEmployeesTable();
          // For new employees, re-open so docs can be uploaded.
          if (!id) {
            setTimeout(() => openEmployeeModal(saved.id), 400);
          } else {
            setTimeout(closeModal, 400);
          }
        } catch (err) { msg.textContent = err.message || 'Save failed.'; }
      });

      modalBody.querySelector('[data-sm-emp-cancel]') && modalBody.querySelector('[data-sm-emp-cancel]').addEventListener('click', closeModal);
      const delBtn = modalBody.querySelector('[data-sm-emp-modal-del]');
      delBtn && delBtn.addEventListener('click', async () => {
        if (!confirm('Delete ' + empFullName(e) + ' permanently? Storage files will remain in Firebase and can be cleaned up there.')) return;
        try { await ns.fb.adminDeleteEmployee(id); await loadEmployeesTable(); closeModal(); }
        catch (err) { alert(err.message || 'Delete failed.'); }
      });

      // Document upload wiring.
      const docUploadBtn = modalBody.querySelector('[data-sm-doc-upload]');
      const docFileInput = modalBody.querySelector('[data-sm-doc-file]');
      const docLabelInput = modalBody.querySelector('[data-sm-doc-label]');
      const docMsg = modalBody.querySelector('[data-sm-doc-msg]');
      const docListEl = modalBody.querySelector('[data-sm-doc-list]');
      docUploadBtn && docUploadBtn.addEventListener('click', async () => {
        const file = docFileInput.files && docFileInput.files[0];
        if (!file) { docMsg.textContent = 'Pick a file first.'; return; }
        if (file.size > 25 * 1024 * 1024) { docMsg.textContent = 'File is over 25 MB.'; return; }
        docMsg.textContent = 'Uploading…';
        docUploadBtn.disabled = true;
        try {
          const meta = await ns.fb.adminUploadEmployeeDoc(id, file, docLabelInput.value || '');
          const fresh = await ns.fb.adminGetEmployee(id);
          const docs = (fresh && fresh.documents) ? fresh.documents.slice() : (e.documents || []).slice();
          docs.push(meta);
          await ns.fb.adminSaveEmployee(id, { documents: docs });
          e.documents = docs;
          // Refresh just the docs list inline.
          docListEl.innerHTML = '<div style="display:grid;grid-template-columns:1fr;gap:6px;margin-top:8px">' +
            docs.map((d, i) =>
              '<div style="display:flex;gap:10px;align-items:center;justify-content:space-between;padding:8px 12px;border:1px solid var(--sm-line,#e5e7eb);border-radius:10px;background:#fafafa">' +
                '<div style="min-width:0;flex:1">' +
                  '<div style="font-weight:600">' + esc(d.label || d.name) + '</div>' +
                  '<div class="sm-field__help" style="word-break:break-all">' + esc(d.name) + (d.size ? ' · ' + fmtBytes(d.size) : '') + (d.uploadedAt ? ' · ' + esc(String(d.uploadedAt).slice(0, 10)) : '') + '</div>' +
                '</div>' +
                '<div style="display:flex;gap:6px">' +
                  (d.url ? '<a class="sm-btn sm-btn--light sm-btn--sm" href="' + esc(d.url) + '" target="_blank" rel="noopener">View</a>' : '') +
                  '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-doc-del="' + i + '" style="color:var(--sm-danger)">Remove</button>' +
                '</div>' +
              '</div>'
            ).join('') +
          '</div>';
          docFileInput.value = '';
          docLabelInput.value = '';
          docMsg.textContent = '✓ Uploaded.';
        } catch (err) {
          console.error('[employees] upload failed', err);
          docMsg.textContent = (err.code === 'storage/unauthorized')
            ? 'Permission denied. Make sure Firebase Storage is enabled and storage.rules has been published with the employees/** rule.'
            : (err.message || 'Upload failed.');
        } finally {
          docUploadBtn.disabled = false;
        }
      });
      docListEl && docListEl.addEventListener('click', async (ev2) => {
        const rm = ev2.target.closest('[data-sm-doc-del]');
        if (!rm) return;
        const i = parseInt(rm.dataset.smDocDel, 10);
        const d = (e.documents || [])[i];
        if (!d) return;
        if (!confirm('Remove "' + (d.label || d.name) + '"?')) return;
        try {
          await ns.fb.adminDeleteEmployeeDoc(d.path);
          const docs = (e.documents || []).slice();
          docs.splice(i, 1);
          await ns.fb.adminSaveEmployee(id, { documents: docs });
          e.documents = docs;
          openEmployeeModal(id);
        } catch (err) { alert(err.message || 'Remove failed.'); }
      });
    }

    function csvCellEmp(v) {
      const s = (v == null) ? '' : String(v);
      return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    }
    function downloadEmployeesCsv() {
      if (!cache.employees || !cache.employees.length) { alert('No employees to export.'); return; }
      const header = ['Employee ID','First name','Last name','Preferred name','Email','Phone','Job title','Department','Status','Employment type','Work location','Start date','End date','Manager','Pay type','Pay rate'];
      const rows = cache.employees.map((e) => [
        e.employeeId || '', e.firstName || '', e.lastName || '', e.preferredName || '',
        e.email || '', e.phone || '', e.jobTitle || '', e.department || '',
        e.status || '', e.employmentType || '', e.workLocation || '',
        e.startDate || '', e.endDate || '', e.manager || '',
        e.payType || '', e.payRate || ''
      ]);
      const lines = [header, ...rows].map((row) => row.map(csvCellEmp).join(','));
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url; link.download = 'employees-' + stamp + '.csv';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }

    empRows && empRows.addEventListener('click', (ev) => {
      const editBtn = ev.target.closest('[data-sm-emp-edit]');
      if (editBtn) openEmployeeModal(editBtn.dataset.smEmpEdit);
    });
    empChartWrap && empChartWrap.addEventListener('click', (ev) => {
      const card = ev.target.closest('[data-sm-emp-edit]');
      if (card) openEmployeeModal(card.dataset.smEmpEdit);
    });
    empNewBtn && empNewBtn.addEventListener('click', () => openEmployeeModal(null));
    empDownloadBtn && empDownloadBtn.addEventListener('click', downloadEmployeesCsv);
    empSearchInput && empSearchInput.addEventListener('input', (() => {
      let t;
      return () => { clearTimeout(t); t = setTimeout(renderEmployeesView, 200); };
    })());
    empStatusFilter && empStatusFilter.addEventListener('change', renderEmployeesView);
    empLocFilter && empLocFilter.addEventListener('change', renderEmployeesView);
    empCertFilter && empCertFilter.addEventListener('change', renderEmployeesView);
    empViewToggleBtn && empViewToggleBtn.addEventListener('click', () => {
      empView = (empView === 'table') ? 'chart' : 'table';
      empViewToggleBtn.textContent = (empView === 'table') ? 'Chart view' : 'Table view';
      renderEmployeesView();
    });

    /* ---------- SCHEDULE TAB ---------- */
    // Custom calendar — no library. Week view = 7 day columns × hourly
    // grid (6am–10pm). Month view = classic 6×7 grid with chip events.
    // Class events are virtual-projected from cna_classes / iv_classes
    // so they always reflect the source of truth without a sync job.
    let schedLoaded = false;
    let schedView = 'week';
    let schedAnchor = (() => { const d = new Date(); d.setHours(0,0,0,0); return d; })();
    const SCHED_HOUR_START = 6;   // 6am
    const SCHED_HOUR_END   = 22;  // 10pm
    const SCHED_HOUR_HEIGHT = 48; // px per hour
    cache.scheduleEvents = [];
    cache.scheduleClasses = { cna: [], iv: [] };

    const schedEmpFilter   = root.querySelector('[data-sm-sched-emp-filter]');
    const schedTypeFilter  = root.querySelector('[data-sm-sched-type-filter]');
    const schedNewBtn      = root.querySelector('[data-sm-sched-new]');
    const schedPrevBtn     = root.querySelector('[data-sm-sched-prev]');
    const schedTodayBtn    = root.querySelector('[data-sm-sched-today]');
    const schedNextBtn     = root.querySelector('[data-sm-sched-next]');
    const schedLabel       = root.querySelector('[data-sm-sched-label]');
    const schedCanvas      = root.querySelector('[data-sm-sched-canvas]');
    const schedViewBtns    = root.querySelectorAll('[data-sm-sched-view]');
    const schedPendingBtn  = root.querySelector('[data-sm-sched-pending]');
    const schedPendingBdg  = root.querySelector('[data-sm-sched-pending-badge]');

    function schedFmtDateISO(d) {
      const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), day = String(d.getDate()).padStart(2,'0');
      return y + '-' + m + '-' + day;
    }
    function schedStartOfWeek(d) {
      const o = new Date(d); o.setHours(0,0,0,0);
      o.setDate(o.getDate() - o.getDay()); // Sunday-anchored
      return o;
    }
    function schedAddDays(d, n) { const o = new Date(d); o.setDate(o.getDate() + n); return o; }
    function schedStartOfMonth(d) { const o = new Date(d.getFullYear(), d.getMonth(), 1); return o; }
    function schedRangeLabel() {
      if (schedView === 'week') {
        const s = schedStartOfWeek(schedAnchor);
        const e = schedAddDays(s, 6);
        const sameMonth = s.getMonth() === e.getMonth();
        const fmt = (d, mo) => d.toLocaleString(undefined, mo ? { month: 'short', day: 'numeric' } : { day: 'numeric' });
        return sameMonth
          ? s.toLocaleString(undefined, { month: 'long' }) + ' ' + s.getDate() + ' – ' + e.getDate() + ', ' + e.getFullYear()
          : fmt(s, true) + ' – ' + fmt(e, true) + ', ' + e.getFullYear();
      }
      return schedAnchor.toLocaleString(undefined, { month: 'long', year: 'numeric' });
    }

    // Parse "8:00 AM" / "8 AM" / "08:00" into "HH:MM" 24-hour.
    function schedParseTime(s) {
      if (!s) return '';
      const m = String(s).trim().match(/^(\d{1,2})(?::(\d{2}))?\s*([AP]M)?$/i);
      if (!m) return '';
      let h = parseInt(m[1], 10), mi = parseInt(m[2] || '0', 10);
      const ap = (m[3] || '').toUpperCase();
      if (ap === 'PM' && h < 12) h += 12;
      if (ap === 'AM' && h === 12) h = 0;
      return String(h).padStart(2,'0') + ':' + String(mi).padStart(2,'0');
    }
    function schedISO(date, hhmm) { return date + 'T' + (hhmm || '00:00'); }

    // Expand cna_classes + iv_classes sessions[] into virtual events
    // for the current range. These are read-only on the calendar — to
    // assign an instructor, the admin creates a real schedule_events
    // doc of type 'shift' that links to the class.
    function schedVirtualClassEvents(rangeStart, rangeEnd) {
      const out = [];
      const push = (cls, coll) => {
        (cls.sessions || []).forEach((s, i) => {
          if (!s.date) return;
          if (s.date < rangeStart || s.date > rangeEnd) return;
          out.push({
            id: 'virtual:' + coll + ':' + cls.id + ':' + i,
            virtual: true,
            type: 'class',
            title: (coll === 'cna' ? 'CNA — ' : 'IV — ') + (cls.label || ''),
            start: schedISO(s.date, schedParseTime(s.startTime) || '08:00'),
            end:   schedISO(s.date, schedParseTime(s.endTime)   || '17:00'),
            location: cls.location || '',
            linkedClassId: cls.id,
            linkedClassCollection: coll === 'cna' ? 'cna_classes' : 'iv_classes'
          });
        });
      };
      (cache.scheduleClasses.cna || []).forEach((c) => push(c, 'cna'));
      (cache.scheduleClasses.iv  || []).forEach((c) => push(c, 'iv'));
      return out;
    }

    function schedFilteredEvents(rangeStart, rangeEnd) {
      const empId = schedEmpFilter && schedEmpFilter.value;
      const typeF = schedTypeFilter && schedTypeFilter.value;
      const real = (cache.scheduleEvents || []).filter((e) => {
        if (e.start && e.start.slice(0, 10) > rangeEnd) return false;
        if (e.end && e.end.slice(0, 10) < rangeStart) return false;
        if (empId && e.employeeId !== empId) return false;
        if (typeF && e.type !== typeF) return false;
        return true;
      });
      const virt = schedVirtualClassEvents(rangeStart, rangeEnd).filter((e) => {
        if (typeF && e.type !== typeF) return false;
        // Employee filter on a virtual class shows the class only if
        // there's an assigned shift on it for that employee.
        if (empId) {
          const matched = real.some((r) => r.linkedClassId === e.linkedClassId && r.employeeId === empId);
          if (!matched) return false;
        }
        return true;
      });
      return real.concat(virt);
    }

    function schedRefreshEmployeesFilter() {
      if (!schedEmpFilter) return;
      const cur = schedEmpFilter.value;
      const opts = (cache.employees || []).filter((e) => (e.status || 'active') !== 'terminated')
        .sort((a, b) => String(a.lastName || '').localeCompare(String(b.lastName || '')));
      schedEmpFilter.innerHTML = '<option value="">All employees</option>' +
        opts.map((e) => '<option value="' + esc(e.id) + '">' + esc(empFullName(e)) + '</option>').join('');
      if (cur) schedEmpFilter.value = cur;
    }

    async function loadSchedule() {
      if (!schedCanvas) return;
      schedCanvas.innerHTML = '<div style="text-align:center;padding:40px;color:var(--sm-ink-muted)">Loading…</div>';
      try {
        const tasks = [
          ns.fb.adminListEmployees().catch(() => []),
          ns.fb.adminListCnaClasses().catch(() => []),
          ns.fb.adminListIvClasses().catch(() => []),
          ns.fb.adminListScheduleEvents().catch(() => [])
        ];
        const [emps, cnas, ivs, events] = await Promise.all(tasks);
        cache.employees = emps;
        cache.scheduleClasses = { cna: cnas, iv: ivs };
        cache.scheduleEvents = events;
      } catch (err) {
        console.error('[schedule] load failed', err);
        schedCanvas.innerHTML = '<div style="text-align:center;padding:40px;color:var(--sm-danger)">Unable to load schedule. ' + (err.message || '') + '</div>';
        return;
      }
      schedRefreshEmployeesFilter();
      schedRefreshPendingBadge();
      schedRender();
    }

    function schedRefreshPendingBadge() {
      if (!schedPendingBdg) return;
      const n = (cache.scheduleEvents || []).filter((e) => e.status === 'requested').length;
      if (n > 0) { schedPendingBdg.textContent = String(n); schedPendingBdg.style.display = ''; }
      else { schedPendingBdg.style.display = 'none'; }
    }

    function openPendingRequestsModal() {
      if (!modal || !modalBody) return;
      const list = (cache.scheduleEvents || []).filter((e) => e.status === 'requested')
        .sort((a, b) => String(a.start || '').localeCompare(String(b.start || '')));
      modalTitle && (modalTitle.textContent = 'Time-off requests');
      if (!list.length) {
        modalBody.innerHTML = '<p class="sm-field__help">No pending time-off requests.</p>';
        modal.hidden = false;
        return;
      }
      modalBody.innerHTML = '<div style="display:flex;flex-direction:column;gap:10px">' +
        list.map((r) => {
          const range = (r.start || '').slice(0, 16).replace('T', ' ') + ' — ' + (r.end || '').slice(0, 16).replace('T', ' ');
          return '<div style="border:1px solid var(--sm-line,#e5e7eb);border-radius:10px;padding:12px">' +
            '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap">' +
              '<div style="min-width:0">' +
                '<strong>' + esc(r.employeeName || r.employeeEmail || 'Unknown') + '</strong>' +
                '<div class="sm-field__help">' + esc(range) + (r.allDay ? ' · all day' : '') + '</div>' +
                (r.notes ? '<div class="sm-field__help">Reason: ' + esc(r.notes) + '</div>' : '') +
              '</div>' +
              '<div style="display:flex;gap:6px;flex-wrap:wrap">' +
                '<button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-sm-sched-approve="' + esc(r.id) + '">Approve</button>' +
                '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-sched-deny="' + esc(r.id) + '" style="color:var(--sm-danger)">Deny</button>' +
              '</div>' +
            '</div>' +
          '</div>';
        }).join('') +
      '</div>';
      modal.hidden = false;
      modalBody.addEventListener('click', async (ev) => {
        const ap = ev.target.closest('[data-sm-sched-approve]');
        const dn = ev.target.closest('[data-sm-sched-deny]');
        if (ap) {
          ap.disabled = true; ap.textContent = '…';
          try {
            await ns.fb.adminApproveScheduleRequest(ap.dataset.smSchedApprove);
            const idx = cache.scheduleEvents.findIndex((x) => x.id === ap.dataset.smSchedApprove);
            if (idx >= 0) cache.scheduleEvents[idx].status = 'planned';
            schedRefreshPendingBadge(); schedRender(); openPendingRequestsModal();
          } catch (err) { ap.disabled = false; ap.textContent = 'Approve'; alert(err.message || 'Approve failed.'); }
        } else if (dn) {
          const reason = prompt('Deny reason (optional, shown to the employee):', '');
          if (reason === null) return;
          dn.disabled = true; dn.textContent = '…';
          try {
            await ns.fb.adminDenyScheduleRequest(dn.dataset.smSchedDeny, reason);
            const idx = cache.scheduleEvents.findIndex((x) => x.id === dn.dataset.smSchedDeny);
            if (idx >= 0) { cache.scheduleEvents[idx].status = 'denied'; cache.scheduleEvents[idx].deniedReason = reason; }
            schedRefreshPendingBadge(); schedRender(); openPendingRequestsModal();
          } catch (err) { dn.disabled = false; dn.textContent = 'Deny'; alert(err.message || 'Deny failed.'); }
        }
      }, { once: true });
    }

    function schedRender() {
      if (!schedCanvas) return;
      if (schedLabel) schedLabel.textContent = schedRangeLabel();
      schedViewBtns.forEach((b) => b.classList.toggle('is-active', b.dataset.smSchedView === schedView));
      if (schedView === 'week') schedRenderWeek();
      else schedRenderMonth();
    }

    function schedEventClass(type) {
      if (type === 'class') return ' is-class';
      if (type === 'unavailable') return ' is-unavailable';
      if (type === 'event') return ' is-event';
      return '';
    }

    function schedRenderWeek() {
      const start = schedStartOfWeek(schedAnchor);
      const end = schedAddDays(start, 6);
      const days = []; for (let i = 0; i < 7; i++) days.push(schedAddDays(start, i));
      const todayISO = schedFmtDateISO(new Date());
      const events = schedFilteredEvents(schedFmtDateISO(start), schedFmtDateISO(end));

      const headHtml = '<div></div>' + days.map((d) => {
        const isToday = schedFmtDateISO(d) === todayISO;
        return '<div class="' + (isToday ? 'is-today' : '') + '">' +
          d.toLocaleString(undefined, { weekday: 'short' }) + '<br>' + d.getDate() +
        '</div>';
      }).join('');
      const rows = [];
      for (let h = SCHED_HOUR_START; h < SCHED_HOUR_END; h++) {
        const hourLabel = ((h % 12) || 12) + (h < 12 ? ' AM' : ' PM');
        let row = '<div class="sm-sched-week__time">' + hourLabel + '</div>';
        days.forEach((d) => {
          row += '<div class="sm-sched-week__cell" data-sm-sched-cell="' + schedFmtDateISO(d) + 'T' + String(h).padStart(2,'0') + ':00"></div>';
        });
        rows.push(row);
      }
      schedCanvas.innerHTML = '<div class="sm-sched-week">' +
        '<div class="sm-sched-week__head">' + headHtml + '</div>' +
        rows.join('') +
      '</div>';

      // Overlay events onto cells. Position by start time + day column.
      events.forEach((ev) => {
        if (!ev.start || !ev.end) return;
        const d = ev.start.slice(0, 10);
        if (d < schedFmtDateISO(start) || d > schedFmtDateISO(end)) return;
        const col = Math.floor((new Date(d + 'T00:00:00') - start) / 86400000);
        if (col < 0 || col > 6) return;
        const sh = parseInt(ev.start.slice(11, 13), 10) || 0;
        const sm = parseInt(ev.start.slice(14, 16), 10) || 0;
        const eh = parseInt(ev.end.slice(11, 13), 10) || sh;
        const em = parseInt(ev.end.slice(14, 16), 10) || sm;
        const startMin = (sh - SCHED_HOUR_START) * 60 + sm;
        const endMin   = (eh - SCHED_HOUR_START) * 60 + em;
        if (endMin <= 0) return;
        const top = Math.max(0, (startMin / 60) * SCHED_HOUR_HEIGHT);
        const height = Math.max(20, ((endMin - startMin) / 60) * SCHED_HOUR_HEIGHT);
        // Find any cell in that day's column to host the absolute overlay.
        const anchorCell = schedCanvas.querySelector('[data-sm-sched-cell^="' + d + 'T' + String(SCHED_HOUR_START).padStart(2,'0') + ':00"]');
        if (!anchorCell) return;
        // We can't position relative-to-grid easily — but we can attach
        // absolutely positioned overlays via a per-column container.
        anchorCell.style.position = 'relative';
        const el = document.createElement('div');
        el.className = 'sm-sched-event' + schedEventClass(ev.type);
        el.style.top = top + 'px';
        el.style.height = height + 'px';
        el.dataset.smSchedOpen = ev.id;
        const who = ev.employeeName ? '<div class="sm-sched-event__sub">' + esc(ev.employeeName) + '</div>' : '';
        el.innerHTML = '<div class="sm-sched-event__title">' + esc(ev.title || ev.type) + '</div>' + who;
        anchorCell.appendChild(el);
      });
    }

    function schedRenderMonth() {
      const first = schedStartOfMonth(schedAnchor);
      const gridStart = schedStartOfWeek(first);
      const cells = []; for (let i = 0; i < 42; i++) cells.push(schedAddDays(gridStart, i));
      const monthIdx = schedAnchor.getMonth();
      const todayISO = schedFmtDateISO(new Date());
      const events = schedFilteredEvents(schedFmtDateISO(cells[0]), schedFmtDateISO(cells[41]));
      const byDay = Object.create(null);
      events.forEach((ev) => {
        if (!ev.start) return;
        const d = ev.start.slice(0, 10);
        (byDay[d] = byDay[d] || []).push(ev);
      });
      const dayHead = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
        .map((d) => '<div>' + d + '</div>').join('');
      const cellsHtml = cells.map((d) => {
        const iso = schedFmtDateISO(d);
        const isOther = d.getMonth() !== monthIdx;
        const isToday = iso === todayISO;
        const list = (byDay[iso] || []).slice(0, 3);
        const overflow = (byDay[iso] || []).length - list.length;
        return '<div class="sm-sched-month__cell ' + (isOther ? 'is-other' : '') + ' ' + (isToday ? 'is-today' : '') + '" data-sm-sched-cell="' + iso + 'T09:00">' +
          '<div class="sm-sched-date">' + d.getDate() + '</div>' +
          list.map((ev) => '<div class="sm-sched-month__chip' + schedEventClass(ev.type) + '" data-sm-sched-open="' + esc(ev.id) + '">' + esc(ev.title || ev.type) + '</div>').join('') +
          (overflow > 0 ? '<div class="sm-sched-month__chip-more">+' + overflow + ' more</div>' : '') +
        '</div>';
      }).join('');
      schedCanvas.innerHTML = '<div class="sm-sched-month">' +
        '<div class="sm-sched-month__head" style="display:contents">' + dayHead + '</div>' +
        cellsHtml +
      '</div>';
    }

    // ---------- conflict detection ----------
    function schedOverlap(a, b) {
      return a.start < b.end && b.start < a.end;
    }
    function schedFindConflicts(empId, start, end, excludeId) {
      if (!empId) return [];
      const candidate = { start, end };
      return (cache.scheduleEvents || []).filter((e) => {
        if (e.id === excludeId) return false;
        if (e.employeeId !== empId) return false;
        if (e.status === 'denied') return false;
        return schedOverlap(candidate, e);
      });
    }

    // ---------- event modal ----------
    function openScheduleEventModal(id, prefill) {
      if (!modal || !modalBody) return;
      let ev = {};
      let isVirtual = false;
      if (id) {
        if (String(id).startsWith('virtual:')) {
          isVirtual = true;
          const all = schedVirtualClassEvents('0000-00-00', '9999-99-99');
          ev = all.find((x) => x.id === id) || {};
          // For a virtual class, the "open" action means assign someone.
          // We pre-fill a real shift event from it.
          ev = {
            type: 'shift',
            title: 'Instructing: ' + ev.title,
            start: ev.start, end: ev.end,
            location: ev.location || '',
            linkedClassId: ev.linkedClassId,
            linkedClassCollection: ev.linkedClassCollection
          };
        } else {
          ev = (cache.scheduleEvents || []).find((x) => x.id === id) || {};
        }
      } else if (prefill) {
        ev = Object.assign({}, prefill);
      }
      const isNew = !id || isVirtual;
      modalTitle && (modalTitle.textContent = isNew ? 'Add schedule event' : 'Edit event');
      const v = (s) => esc(s == null ? '' : String(s));

      const empOpts = '<option value="">— None —</option>' +
        (cache.employees || []).filter((e) => (e.status || 'active') !== 'terminated')
          .sort((a, b) => String(a.lastName || '').localeCompare(String(b.lastName || '')))
          .map((e) => '<option value="' + esc(e.id) + '"' + (ev.employeeId === e.id ? ' selected' : '') + '>' + esc(empFullName(e)) + '</option>').join('');

      const typeOpts = ['shift', 'class', 'unavailable', 'event'].map((t) =>
        '<option value="' + t + '"' + ((ev.type || 'shift') === t ? ' selected' : '') + '>' + t.replace(/^\w/, c => c.toUpperCase()) + '</option>'
      ).join('');

      const splitDt = (iso) => {
        if (!iso || iso.length < 16) return { d: '', t: '' };
        return { d: iso.slice(0, 10), t: iso.slice(11, 16) };
      };
      const s = splitDt(ev.start || '');
      const e = splitDt(ev.end || '');

      modalBody.innerHTML = '<form class="sm-form" data-sm-sched-form>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Type *</label><select name="type">' + typeOpts + '</select></div>' +
          '<div class="sm-field"><label>Employee</label><select name="employeeId">' + empOpts + '</select></div>' +
        '</div>' +
        '<div class="sm-field"><label>Title *</label><input name="title" value="' + v(ev.title) + '" placeholder="Morning shift — Lake City" required></div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Date *</label><input type="date" name="date" value="' + v(s.d) + '" required></div>' +
          '<div class="sm-field"><label>Start *</label><input type="time" name="startTime" value="' + v(s.t || '08:00') + '" required></div>' +
          '<div class="sm-field"><label>End *</label><input type="time" name="endTime" value="' + v(e.t || '17:00') + '" required></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Location</label><input name="location" value="' + v(ev.location) + '" placeholder="Lake City"></div>' +
          '<div class="sm-field"><label>Notes</label><input name="notes" value="' + v(ev.notes) + '"></div>' +
        '</div>' +
        (ev.linkedClassId ? '<div class="sm-field__help">Linked to ' + esc(ev.linkedClassCollection || '?') + ' · ' + esc(ev.linkedClassId) + '</div>' : '') +
        '<div data-sm-sched-conflict class="sm-field__help" style="margin-top:8px"></div>' +
        '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
          '<button type="submit" class="sm-btn sm-btn--primary">' + (isNew ? 'Create' : 'Save') + '</button>' +
          '<button type="button" class="sm-btn sm-btn--light" data-sm-sched-cancel>Cancel</button>' +
          ((!isNew && id) ? '<button type="button" class="sm-btn sm-btn--light" data-sm-sched-del style="color:var(--sm-danger);margin-left:auto">Delete</button>' : '') +
          '<span class="sm-field__help" data-sm-sched-msg></span>' +
        '</div>' +
      '</form>';
      modal.hidden = false;

      const form  = modalBody.querySelector('[data-sm-sched-form]');
      const msg   = modalBody.querySelector('[data-sm-sched-msg]');
      const confM = modalBody.querySelector('[data-sm-sched-conflict]');

      function checkConflict() {
        confM.textContent = '';
        const fd = new FormData(form);
        const empId = (fd.get('employeeId') || '').toString();
        const date = (fd.get('date') || '').toString();
        const sT = (fd.get('startTime') || '').toString();
        const eT = (fd.get('endTime') || '').toString();
        if (!empId || !date || !sT || !eT) return;
        const startISO = schedISO(date, sT);
        const endISO   = schedISO(date, eT);
        if (endISO <= startISO) { confM.style.color = '#b45309'; confM.textContent = 'End time must be after start time.'; return; }
        const c = schedFindConflicts(empId, startISO, endISO, id && !isVirtual ? id : null);
        if (!c.length) { confM.style.color = '#15803d'; confM.textContent = '✓ No conflicts for that employee.'; return; }
        confM.style.color = '#b91c1c';
        confM.textContent = 'Conflicts with: ' + c.map((x) => (x.title || x.type) + ' (' + (x.start || '').slice(11, 16) + '–' + (x.end || '').slice(11, 16) + ')').join('; ');
      }
      ['employeeId','date','startTime','endTime'].forEach((n) => {
        const el = form.querySelector('[name="' + n + '"]');
        el && el.addEventListener('change', checkConflict);
      });
      checkConflict();

      form.addEventListener('submit', async (sub) => {
        sub.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const fd = new FormData(form);
          const get = (k) => (fd.get(k) || '').toString().trim();
          const date = get('date');
          const startISO = schedISO(date, get('startTime'));
          const endISO   = schedISO(date, get('endTime'));
          if (endISO <= startISO) throw new Error('End time must be after start time.');
          const empId = get('employeeId');
          const conflicts = schedFindConflicts(empId, startISO, endISO, id && !isVirtual ? id : null);
          if (conflicts.length) {
            throw new Error('Conflict: ' + conflicts.map((x) => x.title || x.type).join(', '));
          }
          const emp = empId ? (cache.employees || []).find((x) => x.id === empId) : null;
          const data = {
            type: get('type') || 'shift',
            title: get('title'),
            start: startISO,
            end:   endISO,
            employeeId: empId,
            employeeName: emp ? empFullName(emp) : '',
            // Denormalized so the Firestore rule can grant the employee
            // read access to their own events without a join.
            employeeEmail: emp ? ((emp.loginEmail || emp.email || '').toLowerCase()) : '',
            location: get('location'),
            notes: get('notes'),
            linkedClassId: ev.linkedClassId || '',
            linkedClassCollection: ev.linkedClassCollection || '',
            status: 'planned'
          };
          const savedId = (id && !isVirtual) ? id : null;
          const saved = await ns.fb.adminSaveScheduleEvent(savedId, data);
          // Patch local cache so the calendar re-renders without a round-trip.
          if (savedId) {
            const idx = cache.scheduleEvents.findIndex((x) => x.id === savedId);
            if (idx >= 0) cache.scheduleEvents[idx] = Object.assign({}, cache.scheduleEvents[idx], saved);
          } else {
            cache.scheduleEvents.push(saved);
          }
          msg.textContent = '✓ Saved.';
          schedRender();
          setTimeout(closeModal, 400);
        } catch (err) { msg.textContent = err.message || 'Save failed.'; }
      });

      modalBody.querySelector('[data-sm-sched-cancel]').addEventListener('click', closeModal);
      const delBtn = modalBody.querySelector('[data-sm-sched-del]');
      delBtn && delBtn.addEventListener('click', async () => {
        if (!confirm('Delete this event?')) return;
        try {
          await ns.fb.adminDeleteScheduleEvent(id);
          cache.scheduleEvents = cache.scheduleEvents.filter((x) => x.id !== id);
          schedRender();
          closeModal();
        } catch (err) { alert(err.message || 'Delete failed.'); }
      });
    }

    // ---------- nav + click wiring ----------
    schedCanvas && schedCanvas.addEventListener('click', (e) => {
      const open = e.target.closest('[data-sm-sched-open]');
      if (open) { openScheduleEventModal(open.dataset.smSchedOpen); return; }
      const cell = e.target.closest('[data-sm-sched-cell]');
      if (cell) {
        const iso = cell.dataset.smSchedCell;
        const date = iso.slice(0, 10);
        const t = iso.slice(11, 16) || '09:00';
        const endHour = String(Math.min(SCHED_HOUR_END - 1, (parseInt(t.slice(0, 2), 10) || 9) + 1)).padStart(2, '0');
        openScheduleEventModal(null, {
          type: 'shift',
          start: date + 'T' + t,
          end:   date + 'T' + endHour + ':00'
        });
      }
    });

    schedPrevBtn && schedPrevBtn.addEventListener('click', () => {
      if (schedView === 'week') schedAnchor = schedAddDays(schedAnchor, -7);
      else { const d = new Date(schedAnchor); d.setMonth(d.getMonth() - 1); schedAnchor = d; }
      schedRender();
    });
    schedNextBtn && schedNextBtn.addEventListener('click', () => {
      if (schedView === 'week') schedAnchor = schedAddDays(schedAnchor, 7);
      else { const d = new Date(schedAnchor); d.setMonth(d.getMonth() + 1); schedAnchor = d; }
      schedRender();
    });
    schedTodayBtn && schedTodayBtn.addEventListener('click', () => {
      schedAnchor = new Date(); schedAnchor.setHours(0,0,0,0); schedRender();
    });
    schedViewBtns.forEach((b) => b.addEventListener('click', () => {
      schedView = b.dataset.smSchedView; schedRender();
    }));
    schedEmpFilter  && schedEmpFilter.addEventListener('change', schedRender);
    schedTypeFilter && schedTypeFilter.addEventListener('change', schedRender);
    schedPendingBtn && schedPendingBtn.addEventListener('click', openPendingRequestsModal);

    /* ---------- QUOTES TAB ---------- */
    let quotesLoaded = false;
    const quoteRows         = root.querySelector('[data-sm-quote-rows]');
    const quoteSearch       = root.querySelector('[data-sm-quote-search]');
    const quoteStatusFilter = root.querySelector('[data-sm-quote-filter-status]');
    const quoteDownloadBtn  = root.querySelector('[data-sm-quote-download]');
    const quoteNewBtn       = root.querySelector('[data-sm-quote-new]');
    cache.quotes = [];

    const QUOTE_SERVICES = ['BLS','ACLS','PALS','HeartSaver/CPR','First Aid','CNA','IV Cert','Custom'];
    const QUOTE_STATUSES = ['draft','sent','accepted','rejected','expired','completed'];
    const QUOTE_LOCATIONS = ['On-site at client','Lake City','Gainesville','Jacksonville','Other'];

    function quoteStatusBadge(s) {
      const map = { draft:'#6b7280', sent:'#3b82f6', accepted:'#15803d', rejected:'#b91c1c', expired:'#92400e', completed:'#0e7490' };
      const color = map[s] || '#6b7280';
      const label = (s || 'unknown').replace(/^\w/, (c) => c.toUpperCase());
      return '<span style="display:inline-block;padding:2px 8px;border-radius:999px;background:' + color + '22;color:' + color + ';font-size:12px;font-weight:600">' + label + '</span>';
    }
    function fmtMoney(n) {
      const v = parseFloat(n);
      if (isNaN(v)) return '$0';
      return '$' + v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }

    async function loadQuotesTable() {
      if (!quoteRows) return;
      quoteRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        cache.quotes = await ns.fb.adminListQuotes();
      } catch (err) {
        console.error('[admin] list quotes failed', err);
        quoteRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      renderQuotesTable();
    }

    function renderQuotesTable() {
      const q = (quoteSearch && quoteSearch.value || '').trim().toLowerCase();
      const status = quoteStatusFilter && quoteStatusFilter.value;
      const isOpen = (s) => s === 'draft' || s === 'sent';
      const rows = cache.quotes.filter((r) => {
        if (status === 'open') { if (!isOpen(r.status)) return false; }
        else if (status && r.status !== status) return false;
        if (q) {
          const hay = [r.companyName, r.contactName, r.contactEmail, r.serviceType].filter(Boolean).join(' ').toLowerCase();
          if (!hay.includes(q)) return false;
        }
        return true;
      }).sort((a, b) => String(b.serviceDate || '').localeCompare(String(a.serviceDate || '')));
      if (!rows.length) {
        quoteRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">' +
          (cache.quotes.length ? 'No quotes match the filters.' : 'No quotes yet. Click <strong>+ New quote</strong> to create one.') +
          '</td></tr>';
        return;
      }
      quoteRows.innerHTML = rows.map((r) => '<tr>' +
        '<td><strong>' + esc(r.companyName || '—') + '</strong>' + (r.contactName ? '<br><span class="sm-field__help">' + esc(r.contactName) + '</span>' : '') + '</td>' +
        '<td>' + esc(r.serviceType || '—') + '</td>' +
        '<td>' + esc(r.serviceDate || '—') + '</td>' +
        '<td>' + esc(String(r.studentsCount || '—')) + '</td>' +
        '<td>' + esc(fmtMoney(r.totalAmount)) + '</td>' +
        '<td>' + quoteStatusBadge(r.status || 'draft') + '</td>' +
        '<td>' + esc(r.sentAt ? String(r.sentAt).slice(0, 10) : '—') + '</td>' +
        '<td style="white-space:nowrap"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-quote-edit="' + esc(r.id) + '">Open</button></td>' +
      '</tr>').join('');
    }

    function openQuoteModal(id) {
      if (!modal || !modalBody) return;
      const q = id ? (cache.quotes.find((x) => x.id === id) || {}) : {};
      const isNew = !id;
      modalTitle && (modalTitle.textContent = isNew ? 'New quote' : ('Quote — ' + (q.companyName || '')));
      const v = (s) => esc(s == null ? '' : String(s));
      const sel = (vals, current) => vals.map((x) => '<option value="' + esc(x) + '"' + (String(x).toLowerCase() === String(current || '').toLowerCase() ? ' selected' : '') + '>' + esc(x) + '</option>').join('');

      const acceptedBanner = (q.status === 'accepted' && q.linkedScheduleEventId)
        ? '<div style="padding:10px 14px;border:1px solid #bbf7d0;background:#f0fdf4;border-radius:10px;margin-bottom:10px;color:#166534">✓ Accepted — class added to the calendar. Open the <strong>Schedule</strong> tab to assign instructors.' +
          (q.acceptedVia === 'public-link' ? ' <em>Accepted by client via public link.</em>' : '') +
        '</div>'
        : '';
      const rejectedBanner = (q.status === 'rejected')
        ? '<div style="padding:10px 14px;border:1px solid #fecaca;background:#fef2f2;border-radius:10px;margin-bottom:10px;color:#991b1b">Quote rejected.' +
          (q.rejectedVia === 'public-link' ? ' <em>Declined by client via public link.</em>' : '') +
        '</div>'
        : '';
      // Build a public-link block when we have a token. Uses the site
      // origin + /pages/quote-view?token=... so the same theme template
      // serves the public-facing accept page.
      const publicLink = q.publicToken
        ? (window.location.origin + '/pages/quote-view?token=' + encodeURIComponent(q.publicToken))
        : '';
      const linkBlock = (!isNew && publicLink)
        ? '<div style="margin-top:10px;padding:12px;border:1px solid var(--sm-line,#e5e7eb);border-radius:10px;background:#f8fafc">' +
            '<strong style="font-size:13px">Public client link</strong>' +
            '<div class="sm-field__help">Send this URL to the client. They can view the quote and click Accept without an account.</div>' +
            '<div style="display:flex;gap:8px;align-items:center;margin-top:6px;flex-wrap:wrap">' +
              '<input data-sm-quote-link-input value="' + esc(publicLink) + '" readonly style="flex:1;min-width:240px;font-family:monospace;font-size:12px;padding:6px 8px;border:1px solid var(--sm-line,#e5e7eb);border-radius:6px;background:#fff">' +
              '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-quote-copy>Copy link</button>' +
              '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-quote-mailto>Email to client</button>' +
            '</div>' +
          '</div>'
        : '';

      modalBody.innerHTML = acceptedBanner + rejectedBanner +
        '<form class="sm-form" data-sm-quote-form>' +
        '<details open style="margin-top:0;border:1px solid var(--sm-line,#e5e7eb);border-radius:12px;padding:0 14px 4px">' +
          '<summary style="padding:12px 0;cursor:pointer;font-weight:700">Company & contact</summary>' +
          '<div style="padding:6px 0 12px">' +
            '<div class="sm-form__row">' +
              '<div class="sm-field"><label>Company name *</label><input name="companyName" value="' + v(q.companyName) + '" required></div>' +
              '<div class="sm-field"><label>Contact name</label><input name="contactName" value="' + v(q.contactName) + '"></div>' +
            '</div>' +
            '<div class="sm-form__row">' +
              '<div class="sm-field"><label>Contact email</label><input type="email" name="contactEmail" value="' + v(q.contactEmail) + '"></div>' +
              '<div class="sm-field"><label>Contact phone</label><input name="contactPhone" value="' + v(q.contactPhone) + '"></div>' +
            '</div>' +
          '</div>' +
        '</details>' +
        '<details open style="margin-top:12px;border:1px solid var(--sm-line,#e5e7eb);border-radius:12px;padding:0 14px 4px">' +
          '<summary style="padding:12px 0;cursor:pointer;font-weight:700">Service details</summary>' +
          '<div style="padding:6px 0 12px">' +
            '<div class="sm-form__row">' +
              '<div class="sm-field"><label>Service *</label><select name="serviceType" required><option value="">—</option>' + sel(QUOTE_SERVICES, q.serviceType) + '</select></div>' +
              '<div class="sm-field"><label># Students</label><input type="number" name="studentsCount" value="' + v(q.studentsCount) + '" min="1"></div>' +
              '<div class="sm-field"><label>Location</label><select name="location"><option value="">—</option>' + sel(QUOTE_LOCATIONS, q.location) + '</select></div>' +
            '</div>' +
            '<div class="sm-form__row">' +
              '<div class="sm-field"><label>Service date</label><input type="date" name="serviceDate" value="' + v(q.serviceDate) + '"></div>' +
              '<div class="sm-field"><label>Start time</label><input type="time" name="startTime" value="' + v(q.startTime || '08:00') + '"></div>' +
              '<div class="sm-field"><label>End time</label><input type="time" name="endTime" value="' + v(q.endTime || '17:00') + '"></div>' +
            '</div>' +
            '<div class="sm-field"><label>On-site address (if applicable)</label><input name="onsiteAddress" value="' + v(q.onsiteAddress) + '" placeholder="123 Main St, City, FL"></div>' +
          '</div>' +
        '</details>' +
        '<details open style="margin-top:12px;border:1px solid var(--sm-line,#e5e7eb);border-radius:12px;padding:0 14px 4px">' +
          '<summary style="padding:12px 0;cursor:pointer;font-weight:700">Pricing & status</summary>' +
          '<div style="padding:6px 0 12px">' +
            '<div class="sm-form__row">' +
              '<div class="sm-field"><label>Price per student</label><input type="number" step="0.01" name="pricePerStudent" value="' + v(q.pricePerStudent) + '" placeholder="75.00"></div>' +
              '<div class="sm-field"><label>Travel / setup fee</label><input type="number" step="0.01" name="travelFee" value="' + v(q.travelFee) + '" placeholder="0"></div>' +
              '<div class="sm-field"><label>Total amount</label><input type="number" step="0.01" name="totalAmount" value="' + v(q.totalAmount) + '" placeholder="(auto)"></div>' +
            '</div>' +
            '<div class="sm-form__row">' +
              '<div class="sm-field"><label>Status</label><select name="status">' + sel(QUOTE_STATUSES, q.status || 'draft') + '</select></div>' +
              '<div class="sm-field"><label>Sent on</label><input type="date" name="sentAt" value="' + v(q.sentAt ? String(q.sentAt).slice(0, 10) : '') + '"></div>' +
              '<div class="sm-field"><label>Expires</label><input type="date" name="expiresAt" value="' + v(q.expiresAt) + '" placeholder="Optional"></div>' +
            '</div>' +
            '<div class="sm-field"><label>Internal notes</label><input name="notes" value="' + v(q.notes) + '" placeholder="Confirmed budget, decision-maker, etc."></div>' +
          '</div>' +
        '</details>' +
        linkBlock +
        '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
          '<button type="submit" class="sm-btn sm-btn--primary">Save</button>' +
          (!isNew && q.status !== 'accepted' ? '<button type="button" class="sm-btn sm-btn--primary" data-sm-quote-accept style="background:#15803d;border-color:#15803d">Mark accepted → Calendar</button>' : '') +
          (!isNew && q.status !== 'rejected' && q.status !== 'accepted' ? '<button type="button" class="sm-btn sm-btn--light" data-sm-quote-reject style="color:var(--sm-danger)">Mark rejected</button>' : '') +
          (!isNew ? '<button type="button" class="sm-btn sm-btn--light" data-sm-quote-del style="color:var(--sm-danger);margin-left:auto">Delete</button>' : '') +
          '<span class="sm-field__help" data-sm-quote-msg></span>' +
        '</div>' +
      '</form>';
      modal.hidden = false;

      const form = modalBody.querySelector('[data-sm-quote-form]');
      const msg  = modalBody.querySelector('[data-sm-quote-msg]');

      // Auto-compute total from price × students + travel.
      function recomputeTotal() {
        const fd = new FormData(form);
        const totalField = form.querySelector('[name="totalAmount"]');
        // Don't clobber a manually-entered total — only fill if blank.
        if ((totalField.value || '').trim()) return;
        const p = parseFloat(fd.get('pricePerStudent')) || 0;
        const n = parseFloat(fd.get('studentsCount')) || 0;
        const t = parseFloat(fd.get('travelFee')) || 0;
        const total = p * n + t;
        if (total > 0) totalField.value = total.toFixed(2);
      }
      ['pricePerStudent','studentsCount','travelFee'].forEach((n) => {
        const el = form.querySelector('[name="' + n + '"]'); el && el.addEventListener('input', recomputeTotal);
      });

      function gatherData() {
        const fd = new FormData(form);
        const get = (k) => (fd.get(k) || '').toString().trim();
        return {
          companyName:    get('companyName'),
          contactName:    get('contactName'),
          contactEmail:   get('contactEmail').toLowerCase(),
          contactPhone:   get('contactPhone'),
          serviceType:    get('serviceType'),
          studentsCount:  get('studentsCount') ? parseInt(get('studentsCount'), 10) : null,
          location:       get('location'),
          serviceDate:    get('serviceDate'),
          startTime:      get('startTime'),
          endTime:        get('endTime'),
          onsiteAddress:  get('onsiteAddress'),
          pricePerStudent:get('pricePerStudent') ? parseFloat(get('pricePerStudent')) : null,
          travelFee:      get('travelFee') ? parseFloat(get('travelFee')) : null,
          totalAmount:    get('totalAmount') ? parseFloat(get('totalAmount')) : null,
          status:         get('status') || 'draft',
          sentAt:         get('sentAt'),
          expiresAt:      get('expiresAt'),
          notes:          get('notes')
        };
      }

      form.addEventListener('submit', async (sub) => {
        sub.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const data = gatherData();
          if (!data.companyName) throw new Error('Company name is required.');
          const saved = await ns.fb.adminSaveQuote(id || null, data);
          msg.textContent = '✓ Saved.';
          await loadQuotesTable();
          if (!id) { setTimeout(() => openQuoteModal(saved.id), 400); }
          else { setTimeout(closeModal, 400); }
        } catch (err) { msg.textContent = err.message || 'Save failed.'; }
      });

      // Public link helpers — copy to clipboard and mailto prefill.
      const linkInput = modalBody.querySelector('[data-sm-quote-link-input]');
      const copyBtn   = modalBody.querySelector('[data-sm-quote-copy]');
      const mailtoBtn = modalBody.querySelector('[data-sm-quote-mailto]');
      copyBtn && copyBtn.addEventListener('click', async () => {
        if (!linkInput) return;
        try {
          if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(linkInput.value);
          } else {
            linkInput.select(); document.execCommand('copy');
          }
          copyBtn.textContent = '✓ Copied';
          setTimeout(() => { copyBtn.textContent = 'Copy link'; }, 1800);
        } catch (_) { alert(linkInput.value); }
      });
      mailtoBtn && mailtoBtn.addEventListener('click', () => {
        if (!linkInput) return;
        const subj = encodeURIComponent('STAT Med Training quote — ' + (q.companyName || ''));
        const body = encodeURIComponent(
          'Hi ' + (q.contactName || 'there') + ',\n\n' +
          'Here\'s the STAT Med Training quote for ' + (q.companyName || 'your team') + '.\n\n' +
          'Service: ' + (q.serviceType || '—') + '\n' +
          'Students: ' + (q.studentsCount || '—') + '\n' +
          'Date: ' + (q.serviceDate || 'TBD') + '\n' +
          'Total: $' + (q.totalAmount || 0) + '\n\n' +
          'Review and accept the quote here:\n' + linkInput.value + '\n\n' +
          'Reply to this email with any questions.\n\nThanks,\nSTAT Med Training'
        );
        window.location.href = 'mailto:' + (q.contactEmail || '') + '?subject=' + subj + '&body=' + body;
      });

      const acceptBtn = modalBody.querySelector('[data-sm-quote-accept]');
      acceptBtn && acceptBtn.addEventListener('click', async () => {
        if (!confirm('Mark this quote accepted and add the class to the calendar? This will create a class-type event on ' + (q.serviceDate || 'the service date') + '.')) return;
        msg.textContent = 'Accepting…';
        acceptBtn.disabled = true;
        try {
          // Save current form state first so any edits are persisted.
          const data = gatherData();
          await ns.fb.adminSaveQuote(id, data);
          const res = await ns.fb.adminAcceptQuote(id);
          msg.textContent = '✓ Accepted — class added to the calendar.';
          if (schedLoaded) { try { await loadSchedule(); } catch (_) {} }
          await loadQuotesTable();
          setTimeout(() => openQuoteModal(id), 600);
        } catch (err) {
          msg.textContent = err.message || 'Accept failed.';
          acceptBtn.disabled = false;
        }
      });

      const rejectBtn = modalBody.querySelector('[data-sm-quote-reject]');
      rejectBtn && rejectBtn.addEventListener('click', async () => {
        if (!confirm('Mark this quote rejected?')) return;
        try {
          await ns.fb.adminSaveQuote(id, Object.assign(gatherData(), { status: 'rejected' }));
          await loadQuotesTable();
          setTimeout(() => openQuoteModal(id), 200);
        } catch (err) { msg.textContent = err.message || 'Reject failed.'; }
      });

      const delBtn = modalBody.querySelector('[data-sm-quote-del]');
      delBtn && delBtn.addEventListener('click', async () => {
        if (!confirm('Delete this quote permanently? (The calendar event, if any, will stay — remove it from Schedule if you also want to drop the class.)')) return;
        try { await ns.fb.adminDeleteQuote(id); await loadQuotesTable(); closeModal(); }
        catch (err) { alert(err.message || 'Delete failed.'); }
      });
    }

    function csvCellQuote(v) {
      const s = (v == null) ? '' : String(v);
      return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    }
    function downloadQuotesCsv() {
      if (!cache.quotes || !cache.quotes.length) { alert('No quotes to export.'); return; }
      const header = ['Company','Contact','Email','Phone','Service','Students','Date','Total','Status','Sent','Expires','Notes'];
      const rows = cache.quotes.map((q) => [
        q.companyName || '', q.contactName || '', q.contactEmail || '', q.contactPhone || '',
        q.serviceType || '', q.studentsCount || '', q.serviceDate || '', q.totalAmount || '',
        q.status || '', q.sentAt || '', q.expiresAt || '', q.notes || ''
      ]);
      const lines = [header, ...rows].map((row) => row.map(csvCellQuote).join(','));
      const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = url; link.download = 'quotes-' + stamp + '.csv';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }

    quoteRows && quoteRows.addEventListener('click', (ev) => {
      const editBtn = ev.target.closest('[data-sm-quote-edit]');
      if (editBtn) openQuoteModal(editBtn.dataset.smQuoteEdit);
    });
    quoteNewBtn       && quoteNewBtn.addEventListener('click', () => openQuoteModal(null));
    quoteDownloadBtn  && quoteDownloadBtn.addEventListener('click', downloadQuotesCsv);
    quoteSearch       && quoteSearch.addEventListener('input', (() => {
      let t; return () => { clearTimeout(t); t = setTimeout(renderQuotesTable, 200); };
    })());
    quoteStatusFilter && quoteStatusFilter.addEventListener('change', renderQuotesTable);

    schedNewBtn && schedNewBtn.addEventListener('click', () => openScheduleEventModal(null, {
      type: 'shift',
      start: schedFmtDateISO(new Date()) + 'T09:00',
      end:   schedFmtDateISO(new Date()) + 'T10:00'
    }));

    /* ---------- BACKGROUND CHECKS (ORI) TAB ---------- */
    /* ---------- RENEWALS (certification expiry reminders) ---------- */
    let renewalsLoaded = false;
    const certRows      = root.querySelector('[data-sm-cert-rows]');
    const certForm      = root.querySelector('[data-sm-cert-form]');
    const certFormWrap  = root.querySelector('[data-sm-cert-formwrap]');
    const certFormTitle = root.querySelector('[data-sm-cert-form-title]');
    const certMsg       = root.querySelector('[data-sm-cert-msg]');
    const certNewBtn    = root.querySelector('[data-sm-cert-new]');
    const certCancelBtn = root.querySelector('[data-sm-cert-cancel]');
    const certFilter    = root.querySelector('[data-sm-cert-filter]');
    const certImportBtn = root.querySelector('[data-sm-cert-import-btn]');
    const certImportWrap= root.querySelector('[data-sm-cert-import-wrap]');
    const certCsv       = root.querySelector('[data-sm-cert-csv]');
    const certImportRun = root.querySelector('[data-sm-cert-import-run]');
    const certImportCancel = root.querySelector('[data-sm-cert-import-cancel]');
    const certImportMsg = root.querySelector('[data-sm-cert-import-msg]');
    cache.certs = [];

    function certTodayStr() { return new Date().toISOString().slice(0, 10); }
    function certDaysLeft(expiresDate) {
      if (!expiresDate) return null;
      const exp = new Date(expiresDate + 'T12:00:00Z').getTime();
      const t0 = new Date(certTodayStr() + 'T12:00:00Z').getTime();
      return Math.round((exp - t0) / 86400000);
    }
    function certFmtDate(ymd) {
      if (!ymd) return '—';
      try { return new Date(ymd + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
      catch (e) { return ymd; }
    }

    function renderCertRows() {
      if (!certRows) return;
      const f = (certFilter && certFilter.value) || 'all';
      let list = cache.certs.slice();
      list = list.filter((c) => {
        const dl = certDaysLeft(c.expiresDate);
        if (dl == null) return false;
        if (f === 'expired') return dl < 0;
        if (f === '90') return dl >= 0 && dl <= 90;
        if (f === '30') return dl >= 0 && dl <= 30;
        if (f === '7')  return dl >= 0 && dl <= 7;
        return true; // all upcoming (incl. expired at bottom)
      });
      if (!list.length) {
        certRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No certifications match this filter.</td></tr>';
        return;
      }
      certRows.innerHTML = list.map((c) => {
        const dl = certDaysLeft(c.expiresDate);
        let dlLabel, dlColor;
        if (dl < 0) { dlLabel = Math.abs(dl) + 'd ago'; dlColor = 'var(--sm-danger)'; }
        else if (dl <= 7) { dlLabel = dl + ' days'; dlColor = '#d9480f'; }
        else if (dl <= 30) { dlLabel = dl + ' days'; dlColor = '#b26a00'; }
        else { dlLabel = dl + ' days'; dlColor = 'var(--sm-ink-muted)'; }
        const contact = [c.email, c.phone].filter(Boolean).join('<br>') || '—';
        const sent = ['90','30','7'].filter((s) => c['reminder' + s + 'SentAt']).map((s) => s + 'd').join(', ') || '—';
        const chLabel = c.channels === 'email' ? 'Email' : c.channels === 'sms' ? 'Text' : 'Email+Text';
        const name = ((c.firstName || '') + ' ' + (c.lastName || '')).trim() || '—';
        const optTag = c.optOut ? ' <span style="color:var(--sm-danger);font-size:11px">(opted out)</span>' : '';
        return '<tr data-sm-cert-id="' + c.id + '" style="cursor:pointer">' +
          '<td>' + name + optTag + '</td>' +
          '<td>' + (c.course || '—') + '</td>' +
          '<td style="white-space:nowrap">' + certFmtDate(c.expiresDate) + '</td>' +
          '<td style="white-space:nowrap;color:' + dlColor + ';font-weight:600">' + dlLabel + '</td>' +
          '<td style="font-size:13px">' + contact + '</td>' +
          '<td style="font-size:13px">' + chLabel + '</td>' +
          '<td style="font-size:13px;color:var(--sm-ink-muted)">' + sent + '</td>' +
          '<td><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-cert-del="' + c.id + '" style="color:var(--sm-danger)">Delete</button></td>' +
        '</tr>';
      }).join('');
    }

    async function loadRenewalsTable() {
      if (!certRows) return;
      certRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try { cache.certs = await ns.fb.adminListCerts({ limit: 2000 }); }
      catch (err) {
        console.error('[admin] list certs failed', err);
        certRows.innerHTML = '<tr><td colspan="8" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      renderCertRows();
    }

    function openCertForm(cert) {
      if (!certForm || !certFormWrap) return;
      certForm.reset();
      certForm.id.value = cert ? cert.id : '';
      if (cert) {
        certForm.firstName.value = cert.firstName || '';
        certForm.lastName.value = cert.lastName || '';
        certForm.email.value = cert.email || '';
        certForm.phone.value = cert.phone || '';
        certForm.course.value = cert.course || '';
        certForm.channels.value = cert.channels || 'both';
        certForm.issueDate.value = cert.issueDate || '';
        certForm.expiresDate.value = cert.expiresDate || '';
        certForm.optOut.checked = !!cert.optOut;
      }
      if (certFormTitle) certFormTitle.textContent = cert ? 'Edit certification' : 'Add certification';
      certFormWrap.hidden = false;
      certFormWrap.open = true;
      if (certMsg) certMsg.textContent = '';
      try { certForm.firstName.focus(); } catch (e) {}
    }

    certNewBtn && certNewBtn.addEventListener('click', () => openCertForm(null));
    certCancelBtn && certCancelBtn.addEventListener('click', () => { if (certFormWrap) certFormWrap.hidden = true; });
    certFilter && certFilter.addEventListener('change', renderCertRows);

    certRows && certRows.addEventListener('click', async (e) => {
      const del = e.target.closest('[data-sm-cert-del]');
      if (del) {
        e.stopPropagation();
        if (!confirm('Delete this certification record? Reminders will stop for this person.')) return;
        del.disabled = true;
        try { await ns.fb.adminDeleteCert(del.dataset.smCertDel); await loadRenewalsTable(); }
        catch (err) { del.disabled = false; alert(err.message || 'Delete failed.'); }
        return;
      }
      const row = e.target.closest('[data-sm-cert-id]');
      if (row) {
        const cert = cache.certs.find((c) => c.id === row.dataset.smCertId);
        if (cert) openCertForm(cert);
      }
    });

    certForm && certForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (!certMsg) return;
      const fd = new FormData(certForm);
      if (!fd.get('issueDate') && !fd.get('expiresDate')) { certMsg.textContent = 'Enter an issue date or an expiration date.'; return; }
      if (!fd.get('email') && !fd.get('phone')) { certMsg.textContent = 'Enter an email or a phone so we can reach them.'; return; }
      certMsg.textContent = 'Saving…';
      try {
        await ns.fb.adminSaveCert(fd.get('id') || null, {
          firstName: fd.get('firstName'), lastName: fd.get('lastName'),
          email: fd.get('email'), phone: fd.get('phone'),
          course: fd.get('course'), channels: fd.get('channels'),
          issueDate: fd.get('issueDate'), expiresDate: fd.get('expiresDate'),
          optOut: !!fd.get('optOut'), source: 'manual'
        });
        certMsg.textContent = '✓ Saved.';
        certFormWrap.hidden = true;
        await loadRenewalsTable();
      } catch (err) {
        console.error('[admin] save cert failed', err);
        certMsg.textContent = err.message || 'Save failed.';
      }
    });

    // CSV import
    certImportBtn && certImportBtn.addEventListener('click', () => { if (certImportWrap) { certImportWrap.hidden = false; certImportWrap.open = true; } });
    certImportCancel && certImportCancel.addEventListener('click', () => { if (certImportWrap) certImportWrap.hidden = true; });
    function parseCertCsv(text) {
      const lines = String(text || '').split(/\r?\n/).filter((l) => l.trim() !== '');
      if (lines.length < 2) return [];
      const splitRow = (line) => {
        // minimal CSV: handles quoted fields with commas
        const out = []; let cur = ''; let q = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (q) { if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else q = false; } else cur += ch; }
          else { if (ch === '"') q = true; else if (ch === ',') { out.push(cur); cur = ''; } else cur += ch; }
        }
        out.push(cur); return out;
      };
      const headers = splitRow(lines[0]).map((h) => h.trim().toLowerCase());
      const key = (h) => ({ firstname: 'firstName', first: 'firstName', lastname: 'lastName', last: 'lastName', email: 'email', phone: 'phone', course: 'course', issuedate: 'issueDate', issued: 'issueDate', completed: 'issueDate', completeddate: 'issueDate', expiresdate: 'expiresDate', expires: 'expiresDate', expiration: 'expiresDate', renewaldate: 'expiresDate', channels: 'channels', channel: 'channels' }[h] || null);
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const cells = splitRow(lines[i]);
        const obj = {};
        headers.forEach((h, idx) => { const k = key(h); if (k) obj[k] = (cells[idx] || '').trim(); });
        if (Object.keys(obj).length) rows.push(obj);
      }
      return rows;
    }
    certImportRun && certImportRun.addEventListener('click', async () => {
      if (!certImportMsg) return;
      const rows = parseCertCsv(certCsv ? certCsv.value : '');
      if (!rows.length) { certImportMsg.textContent = 'Nothing to import — check the header row and data.'; return; }
      certImportRun.disabled = true;
      certImportMsg.textContent = 'Importing ' + rows.length + ' rows…';
      try {
        const res = await ns.fb.adminImportCerts(rows);
        certImportMsg.textContent = '✓ Added ' + res.added + ', skipped ' + res.skipped + (res.errors.length ? ' · ' + res.errors.length + ' errors' : '') + '.';
        if (certCsv) certCsv.value = '';
        await loadRenewalsTable();
      } catch (err) {
        console.error('[admin] import certs failed', err);
        certImportMsg.textContent = err.message || 'Import failed.';
      } finally { certImportRun.disabled = false; }
    });

    /* ---------- BASELINE TRACKER ---------- */
    let baselineLoaded = false;
    const blRoot     = root.querySelector('[data-sm-baseline-root]');
    const blCopyBtn  = root.querySelector('[data-sm-baseline-copy]');
    const blClearBtn = root.querySelector('[data-sm-baseline-clear]');
    const blSaved    = root.querySelector('[data-sm-baseline-saved]');
    let blData = {};
    let blSaveTimer = null;
    const SM_BASELINE_MODEL = [
      { title: 'Sales — Shopify', src: 'Analytics → Reports · range: Last 90 days', metrics: [
        { k: 'sales_total', label: 'Total sales', hint: '“Total sales over time” ($)' },
        { k: 'sales_orders', label: 'Orders', hint: 'order count' },
        { k: 'sales_aov', label: 'Average order value', hint: '“Average order value over time” ($)' },
        { k: 'sales_cvr', label: 'Online store conversion rate', hint: '“Conversion rate over time” (%)' },
        { k: 'sales_sessions', label: 'Sessions', hint: '“Sessions over time”' }
      ]},
      { title: 'Traffic & SEO — GA4', src: 'Engagement → Landing page; Acquisition → Traffic acquisition (Organic Search)', metrics: [
        { k: 'ga_organic', label: 'Organic Search sessions', hint: 'last 90 days' },
        { k: 'ga_organic_conv', label: 'Organic conversions', hint: 'key events / purchases' },
        { k: 'ga_ocala', label: 'BLS Ocala page sessions', hint: 'Landing page report' },
        { k: 'ga_gnv', label: 'BLS Gainesville page sessions', hint: 'Landing page report' },
        { k: 'ga_jax', label: 'BLS Jacksonville page sessions', hint: 'Landing page report' },
        { k: 'ga_lc', label: 'BLS Lake City page sessions', hint: 'Landing page report' }
      ]},
      { title: 'Search visibility — Search Console', src: 'Performance · last 3 months', metrics: [
        { k: 'gsc_clicks', label: 'Total clicks', hint: 'Performance report' },
        { k: 'gsc_impr', label: 'Total impressions', hint: 'Performance report' },
        { k: 'gsc_pos', label: 'Average position', hint: 'lower is better', lowerBetter: true }
      ]},
      { title: 'Local — Google Business Profiles', src: 'Per office: reviews count + average rating', metrics: [
        { k: 'gbp_ocala_rev', label: 'Ocala — reviews', hint: '# of reviews' },
        { k: 'gbp_ocala_rating', label: 'Ocala — rating', hint: 'avg stars' },
        { k: 'gbp_gnv_rev', label: 'Gainesville — reviews', hint: '# of reviews' },
        { k: 'gbp_jax_rev', label: 'Jacksonville — reviews', hint: '# of reviews' },
        { k: 'gbp_lc_rev', label: 'Lake City — reviews', hint: '# of reviews' }
      ]}
    ];
    const BL_PERIODS = ['base', 'p30', 'p90'];
    function blNum(v) { if (v == null) return NaN; const n = parseFloat(String(v).replace(/[^0-9.\-]/g, '')); return isFinite(n) ? n : NaN; }
    function blPct(base, cur, lowerBetter) {
      const b = blNum(base), c = blNum(cur);
      if (!isFinite(b) || !isFinite(c) || b === 0) return { text: '—', cls: '' };
      const pct = (c - b) / Math.abs(b) * 100;
      const good = lowerBetter ? pct < 0 : pct > 0;
      const cls = pct === 0 ? '' : (good ? 'up' : 'down');
      return { text: (pct > 0 ? '+' : '') + pct.toFixed(Math.abs(pct) >= 10 ? 0 : 1) + '%', cls: cls };
    }
    function blEsc(v) { return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
    function blRecompute(k, m) {
      const el = blRoot && blRoot.querySelector('[data-sm-bl-delta="' + k + '"]');
      if (!el) return;
      const latest = (blData[k + '_p90'] != null && blData[k + '_p90'] !== '') ? blData[k + '_p90'] : blData[k + '_p30'];
      const r = blPct(blData[k + '_base'], latest, m && m.lowerBetter);
      el.textContent = r.text;
      el.className = 'sm-bl-delta ' + r.cls;
    }
    function blRender() {
      if (!blRoot) return;
      blRoot.innerHTML = SM_BASELINE_MODEL.map(function (sec) {
        const rows = sec.metrics.map(function (m) {
          const inputs = BL_PERIODS.map(function (p) {
            const id = m.k + '_' + p;
            return '<td style="text-align:right"><input class="sm-bl-val" inputmode="decimal" data-sm-bl-key="' + m.k + '" data-sm-bl-id="' + id + '" value="' + blEsc(blData[id]) + '"></td>';
          }).join('');
          return '<tr><td><span class="sm-bl-metric">' + blEsc(m.label) + '</span><br><span class="sm-bl-hint">' + blEsc(m.hint) + '</span></td>' +
            inputs + '<td style="text-align:center"><span class="sm-bl-delta" data-sm-bl-delta="' + m.k + '">—</span></td></tr>';
        }).join('');
        return '<h3 class="sm-h4" style="margin:18px 0 2px">' + blEsc(sec.title) + '</h3>' +
          '<p class="sm-field__help" style="margin:0 0 8px">' + blEsc(sec.src) + '</p>' +
          '<div style="overflow-x:auto"><table class="sm-table"><thead><tr><th>Metric</th><th style="text-align:right">Baseline</th><th style="text-align:right">+30d</th><th style="text-align:right">+90d</th><th style="text-align:center">Change</th></tr></thead><tbody>' + rows + '</tbody></table></div>';
      }).join('');
      SM_BASELINE_MODEL.forEach(function (s) { s.metrics.forEach(function (m) { blRecompute(m.k, m); }); });
    }
    function blFlashSaved(txt) { if (blSaved) { blSaved.textContent = txt || 'Saved ✓'; clearTimeout(blFlashSaved._t); blFlashSaved._t = setTimeout(function () { blSaved.textContent = 'Saved to your account'; }, 1400); } }
    function blQueueSave() {
      clearTimeout(blSaveTimer);
      blSaveTimer = setTimeout(async function () {
        try { await ns.fb.adminSaveBaseline(blData); blFlashSaved(); }
        catch (e) { console.error('[baseline] save failed', e); if (blSaved) blSaved.textContent = 'Save failed'; }
      }, 700);
    }
    async function loadBaseline() {
      if (!blRoot) return;
      try { blData = await ns.fb.adminGetBaseline(); }
      catch (e) { console.error('[baseline] load failed', e); blData = {}; }
      blRender();
      ['d_base', 'd_30', 'd_90'].forEach(function (id) {
        const el = root.querySelector('[data-sm-bl-date="' + id + '"]');
        if (el && blData[id]) el.value = blData[id];
      });
      if (blSaved) blSaved.textContent = 'Saved to your account';
    }
    blRoot && blRoot.addEventListener('input', function (e) {
      const t = e.target;
      if (!t.classList || !t.classList.contains('sm-bl-val')) return;
      blData[t.dataset.smBlId] = t.value;
      const m = (function () { let f = null; SM_BASELINE_MODEL.forEach(function (s) { s.metrics.forEach(function (x) { if (x.k === t.dataset.smBlKey) f = x; }); }); return f; })();
      blRecompute(t.dataset.smBlKey, m);
      blQueueSave();
    });
    root.querySelectorAll('[data-sm-bl-date]').forEach(function (el) {
      el.addEventListener('input', function () { blData[el.dataset.smBlDate] = el.value; blQueueSave(); });
    });
    blCopyBtn && blCopyBtn.addEventListener('click', function () {
      const lines = ['STAT Med — Baseline snapshot',
        'Baseline: ' + (blData.d_base || '?') + ' | +30d: ' + (blData.d_30 || '?') + ' | +90d: ' + (blData.d_90 || '?'), ''];
      SM_BASELINE_MODEL.forEach(function (s) {
        lines.push('## ' + s.title);
        s.metrics.forEach(function (m) {
          const b = blData[m.k + '_base'] || '', p30 = blData[m.k + '_p30'] || '', p90 = blData[m.k + '_p90'] || '';
          if (b === '' && p30 === '' && p90 === '') return;
          lines.push('- ' + m.label + ': baseline=' + (b || '—') + ', +30d=' + (p30 || '—') + ', +90d=' + (p90 || '—'));
        });
      });
      const text = lines.join('\n');
      const done = function () { blFlashSaved('Copied ✓'); };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done, function () {
          try { const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta); done(); } catch (e) { if (blSaved) blSaved.textContent = 'Copy failed'; }
        });
      }
    });
    blClearBtn && blClearBtn.addEventListener('click', function () {
      if (blClearBtn.dataset.armed !== '1') { blClearBtn.dataset.armed = '1'; blClearBtn.textContent = 'Tap again to clear'; setTimeout(function () { blClearBtn.dataset.armed = '0'; blClearBtn.textContent = 'Clear all'; }, 2500); return; }
      blData = {}; blRender();
      root.querySelectorAll('[data-sm-bl-date]').forEach(function (el) { el.value = ''; });
      blQueueSave(); blClearBtn.dataset.armed = '0'; blClearBtn.textContent = 'Clear all';
    });

    let oriLoaded = false;
    const oriRows = root.querySelector('[data-sm-ori-rows]');
    const oriNewBtn = root.querySelector('[data-sm-ori-new]');
    const oriFilterAgency = root.querySelector('[data-sm-ori-filter-agency]');
    cache.oris = [];

    function refreshOriAgencyFilter() {
      if (!oriFilterAgency) return;
      const current = oriFilterAgency.value;
      const agencies = Array.from(new Set(cache.oris.map((o) => o.agency).filter(Boolean))).sort();
      oriFilterAgency.innerHTML = '<option value="">All agencies</option>' +
        agencies.map((a) => '<option value="' + a.replace(/"/g,'&quot;') + '">' + a + '</option>').join('');
      if (current) oriFilterAgency.value = current;
    }

    async function loadOriTable() {
      if (!oriRows) return;
      oriRows.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">Loading…</td></tr>';
      try {
        cache.oris = await ns.fb.adminListOris();
      } catch (err) {
        console.error('[admin] list ORIs failed', err);
        oriRows.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:20px;color:var(--sm-danger)">Unable to load. ' + (err.message || '') + '</td></tr>';
        return;
      }
      refreshOriAgencyFilter();
      if (!cache.oris.length) {
        oriRows.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No ORIs yet. Click + Add ORI.</td></tr>';
        return;
      }
      const filterAgency = oriFilterAgency ? oriFilterAgency.value : '';
      const rows = cache.oris.filter((o) => !filterAgency || o.agency === filterAgency);
      if (!rows.length) {
        oriRows.innerHTML = '<tr><td colspan="7" style="text-align:center;padding:20px;color:var(--sm-ink-muted)">No ORIs match.</td></tr>';
        return;
      }
      oriRows.innerHTML = rows.map((o) => {
        return '<tr>' +
          '<td><strong>' + (o.agency || '—') + '</strong></td>' +
          '<td>' + (o.label || '—') + '</td>' +
          '<td style="font-family:monospace">' + (o.oriNumber || '—') + '</td>' +
          '<td>' + (o.price || '—') + '</td>' +
          '<td style="font-family:monospace;font-size:11px">' + (o.shopifyVariantId || '—') + '</td>' +
          '<td><span class="sm-table__status sm-status--' + (o.active === false ? 'cancelled' : 'confirmed') + '">' + (o.active === false ? 'Inactive' : 'Active') + '</span></td>' +
          '<td style="white-space:nowrap">' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-ori-edit="' + o.id + '">Edit</button> ' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-sm-ori-del="' + o.id + '" style="color:var(--sm-danger)">Delete</button>' +
          '</td>' +
        '</tr>';
      }).join('');
    }

    function oriModalHtml(o) {
      return '<form class="sm-form" data-sm-ori-form>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>Agency</label><input type="text" name="agency" value="' + ((o.agency || '').replace(/"/g,'&quot;')) + '" placeholder="AHCA" required></div>' +
          '<div class="sm-field"><label>Display label</label><input type="text" name="label" value="' + ((o.label || '').replace(/"/g,'&quot;')) + '" placeholder="AHCA — Health Care"></div>' +
        '</div>' +
        '<div class="sm-form__row">' +
          '<div class="sm-field"><label>ORI number</label><input type="text" name="oriNumber" value="' + ((o.oriNumber || '').replace(/"/g,'&quot;')) + '" placeholder="EAHCA1234Z" required></div>' +
          '<div class="sm-field"><label>Price (display)</label><input type="text" name="price" value="' + ((o.price || '').replace(/"/g,'&quot;')) + '" placeholder="$89"></div>' +
        '</div>' +
        '<div class="sm-field"><label>Shopify variant ID</label><input type="text" name="shopifyVariantId" value="' + ((o.shopifyVariantId || '').replace(/"/g,'&quot;')) + '" placeholder="53160491614508"><div class="sm-field__help">The Background Check product variant with this ORI’s price. Find it in the product’s variant URL.</div></div>' +
        '<label class="sm-book__chk sm-mt-16"><input type="checkbox" name="active"' + (o.active === false ? '' : ' checked') + '><span>Active (shown to customers)</span></label>' +
        '<div class="sm-flex sm-gap-12" style="align-items:center;flex-wrap:wrap;margin-top:14px">' +
          '<button type="submit" class="sm-btn sm-btn--primary">Save ORI</button>' +
          '<span class="sm-field__help" data-sm-ori-msg></span>' +
        '</div>' +
      '</form>';
    }

    function openOriModal(id) {
      if (!modal || !modalBody) return;
      const o = id ? (cache.oris.find((x) => x.id === id) || {}) : { active: true };
      modalTitle && (modalTitle.textContent = id ? ('Edit ORI — ' + (o.label || o.oriNumber || '')) : 'Add ORI');
      modalBody.innerHTML = oriModalHtml(o);
      modal.hidden = false;
      const form = modalBody.querySelector('[data-sm-ori-form]');
      const msg  = modalBody.querySelector('[data-sm-ori-msg]');
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        msg.textContent = 'Saving…';
        try {
          const fd = new FormData(form);
          const agency = (fd.get('agency') || '').toString().trim();
          const oriNumber = (fd.get('oriNumber') || '').toString().trim();
          if (!agency) throw new Error('Agency is required.');
          if (!oriNumber) throw new Error('ORI number is required.');
          const data = {
            agency: agency,
            label: (fd.get('label') || '').toString().trim() || agency,
            oriNumber: oriNumber,
            price: (fd.get('price') || '').toString().trim(),
            shopifyVariantId: (fd.get('shopifyVariantId') || '').toString().trim(),
            active: !!fd.get('active')
          };
          await ns.fb.adminSaveOri(id || null, data);
          msg.textContent = '✓ Saved.';
          await loadOriTable();
          setTimeout(closeModal, 500);
        } catch (err) {
          console.error('[admin] save ORI failed', err);
          msg.textContent = err.message || 'Save failed.';
        }
      });
    }

    oriRows && oriRows.addEventListener('click', async (e) => {
      const editBtn = e.target.closest('[data-sm-ori-edit]');
      const delBtn  = e.target.closest('[data-sm-ori-del]');
      if (editBtn) openOriModal(editBtn.dataset.smOriEdit);
      else if (delBtn) {
        if (!confirm('Delete this ORI?')) return;
        delBtn.disabled = true;
        try { await ns.fb.adminDeleteOri(delBtn.dataset.smOriDel); await loadOriTable(); }
        catch (err) { delBtn.disabled = false; alert(err.message || 'Delete failed.'); }
      }
    });
    oriNewBtn && oriNewBtn.addEventListener('click', () => openOriModal(null));
    oriFilterAgency && oriFilterAgency.addEventListener('change', loadOriTable);

    /* ---------- ANALYTICS TAB ---------- */
    let analyticsLoaded = false;
    /* ---------- ANALYTICS DASHBOARD ---------- */
    const anxRoot   = root.querySelector('[data-sm-analytics-root]');
    const anxEditor = root.querySelector('[data-sm-anx-editor]');
    const anxStatus = root.querySelector('[data-sm-analytics-status]');
    const anxFromIn = root.querySelector('[data-anx-from]');
    const anxToIn   = root.querySelector('[data-anx-to]');
    const anxApply  = root.querySelector('[data-anx-apply]');
    const anxPresets= root.querySelector('[data-sm-anx-presets]');
    const anxRefresh= root.querySelector('[data-sm-analytics-refresh]');
    const anxEditBtn= root.querySelector('[data-sm-anx-edit]');
    function anxYmd(dt){ return dt.getFullYear()+'-'+String(dt.getMonth()+1).padStart(2,'0')+'-'+String(dt.getDate()).padStart(2,'0'); }
    // Default range: this calendar month → today.
    let anxFrom = anxYmd(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    let anxTo   = smTodayLocal();
    let anxConfig = { grossTargetMonthly:0, locationTargetsMonthly:{}, productTargetsMonthly:{}, visitsByMonth:{} };
    let anxCur = null, anxPrev = null, anxRangeObj = null;

    const C_UP='#0f9d6b', C_DOWN='#d6453f', C_WARN='#c1841a';
    function fmtMoney(n, currency){ const c=currency||'USD'; try{ return new Intl.NumberFormat(undefined,{style:'currency',currency:c,maximumFractionDigits:0}).format(n||0);}catch(e){return '$'+Math.round(n||0).toLocaleString();} }
    function fmtMoney2(n){ try{ return new Intl.NumberFormat(undefined,{style:'currency',currency:'USD'}).format(n||0);}catch(e){return '$'+(n||0).toFixed(2);} }
    function anxColor(pct){ return pct>=100?C_UP: pct>=85?'var(--sm-brand)':C_WARN; }
    function anxWash(pct){ return pct>=100?'rgba(15,157,107,.12)': pct>=85?'var(--sm-brand-tint,rgba(0,113,255,.1))':'rgba(193,132,26,.14)'; }
    function delta(cur,prev){ if(!prev) return {up:cur>=0,pct: cur>0?100:0}; const p=(cur-prev)/prev*100; return {up:p>=0,pct:Math.abs(p)}; }
    function deltaChip(cur,prev){ const d=delta(cur,prev); return '<span class="sm-anx-delta" style="color:'+(d.up?C_UP:C_DOWN)+';background:'+(d.up?'rgba(15,157,107,.12)':'rgba(214,69,63,.12)')+'">'+(d.up?'▲':'▼')+' '+d.pct.toFixed(1)+'%</span>'; }

    // Date ranges + pace for each period (calendar-aware; targets are monthly).
    function anxRange(period){
      const now=new Date(), y=now.getFullYear(), m=now.getMonth(), d=now.getDate();
      const fmtD=function(dt){ return dt.toLocaleDateString(undefined,{month:'short',day:'numeric'}); };
      let curFrom, curTo=now, prevFrom, prevTo, elapsed, factor, label, title;
      if(period==='week'){
        const dow=(now.getDay()+6)%7; curFrom=new Date(y,m,d-dow);
        prevTo=new Date(curFrom.getTime()-1); prevFrom=new Date(curFrom.getTime()-7*86400000);
        elapsed=Math.min(1,((now-curFrom)/86400000+1)/7); factor=7/30.44; label='this week';
        const weekEnd=new Date(curFrom.getTime()+6*86400000);
        title='Week of '+fmtD(curFrom)+' – '+fmtD(weekEnd)+' (Mon–Sun)';
      } else if(period==='quarter'){
        const q=Math.floor(m/3); curFrom=new Date(y,q*3,1);
        prevFrom=new Date(y,q*3-3,1); prevTo=new Date(y,q*3,1,0,0,-1);
        const daysIn=(new Date(y,q*3+3,0)-curFrom)/86400000; elapsed=Math.min(1,((now-curFrom)/86400000)/daysIn); factor=3; label='this quarter';
        title='Q'+(q+1)+' '+y;
      } else if(period==='year'){
        curFrom=new Date(y,0,1); prevFrom=new Date(y-1,0,1); prevTo=new Date(y,0,1,0,0,-1);
        elapsed=Math.min(1,((now-curFrom)/86400000)/365); factor=12; label='this year';
        title=String(y);
      } else {
        curFrom=new Date(y,m,1); prevFrom=new Date(y,m-1,1); prevTo=new Date(y,m,1,0,0,-1);
        elapsed=Math.min(1,d/new Date(y,m+1,0).getDate()); factor=1; label='this month';
        const mEnd=new Date(y,m+1,0);
        title=now.toLocaleDateString(undefined,{month:'long',year:'numeric'})+' ('+fmtD(curFrom)+' – '+fmtD(mEnd)+')';
      }
      const rangeText=fmtD(curFrom)+' – '+fmtD(curTo)+', '+curTo.getFullYear();
      return {curFrom,curTo,prevFrom,prevTo,elapsed,factor,label,title,rangeText};
    }

    // Build a range object from an arbitrary from/to (YYYY-MM-DD). Previous
    // period = the same-length span immediately before. Targets scale by the
    // range's length in months; pace is how much of the range has elapsed.
    function anxCustomRange(fromStr, toStr){
      let from=new Date(fromStr+'T00:00:00'), to=new Date(toStr+'T23:59:59');
      if(isNaN(from.getTime())||isNaN(to.getTime())||to<from){ return anxRange('month'); }
      const now=new Date();
      const spanMs=to-from;
      const prevTo=new Date(from.getTime()-1);
      const prevFrom=new Date(from.getTime()-spanMs-1);
      const rangeDays=Math.round(spanMs/86400000)+1;
      const factor=rangeDays/30.44;
      const effEnd=now<to?now:to;
      let elapsed=spanMs>0?((effEnd-from)/spanMs):1; elapsed=Math.max(0.02,Math.min(1,elapsed));
      const fmtD=function(dt){ return dt.toLocaleDateString(undefined,{month:'short',day:'numeric'}); };
      const title=fmtD(from)+' – '+fmtD(to)+', '+to.getFullYear();
      return {curFrom:from, curTo:effEnd, prevFrom, prevTo, elapsed, factor, label:'this range', title, rangeText:title};
    }
    function anxMonthKey(dt){ return dt.getFullYear()+'-'+String(dt.getMonth()+1).padStart(2,'0'); }
    function anxVisits(from,to){
      let total=0; const cur=new Date(from.getFullYear(),from.getMonth(),1);
      while(cur<=to){
        const v=Number(anxConfig.visitsByMonth[anxMonthKey(cur)]||0);
        const mStart=new Date(cur.getFullYear(),cur.getMonth(),1), mEnd=new Date(cur.getFullYear(),cur.getMonth()+1,0);
        const ovStart=new Date(Math.max(mStart,from)), ovEnd=new Date(Math.min(mEnd,to));
        const ovDays=Math.max(0,(ovEnd-ovStart)/86400000+1);
        total+=v*(ovDays/mEnd.getDate());
        cur.setMonth(cur.getMonth()+1);
      }
      return Math.round(total);
    }

    async function loadAnalytics(){
      if(!anxRoot) return;
      anxRangeObj=anxCustomRange(anxFrom, anxTo);
      if(anxFromIn) anxFromIn.value=anxFrom; if(anxToIn) anxToIn.value=anxTo;
      anxStatus.textContent='Loading from Shopify…'; anxStatus.style.color='var(--sm-ink-muted)';
      anxRoot.style.opacity='.5';
      try{
        anxConfig = await ns.fb.adminGetAnalyticsConfig();
        const rng=anxRangeObj;
        const [cur,prev]=await Promise.all([
          ns.fb.analyticsCall({action:'summary',from:rng.curFrom.toISOString(),to:rng.curTo.toISOString()}),
          ns.fb.analyticsCall({action:'summary',from:rng.prevFrom.toISOString(),to:rng.prevTo.toISOString()})
        ]);
        anxCur=cur; anxPrev=prev;
      }catch(err){
        anxRoot.style.opacity='1';
        anxStatus.textContent='✗ '+(err.message||'Failed to load analytics'); anxStatus.style.color='var(--sm-danger)';
        return;
      }
      anxRoot.style.opacity='1';
      anxStatus.innerHTML='<strong>'+esc(anxRangeObj.title||'')+'</strong> · '+(anxCur.orders||0)+' orders. Live Shopify sales; targets & visits are yours.';
      anxStatus.style.color='var(--sm-ink-muted)';
      renderAnalytics();
    }

    function anxKpi(label, valStr, cur, prev, manual){
      return '<div class="sm-card sm-anx-kpi">'+
        '<span class="sm-anx-lbl">'+label+(manual?' <span class="sm-anx-manual">manual</span>':'')+'</span>'+
        '<div class="sm-anx-val">'+valStr+'</div>'+
        '<div class="sm-anx-foot">'+deltaChip(cur,prev)+'<span class="sm-anx-vs">vs prior period</span></div>'+
      '</div>';
    }

    function anxTargetRows(items, targetsMap){
      // items: [{name, revenue, colorVar?}]
      return items.map(function(it){
        const target=Number(targetsMap[it.name]||0)*anxRangeObj.factor;
        const pct= target>0? Math.round(it.revenue/target*100) : 0;
        const barCol= pct>=100? C_UP : pct>=85? (it.colorVar? 'var('+it.colorVar+')':'var(--sm-brand)') : C_WARN;
        const tgtTxt= target>0? ('of '+fmtMoney(target)+' target') : 'no target set';
        const chip= target>0? '<span class="sm-anx-pctchip" style="color:'+anxColor(pct)+';background:'+anxWash(pct)+'">'+pct+'%</span>' : '';
        return '<div class="sm-anx-trow">'+
          '<div class="sm-anx-thead"><span class="sm-anx-rname">'+(it.colorVar?'<span class="sm-anx-dot" style="background:var('+it.colorVar+')"></span>':'')+esc(it.name)+'</span>'+chip+'</div>'+
          '<div class="sm-anx-trk"><i style="width:'+Math.min(100,pct)+'%;background:'+barCol+'"></i></div>'+
          '<div class="sm-anx-rval"><b>'+fmtMoney(it.revenue)+'</b> <span class="sm-anx-tgt">'+tgtTxt+'</span></div>'+
        '</div>';
      }).join('');
    }

    const LOC_COLORS=['--sm-brand','--sm-loc-jax','--sm-loc-gnv','--sm-loc-oca'];
    var anxSubTab = 'overview', anxChartMetric = 'revenue';
    function renderAnalytics(){
      if(!anxCur){ return; }
      root.querySelectorAll('[data-anx-sub]').forEach(function(b){ b.classList.toggle('is-active', b.dataset.anxSub===anxSubTab); });
      if(anxSubTab==='locprod') renderAnxLocProd();
      else if(anxSubTab==='products') renderAnxProducts();
      else if(anxSubTab==='marketing') renderAnxMarketing();
      else if(anxSubTab==='funnel') renderAnxFunnel();
      else renderAnxOverview();
    }

    // Every product sold, vs its monthly target (scaled to the range).
    function renderAnxProducts(){
      const cur=anxCur;
      const prodNames={}; (cur.topProducts||[]).forEach(function(p){ prodNames[p.title]=p.revenue; });
      Object.keys(anxConfig.productTargetsMonthly||{}).forEach(function(k){ if(!(k in prodNames)) prodNames[k]=0; });
      const prodItems=Object.keys(prodNames).map(function(n){return {name:n, revenue:prodNames[n]};}).sort(function(a,b){return b.revenue-a.revenue;});
      const unitMap={}; (cur.topProducts||[]).forEach(function(p){ unitMap[p.title]=p.units||0; });
      anxRoot.innerHTML =
        '<div class="sm-card sm-anx-panel"><div class="sm-anx-ph"><h3>Sales by product · vs target</h3><span class="sm-anx-note">'+prodItems.length+' products · '+anxRangeObj.title+'</span></div>'+
          '<div class="sm-anx-rows">'+(prodItems.length? anxTargetRows(prodItems, anxConfig.productTargetsMonthly) : '<div class="sm-anx-empty">No product sales in this range.</div>')+'</div></div>'+
        '<div class="sm-card sm-anx-panel" style="margin-top:14px"><div class="sm-anx-ph"><h3>Units sold</h3></div>'+
          '<div style="overflow-x:auto;padding:6px 12px 12px"><table class="sm-table" style="font-size:13px"><thead><tr><th>Product / variant</th><th style="text-align:right">Units</th><th style="text-align:right">Revenue</th></tr></thead><tbody>'+
          (cur.topProducts||[]).map(function(p){ return '<tr><td>'+esc(p.title)+'</td><td class="tnum" style="text-align:right">'+(p.units||0)+'</td><td class="tnum" style="text-align:right">'+fmtMoney(p.revenue)+'</td></tr>'; }).join('')+
          '</tbody></table></div></div>';
    }

    // Advertising performance grid (shares the Metrics tab's data + add-month).
    async function renderAnxMarketing(){
      anxRoot.innerHTML='<p class="sm-field__help" style="padding:16px">Loading…</p>';
      await ensureMetrics();
      renderMetricsInto(anxRoot, [{ title:'Advertising performance', metrics:AD_METRICS }], 'Advertising performance', 'Manually-entered ad metrics by month. Add a column each month with “+ Add month”. Also editable under Backend → Metrics.');
    }
    // Sales-funnel grid (manual — Shopify funnel rates aren't per-month via API).
    async function renderAnxFunnel(){
      anxRoot.innerHTML='<p class="sm-field__help" style="padding:16px">Loading…</p>';
      await ensureMetrics();
      renderMetricsInto(anxRoot, [{ title:'Sales funnel', metrics:FUNNEL_METRICS }], 'Sales funnel', 'Manually-entered funnel rates by month (Shopify shows these but not per-month via the API). Add a column each month with “+ Add month”. Also editable under Backend → Metrics.');
    }

    function anxBreakdownCard(cur){
      const b=cur.salesBreakdown; if(!b) return '';
      const row=function(label,val,neg,strong){ return '<div class="smb-sumrow'+(strong?' total':'')+'" style="padding:7px 0"><span'+(strong?' style="font-weight:800"':'')+'>'+label+'</span><span class="tnum"'+(strong?' style="font-weight:800"':(neg?' style="color:var(--sm-danger)"':''))+'>'+(neg&&val>0?'-':'')+fmtMoney(val)+'</span></div>'; };
      return '<div class="sm-card sm-anx-panel"><div class="sm-anx-ph"><h3>Total sales breakdown</h3><span class="sm-anx-note">this period</span></div>'+
        '<div style="padding:8px 17px 16px">'+
          row('Gross sales', b.grossSales)+
          row('Discounts', b.discounts, true)+
          row('Returns / reversals', b.returns, true)+
          row('Net sales', b.netSales, false, false)+
          row('Shipping charges', b.shipping)+
          row('Taxes', b.taxes)+
          row('Total sales', b.totalSales, false, true)+
        '</div></div>';
    }
    function anxGrossCard(cur, rng){
      const gTarget=Number(anxConfig.grossTargetMonthly||0)*rng.factor;
      const gPct= gTarget>0? Math.round(cur.revenue/gTarget*100):0;
      const proj= rng.elapsed>0? cur.revenue/rng.elapsed:cur.revenue;
      const ahead= gPct>=Math.round(rng.elapsed*100);
      return '<div class="sm-card sm-anx-panel"><div class="sm-anx-ph"><h3>Gross sales target</h3></div>'+
        '<div class="sm-anx-gross">'+
          '<div><span class="sm-anx-gross-val">'+fmtMoney(cur.revenue)+'</span><span class="sm-anx-gross-of">of '+(gTarget>0?fmtMoney(gTarget):'—')+'</span></div>'+
          '<div class="sm-anx-bar2"><i style="width:'+Math.min(100,gPct)+'%;background:'+(gPct>=100?C_UP:gPct>=85?'linear-gradient(90deg,var(--sm-brand),#0a4fc4)':C_WARN)+'"></i></div>'+
          (gTarget>0?
            '<div class="sm-anx-gmeta"><span class="sm-anx-pill" style="color:'+(ahead?C_UP:C_WARN)+';background:'+(ahead?'rgba(15,157,107,.12)':'rgba(193,132,26,.14)')+'">'+(ahead?'▲ Ahead of pace':'▼ Behind pace')+'</span><span style="font-weight:800;color:'+anxColor(gPct)+'">'+gPct+'% to goal</span></div>'+
            '<div class="sm-anx-proj">Pace projects <b>'+fmtMoney(proj)+'</b> by period end · '+Math.round(rng.elapsed*100)+'% elapsed.</div>'
            : '<div class="sm-anx-proj">Set a monthly gross target with “Targets &amp; visits”.</div>')+
        '</div></div>';
    }

    function renderAnxOverview(){
      const rng=anxRangeObj, cur=anxCur, prev=anxPrev||{};
      const visitsCur=anxVisits(rng.curFrom,rng.curTo), visitsPrev=anxVisits(rng.prevFrom,rng.prevTo);
      const convCur= visitsCur? (cur.orders/visitsCur*100):0, convPrev= visitsPrev? ((prev.orders||0)/visitsPrev*100):0;

      // Metric-selectable daily bar chart (Y = metric, X = date).
      const series=cur.dailySeries||[];
      const METRICS={ revenue:{label:'Revenue',fmt:fmtMoney,val:function(d){return d.revenue||0;}},
                      orders:{label:'Orders',fmt:function(n){return String(Math.round(n));},val:function(d){return d.orders||0;}},
                      aov:{label:'Avg order',fmt:fmtMoney2,val:function(d){return d.orders? d.revenue/d.orders:0;}} };
      const md=METRICS[anxChartMetric]||METRICS.revenue;
      const vals=series.map(md.val); const maxD=Math.max(1,Math.max.apply(null,vals.length?vals:[0]));
      const bars=series.map(function(d){ const v=md.val(d); const h=Math.max(3,Math.round(v/maxD*100));
        return '<div class="sm-anx-bar" title="'+d.date+' · '+md.fmt(v)+'"><i style="height:'+h+'%"></i></div>'; }).join('');
      const metricBtns=['revenue','orders','aov'].map(function(k){ return '<button type="button" class="sm-btn sm-btn--sm '+(anxChartMetric===k?'sm-btn--primary':'sm-btn--light')+'" data-anx-metric="'+k+'">'+METRICS[k].label+'</button>'; }).join('');

      anxRoot.innerHTML =
        '<div class="sm-anx-kpis">'+
          anxKpi('Revenue', fmtMoney(cur.revenue), cur.revenue, prev.revenue||0)+
          anxKpi('Orders', String(cur.orders||0), cur.orders||0, prev.orders||0)+
          anxKpi('Avg. order', fmtMoney2(cur.aov), cur.aov||0, prev.aov||0)+
          anxKpi('Sessions', visitsCur.toLocaleString(), visitsCur, visitsPrev, true)+
          anxKpi('Conversion', convCur.toFixed(2)+'%', convCur, convPrev, true)+
        '</div>'+
        '<div class="sm-anx-grid">'+
          '<div class="sm-card sm-anx-panel"><div class="sm-anx-ph"><h3>'+md.label+' by day</h3><div class="sm-flex sm-gap-12" style="flex-wrap:wrap">'+metricBtns+'</div></div>'+
            '<div class="sm-anx-chart">'+(bars||'<div class="sm-anx-empty">No sales in this period yet.</div>')+'</div></div>'+
          anxGrossCard(cur, rng)+
        '</div>'+
        '<div class="sm-anx-grid2">'+
          anxBreakdownCard(cur)+
          '<div class="sm-card sm-anx-panel"><div class="sm-anx-ph"><h3>Website &amp; conversion</h3><span class="sm-anx-manual">manual sessions</span></div>'+
            '<div class="sm-anx-funnel">'+
              anxFunnelStep('Sessions', visitsCur, visitsCur)+
              anxFunnelStep('Completed orders', cur.orders||0, visitsCur)+
              '<div class="sm-anx-proj" style="padding:2px 4px">Conversion: <b>'+convCur.toFixed(2)+'%</b> of sessions became orders '+anxRangeObj.label+'.</div>'+
            '</div></div>'+
        '</div>';
      anxRoot.querySelectorAll('[data-anx-metric]').forEach(function(b){ b.addEventListener('click', function(){ anxChartMetric=b.dataset.anxMetric; renderAnalytics(); }); });
    }

    function renderAnxLocProd(){
      const cur=anxCur;
      const locNames={}; (cur.locations||[]).forEach(function(l){locNames[l.location]=l.revenue;});
      Object.keys(anxConfig.locationTargetsMonthly||{}).forEach(function(k){ if(!(k in locNames)) locNames[k]=0; });
      const locItems=Object.keys(locNames).map(function(n,i){return {name:n, revenue:locNames[n], colorVar:LOC_COLORS[i%LOC_COLORS.length]};}).sort(function(a,b){return b.revenue-a.revenue;});
      const lp=cur.locationProducts||[];
      const perLoc=lp.slice().sort(function(a,b){ const ar=a.products.reduce(function(s,p){return s+p.revenue;},0), br=b.products.reduce(function(s,p){return s+p.revenue;},0); return br-ar; }).map(function(L){
        const tot=L.products.reduce(function(s,p){return s+p.revenue;},0);
        return '<div class="sm-card sm-anx-panel"><div class="sm-anx-ph"><h3>'+esc(L.location)+' — products</h3><span class="sm-anx-note">'+fmtMoney(tot)+'</span></div>'+
          '<div style="overflow-x:auto;padding:6px 12px 12px"><table class="sm-table" style="font-size:13px"><thead><tr><th>Product / variant</th><th style="text-align:right">Units</th><th style="text-align:right">Revenue</th></tr></thead><tbody>'+
          L.products.map(function(p){return '<tr><td>'+esc(p.title)+'</td><td class="tnum" style="text-align:right">'+p.units+'</td><td class="tnum" style="text-align:right">'+fmtMoney(p.revenue)+'</td></tr>';}).join('')+
          '</tbody></table></div></div>';
      }).join('');
      anxRoot.innerHTML =
        '<div class="sm-card sm-anx-panel"><div class="sm-anx-ph"><h3>Sales by location · vs target</h3></div><div class="sm-anx-rows">'+
          (locItems.length?anxTargetRows(locItems,anxConfig.locationTargetsMonthly):'<div class="sm-anx-empty">No location sales yet.</div>')+'</div></div>'+
        (perLoc || '<div class="sm-card sm-anx-panel" style="padding:16px"><p class="sm-anx-empty">No per-location product sales in this period. (Orders need the “Location” line-item property, which the booking flow adds.)</p></div>');
    }
    function anxFunnelStep(label,val,top){ const w=top?Math.round(val/top*100):0;
      return '<div class="sm-anx-fstep"><span class="sm-anx-fl">'+label+'</span><div class="sm-anx-fbar"><span class="sm-anx-ffill" style="width:'+w+'%"></span><b>'+val.toLocaleString()+'</b></div></div>'; }

    /* ----- Targets & visits editor ----- */
    function openAnxEditor(){
      if(!anxEditor) return;
      const locKeys={}; (anxCur&&anxCur.locations||[]).forEach(function(l){locKeys[l.location]=1;});
      Object.keys(anxConfig.locationTargetsMonthly||{}).forEach(function(k){locKeys[k]=1;});
      const prodKeys={}; (anxCur&&anxCur.topProducts||[]).forEach(function(p){prodKeys[p.title]=1;});
      Object.keys(anxConfig.productTargetsMonthly||{}).forEach(function(k){prodKeys[k]=1;});
      // last 6 months for visits
      const months=[]; const now=new Date();
      for(let i=0;i<6;i++){ const dt=new Date(now.getFullYear(),now.getMonth()-i,1); months.push(anxMonthKey(dt)); }
      const locRows=Object.keys(locKeys).map(function(n){return '<div class="sm-field"><label>'+esc(n)+'</label><input type="number" min="0" step="50" data-anx-loc="'+esc(n)+'" value="'+(anxConfig.locationTargetsMonthly[n]||'')+'" placeholder="0"></div>';}).join('')||'<p class="sm-field__help">Location targets appear here once you have sales with a Location property.</p>';
      const prodRows=Object.keys(prodKeys).map(function(n){return '<div class="sm-field"><label>'+esc(n)+'</label><input type="number" min="0" step="50" data-anx-prod="'+esc(n)+'" value="'+(anxConfig.productTargetsMonthly[n]||'')+'" placeholder="0"></div>';}).join('')||'<p class="sm-field__help">Product targets appear here once products have sold.</p>';
      const visRows=months.map(function(k){ const nm=new Date(k+'-01T12:00:00').toLocaleDateString(undefined,{month:'short',year:'numeric'}); return '<div class="sm-field"><label>'+nm+'</label><input type="number" min="0" step="10" data-anx-vis="'+k+'" value="'+(anxConfig.visitsByMonth[k]||'')+'" placeholder="0"></div>';}).join('');
      anxEditor.hidden=false;
      anxEditor.innerHTML='<div class="sm-card" style="padding:20px;margin-top:16px" data-anx-form>'+
        '<div class="sm-flex" style="justify-content:space-between;align-items:center"><h3 class="sm-h4" style="margin:0">Targets &amp; website visits</h3><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-anx-close>Close</button></div>'+
        '<p class="sm-field__help">Enter <strong>monthly</strong> figures — the dashboard scales them to week / quarter / year automatically.</p>'+
        '<div class="sm-field sm-mt-16" style="max-width:280px"><label>Gross sales target (per month)</label><input type="number" min="0" step="100" data-anx-gross value="'+(anxConfig.grossTargetMonthly||'')+'" placeholder="0"></div>'+
        '<h4 class="sm-h4 sm-mt-24" style="margin-bottom:4px">Per-location target / month</h4><div class="sm-form__row" style="flex-wrap:wrap">'+locRows+'</div>'+
        '<h4 class="sm-h4 sm-mt-24" style="margin-bottom:4px">Per-product target / month</h4><div class="sm-form__row" style="flex-wrap:wrap">'+prodRows+'</div>'+
        '<h4 class="sm-h4 sm-mt-24" style="margin-bottom:4px">Website visits / month <span class="sm-anx-manual">manual</span></h4><div class="sm-form__row" style="flex-wrap:wrap">'+visRows+'</div>'+
        '<div class="sm-flex sm-gap-12 sm-mt-24" style="align-items:center"><button type="button" class="sm-btn sm-btn--primary" data-anx-save>Save</button><span class="sm-field__help" data-anx-msg></span></div>'+
      '</div>';
      anxEditor.querySelector('[data-anx-close]').addEventListener('click',function(){ anxEditor.hidden=true; anxEditor.innerHTML=''; });
      anxEditor.querySelector('[data-anx-save]').addEventListener('click',async function(){
        const msg=anxEditor.querySelector('[data-anx-msg]'); msg.textContent='Saving…';
        const gross=parseFloat(anxEditor.querySelector('[data-anx-gross]').value)||0;
        const locT={}, prodT={}, vis={};
        anxEditor.querySelectorAll('[data-anx-loc]').forEach(function(i){ const v=parseFloat(i.value); if(v>0) locT[i.dataset.anxLoc]=v; });
        anxEditor.querySelectorAll('[data-anx-prod]').forEach(function(i){ const v=parseFloat(i.value); if(v>0) prodT[i.dataset.anxProd]=v; });
        Object.assign(vis, anxConfig.visitsByMonth||{});
        anxEditor.querySelectorAll('[data-anx-vis]').forEach(function(i){ const v=parseFloat(i.value); if(v>0) vis[i.dataset.anxVis]=v; else delete vis[i.dataset.anxVis]; });
        try{
          await ns.fb.adminSaveAnalyticsConfig({ grossTargetMonthly:gross, locationTargetsMonthly:locT, productTargetsMonthly:prodT, visitsByMonth:vis });
          anxConfig={ grossTargetMonthly:gross, locationTargetsMonthly:locT, productTargetsMonthly:prodT, visitsByMonth:vis };
          msg.textContent='✓ Saved.'; renderAnalytics();
          setTimeout(function(){ anxEditor.hidden=true; anxEditor.innerHTML=''; }, 700);
        }catch(err){ msg.textContent=err.message||'Save failed.'; }
      });
    }

    // Custom date range: Apply button + change on either date input.
    function anxApplyDates(){ if(anxFromIn&&anxFromIn.value) anxFrom=anxFromIn.value; if(anxToIn&&anxToIn.value) anxTo=anxToIn.value;
      if(anxPresets) anxPresets.querySelectorAll('button').forEach(function(x){x.classList.remove('is-active');}); loadAnalytics(); }
    anxApply && anxApply.addEventListener('click', anxApplyDates);
    anxFromIn && anxFromIn.addEventListener('change', anxApplyDates);
    anxToIn && anxToIn.addEventListener('change', anxApplyDates);
    // Quick presets set the two dates.
    anxPresets && anxPresets.addEventListener('click', function(e){ const b=e.target.closest('[data-preset]'); if(!b) return;
      const now=new Date(), y=now.getFullYear(), m=now.getMonth(), d=now.getDate();
      let f, t=anxYmd(now);
      if(b.dataset.preset==='this-week'){ const dow=(now.getDay()+6)%7; f=anxYmd(new Date(y,m,d-dow)); }
      else if(b.dataset.preset==='last-month'){ f=anxYmd(new Date(y,m-1,1)); t=anxYmd(new Date(y,m,0)); }
      else if(b.dataset.preset==='ytd'){ f=anxYmd(new Date(y,0,1)); }
      else { f=anxYmd(new Date(y,m,1)); } // this-month
      anxFrom=f; anxTo=t;
      anxPresets.querySelectorAll('button').forEach(function(x){x.classList.toggle('is-active',x===b);});
      loadAnalytics();
    });
    anxRefresh && anxRefresh.addEventListener('click', loadAnalytics);
    anxEditBtn && anxEditBtn.addEventListener('click', openAnxEditor);
    var anxSubNav = root.querySelector('[data-sm-anx-subtab]');
    anxSubNav && anxSubNav.addEventListener('click', function(e){ var b=e.target.closest('[data-anx-sub]'); if(!b) return; anxSubTab=b.dataset.anxSub; renderAnalytics(); });

    // Auto-show app if already signed in (or mock mode)
    await ns.fb.onReady(() => {});
    if (ns.fb.isMock() || ns.fb.currentUser()) {
      showApp();
      await loadLookups();
      await loadBookings();
    } else {
      showLogin();
    }
  }

  /* ---------- v2 Booking (multi-step course/location/date/time/info) ---------- */
  // Cache + resolver for picking a Shopify variant whose option values include
  // BOTH the course-option block's match_value AND the picked location name.
  const _productCache = {};
  async function fetchProduct(handle) {
    if (!handle) return null;
    if (_productCache[handle]) return _productCache[handle];
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error('product ' + handle + ' returned ' + res.status);
      _productCache[handle] = await res.json();
      return _productCache[handle];
    } catch (e) { console.error('[sm-booking] fetchProduct failed', handle, e); return null; }
  }
  function variantOptionValues(v) { return [v.option1, v.option2, v.option3].filter(Boolean); }
  // Normalize so "Self-Guided" / "Self Guided" / "self_guided" all collapse to "selfguided",
  // and "STAT Med Training : Gainesville FL" still contains "gainesville". Lets the
  // variant resolver tolerate punctuation, spacing, and longer Firestore location names.
  function normalizeOptionKey(s) { return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ''); }
  function resolveVariant(product, mustHaveValues) {
    if (!product || !product.variants) return null;
    const needles = mustHaveValues.filter(Boolean).map(normalizeOptionKey).filter(Boolean);
    if (!needles.length) return null;
    return product.variants.find((v) => {
      if (v.available === false) return false;
      const opts = variantOptionValues(v).map(normalizeOptionKey).filter(Boolean);
      return needles.every((needle) => opts.some((opt) => opt.indexOf(needle) > -1 || needle.indexOf(opt) > -1));
    }) || null;
  }

  async function initBookingV2() {
    const roots = document.querySelectorAll('[data-sm-booking-v2]');
    if (!roots.length) return;
    // On booking pages, refresh should land at the top instead of restoring
    // the previous scroll position (which often was deep in the form).
    if ('scrollRestoration' in history) {
      try { history.scrollRestoration = 'manual'; } catch (e) {}
    }
    const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const DAYS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

    roots.forEach(async (root) => {
      const form = root.querySelector('[data-sm-book-form]');
      const loadingEl = root.querySelector('[data-sm-book-loading]');
      const loadErrEl = root.querySelector('[data-sm-book-load-error]');
      const locEl = root.querySelector('[data-sm-book-locs]');
      const calEl = root.querySelector('[data-sm-book-cal]');
      const timesEl = root.querySelector('[data-sm-book-times]');
      const summaryEl = root.querySelector('[data-sm-book-summary]');
      const submitBtn = root.querySelector('[data-sm-book-submit]');
      const successEl = root.querySelector('[data-sm-book-success]');
      const successIdEl = root.querySelector('[data-sm-book-success-id]');
      const proofWrap = root.querySelector('[data-sm-book-proof]');
      const proofInput = root.querySelector('[data-sm-book-proof-input]');
      const proofStatus = root.querySelector('[data-sm-book-proof-status]');
      const proofListEl = root.querySelector('[data-sm-book-proof-list]');
      const bookEl = root.querySelector('.sm-book');
      const defaultMax = parseInt(bookEl.dataset.defaultMaxPerSlot || '6', 10);
      const forceLocationId = (bookEl.dataset.forceLocation || '').trim();
      const cancelVariantId = (bookEl.dataset.cancelVariantId || '').trim();
      // Location-first flow: customer picks location before course. Only
      // honored when force_location isn't set (force_location wins).
      const locationFirst = !forceLocationId && bookEl.dataset.locationFirst === 'true';

      // Renumber the "Step N" labels so hidden steps (e.g. Location step on
      // forced-location landing pages) don't leave gaps like 1 → 3 → 4.
      function renumberSteps() {
        const visible = Array.from(root.querySelectorAll('.sm-book__step'))
          .filter((el) => el.offsetParent !== null || (el.style.display !== 'none' && !el.hidden));
        visible.forEach((el, i) => {
          const label = el.querySelector('.sm-book__step-label');
          if (label) label.textContent = 'Step ' + (i + 1);
        });
      }

      let state = {
        courseEl: null, serviceId: null, serviceName: null, variantId: null, maxPerSlot: defaultMax,
        locations: [], locationId: null, location: null,
        calOffset: 0, date: null, time: null,
        blocked: {}, booked: {}, blockedTimes: [], schedule: null,
        proofs: []
      };

      // Proof upload shows only for Skills-Only courses. Triggers:
      //  1. the service's admin "Requires proof-of-completion upload" flag, or
      //  2. any of the tile's identifiers reading "skills only" — the service
      //     id (pals-skills-only-stat-ocala), the tile match_value ("Skills
      //     Only"), or the service name. Covers every page/naming convention.
      // Regular courses (blended, self-guided, etc.) never match.
      function smRequiresProof() {
        if (state.service && state.service.requiresProofUpload === true) return true;
        const hay = ((state.serviceId || '') + ' ' + (state.matchValue || '') + ' ' + (state.serviceName || '')).toLowerCase();
        return /skills[\s_-]*only/.test(hay);
      }
      // Show/hide the proof upload field for the current course selection.
      // Toggle display directly — the wrapper has class .sm-field
      // (display:grid), which overrides the [hidden] attribute, so setting
      // .hidden alone would leave the field visible for every course.
      // Local HTML-escape — the admin's esc() lives in initAdmin's scope and
      // isn't visible here, so proof rendering needs its own.
      function esc(v) { return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
      // Office photo for a location tile: the photo set in the staff admin
      // (Locations → Image URL) wins; otherwise the theme's "STAT Med — Office
      // photos" setting for that office. Shopify CDN photos are resized for the
      // tile. If a photo can't be shown (e.g. a format the browser can't read),
      // the tile falls back to the office's initial.
      function locTileMedia(loc) {
        // Built-in photos (Content → Files) in case the theme settings aren't saved.
        const imgs = {
          'lake-city': 'https://cdn.shopify.com/s/files/1/0860/7868/5484/files/Lake_City_Outside_View.heic?v=1790614185',
          'gainesville': 'https://cdn.shopify.com/s/files/1/0860/7868/5484/files/Gainesville_Outside.png?v=1790607962',
          'jacksonville': 'https://cdn.shopify.com/s/files/1/0860/7868/5484/files/Jacksonville_Office_Outside.png?v=1790611915',
          'ocala': 'https://cdn.shopify.com/s/files/1/0860/7868/5484/files/Ocala_Outside_Front.png?v=1790607999'
        };
        const fromSettings = (window.StatMedConfig && window.StatMedConfig.locationImages) || {};
        Object.keys(fromSettings).forEach((k) => { if (fromSettings[k]) imgs[k] = fromSettings[k]; });
        const hay = ((loc.id || '') + ' ' + (loc.name || '') + ' ' + (loc.city || '')).toLowerCase();
        const key = Object.keys(imgs).find((k) => hay.indexOf(k) > -1 || hay.indexOf(k.replace(/-/g, ' ')) > -1);
        let src = loc.imageUrl || (key ? imgs[key] : '');
        if (src && /cdn\.shopify\.com\//.test(src) && !/[?&]width=/.test(src)) src += (src.indexOf('?') > -1 ? '&' : '?') + 'width=640';
        const label = String(loc.city || loc.name || loc.id || '').replace(/^.*:\s*/, '');
        const initial = '<div class="sm-book__loc-placeholder"' + (src ? ' hidden' : '') + '>' + esc(label.charAt(0)) + '</div>';
        if (!src) return initial;
        return '<img src="' + esc(src) + '" alt="' + esc(label) + ' office" loading="lazy" onerror="this.hidden=true;this.nextElementSibling&&(this.nextElementSibling.hidden=false)">' + initial;
      }
      function clearProofs() {
        (state.proofs || []).forEach((p) => { if (p.localUrl) { try { URL.revokeObjectURL(p.localUrl); } catch (e) {} } });
        state.proofs = [];
        renderProofList();
      }
      function updateProofField() {
        if (!proofWrap) return;
        const need = smRequiresProof();
        proofWrap.hidden = !need;
        proofWrap.style.display = need ? '' : 'none';
        if (!need) { clearProofs(); if (proofStatus) proofStatus.textContent = ''; if (proofInput) proofInput.value = ''; }
      }

      // Render the list of uploaded proofs with a thumbnail/link + remove.
      function renderProofList() {
        if (!proofListEl) return;
        proofListEl.innerHTML = (state.proofs || []).map((p, i) => {
          const view = p.localUrl || p.url || '';
          const thumb = p.isImage && view
            ? '<a href="' + esc(view) + '" target="_blank" rel="noopener"><img src="' + esc(view) + '" alt="" style="width:44px;height:44px;object-fit:cover;border-radius:8px;border:1px solid var(--sm-border-soft);display:block"></a>'
            : '<span style="width:44px;height:44px;border-radius:8px;border:1px solid var(--sm-border-soft);display:inline-flex;align-items:center;justify-content:center;background:var(--sm-surface-alt,#f1f5fa);font-size:11px;font-weight:700;color:var(--sm-ink-muted)">' + (p.uploading ? '…' : 'PDF') + '</span>';
          const nameHtml = view && !p.uploading
            ? '<a href="' + esc(view) + '" target="_blank" rel="noopener" style="font-weight:600;text-decoration:none">' + esc(p.name) + '</a>'
            : '<span style="font-weight:600">' + esc(p.name) + '</span>';
          const status = p.uploading ? '<span class="sm-field__help">Uploading…</span>'
            : (p.error ? '<span class="sm-field__error is-shown" style="margin:0">' + esc(p.error) + '</span>' : '<span class="sm-field__help" style="color:var(--sm-success,#0f9d6b)">✓ uploaded</span>');
          return '<div class="sm-book__proof-item" style="display:flex;align-items:center;gap:10px;padding:8px 0;border-top:1px solid var(--sm-border-soft)">' +
            thumb +
            '<div style="flex:1;min-width:0">' + nameHtml + '<br>' + status + '</div>' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-proof-remove="' + i + '">Remove</button>' +
          '</div>';
        }).join('');
      }
      proofListEl && proofListEl.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-proof-remove]');
        if (!btn) return;
        const i = parseInt(btn.dataset.proofRemove, 10);
        const p = state.proofs[i];
        if (p && p.localUrl) { try { URL.revokeObjectURL(p.localUrl); } catch (err) {} }
        state.proofs.splice(i, 1);
        renderProofList();
      });

      // Upload each chosen file to Firebase Storage; append to the list.
      proofInput && proofInput.addEventListener('change', async () => {
        clearErr('proof');
        const files = Array.from(proofInput.files || []);
        proofInput.value = ''; // allow re-picking the same file later
        for (const file of files) {
          if (file.size > 15 * 1024 * 1024) {
            state.proofs.push({ name: file.name, error: 'Over 15 MB — please use a smaller file.' });
            renderProofList();
            continue;
          }
          const entry = { name: file.name, uploading: true, isImage: /^image\//.test(file.type),
            localUrl: /^image\//.test(file.type) ? URL.createObjectURL(file) : '' };
          state.proofs.push(entry);
          renderProofList();
          try {
            const res = await ns.fb.uploadProof(file, {
              customerEmail: (form.email && form.email.value) || '',
              productId: state.serviceId || ''
            });
            entry.path = res.path || ''; entry.url = res.url || ''; entry.uploading = false;
          } catch (err) {
            console.error('[sm-booking] proof upload failed', err);
            entry.uploading = false; entry.error = 'Upload failed. Please try again.';
          }
          renderProofList();
        }
      });

      // Parse a slot label ("9:00 AM", "11:30 AM", "13:00") to minutes past
      // midnight so we can test it against admin-blocked time ranges.
      function smSlotToMin(str) {
        if (!str) return null;
        str = String(str).trim();
        let m = str.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
        if (m) { let h = parseInt(m[1], 10) % 12; if (/[Pp]/.test(m[3])) h += 12; return h * 60 + parseInt(m[2], 10); }
        m = str.match(/^(\d{1,2})\s*([AaPp][Mm])$/);
        if (m) { let h = parseInt(m[1], 10) % 12; if (/[Pp]/.test(m[2])) h += 12; return h * 60; }
        m = str.match(/^(\d{1,2}):(\d{2})$/);
        if (m) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
        return null;
      }
      // True if a slot [start,end) overlaps any admin-blocked range for the day.
      function smSlotBlocked(startLabel, endLabel) {
        const startMin = smSlotToMin(startLabel);
        if (startMin == null) return false;
        let endMin = smSlotToMin(endLabel);
        if (endMin == null) endMin = startMin + (((state.service && state.service.durationMinutes) || 60));
        return (state.blockedTimes || []).some((b) => startMin < b.endMin && endMin > b.startMin);
      }

      // Same-day lead-time cutoff: on today's date (Eastern), a slot is too
      // soon if it starts within `leadMinutes` of the current Eastern time.
      const leadMinutes = Math.max(0, Math.round((parseFloat(bookEl.dataset.minLeadHours) || 2) * 60));
      function smSlotTooSoon(startLabel) {
        if (!leadMinutes) return false;
        const en = smEasternNow();
        if (state.date !== en.dateStr) return false; // only affects today
        const startMin = smSlotToMin(startLabel);
        if (startMin == null) return false;
        return startMin < (en.minutes + leadMinutes);
      }

      function showErr(key, msg) {
        const el = root.querySelector('[data-err="' + key + '"]');
        if (el) { el.textContent = msg; el.classList.add('is-shown'); }
      }
      function clearErr(key) {
        const el = root.querySelector('[data-err="' + key + '"]');
        if (el) { el.textContent = ''; el.classList.remove('is-shown'); }
      }
      function unlock(stepName) {
        const step = root.querySelector('[data-step="' + stepName + '"]');
        if (step) step.classList.remove('is-locked');
      }
      function scrollToStep(stepName) {
        // Only scroll once a real user has interacted with the form.
        // The initial bootstrap auto-click uses synthetic .click(), which
        // doesn't flip this flag — so the page won't jump on load.
        if (!state.userInteracted) return;
        const step = root.querySelector('[data-step="' + stepName + '"]');
        if (!step) return;
        // Defer so the step has rendered before we measure its position.
        setTimeout(() => {
          try { step.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (e) {}
        }, 120);
      }
      // Mark the widget "interacted with" only on TRUSTED user events
      // (real clicks/keystrokes). Synthetic .click() calls don't set it.
      root.addEventListener('click', (e) => { if (e.isTrusted) state.userInteracted = true; }, true);
      root.addEventListener('keydown', (e) => { if (e.isTrusted) state.userInteracted = true; }, true);
      root.addEventListener('change', (e) => { if (e.isTrusted) state.userInteracted = true; }, true);
      function lockFrom(stepName) {
        const order = ['location','date','time','info'];
        const idx = order.indexOf(stepName);
        if (idx < 0) return;
        order.slice(idx).forEach((s) => {
          const step = root.querySelector('[data-step="' + s + '"]');
          if (step) step.classList.add('is-locked');
        });
      }
      function updateSummary() {
        if (!summaryEl) return;
        const parts = [];
        if (state.serviceName) parts.push(state.serviceName);
        if (state.location) parts.push(state.location.name);
        if (state.date) parts.push(fmtDateLong(state.date + 'T00:00:00'));
        if (state.time) parts.push(state.time);
        summaryEl.textContent = parts.join(' · ');
      }

      /* Step 1 — Course option */
      root.querySelectorAll('[data-course]').forEach((tile) => {
        tile.addEventListener('click', async () => {
          if (!tile.dataset.serviceId) { showErr('course', 'This course option is missing its service ID.'); return; }
          root.querySelectorAll('[data-course]').forEach((t) => t.classList.remove('is-selected'));
          tile.classList.add('is-selected');
          state.courseEl = tile;
          state.serviceId = tile.dataset.serviceId;
          state.serviceName = tile.dataset.serviceName;
          state.variantId = tile.dataset.variantId;
          state.productHandle = tile.dataset.productHandle || '';
          state.matchValue = tile.dataset.matchValue || '';
          const tileMax = parseInt(tile.dataset.maxPerSlot || String(defaultMax), 10);
          state.maxPerSlot = tileMax;
          state.service = null;
          state.groupServiceIds = null;
          // Firestore is the source of truth for maxPerSlot — override the
          // template fallback if the service doc has a value. Logged when a
          // mismatch is detected so wrong slot counts surface quickly.
          try {
            if (ns.fb && typeof ns.fb.getService === 'function') {
              state.service = await ns.fb.getService(state.serviceId);
              if (state.service && state.service.maxPerSlot) {
                if (state.service.maxPerSlot !== tileMax) {
                  console.log('[sm-booking] maxPerSlot override: template=' + tileMax + ' firestore=' + state.service.maxPerSlot + ' (service=' + state.serviceId + ')');
                }
                state.maxPerSlot = state.service.maxPerSlot;
              } else if (!state.service) {
                console.warn('[sm-booking] no Firestore doc for service "' + state.serviceId + '" — using tile fallback maxPerSlot=' + tileMax);
              }
              // Resource-group sharing: collect every service that shares
              // this group so the calendar treats them as one bucket.
              // Also check the resource_group doc — if it has
              // sharedAcrossLocations: true, the slot is consumed across
              // every location (single shared physical resource).
              state.crossLocations = false;
              if (state.service && state.service.resourceGroup && typeof ns.fb.getServicesInGroup === 'function') {
                try {
                  const siblings = await ns.fb.getServicesInGroup(state.service.resourceGroup);
                  if (siblings && siblings.length > 1) {
                    state.groupServiceIds = siblings;
                    console.log('[sm-booking] shared resource group "' + state.service.resourceGroup + '" → ' + siblings.length + ' services share capacity');
                  }
                  if (typeof ns.fb.getResourceGroup === 'function') {
                    const groupDoc = await ns.fb.getResourceGroup(state.service.resourceGroup);
                    if (groupDoc && groupDoc.sharedAcrossLocations) {
                      state.crossLocations = true;
                      console.log('[sm-booking] resource group "' + state.service.resourceGroup + '" is sharedAcrossLocations — slots consumed across all locations');
                    }
                  }
                } catch (e) {
                  console.warn('[sm-booking] getServicesInGroup / getResourceGroup failed', e);
                }
              }
            }
          } catch (e) {
            console.warn('[sm-booking] getService failed', e);
          }
          clearErr('course');
          updateProofField();
          if (locationFirst) {
            // Location was picked first; preserve it and skip straight to
            // the date step. Calendar needs fresh blocked-dates for the
            // newly chosen service.
            state.date = null; state.time = null;
            lockFrom('date'); unlock('date');
            try {
              state.blocked = await ns.fb.getBlockedDates({ locationId: state.locationId, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null });
            } catch (e) { console.error('[sm-booking] getBlockedDates failed', e); state.blocked = {}; }
            state.calOffset = 0;
            renderCal();
            scrollToStep('date');
          } else {
            state.locationId = null; state.location = null; state.date = null; state.time = null;
            lockFrom('location'); unlock('location');
            await loadLocations();
            scrollToStep('location');
          }
          updateSummary();
        });
      });

      // Auto-select the recommended course tile on initial load (so
      // Self-Guided is pre-picked with the blue ring + Recommended badge).
      // We wait for Firestore to be ready first — otherwise the click handler's
      // location-loading step bails out and the user has to click again.
      (async function autoPickRecommended() {
        const rec = root.querySelector('[data-course][data-recommended="true"]');
        if (!rec) return;
        if (root.querySelector('[data-course].is-selected')) return;
        try {
          if (ns.fb && typeof ns.fb.onReady === 'function') {
            await ns.fb.onReady(() => {});
          }
          // Synthetic click — isTrusted is false, so scrollToStep skips it.
          rec.click();
        } catch (e) { console.warn('[sm-booking] auto-select recommended failed', e); }
      })();

      // Stale-tab mitigation: when the tab wakes after >10 min hidden,
      // silently re-fetch availability so the customer acts on fresh data.
      // We don't touch the times grid once a time is picked — the submit
      // transaction is the final validator for that case.
      smOnWake(10 * 60 * 1000, async () => {
        try {
          // If the previously-picked date has slipped into the past while
          // the tab was away, clear it before re-rendering so the customer
          // can't continue with a stale selection.
          if (state.date && state.date < smTodayLocal()) {
            state.date = null;
            state.time = null;
            lockFrom('date');
          }
          if (!state.locationId || !state.serviceId) return;
          state.blocked = await ns.fb.getBlockedDates({ locationId: state.locationId, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null });
          renderCal();
          if (state.date && !state.time) {
            state.booked = await ns.fb.getBookedSlots({ locationId: state.locationId, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null, crossLocations: state.crossLocations, date: state.date });
            try { state.blockedTimes = await ns.fb.getBlockedTimes({ locationId: state.locationId, date: state.date, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null }); } catch (e) { state.blockedTimes = []; }
            renderTimes();
          }
        } catch (e) { console.warn('[sm-booking] wake refresh failed', e); }
      });

      async function loadLocations() {
        locEl.innerHTML = '<p class="sm-field__help">Loading locations…</p>';
        try {
          if (!ns.fb) {
            throw new Error('StatMed.fb is not available — statmed-firebase.js may be out of date.');
          }
          // When force_location is set, trust the merchant and fetch the
          // location doc directly — no service filter, no active-status
          // filter. This makes the location.services array a soft hint
          // rather than a hard gate, so renaming services doesn't break
          // every per-location booking page.
          if (forceLocationId) {
            if (typeof ns.fb.getLocationById === 'function') {
              const loc = await ns.fb.getLocationById(forceLocationId);
              state.locations = loc ? [loc] : [];
            } else {
              // Fallback for older firebase.js that doesn't have getLocationById
              state.locations = await ns.fb.getLocationsForService('');
            }
          } else {
            if (typeof ns.fb.getLocationsForService !== 'function') {
              throw new Error('StatMed.fb.getLocationsForService is not available.');
            }
            state.locations = await ns.fb.getLocationsForService(state.serviceId);
          }
        } catch (e) {
          console.error('[sm-booking] loadLocations failed:', e);
          locEl.innerHTML = '<p class="sm-field__help" style="color:var(--sm-danger)">Unable to load locations. (' + (e && e.message ? e.message : 'unknown error') + ')</p>';
          return;
        }
        if (!state.locations.length) {
          locEl.innerHTML = '<p class="sm-field__help">No locations currently offer ' + (state.serviceName || 'this option') + '. Please contact us.</p>';
          return;
        }
        // Forced location (per-location landing pages): auto-select and skip the step.
        if (forceLocationId) {
          // Prefer exact match. If we have a single location from
          // getLocationById (prefix fallback already resolved any mismatch),
          // accept it as the forced location even when its doc ID isn't
          // exactly forceLocationId. This avoids the merchant having to
          // edit every page's force_location setting after renaming docs.
          const forced = state.locations.find((l) => l.id === forceLocationId)
                       || (state.locations.length === 1 ? state.locations[0] : null);
          if (forced) {
            const locStep = root.querySelector('[data-step="location"]');
            if (locStep) locStep.style.display = 'none';
            renumberSteps();
            state.locationId = forced.id;
            state.location = forced;
            console.log('[sm-booking] forced location resolved:', forced.id, '| slots:', (forced.slots && forced.slots.length) || 0, '| openDays:', forced.openDays || '(none)');
            state.date = null; state.time = null;
            clearErr('location');
            lockFrom('date'); unlock('date');
            try {
              state.blocked = await ns.fb.getBlockedDates({ locationId: state.locationId, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null });
            } catch (e) { console.error('[sm-booking] getBlockedDates failed', e); state.blocked = {}; }
            state.calOffset = 0;
            renderCal();
            updateSummary();
            scrollToStep('date');
            return;
          }
          // forceLocationId is set but no matching location doc exists.
          // Either the template's force_location value is wrong, or the
          // location doc doesn't exist in Firestore. Surface details to
          // the console so the merchant can fix it; show a softer
          // customer-facing message.
          console.warn('[sm-booking] forced location "' + forceLocationId + '" not found in Firestore. Either create a /locations/' + forceLocationId + ' doc or update the booking section\'s "Force location" setting to match an existing doc ID.');
          try {
            if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
              ns.fb.logBookingFailure({
                reason: 'forced_location_not_found',
                forceLocationId: forceLocationId,
                serviceId: state.serviceId || '',
                serviceName: state.serviceName || '',
                matchValue: state.matchValue || '',
                productHandle: state.productHandle || ''
              });
            }
          } catch (e) {}
          locEl.innerHTML = '<p class="sm-field__help" style="color:var(--sm-danger)">' + (state.serviceName || 'This option') + ' is not offered at this location. Please pick another course or visit our locations page.</p>';
          return;
        }
        locEl.innerHTML = state.locations.map((loc) => (
          '<button type="button" class="sm-book__loc-tile" data-loc="' + loc.id + '">' +
            locTileMedia(loc) +
            '<strong>' + (loc.name || loc.id) + '</strong>' +
            (loc.city ? '<span class="sm-book__loc-city">' + loc.city + '</span>' : '') +
          '</button>'
        )).join('');
        locEl.querySelectorAll('[data-loc]').forEach((btn) => {
          btn.addEventListener('click', async () => {
            locEl.querySelectorAll('[data-loc]').forEach((b) => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');
            state.locationId = btn.dataset.loc;
            state.location = state.locations.find((l) => l.id === state.locationId);
            state.date = null; state.time = null;
            clearErr('location');
            lockFrom('date'); unlock('date');
            state.blocked = await ns.fb.getBlockedDates({ locationId: state.locationId, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null });
            state.calOffset = 0;
            renderCal();
            updateSummary();
            scrollToStep('date');
          });
        });
      }

      // Location-first mode: render the location grid up front (no service
      // filter), and on click filter course tiles to only ones the picked
      // location offers, then unlock the course step.
      function renderLocationFirstPicker() {
        if (!locEl) return;
        if (!state.locations.length) {
          locEl.innerHTML = '<p class="sm-field__help" style="color:var(--sm-danger)">No active locations configured. Add at least one in the admin Locations tab.</p>';
          return;
        }
        locEl.innerHTML = state.locations.map((loc) => (
          '<button type="button" class="sm-book__loc-tile" data-loc-first="' + loc.id + '">' +
            locTileMedia(loc) +
            '<strong>' + (loc.name || loc.id) + '</strong>' +
            (loc.city ? '<span class="sm-book__loc-city">' + loc.city + '</span>' : '') +
          '</button>'
        )).join('');
        locEl.querySelectorAll('[data-loc-first]').forEach((btn) => {
          btn.addEventListener('click', async () => {
            locEl.querySelectorAll('[data-loc-first]').forEach((b) => b.classList.remove('is-selected'));
            btn.classList.add('is-selected');
            state.locationId = btn.dataset.locFirst;
            state.location = state.locations.find((l) => l.id === state.locationId);
            state.serviceId = null; state.service = null; state.date = null; state.time = null;
            clearErr('location'); clearErr('course');
            // Filter course tiles to only those whose service_id is in
            // location.services. Tiles without a service_id are always
            // shown (defensive). Selected state is cleared.
            const offered = (state.location && Array.isArray(state.location.services)) ? state.location.services : null;
            let visibleCount = 0;
            root.querySelectorAll('[data-course]').forEach((tile) => {
              tile.classList.remove('is-selected');
              const sid = tile.dataset.serviceId || '';
              const allowed = !offered || !sid || offered.indexOf(sid) > -1;
              tile.style.display = allowed ? '' : 'none';
              if (allowed) visibleCount++;
            });
            // Unlock the course step now that a location is chosen.
            const courseStep = root.querySelector('[data-step="course"]');
            if (courseStep) courseStep.classList.remove('is-locked');
            lockFrom('date');
            if (visibleCount === 0) {
              showErr('course', (state.location.name || state.locationId) + ' has no course options assigned. Add services to this location in admin.');
            }
            updateSummary();
          });
        });
      }

      /* Step 3 — Calendar */
      function renderCal() {
        const today = new Date();
        const display = new Date(today.getFullYear(), today.getMonth() + state.calOffset, 1);
        const month = display.getMonth();
        const year = display.getFullYear();
        const firstDay = new Date(year, month, 1).getDay();
        const dInMonth = new Date(year, month + 1, 0).getDate();
        // Day-of-week priority: service.openDaysByLocation[locId] (per-service
        // per-location override — e.g. Heartsaver only Mon/Wed/Fri at Lake City)
        // > location.openDays > schedule defaults > Mon–Sat.
        const fromSvcDays = state.service && state.service.openDaysByLocation && state.location
                           && state.service.openDaysByLocation[state.location.id];
        const openDays = (Array.isArray(fromSvcDays) && fromSvcDays.length ? fromSvcDays : null)
                       || (state.location && state.location.openDays)
                       || (state.schedule && state.schedule.defaultDays)
                       || [1,2,3,4,5,6];
        // Per-service-per-location scheduled sessions. When configured, only
        // the listed dates are bookable (location.openDays is ignored — the
        // sessions list is the full source of truth for valid dates).
        const sessions = smPickSessions(state.service, state.location && state.location.id);
        const sessionDates = smSessionDates(sessions);

        let html = '<div class="sm-book__cal-header">' +
          '<button type="button" class="sm-book__cal-nav" data-cal-dir="-1" aria-label="Previous month">‹</button>' +
          '<span class="sm-book__cal-title">' + MONTHS[month] + ' ' + year + '</span>' +
          '<button type="button" class="sm-book__cal-nav" data-cal-dir="1" aria-label="Next month">›</button>' +
        '</div>' +
        '<table class="sm-book__cal-grid"><thead><tr>' +
          DAYS.map((d) => '<th>' + d + '</th>').join('') +
        '</tr></thead><tbody>';

        let day = 1;
        for (let row = 0; row < 6; row++) {
          html += '<tr>';
          for (let col = 0; col < 7; col++) {
            const idx = row * 7 + col;
            if (idx < firstDay || day > dInMonth) { html += '<td></td>'; continue; }
            const mm = String(month + 1).padStart(2,'0');
            const dd = String(day).padStart(2,'0');
            const dateStr = year + '-' + mm + '-' + dd;
            const cellDate = new Date(year, month, day);
            const isPast = cellDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
            const isBlocked = !!state.blocked[dateStr];
            // When sessions are configured, ONLY listed dates are valid
            // (location.openDays is ignored). Otherwise use openDays.
            const isClosed = sessionDates
              ? !sessionDates[dateStr]
              : openDays.indexOf(cellDate.getDay()) === -1;
            const isSelected = state.date === dateStr;
            const disabled = isPast || isBlocked || isClosed;
            const cls = 'sm-book__cal-day' + (disabled ? ' is-unavailable' : '') + (isSelected ? ' is-selected' : '');
            html += '<td><button type="button" class="' + cls + '"' + (disabled ? ' disabled' : '') + ' data-cal-date="' + dateStr + '">' + day + '</button></td>';
            day++;
          }
          html += '</tr>';
          if (day > dInMonth) break;
        }
        html += '</tbody></table>' +
          '<div class="sm-book__cal-legend">' +
            '<span><span class="sm-book__dot sm-book__dot--open"></span>Open</span>' +
            '<span><span class="sm-book__dot sm-book__dot--closed"></span>Closed / blocked</span>' +
          '</div>';
        calEl.innerHTML = html;
        calEl.querySelectorAll('[data-cal-dir]').forEach((b) => b.addEventListener('click', () => { state.calOffset += parseInt(b.dataset.calDir, 10); renderCal(); }));
        calEl.querySelectorAll('[data-cal-date]').forEach((b) => {
          b.addEventListener('click', async () => {
            if (b.disabled) return;
            state.date = b.dataset.calDate;
            state.time = null;
            clearErr('date');
            renderCal();
            lockFrom('time'); unlock('time');
            state.booked = await ns.fb.getBookedSlots({ locationId: state.locationId, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null, crossLocations: state.crossLocations, date: state.date });
            try { state.blockedTimes = await ns.fb.getBlockedTimes({ locationId: state.locationId, date: state.date, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null }); } catch (e) { state.blockedTimes = []; }
            renderTimes();
            scrollToStep('time');
            updateSummary();
          });
        });
      }

      /* Step 4 — Time */
      function renderTimes() {
        // Sessions-mode (per-service per-location scheduled dates) overrides
        // the location's slots list entirely. Each session becomes one tile
        // labeled "start – end". If sessions are not configured, fall back
        // to location.slots / schedule defaults / hardcoded fallback.
        const sessions = smPickSessions(state.service, state.location && state.location.id);
        let slotEntries; // [{ time, label, endTime? }]
        if (sessions && state.date) {
          const todays = smSessionsForDate(sessions, state.date);
          slotEntries = todays.map((s) => ({
            time: s.startTime || '',
            endTime: s.endTime || '',
            label: (s.startTime || '') + (s.endTime ? ' – ' + s.endTime : '')
          })).filter((e) => e.time);
          console.log('[sm-booking] renderTimes: sessions mode — ' + slotEntries.length + ' sessions for ' + state.date);
        } else {
          // Time-slot priority: service.slotsByLocation[locId] (per-service
          // per-location time-of-day windows — e.g. Heartsaver at Lake City)
          // > location.slots > schedule defaults > hardcoded.
          const fromSvcLoc = state.service && state.service.slotsByLocation && state.location
                            && state.service.slotsByLocation[state.location.id];
          const fromLoc = state.location && state.location.slots;
          const fromSched = state.schedule && state.schedule.defaultSlots;
          const slots = (Array.isArray(fromSvcLoc) && fromSvcLoc.length ? fromSvcLoc : null)
                     || (fromLoc && fromLoc.length ? fromLoc : null)
                     || (fromSched && fromSched.length ? fromSched : null)
                     || ['9:00 AM','11:00 AM','1:00 PM','3:00 PM'];
          const slotSource = (Array.isArray(fromSvcLoc) && fromSvcLoc.length) ? 'service override'
                           : (fromLoc && fromLoc.length) ? 'location'
                           : (fromSched && fromSched.length) ? 'schedule defaults'
                           : 'hardcoded fallback';
          console.log('[sm-booking] renderTimes: using ' + slots.length + ' slots from ' + slotSource);
          slotEntries = slots.map((t) => ({ time: t, label: t }));
        }

        if (!slotEntries.length) {
          timesEl.innerHTML = '<p class="sm-field__help">No time slots available for this day. Try a different date.</p>';
          return;
        }
        const max = state.maxPerSlot;
        const enNow = smEasternNow();
        const isToday = state.date === enNow.dateStr;
        timesEl.innerHTML = slotEntries.map((entry) => {
          const time = entry.time;
          const booked = state.booked[time] || 0;
          const blocked = smSlotBlocked(entry.time, entry.endTime);
          const tooSoon = smSlotTooSoon(entry.time);
          const startMinNow = smSlotToMin(entry.time);
          const passed = isToday && startMinNow != null && startMinNow < enNow.minutes;
          const gated = passed || tooSoon || blocked;   // time/admin gated → grey
          const full = booked >= max;                    // sold out → red "Booked"
          const disabled = gated || full;
          const remaining = max - booked;
          const pct = max > 1 ? Math.round((booked / max) * 100) : (disabled ? 100 : 0);
          const spotText = passed
            ? 'Unavailable'
            : (tooSoon
              ? 'Too soon'
              : (blocked
                ? 'Unavailable'
                : (max === 1
                  ? (full ? 'Booked' : 'Available')
                  : (full ? 'Booked' : (remaining + ' spot' + (remaining !== 1 ? 's' : '') + ' left')))));
          const bar = (max > 1 && !gated) ? '<div class="sm-book__time-bar"><span style="width:' + pct + '%"></span></div>' : '';
          const cls = gated ? ' is-unavailable' : (full ? ' is-full' : '');
          return '<button type="button" class="sm-book__time-tile' + cls + '"' + (disabled ? ' disabled' : '') + ' data-time="' + time + '" data-end-time="' + (entry.endTime || '') + '">' +
            '<span class="sm-book__time-label">' + entry.label + '</span>' +
            '<span class="sm-book__time-spots">' + spotText + '</span>' +
            bar +
          '</button>';
        }).join('');
        timesEl.querySelectorAll('[data-time]').forEach((t) => {
          t.addEventListener('click', () => {
            if (t.disabled) return;
            timesEl.querySelectorAll('[data-time]').forEach((x) => x.classList.remove('is-selected'));
            t.classList.add('is-selected');
            state.time = t.dataset.time;
            state.endTime = t.dataset.endTime || '';
            clearErr('time');
            lockFrom('info'); unlock('info');
            updateSummary();
            scrollToStep('info');
          });
        });
      }

      /* Cancellation protection tiles */
      root.querySelectorAll('[data-cancel]').forEach((t) => {
        t.addEventListener('click', () => {
          root.querySelectorAll('[data-cancel]').forEach((x) => x.classList.remove('is-selected'));
          t.classList.add('is-selected');
          const input = form.querySelector('[name="cancelProtection"]');
          if (input) input.value = t.dataset.cancel;
          clearErr('cancelProtection');
        });
      });

      /* Validation + submit */
      function validate() {
        let ok = true;
        const req = (cond, key, msg) => { if (!cond) { showErr(key, msg); ok = false; } else clearErr(key); };
        req(state.serviceId, 'course', 'Please select a course option.');
        req(state.locationId, 'location', 'Please select a location.');
        req(state.date, 'date', 'Please select a date.');
        req(state.time, 'time', 'Please select a time slot.');
        req(form.firstName.value.trim(), 'firstName', 'First name required.');
        req(form.lastName.value.trim(), 'lastName', 'Last name required.');
        const email = form.email.value.trim();
        req(email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email), 'email', 'Valid email required.');
        const phone = form.phone.value.trim().replace(/\D/g, '');
        req(phone.length >= 10, 'phone', 'Valid 10-digit phone required.');
        if (form.cancelProtection) req(form.cancelProtection.value, 'cancelProtection', 'Please choose a cancellation option.');
        if (smRequiresProof()) req((state.proofs || []).some((p) => p.path), 'proof', 'Please upload your proof of online course completion.');
        req(form.chk1.checked, 'chk1', 'Please confirm your information.');
        req(form.chk2.checked, 'chk2', 'Please confirm course completion.');
        return ok;
      }

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!validate()) return;
        // Stale-tab guard: re-check that the picked date is today or later,
        // using a fresh new Date(). Without this, a tab opened days ago
        // could submit a date that was future-at-load but is now past.
        if (state.date && state.date < smTodayLocal()) {
          showErr('date', 'That date has already passed. Please pick a current date.');
          state.date = null; state.time = null;
          lockFrom('date'); unlock('date');
          renderCal();
          submitBtn.disabled = false;
          submitBtn.innerHTML = 'Book appointment <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';
          return;
        }
        // Lead-time guard: the picked slot must still be at least the cutoff
        // ahead of the current Eastern time (catches slots that lapsed while
        // the tab sat open).
        if (state.time && smSlotTooSoon(state.time)) {
          showErr('time', 'That time is too close to now. Please pick a later time slot.');
          state.time = null;
          renderTimes();
          submitBtn.disabled = false;
          submitBtn.innerHTML = 'Book appointment <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';
          return;
        }
        submitBtn.disabled = true;
        submitBtn.textContent = 'Booking…';
        clearErr('submit');
        const bookingData = {
          serviceName: state.serviceName,
          firstName: form.firstName.value.trim(),
          lastName: form.lastName.value.trim(),
          email: form.email.value.trim(),
          phone: form.phone.value.trim(),
          referral: (form.referral && form.referral.value.trim()) || '',
          cancellationProtection: form.cancelProtection ? (form.cancelProtection.value === 'Yes') : false,
          endTime: state.endTime || '',
          proofFiles: (state.proofs || []).filter((p) => p.path).map((p) => ({ name: p.name, path: p.path, url: p.url || '' })),
          proofFileName: ((state.proofs || []).filter((p) => p.path).map((p) => p.name).join(', ')) || '',
          proofUrl: (((state.proofs || []).find((p) => p.url) || {}).url) || '',
          proofPath: (((state.proofs || []).find((p) => p.path) || {}).path) || '',
          shopifyOrderId: ''
        };
        try {
          // Resolve the right Shopify variant for (course option, location).
          // If the course tile carried a product handle, fetch the product and
          // pick the variant whose option values include BOTH the block's
          // match_value AND the selected location's name. Falls back to the
          // tile's static variantId only if no resolution path is available.
          let resolvedVariantId = state.variantId;
          if (state.productHandle) {
            const prod = await fetchProduct(state.productHandle);
            const locName = (state.location && state.location.name) || '';
            const must = [];
            if (state.matchValue) must.push(state.matchValue);
            if (locName) must.push(locName);
            const variant = resolveVariant(prod, must);
            if (variant) {
              resolvedVariantId = variant.id;
            } else if (must.length) {
              // Log every variant + the normalized comparison so we can see why nothing matched.
              let dump = [];
              try {
                dump = (prod.variants || []).map((v) => ({
                  id: v.id,
                  available: v.available,
                  opts: [v.option1, v.option2, v.option3].filter(Boolean),
                  normOpts: [v.option1, v.option2, v.option3].filter(Boolean).map(normalizeOptionKey)
                }));
                console.warn('[sm-booking] no variant matched', {
                  needles: must,
                  needlesNorm: must.map(normalizeOptionKey),
                  productHandle: state.productHandle,
                  variants: dump
                });
              } catch (e) {}
              // Also write the failure to Firestore so the admin can see it
              // happening and diagnose without a customer screenshot.
              try {
                if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
                  ns.fb.logBookingFailure({
                    reason: 'variant_not_matched',
                    serviceId: state.serviceId || '',
                    serviceName: state.serviceName || '',
                    matchValue: state.matchValue || '',
                    locationId: state.locationId || '',
                    locationName: locName || '',
                    productHandle: state.productHandle || '',
                    needles: must,
                    needlesNormalized: must.map(normalizeOptionKey),
                    variants: dump,
                    customerEmail: (bookingData && bookingData.email) || '',
                    customerName: (bookingData && (bookingData.firstName + ' ' + bookingData.lastName)) || ''
                  });
                }
              } catch (e) { /* never block the user on logging */ }
              showErr('course', '"' + (state.matchValue || state.serviceName) + '" is not offered at ' + locName + '. Pick another option or location.');
              submitBtn.disabled = false;
              submitBtn.innerHTML = 'Book appointment <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';
              return;
            }
          }
          const res = await ns.fb.createBookingAtomic({
            locationId: state.locationId,
            serviceId: state.serviceId,
            resourceGroup: (state.service && state.service.resourceGroup) || null,
            crossLocations: state.crossLocations,
            date: state.date,
            time: state.time,
            maxPerSlot: state.maxPerSlot,
            bookingData
          });
          if (resolvedVariantId) {
            const timeDisplay = state.endTime ? (state.time + ' – ' + state.endTime) : state.time;
            const apptDisplay = [fmtApptDate(state.date), timeDisplay].filter(Boolean).join(' · ');
            const bookingProps = {
              'Booking ID': res.id || '',
              'Service': state.serviceName || '',
              'Location': (state.location && state.location.name) || state.locationId,
              // Ready-to-send single field for SMS/email reminders (no Zapier
              // splitting needed): "Mon, Sep 8, 2026 · 2:00 PM".
              'Appointment': apptDisplay,
              'Date': state.date,
              'Time': timeDisplay,
              'First Name': bookingData.firstName || '',
              'Last Name': bookingData.lastName || '',
              'Name': bookingData.firstName + ' ' + bookingData.lastName,
              'Email': bookingData.email,
              'Phone': bookingData.phone,
              'Cancellation Protection': bookingData.cancellationProtection ? 'Yes' : 'No'
            };
            if (bookingData.referral) bookingProps['Referral'] = bookingData.referral;
            if ((bookingData.proofFiles && bookingData.proofFiles.length) || bookingData.proofPath) {
              const files = bookingData.proofFiles && bookingData.proofFiles.length ? bookingData.proofFiles : [{ name: bookingData.proofFileName, path: bookingData.proofPath, url: bookingData.proofUrl }];
              bookingProps['Proof of Completion'] = files.length + ' file' + (files.length !== 1 ? 's' : '') + ': ' + files.map((f) => f.name).join(', ');
              bookingProps['_Proof Files'] = files.map((f) => f.path).filter(Boolean).join(' | ');
            }

            const items = [{ id: Number(resolvedVariantId), quantity: 1, properties: bookingProps }];
            // Charge for cancellation protection by adding the protection
            // variant as a separate line item. The setting accepts either
            // a numeric variant ID (used as-is) or a product handle
            // (fetched, first variant used). Skipped silently if empty.
            if (bookingData.cancellationProtection && cancelVariantId) {
              let protectionVariantId = null;
              if (/^\d+$/.test(cancelVariantId)) {
                protectionVariantId = Number(cancelVariantId);
              } else {
                try {
                  const cancelProd = await fetchProduct(cancelVariantId);
                  const firstVariant = cancelProd && cancelProd.variants && cancelProd.variants[0];
                  if (firstVariant) protectionVariantId = firstVariant.id;
                  else console.warn('[sm-booking] cancellation product "' + cancelVariantId + '" has no variants');
                } catch (e) {
                  console.warn('[sm-booking] could not fetch cancellation product "' + cancelVariantId + '"', e);
                }
              }
              if (protectionVariantId) {
                items.push({
                  id: protectionVariantId,
                  quantity: 1,
                  properties: {
                    // Kept minimal on purpose: the appointment Date/Time live on
                    // the main service line item only, so integrations (Zapier)
                    // see each appointment value once instead of a 2-item array.
                    'Booking ID': res.id || '',
                    'For': state.serviceName || ''
                  }
                });
              }
            }

            try {
              const cartResp = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ items })
              });
              if (!cartResp.ok) {
                const errBody = await cartResp.text();
                throw new Error('cart/add ' + cartResp.status + ': ' + errBody.slice(0, 240));
              }
              // Also stamp the booking on the cart as ORDER-LEVEL note attributes.
              // Unlike line-item properties (which Zapier returns as per-item
              // arrays — hence the stray leading commas and split fields), these
              // come through as clean, single, individually-named order fields.
              try {
                await fetch('/cart/update.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                  body: JSON.stringify({ attributes: {
                    'First Name': bookingData.firstName || '',
                    'Last Name': bookingData.lastName || '',
                    'Phone': bookingData.phone || '',
                    'Email': bookingData.email || '',
                    'Appointment': apptDisplay,
                    'Appointment Date': state.date || '',
                    'Appointment Time': timeDisplay,
                    'Service': state.serviceName || '',
                    'Location': (state.location && state.location.name) || state.locationId || '',
                    'Booking ID': res.id || '',
                    'Cancellation Protection': bookingData.cancellationProtection ? 'Yes' : 'No'
                  } })
                });
              } catch (attrErr) { /* non-fatal: line-item properties still carry the data */ }
              window.location.href = '/cart';
            } catch (cartErr) {
              console.error('[sm-booking] cart add failed', cartErr);
              try {
                if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
                  ns.fb.logBookingFailure({
                    reason: 'cart_add_failed',
                    error: (cartErr && cartErr.message) || String(cartErr),
                    serviceId: state.serviceId || '',
                    serviceName: state.serviceName || '',
                    matchValue: state.matchValue || '',
                    locationId: state.locationId || '',
                    locationName: (state.location && state.location.name) || '',
                    productHandle: state.productHandle || '',
                    variantId: resolvedVariantId || '',
                    date: state.date || '',
                    time: state.time || '',
                    customerEmail: (bookingData && bookingData.email) || '',
                    customerName: (bookingData && (bookingData.firstName + ' ' + bookingData.lastName)) || ''
                  });
                }
              } catch (e) {}
              showErr('submit', 'Could not add to cart. ' + (cartErr.message || 'Please try again.'));
              submitBtn.disabled = false;
              submitBtn.innerHTML = 'Book appointment <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';
            }
          } else {
            form.hidden = true;
            successEl.hidden = false;
            if (successIdEl) successIdEl.textContent = res.id || 'Confirmed';
          }
        } catch (err) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = 'Book appointment <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>';
          // Log the failure (slot full vs. anything else) so it shows in admin.
          try {
            if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
              ns.fb.logBookingFailure({
                reason: (err && err.code === 'SLOT_FULL') ? 'slot_full' : 'booking_create_failed',
                error: (err && err.message) || String(err),
                serviceId: state.serviceId || '',
                serviceName: state.serviceName || '',
                matchValue: state.matchValue || '',
                locationId: state.locationId || '',
                locationName: (state.location && state.location.name) || '',
                productHandle: state.productHandle || '',
                date: state.date || '',
                time: state.time || '',
                customerEmail: (bookingData && bookingData.email) || '',
                customerName: (bookingData && (bookingData.firstName + ' ' + bookingData.lastName)) || ''
              });
            }
          } catch (e) {}
          if (err && err.code === 'SLOT_FULL') {
            showErr('time', 'That slot was just taken. Please pick another time.');
            state.booked = await ns.fb.getBookedSlots({ locationId: state.locationId, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null, crossLocations: state.crossLocations, date: state.date });
            try { state.blockedTimes = await ns.fb.getBlockedTimes({ locationId: state.locationId, date: state.date, serviceId: state.serviceId, resourceGroup: (state.service && state.service.resourceGroup) || null }); } catch (e) { state.blockedTimes = []; }
            renderTimes();
          } else {
            showErr('submit', 'Booking failed. Please try again. (' + ((err && err.message) || 'unknown error') + ')');
            console.error('[sm-booking] submit failed', err);
          }
        }
      });

      /* Initial load: schedule settings only. Failure here is non-fatal —
         the form still works with hardcoded defaults. We only log the
         real error to the console so we can actually debug it. */
      if (loadingEl) loadingEl.hidden = false;
      try {
        if (!ns.fb || typeof ns.fb.getScheduleSettings !== 'function') {
          throw new Error('StatMed.fb.getScheduleSettings is not available — statmed-firebase.js may be out of date or missing.');
        }
        state.schedule = await ns.fb.getScheduleSettings();
      } catch (e) {
        console.error('[sm-booking] schedule load failed (non-fatal, using defaults):', e);
        state.schedule = { defaultDays: [1,2,3,4,5,6], defaultSlots: ['9:00 AM','11:00 AM','1:00 PM','3:00 PM'] };
      }

      // Location-first init: move the location step DOM before the course
      // step so it renders first, swap the visible step labels, eagerly
      // load all active locations into the picker, and lock the course
      // step until a location is chosen.
      if (locationFirst) {
        const courseStep   = root.querySelector('[data-step="course"]');
        const locationStep = root.querySelector('[data-step="location"]');
        if (courseStep && locationStep) {
          locationStep.parentNode.insertBefore(locationStep, courseStep);
          const lLabel = locationStep.querySelector('.sm-book__step-label');
          const cLabel = courseStep.querySelector('.sm-book__step-label');
          if (lLabel) lLabel.textContent = 'Step 1';
          if (cLabel) cLabel.textContent = 'Step 2';
          locationStep.classList.remove('is-locked');
          courseStep.classList.add('is-locked');
          // Hide every course tile until a location is chosen. Otherwise
          // pages with one block per (format, location) show all variants
          // up front, which is confusing.
          root.querySelectorAll('[data-course]').forEach((t) => { t.style.display = 'none'; });
        }
        try {
          state.locations = await ns.fb.getLocationsForService('');
        } catch (e) { console.warn('[sm-booking] location-first load failed', e); state.locations = []; }
        renderLocationFirstPicker();
      }

      if (loadingEl) loadingEl.hidden = true;
    });
  }

  /* ---------- CNA Exam Only registration ---------- */
  async function initCnaExamForms() {
    const roots = document.querySelectorAll('[data-sm-cna-exam]');
    if (!roots.length) return;

    roots.forEach(async (root) => {
      const productHandle = root.dataset.productHandle;
      const tiles = root.querySelectorAll('[data-cna-exam-tile]');
      const variantInput = root.querySelector('[data-cna-exam-variant-input]');
      const testInput = root.querySelector('[data-cna-exam-test-input]');
      const summaryEl = root.querySelector('[data-cna-exam-summary]');
      const statusEl = root.querySelector('[data-cna-exam-status]');
      const testErrEl = root.querySelector('[data-cna-exam-test-err]');
      const form = root.querySelector('[data-sm-cna-exam-form]');
      const submitBtn = root.querySelector('[data-cna-exam-submit]');

      let chosen = null;
      let product = null;

      // Lazy-fetch the product once a tile is clicked, then resolve the
      // matching variant and stamp the form's `id` field. Falls back to the
      // default variant id if no match is found.
      async function pickTest(tile) {
        tiles.forEach((t) => t.classList.remove('is-selected'));
        tile.classList.add('is-selected');
        chosen = {
          matchValue: tile.dataset.matchValue || '',
          name: tile.dataset.testName || tile.dataset.matchValue || ''
        };
        if (testInput) testInput.value = chosen.name;
        if (testErrEl) testErrEl.textContent = '';

        if (productHandle && !product) {
          try { product = await fetchProduct(productHandle); }
          catch (e) { console.error('[cna-exam] fetchProduct failed', e); }
        }
        if (product && variantInput) {
          const variant = resolveVariant(product, [chosen.matchValue]);
          if (variant) variantInput.value = variant.id;
        }

        if (summaryEl) summaryEl.textContent = chosen.name;
      }

      tiles.forEach((t) => t.addEventListener('click', () => pickTest(t)));

      if (form) {
        form.addEventListener('submit', (e) => {
          if (!chosen) {
            e.preventDefault();
            if (testErrEl) testErrEl.textContent = 'Please choose a test.';
            return;
          }
          submitBtn && (submitBtn.disabled = true);
          statusEl && (statusEl.textContent = 'Adding to cart…');
        });
      }
    });
  }

  /* ---------- Mega header + mobile drawer ---------- */
  function initHeader() {
    const root = document.querySelector('[data-sm-header]');
    if (!root) return;

    /* Desktop mega menu — hover with delay-out, click toggle, ESC + outside-click close. */
    const items = root.querySelectorAll('[data-mega-item]');
    let openItem = null;
    let closeTimer = null;

    function openPanel(item) {
      cancelClose();
      if (openItem && openItem !== item) closePanel(openItem, true);
      const trigger = item.querySelector('[data-mega-trigger]');
      const panel = item.querySelector('[data-mega-panel]');
      if (!panel) return;
      panel.hidden = false;
      // force reflow so the transition runs
      void panel.offsetWidth;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      trigger && trigger.setAttribute('aria-expanded', 'true');
      openItem = item;
    }
    function closePanel(item, immediate) {
      if (!item) return;
      const trigger = item.querySelector('[data-mega-trigger]');
      const panel = item.querySelector('[data-mega-panel]');
      if (!panel) return;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      trigger && trigger.setAttribute('aria-expanded', 'false');
      const finish = () => { panel.hidden = true; };
      if (immediate) finish();
      else setTimeout(finish, 220);
      if (openItem === item) openItem = null;
    }
    function scheduleClose(item) {
      cancelClose();
      closeTimer = setTimeout(() => closePanel(item), 180);
    }
    function cancelClose() {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    }

    items.forEach((item) => {
      const trigger = item.querySelector('[data-mega-trigger]');
      item.addEventListener('mouseenter', () => openPanel(item));
      item.addEventListener('mouseleave', () => scheduleClose(item));
      trigger && trigger.addEventListener('click', (e) => {
        e.preventDefault();
        if (openItem === item) closePanel(item);
        else openPanel(item);
      });
      trigger && trigger.addEventListener('focus', () => openPanel(item));
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && openItem) closePanel(openItem);
    });
    document.addEventListener('click', (e) => {
      if (!openItem) return;
      if (!openItem.contains(e.target)) closePanel(openItem);
    });

    /* Mobile drawer */
    const drawer = root.querySelector('[data-sm-drawer]');
    const openBtn = root.querySelector('[data-sm-drawer-open]');
    const closeBtns = root.querySelectorAll('[data-sm-drawer-close]');

    function openDrawer() {
      if (!drawer) return;
      drawer.hidden = false;
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      openBtn && openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function closeDrawer() {
      if (!drawer) return;
      drawer.classList.remove('is-open');
      openBtn && openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      setTimeout(() => { drawer.hidden = true; }, 280);
    }
    openBtn && openBtn.addEventListener('click', openDrawer);
    closeBtns.forEach((b) => b.addEventListener('click', closeDrawer));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer && drawer.classList.contains('is-open')) closeDrawer();
    });
  }

  /* ---------- Search box ---------- */
  function initSearch() {
    document.querySelectorAll('[data-sm-search]').forEach((root) => {
      const serviceSel = root.querySelector('[data-sm-search-service]');
      const locationSel = root.querySelector('[data-sm-search-location]');
      const submitBtn = root.querySelector('[data-sm-search-submit]');
      const msgEl = root.parentNode && root.parentNode.querySelector('[data-sm-search-msg]');

      function urlForCombo() {
        if (!serviceSel || !serviceSel.value) return null;
        const opt = serviceSel.options[serviceSel.selectedIndex];
        if (!opt) return null;
        const direct = opt.dataset.direct;
        if (direct) return direct;
        const loc = locationSel && locationSel.value;
        if (!loc) return null;
        const key = loc.replace(/-/g, '');
        const map = {
          'lakecity': opt.dataset.lakeCity,
          'gainesville': opt.dataset.gainesville,
          'jacksonville': opt.dataset.jacksonville,
          'ocala': opt.dataset.ocala
        };
        return map[key] || null;
      }

      function syncAvailability() {
        if (!serviceSel || !locationSel) return;
        const opt = serviceSel.options[serviceSel.selectedIndex];
        if (!opt || !opt.value) {
          // No service yet — re-enable all locations
          locationSel.querySelectorAll('option').forEach((o) => { o.disabled = false; });
          msgEl && (msgEl.textContent = '');
          return;
        }
        // If direct URL, location doesn't matter
        if (opt.dataset.direct) {
          locationSel.querySelectorAll('option').forEach((o) => { o.disabled = false; });
          msgEl && (msgEl.textContent = '');
          return;
        }
        // Disable locations the service isn't offered at
        const map = {
          'lake-city': opt.dataset.lakeCity,
          'gainesville': opt.dataset.gainesville,
          'jacksonville': opt.dataset.jacksonville,
          'ocala': opt.dataset.ocala
        };
        const available = [];
        locationSel.querySelectorAll('option').forEach((o) => {
          if (!o.value) return;
          const has = !!map[o.value];
          o.disabled = !has;
          if (has) available.push(o);
        });
        // If currently selected location is now disabled, clear it
        if (locationSel.value && !map[locationSel.value]) locationSel.value = '';
        // Offered at one office only (e.g. Heartsaver, CNA → Lake City): pick it and say so.
        if (available.length === 1) locationSel.value = available[0].value;
        if (!available.length && msgEl) {
          msgEl.textContent = (opt.text || 'This service') + ' is not currently available. Please contact us.';
        } else if (msgEl) {
          msgEl.textContent = available.length === 1
            ? (opt.text || 'This service').trim() + ' is offered at our ' + available[0].text.replace(/,\s*FL$/, '') + ' office.'
            : '';
        }
      }

      serviceSel && serviceSel.addEventListener('change', syncAvailability);

      submitBtn && submitBtn.addEventListener('click', () => {
        const url = urlForCombo();
        if (!url) {
          if (!serviceSel.value) { msgEl && (msgEl.textContent = 'Please pick a service.'); return; }
          msgEl && (msgEl.textContent = 'Please pick a location.');
          return;
        }
        msgEl && (msgEl.textContent = '');
        window.location.href = url;
      });
    });
  }

  /* ---------- Fingerprint appointment form ---------- */
  async function initFingerprintForm() {
    const roots = document.querySelectorAll('[data-sm-fp]');
    if (!roots.length) return;

    roots.forEach(async (root) => {
      const productHandle = root.dataset.productHandle;
      const dynamic = root.dataset.dynamicOris === 'true';
      const agencyField = root.querySelector('[data-sm-fp-agency-field]');
      const agencySelect = root.querySelector('[data-sm-fp-agency]');
      const select = root.querySelector('[data-sm-fp-ori]');
      const variantInput = root.querySelector('[data-sm-fp-variant]');
      const oriTypeInput = root.querySelector('[data-sm-fp-ori-type]');
      const oriNumberInput = root.querySelector('[data-sm-fp-ori-number]');
      const descEl = root.querySelector('[data-sm-fp-desc]');
      const summaryEl = root.querySelector('[data-sm-fp-summary]');
      const summaryLabel = root.querySelector('[data-sm-fp-summary-label]');
      const summaryNumber = root.querySelector('[data-sm-fp-summary-number]');
      const summaryCost = root.querySelector('[data-sm-fp-summary-cost]');
      const form = root.querySelector('[data-sm-fp-form]');
      const submitBtn = root.querySelector('[data-sm-fp-submit]');
      const statusEl = root.querySelector('[data-sm-fp-status]');

      const dateInput = root.querySelector('input[type="date"]');

      // Admin blocking for this location + background-check service. All
      // fingerprint services belong to the "background-check" family, which is
      // what staff pick in the admin "block specific services" dropdown, so we
      // scope block lookups to that family (not the per-location service id).
      const fpLocId = root.dataset.locationId;
      const fpRawSvc = root.dataset.serviceId || 'background-check';
      const fpBlockSvc = fpRawSvc.indexOf('background-check') === 0 ? 'background-check' : fpRawSvc;
      // Service doc (if any) gives the resource group, appointment length and
      // per-slot capacity (background checks default to 1 person per slot).
      let fpService = null;
      if (ns.fb && typeof ns.fb.getService === 'function') {
        try { fpService = await ns.fb.getService(fpRawSvc); } catch (e) { fpService = null; }
      }
      const fpGroup = (fpService && fpService.resourceGroup) || null;
      const fpDuration = (fpService && Number(fpService.durationMinutes)) || 30;
      const fpCap = (fpService && Number(fpService.maxPerSlot)) || 1;
      let fpCross = false;
      if (fpGroup && ns.fb && typeof ns.fb.getResourceGroup === 'function') {
        try { const g = await ns.fb.getResourceGroup(fpGroup); fpCross = !!(g && g.sharedAcrossLocations); } catch (e) {}
      }
      // Match blocks on the per-office id AND the family id, plus the group.
      const fpBlockOpts = { locationId: fpLocId, serviceId: fpBlockSvc, serviceIds: [fpRawSvc, fpBlockSvc], resourceGroup: fpGroup, family: 'background-check' };
      let fpBlockedDates = {};
      if (ns.fb && typeof ns.fb.getBlockedDates === 'function') {
        try { fpBlockedDates = await ns.fb.getBlockedDates(fpBlockOpts); } catch (e) { fpBlockedDates = {}; }
      }
      let fpRefreshTimes = null; // set below once the date/time inputs are wired
      // "9:00 AM" / "1:30 PM" → minutes past midnight, for overlap checks.
      function fpLabelToMin(label) {
        const m = String(label || '').match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
        if (!m) return null;
        let h = parseInt(m[1], 10); const mm = parseInt(m[2], 10); const ap = m[3].toUpperCase();
        if (ap === 'PM' && h !== 12) h += 12;
        if (ap === 'AM' && h === 12) h = 0;
        return h * 60 + mm;
      }

      let product = null;
      let oriData = []; // dynamic ORIs from Firestore

      // Restrict date picker to today and forward (no past dates)
      if (dateInput) {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        dateInput.min = yyyy + '-' + mm + '-' + dd;

        // Restrict to allowed weekdays. The native date picker can't grey
        // out individual weekdays, so we validate on change: if the chosen
        // date's day-of-week isn't allowed, clear it and show a hint.
        const allowedDays = (root.dataset.allowedDays || '')
          .split(',').map((s) => s.trim()).filter((s) => s !== '').map(Number);
        // Per-day time-slot overrides. JSON map { "0":"...", "1":"...", ... }
        // (comma-separated time strings) keyed by JS day-of-week.
        let timesByDay = null;
        try { timesByDay = JSON.parse(root.dataset.timesByDay || 'null'); } catch (e) {}
        const timeSelect = root.querySelector('#sm-fp-time');
        // Server-rendered time list, used when there are no per-day overrides
        // so admin-blocked time ranges can still be filtered out.
        const baseTimeOpts = timeSelect
          ? Array.prototype.slice.call(timeSelect.options).map((o) => o.value).filter(Boolean)
          : [];
        const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

        function fpHint() {
          let hint = dateInput.parentNode.querySelector('[data-sm-fp-day-hint]');
          if (!hint) {
            hint = document.createElement('div');
            hint.setAttribute('data-sm-fp-day-hint', '');
            hint.className = 'sm-field__error';
            hint.style.color = 'var(--sm-danger)';
            dateInput.parentNode.appendChild(hint);
          }
          return hint;
        }

        // Rebuild the time dropdown for the chosen day, dropping any slot that
        // falls inside an admin-blocked time range for that exact date.
        // Returns the number of remaining (bookable) slots.
        async function applyTimesForDay(dow) {
          if (!timeSelect) return 0;
          const prev = timeSelect.value;
          const opts = timesByDay
            ? (timesByDay[String(dow)] || '').split(',').map((s) => s.trim()).filter(Boolean)
            : baseTimeOpts.slice();
          let ranges = [];
          let booked = {};
          const d = dateInput.value;
          if (d && ns.fb && typeof ns.fb.getBlockedTimes === 'function') {
            try { ranges = await ns.fb.getBlockedTimes(Object.assign({ date: d }, fpBlockOpts)); } catch (e) { ranges = []; }
          }
          // Hide times that are already taken (slot_counters: confirmed + live holds).
          if (d && ns.fb && typeof ns.fb.getBookedSlots === 'function') {
            try { booked = (await ns.fb.getBookedSlots({ locationId: fpLocId, serviceId: fpRawSvc, resourceGroup: fpGroup, crossLocations: fpCross, date: d })) || {}; } catch (e) { booked = {}; }
          }
          const visible = opts.filter((t) => {
            if ((Number(booked[t]) || 0) >= fpCap) return false;
            const m = fpLabelToMin(t);
            if (m == null) return true;
            // Hide any appointment that overlaps a blocked range, not just ones starting inside it.
            const end = m + fpDuration;
            return !ranges.some((r) => m < r.endMin && end > r.startMin);
          });
          timeSelect.innerHTML = '<option value="">Choose a time</option>' +
            visible.map((t) => '<option value="' + t.replace(/"/g, '&quot;') + '">' + t + '</option>').join('');
          if (prev && visible.indexOf(prev) > -1) timeSelect.value = prev;
          return visible.length;
        }

        // Single change handler: weekday restriction → full-day block → time
        // blocks. Native date inputs can't grey out days, so blocked dates are
        // cleared with a message when picked.
        async function onFpDateChange() {
          if (!dateInput.value) return;
          const hint = fpHint();
          const dow = new Date(dateInput.value + 'T12:00:00').getDay();
          if (allowedDays.length && allowedDays.indexOf(dow) === -1) {
            dateInput.value = '';
            hint.textContent = 'Background checks are only available on: ' +
              allowedDays.map((n) => dayNames[n]).join(', ') + '. Please pick one of those days.';
            return;
          }
          if (fpBlockedDates[dateInput.value]) {
            dateInput.value = '';
            hint.textContent = 'That date is unavailable at this location. Please choose another day.';
            return;
          }
          hint.textContent = '';
          const remaining = await applyTimesForDay(dow);
          if (remaining === 0) {
            hint.textContent = 'All appointment times are blocked on that date. Please choose another day.';
          }
        }
        dateInput.addEventListener('change', onFpDateChange);
        fpRefreshTimes = async () => { if (dateInput.value) await applyTimesForDay(new Date(dateInput.value + 'T12:00:00').getDay()); };
      }

      // Dynamic mode: load ORIs from Firestore, populate the agency select,
      // then filter the ORI select to the chosen agency. Each ORI option
      // carries its own variant ID (no resolveVariant needed).
      if (dynamic && ns.fb && typeof ns.fb.getOris === 'function') {
        try { oriData = await ns.fb.getOris(); }
        catch (e) { console.error('[fp] getOris failed', e); }
        if (agencyField && agencySelect && oriData.length) {
          agencyField.hidden = false;
          const agencies = Array.from(new Set(oriData.map((o) => o.agency).filter(Boolean))).sort();
          agencySelect.innerHTML = '<option value="">Choose your agency</option>' +
            agencies.map((a) => '<option value="' + a.replace(/"/g,'&quot;') + '">' + a + '</option>').join('');
          const fillOriSelect = (agency) => {
            const matching = oriData.filter((o) => o.agency === agency);
            select.innerHTML = '<option value="">Choose your ORI</option>' +
              matching.map((o) => '<option value="' + o.id + '"' +
                ' data-variant-id="' + (o.shopifyVariantId || '') + '"' +
                ' data-ori-label="' + (o.label || o.agency || '').replace(/"/g,'&quot;') + '"' +
                ' data-ori-number="' + (o.oriNumber || '').replace(/"/g,'&quot;') + '"' +
                ' data-cost="' + (o.price || '').replace(/"/g,'&quot;') + '">' +
                (o.label || o.oriNumber) + (o.oriNumber ? ' (' + o.oriNumber + ')' : '') + (o.price ? ' — ' + o.price : '') +
              '</option>').join('');
          };
          select.innerHTML = '<option value="">Choose an agency first</option>';
          agencySelect.addEventListener('change', () => { fillOriSelect(agencySelect.value); if (summaryEl) summaryEl.hidden = true; });
        }
      }

      async function pickOri() {
        const opt = select.options[select.selectedIndex];
        if (!opt || !opt.value) {
          oriTypeInput && (oriTypeInput.value = '');
          oriNumberInput && (oriNumberInput.value = '');
          descEl && (descEl.textContent = '');
          if (summaryEl) summaryEl.hidden = true;
          return;
        }
        const matchValue = opt.dataset.matchValue || '';
        const label = opt.dataset.oriLabel || '';
        const oriNumber = opt.dataset.oriNumber || '';
        const cost = opt.dataset.cost || '';
        const directVariantId = opt.dataset.variantId || '';

        oriTypeInput && (oriTypeInput.value = label);
        oriNumberInput && (oriNumberInput.value = oriNumber);

        // Populate the visible summary card
        if (summaryEl) {
          summaryEl.hidden = false;
          if (summaryLabel) summaryLabel.textContent = label;
          if (summaryNumber) summaryNumber.textContent = oriNumber;
          if (summaryCost) summaryCost.textContent = cost;
        }

        // Dynamic mode: each ORI carries its own variant ID — use it
        // directly. Block mode: resolve the variant by match_value.
        if (directVariantId && variantInput) {
          variantInput.value = directVariantId;
          return;
        }
        if (productHandle && !product) {
          try { product = await fetchProduct(productHandle); }
          catch (e) { console.error('[fp] fetchProduct failed', e); }
        }
        if (product && variantInput) {
          const variant = resolveVariant(product, [matchValue]);
          if (variant) variantInput.value = variant.id;
        }
      }

      select && select.addEventListener('change', pickOri);

      // Stale-tab mitigation: reload an untouched form on wake (>10 min)
      // so ORI options and pricing variants are fresh.
      smOnWake(10 * 60 * 1000, () => {
        const touched = Array.from(form ? form.querySelectorAll('input:not([type="hidden"]), select, textarea') : [])
          .some((el) => (el.value || '').trim() !== '');
        if (!touched) window.location.reload();
      });

      const bookingIdInput = root.querySelector('[data-sm-fp-booking-id]');
      if (form) {
        let bookingDone = false;
        let bookedSlotKey = '';
        let processed = false;
        form.addEventListener('submit', async (e) => {
          if (processed) return;
          e.preventDefault();
          // Stale-tab guard: the appointment date must be today or later.
          // Browser date input's `min` attr can drift if the tab was open
          // for days; re-check here against a fresh new Date().
          const apptDate = (form.querySelector('input[type="date"]') || {}).value || '';
          if (apptDate && apptDate < smTodayLocal()) {
            if (statusEl) statusEl.textContent = 'That date has already passed. Please pick a current date.';
            const di = form.querySelector('input[type="date"]');
            if (di) di.value = '';
            return;
          }
          submitBtn && (submitBtn.disabled = true);
          statusEl && (statusEl.textContent = 'Adding to cart…');
          const val = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? (el.value || '').trim() : ''; };
          const oriLabel = (oriTypeInput && oriTypeInput.value) || '';
          // Booking record (best-effort; guarded so a cart retry doesn't
          // create a duplicate Firestore booking).
          const fpRecord = {
            location: root.dataset.locationId || '',
            locationName: root.dataset.locationName || '',
            service: root.dataset.serviceId || 'background-check',
            serviceName: (root.dataset.serviceLabel || 'Level 2 Background Check') + (oriLabel ? ' — ' + oriLabel : ''),
            date: val('properties[Appointment Date]'),
            time: val('properties[Appointment Time]'),
            customer: {
              firstName: val('properties[First Name]'),
              lastName: val('properties[Last Name]'),
              email: val('properties[Email]'),
              phone: val('properties[Phone]')
            },
            email: val('properties[Email]'),
            phone: val('properties[Phone]'),
            oriType: oriLabel,
            oriNumber: (oriNumberInput && oriNumberInput.value) || '',
            notes: val('properties[Notes]'),
            source: 'fingerprint-form'
          };
          // Preferred: hold the slot atomically (slot_counters) so two people
          // can't take the same appointment time. The orders/paid webhook
          // confirms the hold via the Booking ID line-item property.
          // If the customer changed the date/time after an earlier attempt, the
          // old hold no longer matches — start a fresh booking for the new slot.
          const fpSlotKey = (fpRecord.location || '') + '|' + (fpRecord.date || '') + '|' + (fpRecord.time || '');
          if (bookingDone && bookedSlotKey !== fpSlotKey) { bookingDone = false; if (bookingIdInput) bookingIdInput.value = ''; }
          if (!bookingDone && fpRecord.date && fpRecord.time && ns.fb && typeof ns.fb.createBookingAtomic === 'function') {
            try {
              const res = await ns.fb.createBookingAtomic({
                locationId: fpRecord.location, serviceId: fpRecord.service, resourceGroup: fpGroup, crossLocations: fpCross,
                date: fpRecord.date, time: fpRecord.time, maxPerSlot: fpCap, bookingData: fpRecord,
                // Never reuse this browser's earlier hold: a second person booked
                // from the same device must get their own booking (or "just taken").
                ownHoldIds: []
              });
              if (bookingIdInput && res && res.id) bookingIdInput.value = res.id;
              bookingDone = true; bookedSlotKey = fpSlotKey;
            } catch (err) {
              if (err && err.code === 'SLOT_FULL') {
                submitBtn && (submitBtn.disabled = false);
                statusEl && (statusEl.textContent = 'Sorry — that appointment time was just taken. Please pick another time.');
                if (fpRefreshTimes) { try { await fpRefreshTimes(); } catch (e) {} }
                return;
              }
              console.error('[fp] slot hold failed (falling back to a plain booking record)', err);
            }
          }
          if (!bookingDone && ns.fb && typeof ns.fb.createPublicBooking === 'function') {
            try {
              const res = await ns.fb.createPublicBooking(fpRecord);
              if (bookingIdInput && res && res.id) bookingIdInput.value = res.id;
              bookingDone = true; bookedSlotKey = fpSlotKey;
            } catch (err) {
              console.error('[fp] booking record failed (continuing to checkout)', err);
            }
          }
          processed = true;
          try {
            await smCartAdd(form);
            window.location.href = '/cart';
          } catch (cartErr) {
            processed = false;
            submitBtn && (submitBtn.disabled = false);
            statusEl && (statusEl.textContent = 'Could not add to cart. Please refresh the page and try again, or call us.');
            console.error('[fp] cart add failed', cartErr);
            try {
              if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
                ns.fb.logBookingFailure({
                  reason: 'fp_cart_add_failed',
                  error: (cartErr && cartErr.message) || String(cartErr),
                  serviceName: (root.dataset.serviceLabel || 'Level 2 Background Check') + (oriLabel ? ' — ' + oriLabel : ''),
                  locationName: root.dataset.locationName || '',
                  locationId: root.dataset.locationId || '',
                  productHandle: root.dataset.productHandle || '',
                  variantId: (variantInput && variantInput.value) || '',
                  date: val('properties[Appointment Date]'),
                  time: val('properties[Appointment Time]'),
                  customerEmail: val('properties[Email]'),
                  customerName: val('properties[First Name]') + ' ' + val('properties[Last Name]')
                });
              }
            } catch (e) {}
          }
        });
      }
    });
  }

  /* ---------- CNA Class form (class tile picker) ---------- */
  // POST a registration form to /cart/add.js instead of letting it submit
  // natively. A native POST that Shopify rejects (stale/wrong variant)
  // strands the customer on Shopify's error page and is invisible to our
  // failure log; the fetch version surfaces the error to the caller.
  async function smCartAdd(form) {
    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      body: new FormData(form),
      headers: { 'Accept': 'application/json' }
    });
    if (!resp.ok) {
      const body = await resp.text();
      throw new Error('cart/add ' + resp.status + ': ' + body.slice(0, 200));
    }
  }

  // Shared deposit / pay-in-full chooser used by the CNA + IV class forms.
  // Returns { getMode(), show(fullPriceCents) } or null when the section
  // has no deposit UI. Money values are in cents (Shopify /product.js units).
  function smInitPayChoice(root, opts) {
    const wrap = root.querySelector('[data-pay]');
    if (!wrap) return null;
    const depositCents = Math.round((Number(opts.depositAmount) || 100) * 100);
    const optDep = root.querySelector('[data-pay-opt="deposit"]');
    const optFull = root.querySelector('[data-pay-opt="full"]');
    const depNote = root.querySelector('[data-pay-deposit-note]');
    const fullNote = root.querySelector('[data-pay-full-note]');
    const amtEl = root.querySelector('[data-pay-deposit-amt]');
    const money = (c) => '$' + ((c / 100) % 1 === 0 ? (c / 100).toFixed(0) : (c / 100).toFixed(2));
    if (amtEl) amtEl.textContent = String(Math.round(depositCents / 100));
    let mode = 'deposit';
    function select(m) {
      mode = m;
      if (optDep) optDep.classList.toggle('is-selected', m === 'deposit');
      if (optFull) optFull.classList.toggle('is-selected', m === 'full');
    }
    optDep && optDep.addEventListener('click', () => select('deposit'));
    optFull && optFull.addEventListener('click', () => select('full'));
    select('deposit');
    return {
      getMode: () => mode,
      show(fullCents) {
        wrap.hidden = false;
        const bal = Math.max(0, (Number(fullCents) || 0) - depositCents);
        if (depNote) depNote.textContent = fullCents ? '· ' + money(bal) + ' balance due before class' : '';
        if (fullNote) fullNote.textContent = fullCents ? '· ' + money(fullCents) : '';
      }
    };
  }

  async function initCnaClassForms() {
    document.querySelectorAll('[data-sm-cna-class]').forEach(async (root) => {
      const productHandle = root.dataset.productHandle;
      const dynamic = root.dataset.dynamicCohorts === 'true';
      const cohortLocation = (root.dataset.cohortLocation || '').trim();
      const tilesWrap = root.querySelector('[data-sm-cna-class-tiles]');
      const variantInput = root.querySelector('[data-cna-class-variant]');
      const locInput = root.querySelector('[data-cna-class-loc]');
      const cohortInput = root.querySelector('[data-cna-class-cohort]');
      const scheduleInput = root.querySelector('[data-cna-class-schedule]');
      const errEl = root.querySelector('[data-cna-class-err]');
      const form = root.querySelector('[data-sm-cna-class-form]');
      const submitBtn = root.querySelector('[data-cna-class-submit]');
      const statusEl = root.querySelector('[data-cna-class-status]');
      let chosen = null;
      let product = null;

      // Deposit / pay-in-full config (from section settings).
      const allowDeposit = root.dataset.allowDeposit === 'true';
      const depositVariantId = (root.dataset.depositVariantId || '').trim();
      const depositAmount = Number(root.dataset.depositAmount || 100) || 100;
      const payChoice = allowDeposit ? smInitPayChoice(root, { depositAmount }) : null;
      // Resolve the full-tuition price (cents) for the chosen class so we
      // can show the balance and record it on the booking.
      async function fullPriceCentsFor(ch) {
        if (!productHandle) return 0;
        if (!product) { try { product = await fetchProduct(productHandle); } catch (e) { return 0; } }
        if (!product) return 0;
        let v = null;
        const idStr = String(ch.fullVariantId || ch.variantId || '');
        if (idStr) v = (product.variants || []).find((x) => String(x.id) === idStr);
        if (!v) { const needles = [ch.matchValue]; if (ch.numDays) needles.push(String(ch.numDays)); v = resolveVariant(product, needles); }
        if (!v && product.variants && product.variants.length === 1) v = product.variants[0];
        return v ? (Number(v.price) || 0) : 0;
      }

      // Dynamic mode: replace the hardcoded blocks with live classes
      // pulled from the admin → CNA Classes tab (Firestore cna_classes).
      if (dynamic && ns.fb && typeof ns.fb.getCnaClasses === 'function') {
        let classes = [];
        try {
          classes = await ns.fb.getCnaClasses({ location: cohortLocation || undefined, futureOnly: true });
        } catch (e) { console.error('[cna-class] getCnaClasses failed', e); }
        // Resolve location display names + addresses from the location docs.
        let locs = [];
        try { locs = (typeof ns.fb.getLocationsForService === 'function') ? await ns.fb.getLocationsForService('') : []; }
        catch (e) {}
        const locDoc  = (id) => locs.find((l) => l.id === id) || {};
        const locName = (id) => locDoc(id).name || id || '';
        const locAddr = (id) => locDoc(id).address || '';
        const fmtDate = (d) => { try { return new Date(d + 'T12:00:00').toLocaleDateString(undefined, { month:'short', day:'numeric' }); } catch(e) { return d; } };
        // Fetch the product once so we can show each class's price by
        // resolving its (location × days) variant up front.
        if (productHandle && !product) {
          try { product = await fetchProduct(productHandle); }
          catch (e) { console.error('[cna-class] fetchProduct failed', e); }
        }
        const fmtMoney = (cents) => {
          if (cents == null || isNaN(cents)) return '';
          const n = cents / 100;
          return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
        };
        // Look up a variant by exact Shopify ID — used to honor the
        // saved shopifyVariantId on each class doc so the tile price
        // matches the variant the customer actually checks out with.
        const variantById = (vid) => {
          if (!product || !vid) return null;
          const idStr = String(vid);
          return (product.variants || []).find((v) => String(v.id) === idStr) || null;
        };
        const priceFor = (lname, nd, vid) => {
          if (!product) return '';
          // Prefer the saved variant (admin picked it explicitly), then
          // fall back to substring matching (legacy classes), then to
          // the only-variant case (single-variant product).
          const v = (vid && variantById(vid))
                 || resolveVariant(product, [lname, String(nd)])
                 || (product.variants && product.variants.length === 1 ? product.variants[0] : null);
          return v ? fmtMoney(v.price) : '';
        };
        if (tilesWrap) {
          if (!classes.length) {
            tilesWrap.innerHTML = '<p class="sm-field__help">No upcoming CNA classes are scheduled right now. Please check back soon or contact us.</p>';
          } else {
            tilesWrap.innerHTML = classes.map((c) => {
              const nd = (c.sessions && c.sessions.length) || 0;
              const range = (c.startDate && c.endDate) ? (fmtDate(c.startDate) + ' — ' + fmtDate(c.endDate)) : (c.startDate ? fmtDate(c.startDate) : '');
              const lname = locName(c.location);
              const addr = locAddr(c.location);
              const total = c.seatsTotal || 0;
              const booked = (typeof c.seatsBooked === 'number') ? c.seatsBooked : 0;
              const avail = total ? Math.max(0, total - booked) : null;
              const full = total && avail <= 0;
              const price = priceFor(lname, nd, c.shopifyVariantId);
              // One row per session day: "Mon Jun 1   8:00 AM – 4:00 PM"
              const sessionRows = (c.sessions || []).map((s) => {
                let dlabel = s.date;
                try { dlabel = new Date(s.date + 'T12:00:00').toLocaleDateString(undefined, { weekday:'short', month:'short', day:'numeric' }); } catch(e) {}
                return '<div class="sm-cna-class__row">' +
                  '<span class="sm-cna-class__row-date">' + dlabel + '</span>' +
                  '<span class="sm-cna-class__row-time">' + (s.startTime || '') + ' – ' + (s.endTime || '') + '</span>' +
                '</div>';
              }).join('');
              // data-schedule keeps a compact string for the cart property.
              const schedAttr = (c.sessions || []).map((s) => fmtDate(s.date) + ' ' + (s.startTime || '') + '–' + (s.endTime || '')).join(' · ');
              return '<button type="button" class="sm-cna-class__tile' + (full ? ' is-full' : '') + '" data-cna-class-tile' +
                (full ? ' disabled' : '') +
                ' data-class-id="' + (c.id || '') + '"' +
                ' data-variant-id="' + String(c.shopifyVariantId || '').replace(/"/g,'&quot;') + '"' +
                ' data-match-value="' + String(lname).replace(/"/g,'&quot;') + '"' +
                ' data-num-days="' + nd + '"' +
                ' data-location="' + String(lname).replace(/"/g,'&quot;') + '"' +
                ' data-location-id="' + String(c.location || '').replace(/"/g,'&quot;') + '"' +
                ' data-start-date="' + String(c.startDate || '').replace(/"/g,'&quot;') + '"' +
                ' data-cohort="' + (c.label || '').replace(/"/g,'&quot;') + (range ? ' (' + range + ')' : '') + '"' +
                ' data-seats-total="' + (total || '') + '"' +
                ' data-seats-booked="' + (booked || 0) + '"' +
                ' data-schedule="' + String(schedAttr).replace(/"/g,'&quot;') + '">' +
                '<div class="sm-cna-class__tile-head">' +
                  '<div class="sm-cna-class__tile-loc">' + lname + '</div>' +
                  (addr ? '<div class="sm-cna-class__tile-addr">' + addr + '</div>' : '') +
                '</div>' +
                '<div class="sm-cna-class__tile-badge">' + range + (nd ? ' · ' + nd + '-day' : '') + '</div>' +
                '<div class="sm-cna-class__tile-rows">' + sessionRows + '</div>' +
                '<div class="sm-cna-class__tile-foot">' +
                  '<span class="sm-cna-class__tile-seats">' + (avail !== null ? (full ? 'Class full' : (avail + ' of ' + total + ' seats open')) : '') + '</span>' +
                  (price ? '<span class="sm-cna-class__tile-price">' + price + '</span>' : '') +
                '</div>' +
              '</button>';
            }).join('');
          }
        }
      }

      const tiles = root.querySelectorAll('[data-cna-class-tile]');
      tiles.forEach((tile) => tile.addEventListener('click', async () => {
        tiles.forEach((t) => t.classList.remove('is-selected'));
        tile.classList.add('is-selected');
        chosen = {
          classId: tile.dataset.classId || '',
          variantId: tile.dataset.variantId || '',
          matchValue: tile.dataset.matchValue || '',
          numDays: tile.dataset.numDays || '',
          location: tile.dataset.location || '',
          locationId: tile.dataset.locationId || '',
          startDate: tile.dataset.startDate || '',
          cohort: tile.dataset.cohort || '',
          schedule: tile.dataset.schedule || '',
          seatsTotal: tile.dataset.seatsTotal || '',
          seatsBooked: tile.dataset.seatsBooked || ''
        };
        if (locInput) locInput.value = chosen.location;
        if (cohortInput) cohortInput.value = chosen.cohort;
        if (scheduleInput) scheduleInput.value = chosen.schedule;
        if (errEl) errEl.textContent = '';

        // Resolve the full-tuition variant for this class. Prefer the class
        // doc's explicit Shopify variant ID; fall back to legacy substring
        // matching by location (+ numDays).
        let fullVariantId = '';
        if (chosen.variantId) {
          fullVariantId = String(chosen.variantId);
        } else if (productHandle) {
          if (!product) {
            try { product = await fetchProduct(productHandle); }
            catch (e) { console.error('[cna-class] fetchProduct failed', e); }
          }
          if (product) {
            const needles = [chosen.matchValue];
            if (chosen.numDays) needles.push(String(chosen.numDays));
            const variant = resolveVariant(product, needles);
            if (variant) {
              fullVariantId = String(variant.id);
            } else if (errEl) {
              errEl.textContent = 'This class isn’t available for online checkout yet — please call us.';
              console.warn('[cna-class] no variant for', needles, product && product.variants);
              try {
                if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
                  ns.fb.logBookingFailure({
                    reason: 'cna_variant_not_matched',
                    serviceName: 'CNA Class' + (chosen.cohort ? ' — ' + chosen.cohort : ''),
                    matchValue: chosen.matchValue || '',
                    locationName: chosen.location || '',
                    locationId: chosen.locationId || '',
                    productHandle: productHandle || '',
                    needles: needles,
                    classId: chosen.classId || '',
                    numDays: chosen.numDays || ''
                  });
                }
              } catch (e) {}
            }
          }
        }
        chosen.fullVariantId = fullVariantId;
        // Default the cart to the full-tuition variant; the submit handler
        // swaps in the deposit variant if the student picks "deposit".
        if (variantInput && fullVariantId) variantInput.value = fullVariantId;
        // Reveal + populate the deposit / pay-in-full chooser.
        if (payChoice) {
          const cents = await fullPriceCentsFor(chosen);
          chosen.fullPriceCents = cents;
          payChoice.show(cents);
        }
      }));

      // Stale-tab mitigation: if the tab wakes after >10 min hidden and the
      // visitor hasn't started the form, reload so class tiles, seat counts,
      // and variants are fresh. A started form is left alone — the seat
      // transaction + cart add validate at submit time.
      smOnWake(10 * 60 * 1000, () => {
        const touched = !!chosen || Array.from(form ? form.querySelectorAll('input:not([type="hidden"]), select, textarea') : [])
          .some((el) => (el.value || '').trim() !== '');
        if (!touched) window.location.reload();
      });

      const bookingIdInput = root.querySelector('[data-cna-class-booking-id]');
      if (form) {
        let processed = false;
        form.addEventListener('submit', async (e) => {
          if (processed) return; // our own form.submit() — let it POST natively
          if (!chosen) {
            e.preventDefault();
            if (errEl) errEl.textContent = 'Please pick a class.';
            return;
          }
          e.preventDefault();
          // Stale-tab guard: the class's startDate (if known) must be in
          // the future. Catches a customer returning to a days-old tab
          // whose class has since started.
          if (chosen.startDate && chosen.startDate < smTodayLocal()) {
            if (errEl) errEl.textContent = 'This class has already started. Please refresh the page and pick an upcoming class.';
            chosen = null;
            return;
          }
          submitBtn && (submitBtn.disabled = true);
          // Pending bookings no longer take a seat — only confirmed ones do
          // (counter is recomputed by admin actions and the orders/paid
          // webhook). Block submission only if the class is currently full
          // of CONFIRMED students, using the cached counts on the tile.
          const tTotal = Number(chosen.seatsTotal || 0);
          const tBooked = Number(chosen.seatsBooked || 0);
          if (tTotal && tBooked >= tTotal) {
            submitBtn && (submitBtn.disabled = false);
            if (errEl) errEl.textContent = 'Sorry, this class just filled up. Please pick another class.';
            return;
          }
          // Deposit vs pay-in-full: pick the cart variant + annotate the order.
          const payMode = (payChoice && payChoice.getMode() === 'deposit' && depositVariantId) ? 'deposit' : 'full';
          const fullCents = Number(chosen.fullPriceCents || 0);
          const depCents = Math.round(depositAmount * 100);
          const balCents = Math.max(0, fullCents - depCents);
          const money = (c) => '$' + ((c / 100) % 1 === 0 ? (c / 100).toFixed(0) : (c / 100).toFixed(2));
          const payPaymentInput = form.querySelector('[data-pay-payment]');
          if (payMode === 'deposit') {
            if (variantInput) variantInput.value = depositVariantId;
            if (payPaymentInput) payPaymentInput.value = 'Deposit ' + money(depCents) + ' paid — ' + money(balCents) + ' balance due before class';
          } else {
            if (variantInput && chosen.fullVariantId) variantInput.value = chosen.fullVariantId;
            if (payPaymentInput) payPaymentInput.value = 'Paid in full';
          }
          // Write a booking record so the class shows on the admin schedule.
          // Best-effort — never blocks checkout.
          if (ns.fb && typeof ns.fb.createPublicBooking === 'function') {
            const val = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? (el.value || '').trim() : ''; };
            try {
              const res = await ns.fb.createPublicBooking({
                location: chosen.locationId || chosen.location || '',
                locationName: chosen.location || '',
                service: productHandle || 'cna-in-person',
                serviceName: 'CNA Class' + (chosen.cohort ? ' — ' + chosen.cohort : ''),
                date: chosen.startDate || '',
                time: '',
                classId: chosen.classId || '',
                cohort: chosen.cohort || '',
                schedule: chosen.schedule || '',
                customer: {
                  firstName: val('properties[First Name]'),
                  lastName: val('properties[Last Name]'),
                  email: val('properties[Email]'),
                  phone: val('properties[Phone]'),
                  dob: val('properties[Date of Birth]')
                },
                email: val('properties[Email]'),
                phone: val('properties[Phone]'),
                payment: {
                  choice: payMode,
                  depositCents: payMode === 'deposit' ? depCents : 0,
                  fullPriceCents: fullCents,
                  balanceCents: payMode === 'deposit' ? balCents : 0,
                  balanceStatus: payMode === 'deposit' ? 'due' : 'paid'
                },
                source: 'cna-class-form'
              });
              if (bookingIdInput && res && res.id) bookingIdInput.value = res.id;
            } catch (err) {
              console.error('[cna-class] booking record failed (continuing to checkout)', err);
            }
          }
          processed = true;
          statusEl && (statusEl.textContent = 'Adding to cart…');
          try {
            await smCartAdd(form);
            window.location.href = '/cart';
          } catch (cartErr) {
            processed = false;
            submitBtn && (submitBtn.disabled = false);
            statusEl && (statusEl.textContent = '');
            if (errEl) errEl.textContent = 'Could not add to cart. Please refresh the page and try again, or call us.';
            console.error('[cna-class] cart add failed', cartErr);
            try {
              if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
                const valLocal = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? (el.value || '').trim() : ''; };
                ns.fb.logBookingFailure({
                  reason: 'cna_cart_add_failed',
                  error: (cartErr && cartErr.message) || String(cartErr),
                  serviceName: 'CNA Class' + (chosen.cohort ? ' — ' + chosen.cohort : ''),
                  classId: chosen.classId || '',
                  locationName: chosen.location || '',
                  locationId: chosen.locationId || '',
                  productHandle: productHandle || '',
                  variantId: (variantInput && variantInput.value) || '',
                  customerEmail: valLocal('properties[Email]'),
                  customerName: valLocal('properties[First Name]') + ' ' + valLocal('properties[Last Name]')
                });
              }
            } catch (e) {}
          }
        });
      }
    });
  }

  /* ---------- IV Class form (class tile picker) ---------- */
  async function initIvClassForms() {
    document.querySelectorAll('[data-sm-iv-class]').forEach(async (root) => {
      const productHandle = root.dataset.productHandle;
      const dynamic = root.dataset.dynamicCohorts === 'true';
      const cohortLocation = (root.dataset.cohortLocation || '').trim();
      const tilesWrap = root.querySelector('[data-sm-iv-class-tiles]');
      const variantInput = root.querySelector('[data-iv-class-variant]');
      const locInput = root.querySelector('[data-iv-class-loc]');
      const cohortInput = root.querySelector('[data-iv-class-cohort]');
      const scheduleInput = root.querySelector('[data-iv-class-schedule]');
      const errEl = root.querySelector('[data-iv-class-err]');
      const form = root.querySelector('[data-sm-iv-class-form]');
      const submitBtn = root.querySelector('[data-iv-class-submit]');
      const statusEl = root.querySelector('[data-iv-class-status]');
      let chosen = null;
      let product = null;

      // Deposit / pay-in-full config (from section settings).
      const allowDeposit = root.dataset.allowDeposit === 'true';
      const depositVariantId = (root.dataset.depositVariantId || '').trim();
      const depositAmount = Number(root.dataset.depositAmount || 100) || 100;
      const payChoice = allowDeposit ? smInitPayChoice(root, { depositAmount }) : null;
      async function fullPriceCentsFor(ch) {
        if (!productHandle) return 0;
        if (!product) { try { product = await fetchProduct(productHandle); } catch (e) { return 0; } }
        if (!product) return 0;
        let v = null;
        const idStr = String(ch.fullVariantId || ch.variantId || '');
        if (idStr) v = (product.variants || []).find((x) => String(x.id) === idStr);
        if (!v) { const needles = [ch.matchValue]; if (ch.numDays) needles.push(String(ch.numDays)); v = resolveVariant(product, needles); }
        if (!v && product.variants && product.variants.length === 1) v = product.variants[0];
        return v ? (Number(v.price) || 0) : 0;
      }

      if (dynamic && ns.fb && typeof ns.fb.getIvClasses === 'function') {
        let classes = [];
        try {
          classes = await ns.fb.getIvClasses({ location: cohortLocation || undefined, futureOnly: true });
        } catch (e) { console.error('[iv-class] getIvClasses failed', e); }
        let locs = [];
        try { locs = (typeof ns.fb.getLocationsForService === 'function') ? await ns.fb.getLocationsForService('') : []; }
        catch (e) {}
        const locDoc  = (id) => locs.find((l) => l.id === id) || {};
        const locName = (id) => locDoc(id).name || id || '';
        const locAddr = (id) => locDoc(id).address || '';
        const fmtDate = (d) => { try { return new Date(d + 'T12:00:00').toLocaleDateString(undefined, { month:'short', day:'numeric' }); } catch(e) { return d; } };
        if (productHandle && !product) {
          try { product = await fetchProduct(productHandle); }
          catch (e) { console.error('[iv-class] fetchProduct failed', e); }
        }
        const fmtMoney = (cents) => {
          if (cents == null || isNaN(cents)) return '';
          const n = cents / 100;
          return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
        };
        const variantById = (vid) => {
          if (!product || !vid) return null;
          const idStr = String(vid);
          return (product.variants || []).find((v) => String(v.id) === idStr) || null;
        };
        const priceFor = (lname, nd, vid) => {
          if (!product) return '';
          const v = (vid && variantById(vid)) || resolveVariant(product, [lname, String(nd)]) || (product.variants && product.variants.length === 1 ? product.variants[0] : null);
          return v ? fmtMoney(v.price) : '';
        };
        if (tilesWrap) {
          if (!classes.length) {
            tilesWrap.innerHTML = '<p class="sm-field__help">No upcoming IV certification classes are scheduled right now. Please check back soon or contact us.</p>';
          } else {
            tilesWrap.innerHTML = classes.map((c) => {
              const nd = (c.sessions && c.sessions.length) || 0;
              const range = (c.startDate && c.endDate) ? (fmtDate(c.startDate) + ' — ' + fmtDate(c.endDate)) : (c.startDate ? fmtDate(c.startDate) : '');
              const lname = locName(c.location);
              const addr = locAddr(c.location);
              const total = c.seatsTotal || 0;
              const booked = (typeof c.seatsBooked === 'number') ? c.seatsBooked : 0;
              const avail = total ? Math.max(0, total - booked) : null;
              const full = total && avail <= 0;
              const price = priceFor(lname, nd, c.shopifyVariantId);
              const sessionRows = (c.sessions || []).map((s) => {
                let dlabel = s.date;
                try { dlabel = new Date(s.date + 'T12:00:00').toLocaleDateString(undefined, { weekday:'short', month:'short', day:'numeric' }); } catch(e) {}
                return '<div class="sm-cna-class__row">' +
                  '<span class="sm-cna-class__row-date">' + dlabel + '</span>' +
                  '<span class="sm-cna-class__row-time">' + (s.startTime || '') + ' – ' + (s.endTime || '') + '</span>' +
                '</div>';
              }).join('');
              const schedAttr = (c.sessions || []).map((s) => fmtDate(s.date) + ' ' + (s.startTime || '') + '–' + (s.endTime || '')).join(' · ');
              return '<button type="button" class="sm-cna-class__tile' + (full ? ' is-full' : '') + '" data-iv-class-tile' +
                (full ? ' disabled' : '') +
                ' data-class-id="' + (c.id || '') + '"' +
                ' data-variant-id="' + String(c.shopifyVariantId || '').replace(/"/g,'&quot;') + '"' +
                ' data-match-value="' + String(lname).replace(/"/g,'&quot;') + '"' +
                ' data-num-days="' + nd + '"' +
                ' data-location="' + String(lname).replace(/"/g,'&quot;') + '"' +
                ' data-location-id="' + String(c.location || '').replace(/"/g,'&quot;') + '"' +
                ' data-start-date="' + String(c.startDate || '').replace(/"/g,'&quot;') + '"' +
                ' data-cohort="' + (c.label || '').replace(/"/g,'&quot;') + (range ? ' (' + range + ')' : '') + '"' +
                ' data-seats-total="' + (total || '') + '"' +
                ' data-seats-booked="' + (booked || 0) + '"' +
                ' data-schedule="' + String(schedAttr).replace(/"/g,'&quot;') + '">' +
                '<div class="sm-cna-class__tile-head">' +
                  '<div class="sm-cna-class__tile-loc">' + lname + '</div>' +
                  (addr ? '<div class="sm-cna-class__tile-addr">' + addr + '</div>' : '') +
                '</div>' +
                '<div class="sm-cna-class__tile-badge">' + range + (nd ? ' · ' + nd + '-day' : '') + '</div>' +
                '<div class="sm-cna-class__tile-rows">' + sessionRows + '</div>' +
                '<div class="sm-cna-class__tile-foot">' +
                  '<span class="sm-cna-class__tile-seats">' + (avail !== null ? (full ? 'Class full' : (avail + ' of ' + total + ' seats open')) : '') + '</span>' +
                  (price ? '<span class="sm-cna-class__tile-price">' + price + '</span>' : '') +
                '</div>' +
              '</button>';
            }).join('');
          }
        }
      }

      const tiles = root.querySelectorAll('[data-iv-class-tile]');
      tiles.forEach((tile) => tile.addEventListener('click', async () => {
        tiles.forEach((t) => t.classList.remove('is-selected'));
        tile.classList.add('is-selected');
        chosen = {
          classId: tile.dataset.classId || '',
          variantId: tile.dataset.variantId || '',
          matchValue: tile.dataset.matchValue || '',
          numDays: tile.dataset.numDays || '',
          location: tile.dataset.location || '',
          locationId: tile.dataset.locationId || '',
          startDate: tile.dataset.startDate || '',
          cohort: tile.dataset.cohort || '',
          schedule: tile.dataset.schedule || '',
          seatsTotal: tile.dataset.seatsTotal || '',
          seatsBooked: tile.dataset.seatsBooked || ''
        };
        if (locInput) locInput.value = chosen.location;
        if (cohortInput) cohortInput.value = chosen.cohort;
        if (scheduleInput) scheduleInput.value = chosen.schedule;
        if (errEl) errEl.textContent = '';

        // Resolve the full-tuition variant. Prefer the class doc's explicit
        // Shopify variant ID; fall back to fuzzy resolution against the product.
        let fullVariantId = '';
        if (chosen.variantId) {
          fullVariantId = String(chosen.variantId);
        } else if (productHandle) {
          if (!product) {
            try { product = await fetchProduct(productHandle); }
            catch (e) { console.error('[iv-class] fetchProduct failed', e); }
          }
          if (product) {
            const needles = [chosen.matchValue];
            if (chosen.numDays) needles.push(String(chosen.numDays));
            let variant = resolveVariant(product, needles);
            if (!variant && chosen.matchValue) variant = resolveVariant(product, [chosen.matchValue]);
            if (!variant && product.variants && product.variants.length === 1 && product.variants[0].available !== false) {
              variant = product.variants[0];
            }
            if (variant) {
              fullVariantId = String(variant.id);
            } else if (errEl) {
              errEl.textContent = 'This class isn’t available for online checkout yet — please call us.';
              console.warn('[iv-class] no variant for', needles, product && product.variants);
              try {
                if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
                  ns.fb.logBookingFailure({
                    reason: 'iv_variant_not_matched',
                    serviceName: 'IV Class' + (chosen.cohort ? ' — ' + chosen.cohort : ''),
                    matchValue: chosen.matchValue || '',
                    locationName: chosen.location || '',
                    locationId: chosen.locationId || '',
                    productHandle: productHandle || '',
                    needles: needles,
                    classId: chosen.classId || '',
                    numDays: chosen.numDays || ''
                  });
                }
              } catch (e) {}
            }
          }
        }
        chosen.fullVariantId = fullVariantId;
        if (variantInput && fullVariantId) variantInput.value = fullVariantId;
        if (payChoice) {
          const cents = await fullPriceCentsFor(chosen);
          chosen.fullPriceCents = cents;
          payChoice.show(cents);
        }
      }));

      // Stale-tab mitigation: reload an untouched form on wake (>10 min)
      // so class tiles, seat counts, and variants are fresh.
      smOnWake(10 * 60 * 1000, () => {
        const touched = !!chosen || Array.from(form ? form.querySelectorAll('input:not([type="hidden"]), select, textarea') : [])
          .some((el) => (el.value || '').trim() !== '');
        if (!touched) window.location.reload();
      });

      const bookingIdInput = root.querySelector('[data-iv-class-booking-id]');
      if (form) {
        let processed = false;
        form.addEventListener('submit', async (e) => {
          if (processed) return;
          if (!chosen) {
            e.preventDefault();
            if (errEl) errEl.textContent = 'Please pick a class.';
            return;
          }
          e.preventDefault();
          // Stale-tab guard: class start date must be in the future.
          if (chosen.startDate && chosen.startDate < smTodayLocal()) {
            if (errEl) errEl.textContent = 'This class has already started. Please refresh the page and pick an upcoming class.';
            chosen = null;
            return;
          }
          submitBtn && (submitBtn.disabled = true);
          if (dynamic && chosen.classId && ns.fb && typeof ns.fb.reserveIvSeat === 'function') {
            statusEl && (statusEl.textContent = 'Reserving your seat…');
            try {
              await ns.fb.reserveIvSeat(chosen.classId);
            } catch (err) {
              submitBtn && (submitBtn.disabled = false);
              statusEl && (statusEl.textContent = '');
              if (errEl) errEl.textContent = (err && err.code === 'SEATS_FULL')
                ? 'Sorry, this class just filled up. Please pick another class.'
                : 'Could not reserve a seat. Please try again.';
              console.error('[iv-class] reserveIvSeat failed', err);
              try {
                if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
                  const valLocal = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? (el.value || '').trim() : ''; };
                  ns.fb.logBookingFailure({
                    reason: (err && err.code === 'SEATS_FULL') ? 'iv_seats_full' : 'iv_seat_reserve_failed',
                    error: (err && err.message) || String(err),
                    serviceName: 'IV Class' + (chosen.cohort ? ' — ' + chosen.cohort : ''),
                    classId: chosen.classId || '',
                    locationName: chosen.location || '',
                    locationId: chosen.locationId || '',
                    productHandle: productHandle || '',
                    customerEmail: valLocal('properties[Email]'),
                    customerName: valLocal('properties[First Name]') + ' ' + valLocal('properties[Last Name]'),
                    lpnLicense: valLocal('properties[LPN License #]')
                  });
                }
              } catch (e) {}
              return;
            }
          }
          // Deposit vs pay-in-full: pick the cart variant + annotate the order.
          const payMode = (payChoice && payChoice.getMode() === 'deposit' && depositVariantId) ? 'deposit' : 'full';
          const fullCents = Number(chosen.fullPriceCents || 0);
          const depCents = Math.round(depositAmount * 100);
          const balCents = Math.max(0, fullCents - depCents);
          const money = (c) => '$' + ((c / 100) % 1 === 0 ? (c / 100).toFixed(0) : (c / 100).toFixed(2));
          const payPaymentInput = form.querySelector('[data-pay-payment]');
          if (payMode === 'deposit') {
            if (variantInput) variantInput.value = depositVariantId;
            if (payPaymentInput) payPaymentInput.value = 'Deposit ' + money(depCents) + ' paid — ' + money(balCents) + ' balance due before class';
          } else {
            if (variantInput && chosen.fullVariantId) variantInput.value = chosen.fullVariantId;
            if (payPaymentInput) payPaymentInput.value = 'Paid in full';
          }
          if (ns.fb && typeof ns.fb.createPublicBooking === 'function') {
            const val = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? (el.value || '').trim() : ''; };
            try {
              const res = await ns.fb.createPublicBooking({
                location: chosen.locationId || chosen.location || '',
                locationName: chosen.location || '',
                service: productHandle || 'iv-certification',
                serviceName: 'IV Class' + (chosen.cohort ? ' — ' + chosen.cohort : ''),
                date: chosen.startDate || '',
                time: '',
                classId: chosen.classId || '',
                cohort: chosen.cohort || '',
                schedule: chosen.schedule || '',
                lpnLicense: val('properties[LPN License #]'),
                customer: {
                  firstName: val('properties[First Name]'),
                  lastName: val('properties[Last Name]'),
                  email: val('properties[Email]'),
                  phone: val('properties[Phone]'),
                  lpnLicense: val('properties[LPN License #]')
                },
                email: val('properties[Email]'),
                phone: val('properties[Phone]'),
                payment: {
                  choice: payMode,
                  depositCents: payMode === 'deposit' ? depCents : 0,
                  fullPriceCents: fullCents,
                  balanceCents: payMode === 'deposit' ? balCents : 0,
                  balanceStatus: payMode === 'deposit' ? 'due' : 'paid'
                },
                source: 'iv-class-form'
              });
              if (bookingIdInput && res && res.id) bookingIdInput.value = res.id;
            } catch (err) {
              console.error('[iv-class] booking record failed (continuing to checkout)', err);
            }
          }
          processed = true;
          statusEl && (statusEl.textContent = 'Adding to cart…');
          try {
            await smCartAdd(form);
            window.location.href = '/cart';
          } catch (cartErr) {
            processed = false;
            submitBtn && (submitBtn.disabled = false);
            statusEl && (statusEl.textContent = '');
            if (errEl) errEl.textContent = 'Could not add to cart. Please refresh the page and try again, or call us.';
            console.error('[iv-class] cart add failed', cartErr);
            try {
              if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
                const valLocal = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? (el.value || '').trim() : ''; };
                ns.fb.logBookingFailure({
                  reason: 'iv_cart_add_failed',
                  error: (cartErr && cartErr.message) || String(cartErr),
                  serviceName: 'IV Class' + (chosen.cohort ? ' — ' + chosen.cohort : ''),
                  classId: chosen.classId || '',
                  locationName: chosen.location || '',
                  locationId: chosen.locationId || '',
                  productHandle: productHandle || '',
                  variantId: (variantInput && variantInput.value) || '',
                  customerEmail: valLocal('properties[Email]'),
                  customerName: valLocal('properties[First Name]') + ' ' + valLocal('properties[Last Name]'),
                  lpnLicense: valLocal('properties[LPN License #]')
                });
              }
            } catch (e) {}
          }
        });
      }
    });
  }

  /* ---------- Exam-register form (clamps date to today+ ) ---------- */
  function initExamRegForms() {
    document.querySelectorAll('[data-sm-exam-reg]').forEach((root) => {
      const dateInput = root.querySelector('[data-exam-reg-date]');
      const form = root.querySelector('[data-sm-exam-reg-form]');
      const submitBtn = root.querySelector('[data-exam-reg-submit]');
      const statusEl = root.querySelector('[data-exam-reg-status]');

      if (dateInput) {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        dateInput.min = yyyy + '-' + mm + '-' + dd;
      }

      if (form) {
        form.addEventListener('submit', () => {
          submitBtn && (submitBtn.disabled = true);
          statusEl && (statusEl.textContent = 'Adding to cart…');
        });
      }
    });
  }

  /* ---------- Elks convention registration form ---------- */
  function initElksForms() {
    document.querySelectorAll('[data-sm-elks]').forEach((root) => {
      const form = root.querySelector('[data-sm-elks-form]');
      const submitBtn = root.querySelector('[data-sm-elks-submit]');
      const statusEl = root.querySelector('[data-sm-elks-status]');
      const successEl = root.querySelector('[data-sm-elks-success]');
      if (!form) return;

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const val = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? (el.value || '').trim() : ''; };
        const firstName = val('firstName');
        const lastName  = val('lastName');
        const lodge     = val('lodge');
        const email     = val('email');
        if (!firstName || !lastName || !lodge || !email) {
          statusEl && (statusEl.textContent = 'Please fill in all required fields.');
          return;
        }
        submitBtn && (submitBtn.disabled = true);
        statusEl && (statusEl.textContent = 'Submitting…');
        try {
          if (!(ns.fb && typeof ns.fb.submitElksRegistration === 'function')) {
            throw new Error('Registration is temporarily unavailable. Please try again shortly.');
          }
          await ns.fb.submitElksRegistration({
            firstName: firstName,
            lastName: lastName,
            lodge: lodge,
            memberNumber: val('memberNumber'),
            email: email,
            event: 'Florida Elks Association Convention',
            source: 'elks-form'
          });
          form.hidden = true;
          if (successEl) successEl.hidden = false;
          try { (successEl || form).scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (err2) {}
        } catch (err) {
          console.error('[elks] submit failed', err);
          submitBtn && (submitBtn.disabled = false);
          statusEl && (statusEl.textContent = err.message || 'Something went wrong. Please try again.');
        }
      });
    });
  }

  /* ---------- AHA online course picker ---------- */
  // Product page for the AHA eCard path: customer picks BLS / ACLS / PALS,
  // the tile carries its own Shopify variant id (resolved in Liquid), and
  // we set the hidden `id` before adding to cart. Reuses smCartAdd().
  function initAhaCoursePicker() {
    document.querySelectorAll('[data-sm-aha-course]').forEach((root) => {
      const tiles = root.querySelectorAll('[data-sm-aha-tile]');
      const form = root.querySelector('[data-sm-aha-form]');
      const variantInput = root.querySelector('[data-sm-aha-variant]');
      const courseNameInput = root.querySelector('[data-sm-aha-course-name]');
      const errEl = root.querySelector('[data-sm-aha-course-err]');
      const statusEl = root.querySelector('[data-sm-aha-status]');
      const submitBtn = root.querySelector('[data-sm-aha-submit]');
      if (!form || !variantInput) return;
      let processed = false;

      tiles.forEach((tile) => tile.addEventListener('click', () => {
        tiles.forEach((t) => t.classList.remove('is-selected'));
        tile.classList.add('is-selected');
        variantInput.value = tile.dataset.variantId || '';
        if (courseNameInput) courseNameInput.value = tile.dataset.courseName || '';
        if (errEl) errEl.textContent = '';
      }));

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (processed) return;
        if (!variantInput.value) {
          if (errEl) errEl.textContent = 'Please select a course first.';
          return;
        }
        processed = true;
        submitBtn && (submitBtn.disabled = true);
        statusEl && (statusEl.textContent = 'Adding to cart…');
        try {
          await smCartAdd(form);
          window.location.href = '/cart';
        } catch (cartErr) {
          processed = false;
          submitBtn && (submitBtn.disabled = false);
          statusEl && (statusEl.textContent = 'Could not add to cart. Please refresh and try again, or call us.');
          console.error('[aha] cart add failed', cartErr);
          try {
            if (ns.fb && typeof ns.fb.logBookingFailure === 'function') {
              const val = (n) => { const el = form.querySelector('[name="' + n + '"]'); return el ? (el.value || '').trim() : ''; };
              ns.fb.logBookingFailure({
                reason: 'aha_cart_add_failed',
                error: (cartErr && cartErr.message) || String(cartErr),
                serviceName: 'AHA Online Course' + (courseNameInput && courseNameInput.value ? ' — ' + courseNameInput.value : ''),
                variantId: variantInput.value || '',
                customerEmail: val('properties[Email]'),
                customerName: val('properties[First Name]') + ' ' + val('properties[Last Name]')
              });
            }
          } catch (e2) {}
        }
      });
    });
  }

  /* ---------- Business portal (B2B) ----------
     Customer-facing portal for business accounts (sections/sm-business.liquid).
     Logins are created by staff ("Send portal invite"); there is no public
     sign-up. Every rendered value goes through esc(). */
  async function initBusinessPortal() {
    const root = document.querySelector('[data-sm-business]');
    if (!root || !ns.fb) return;
    const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' };
    const esc = (v) => String(v == null ? '' : v).replace(/[&<>"'`]/g, (c) => ESC[c]);
    const TIERS = [5, 10, 15, 20, 30, 50];
    const COURSES = [{ key: 'bls', name: 'BLS' }, { key: 'acls', name: 'ACLS' }, { key: 'pals', name: 'PALS' }, { key: 'heartsaver', name: 'Heartsaver CPR/AED/FA' }];
    const loginPanel = root.querySelector('[data-smb-login]');
    const gatePanel = root.querySelector('[data-smb-gate]');
    const appPanel = root.querySelector('[data-smb-app]');
    const statusEl = root.querySelector('[data-smb-status]');
    const emailIn = root.querySelector('[data-smb-email]');
    const passIn = root.querySelector('[data-smb-pass]');
    let biz = null;
    const data = { emps: [], regs: [], certs: [], loaded: false };
    // Register-tab choices survive re-renders (e.g. after adding an employee).
    const reg = { course: null, empIds: new Set(), mode: null, location: '', date: '', officeTime: '', onsiteTime: '', address: '' };

    const money = (n) => (n || n === 0) && !isNaN(Number(n)) ? '$' + Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—';
    const todayNY = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
    const addDays = (ymd, n) => { const p = ymd.split('-').map(Number); return new Date(Date.UTC(p[0], p[1] - 1, p[2] + n, 12)).toISOString().slice(0, 10); };
    const fmtDate = (ymd) => { const p = String(ymd || '').split('-').map(Number); if (p.length !== 3 || !p[0]) return ymd || '—'; return new Date(Date.UTC(p[0], p[1] - 1, p[2], 12)).toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }); };
    const empName = (e) => ((e.firstName || '') + ' ' + (e.lastName || '')).trim() || '(no name)';
    // On-site times come from <input type=time> as "14:30"; show "2:30 PM".
    const fmtTime = (t) => { const m = String(t || '').match(/^(\d{1,2}):(\d{2})$/); if (!m) return String(t || ''); const hh = +m[1]; return ((hh % 12) || 12) + ':' + m[2] + ' ' + (hh >= 12 ? 'PM' : 'AM'); };
    // Highest tier <= n that has a rate (a blank tier falls back to the next
    // smaller one). MUST match b2bOnsiteUnit() in functions/index.js.
    const onsiteUnit = (rates, n) => { let best = null; TIERS.forEach((t) => { const v = Number((rates || {})[t]); if (n >= t && v > 0) best = { tier: t, unit: v }; }); return best; };
    const setStatus = (m, kind) => { if (!statusEl) return; statusEl.textContent = m || ''; statusEl.className = 'sm-field__help smb-status' + (kind === 'err' ? ' smb-msg-err' : kind === 'ok' ? ' smb-msg-ok' : ''); };
    const authMsg = (e) => {
      const c = (e && e.code) || '';
      if (/wrong-password|invalid-credential|user-not-found|invalid-login/.test(c)) return 'That email and password don’t match. First time here, or forgot it? Tap “Forgot / set your password”.';
      if (/too-many-requests/.test(c)) return 'Too many attempts. Wait a minute, or tap “Forgot / set your password”.';
      if (/invalid-email/.test(c)) return 'That doesn’t look like a valid email address.';
      if (/network/.test(c)) return 'Can’t reach the server. Check your internet connection.';
      return 'Sign-in failed. Please try again.';
    };
    const REG_STATUS = {
      invoiced: { label: 'Awaiting payment', cls: 'warn' }, paid: { label: 'Paid', cls: 'ok' },
      completed: { label: 'Completed', cls: 'info' }, cancelled: { label: 'Cancelled', cls: 'bad' }
    };
    const regPill = (s) => { const x = REG_STATUS[s] || REG_STATUS.invoiced; return '<span class="smb-pill smb-pill--' + x.cls + '">' + esc(x.label) + '</span>'; };
    const certPill = (st) => '<span class="smb-pill smb-pill--' + (st.k === 'ok' ? 'ok' : st.k === 'warn' ? 'warn' : st.k === 'exp' ? 'bad' : '') + '">' + esc(st.label) + '</span>';

    /* ----- auth ----- */
    function showOnly(which) { loginPanel.hidden = which !== 'login'; gatePanel.hidden = which !== 'gate'; appPanel.hidden = which !== 'app'; }
    function showGate(title, bodyHtml, actions) {
      const body = gatePanel.querySelector('[data-smb-gate-body]');
      body.innerHTML = '<p class="sm-eyebrow">STAT Med Training</p><h1 class="sm-h3" style="margin:2px 0 8px">' + esc(title) + '</h1>' + bodyHtml +
        '<div class="smb-gate-actions">' + actions.map((a, i) => '<button type="button" class="sm-btn sm-btn--sm ' + (a.primary ? 'sm-btn--primary' : 'sm-btn--light') + '" data-gate-act="' + i + '">' + esc(a.label) + '</button>').join('') + '</div>' +
        '<p class="sm-field__help" data-gate-msg style="margin-top:10px"></p>';
      body.querySelectorAll('[data-gate-act]').forEach((b) => b.addEventListener('click', () => actions[+b.dataset.gateAct].run(body.querySelector('[data-gate-msg]'))));
      showOnly('gate');
    }
    async function signOut() { try { await ns.fb.signOut(); } catch (e) {} biz = null; data.loaded = false; passIn.value = ''; setStatus(''); showOnly('login'); }
    const contactLine = '<p class="sm-field__help" style="margin-top:10px">Questions? Call or email your STAT Med account manager.</p>';

    async function afterAuth() {
      setStatus('Loading your account…');
      const u = ns.fb.currentUser && ns.fb.currentUser();
      const email = (u && u.email) || '';
      // Unconfirmed logins could have been created by someone else (old
      // open sign-up), so the portal only opens for a confirmed email.
      if (u && u.emailVerified === false) {
        let sentKey = 'smb_verify_sent_' + email;
        const send = async (msg) => {
          try { if (typeof ns.fb.sendEmailVerification === 'function') await ns.fb.sendEmailVerification(); msg.className = 'sm-field__help smb-msg-ok'; msg.textContent = 'Link sent to ' + email + '. Check your inbox (and spam).'; try { sessionStorage.setItem(sentKey, '1'); } catch (e) {} }
          catch (e) { msg.className = 'sm-field__help smb-msg-err'; msg.textContent = /too-many/.test((e && e.code) || '') ? 'A link was sent recently. Please wait a few minutes.' : 'Couldn’t send the link. Please try again.'; }
        };
        showGate('Confirm your email', '<p class="sm-field__help" style="font-size:14px">To protect your team’s records, please confirm that <strong>' + esc(email) + '</strong> is your address. Tap “Email me a link”, open the email and click the link, then come back and tap “I’ve confirmed”.</p>', [
          { label: 'Email me a link', primary: true, run: send },
          { label: 'I’ve confirmed', run: async (msg) => { try { if (typeof ns.fb.reloadUser === 'function') await ns.fb.reloadUser(); } catch (e) {} const x = ns.fb.currentUser && ns.fb.currentUser(); if (x && x.emailVerified === false) { msg.className = 'sm-field__help smb-msg-err'; msg.textContent = 'Not confirmed yet. Click the link in the email first.'; return; } afterAuth(); } },
          { label: 'Sign out', run: signOut }
        ]);
        return;
      }
      try { biz = await ns.fb.getMyBusiness(); }
      catch (e) { showOnly('login'); setStatus('We couldn’t load your account. Please try again in a minute, or contact STAT Med.', 'err'); console.warn('[business] load failed', e); return; }
      if (!biz) {
        // Don't sign out: this may be a staff member previewing the portal,
        // and signing out here would also sign them out of the staff admin.
        showGate('This login isn’t linked to a business', '<p class="sm-field__help" style="font-size:14px">You’re signed in as <strong>' + esc(email) + '</strong>, but no business account uses this email. If your company has an account, ask STAT Med to add this email as an account admin.</p>' + contactLine, [{ label: 'Sign out', primary: true, run: signOut }]);
        return;
      }
      if (biz.active === false) {
        showGate('Your account is paused', '<p class="sm-field__help" style="font-size:14px">The business portal for <strong>' + esc(biz.name || 'your company') + '</strong> is currently paused, so new registrations can’t be made. Please contact STAT Med to reactivate it.</p>' + contactLine, [{ label: 'Sign out', primary: true, run: signOut }]);
        return;
      }
      setStatus('');
      showApp();
    }
    async function doSignIn(e) {
      if (e) e.preventDefault();
      const email = (emailIn.value || '').trim().toLowerCase(), pass = passIn.value || '';
      if (!email || !pass) { setStatus('Enter your email and password.', 'err'); return; }
      const btn = root.querySelector('[data-smb-signin]'); btn.disabled = true;
      setStatus('Signing in…');
      try { await ns.fb.signIn(email, pass); await afterAuth(); } catch (err) { setStatus(authMsg(err), 'err'); }
      btn.disabled = false;
    }
    async function doForgot() {
      const email = (emailIn.value || '').trim().toLowerCase();
      if (!email) { setStatus('Type your work email above first, then tap “Forgot / set your password”.', 'err'); emailIn.focus(); return; }
      if (typeof ns.fb.sendPasswordReset !== 'function') { setStatus('Password reset isn’t available right now. Please contact STAT Med.', 'err'); return; }
      setStatus('Sending…');
      try { await ns.fb.sendPasswordReset(email); setStatus('If ' + email + ' is set up for the business portal, a link to set your password is on its way. Check your inbox (and spam).', 'ok'); }
      catch (err) { setStatus(/invalid-email/.test((err && err.code) || '') ? 'That doesn’t look like a valid email address.' : /too-many/.test((err && err.code) || '') ? 'Too many requests. Please wait a few minutes.' : 'Couldn’t send the link. Please try again.', 'err'); }
    }

    root.querySelector('[data-smb-form]').addEventListener('submit', doSignIn);
    root.querySelector('[data-smb-forgot]').addEventListener('click', doForgot);
    root.querySelector('[data-smb-signout]').addEventListener('click', signOut);
    root.querySelectorAll('[data-smb-nav]').forEach((n) => n.addEventListener('click', () => go(n.dataset.smbNav)));
    function go(v) {
      root.querySelectorAll('[data-smb-nav]').forEach((n) => { const on = n.dataset.smbNav === v; n.classList.toggle('is-active', on); if (on) n.setAttribute('aria-current', 'page'); else n.removeAttribute('aria-current'); });
      root.querySelectorAll('[data-smb-view]').forEach((s) => { s.hidden = s.dataset.smbView !== v; });
      const top = root.getBoundingClientRect().top + window.scrollY - 10; if (window.scrollY > top) window.scrollTo(0, Math.max(0, top));
    }

    async function showApp() {
      showOnly('app');
      const name = biz.name || 'Your business';
      root.querySelector('[data-smb-bizname]').textContent = name;
      root.querySelector('[data-smb-logo]').textContent = name.split(/\s+/).filter((w) => /^[A-Za-z0-9]/.test(w)).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || 'B';
      root.querySelector('[data-smb-bizloc]').textContent = [biz.city, biz.state].filter(Boolean).join(', ');
      const u = ns.fb.currentUser && ns.fb.currentUser();
      root.querySelector('[data-smb-whoami]').textContent = (u && u.email) || '';
      root.querySelectorAll('[data-smb-view]').forEach((v) => { v.innerHTML = '<p class="sm-field__help">Loading…</p>'; });
      go('overview');
      await loadData();
      renderAll();
    }
    async function loadData() {
      const safe = (p) => p.then((x) => x || []).catch((e) => { console.warn('[business] load', e); return null; });
      const r = await Promise.all([safe(ns.fb.adminListBizEmployees(biz.id)), safe(ns.fb.adminListRegistrations(biz.id)), safe(ns.fb.adminListCertifications(biz.id))]);
      data.emps = r[0] || []; data.regs = r[1] || []; data.certs = r[2] || [];
      data.failed = { emps: !r[0], regs: !r[1], certs: !r[2] };
      // Drop selections for employees who no longer exist.
      const ids = new Set(data.emps.map((e) => e.id));
      Array.from(reg.empIds).forEach((id) => { if (!ids.has(id)) reg.empIds.delete(id); });
      data.loaded = true;
    }
    // Every tab reads the same data, so a roster change refreshes them all.
    function renderAll() { renderOverview(); renderTrainings(); renderRegister(); renderEmployees(); renderCerts(); }
    async function refreshAll() { await loadData(); renderAll(); }
    const head = (eyebrow, title) => '<div class="smb-head"><p class="sm-eyebrow">' + esc(eyebrow) + '</p><h1 class="sm-h3">' + esc(title) + '</h1></div>';
    const loadErr = (what) => '<p class="sm-field__help smb-msg-err" style="margin:0 0 12px">Couldn’t load ' + esc(what) + '. Refresh the page to try again.</p>';
    const certStats = () => { const s = data.certs.map((c) => smCertStatus(c.renewalDate)); return { ok: s.filter((x) => x.k === 'ok').length, warn: s.filter((x) => x.k === 'warn').length, exp: s.filter((x) => x.k === 'exp').length }; };
    const regDate = (r) => (r.schedule && r.schedule.date) || '';
    const regWhere = (r) => { const s = r.schedule || {}; return r.mode === 'onsite' ? ('At your location' + (s.address ? ': ' + s.address : '')) : ('STAT Med ' + (s.location || 'office')); };
    const regStatus = (r) => (REG_STATUS[r.status] ? r.status : 'invoiced');
    const upcomingRegs = () => data.regs.filter((r) => regStatus(r) !== 'cancelled' && regDate(r) >= todayNY()).sort((a, b) => (regDate(a) + (a.schedule && a.schedule.time || '')).localeCompare(regDate(b) + (b.schedule && b.schedule.time || '')));

    function trainingCard(r, opts) {
      const s = r.schedule || {}; const st = regStatus(r);
      const people = (r.employees || []);
      const count = Number(r.count) || people.length;
      return '<div class="smb-train"><div class="smb-train__top"><div style="min-width:0">' +
          '<div class="smb-train__title">' + esc(r.courseName || r.course || 'Training') + ' · ' + esc(fmtDate(s.date)) + (s.time ? ' · ' + esc(fmtTime(s.time)) : '') + '</div>' +
          '<div class="smb-train__meta">' + esc(regWhere(r)) + ' · ' + esc(count) + ' attendee' + (count === 1 ? '' : 's') + (r.total != null ? ' · ' + esc(money(r.total)) : '') + '</div>' +
        '</div>' + regPill(st) + '</div>' +
        (st === 'invoiced' ? '<div class="smb-train__pay">' + (/^https:\/\//.test(r.invoiceUrl || '') ? '<a class="sm-btn sm-btn--primary sm-btn--sm" href="' + esc(r.invoiceUrl) + '" target="_blank" rel="noopener">Pay invoice</a>' : '') + '<span class="sm-field__help">Seats are held until the invoice is paid.</span>' + '<p class="sm-policy-link">By ' + 'paying' + ', you agree to our <a href="' + esc(((window.StatMedConfig && window.StatMedConfig.refundPolicyUrl) || '/pages/refund-policy') + '#other-situations') + '" target="_blank" rel="noopener">Refund Policy</a>.</p>' + '</div>' : '') +
        (!opts || opts.attendees !== false ? (people.length ? '<details><summary>Attendees (' + esc(people.length) + ')</summary><ul>' + people.map((p) => '<li>' + esc(p.name || '') + (p.employeeId ? ' <span class="sm-field__help">#' + esc(p.employeeId) + '</span>' : '') + '</li>').join('') + '</ul></details>' : '') : '') +
        '</div>';
    }

    function renderOverview() {
      const el = root.querySelector('[data-smb-view="overview"]');
      const cs = certStats();
      const due = data.certs.map((c) => ({ c, s: smCertStatus(c.renewalDate) })).filter((x) => x.s.k === 'warn' || x.s.k === 'exp').sort((a, b) => (a.s.days || 0) - (b.s.days || 0));
      const up = upcomingRegs().slice(0, 3);
      el.innerHTML = head('Dashboard', 'Welcome, ' + (biz.name || '')) +
        '<div class="smb-kpis">' +
          '<div class="sm-card smb-kpi"><span class="sm-field__help">Employees</span><div class="v">' + esc(data.emps.length) + '</div></div>' +
          '<div class="sm-card smb-kpi"><span class="sm-field__help">Certs valid</span><div class="v" style="color:var(--sm-success,#0f9d6b)">' + esc(cs.ok) + '</div></div>' +
          '<div class="sm-card smb-kpi"><span class="sm-field__help">Expiring ≤60 days</span><div class="v" style="color:#b07d12">' + esc(cs.warn) + '</div></div>' +
          '<div class="sm-card smb-kpi"><span class="sm-field__help">Expired</span><div class="v" style="color:var(--sm-danger,#d6453f)">' + esc(cs.exp) + '</div></div>' +
        '</div>' +
        '<div class="sm-card smb-panel"><div class="sm-flex" style="justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:10px"><h3 class="sm-h4" style="margin:0">Upcoming training</h3>' + (data.regs.length ? '<button type="button" class="smb-link" data-smb-go="trainings">See all trainings</button>' : '') + '</div>' +
          (up.length ? up.map((r) => trainingCard(r, { attendees: false })).join('') : '<p class="sm-field__help" style="margin:0">No training scheduled yet. Tap “Register training” to book your team.</p>') +
        '</div>' +
        (due.length ? '<div class="sm-card smb-panel"><h3 class="sm-h4">Renewals coming due</h3><div class="smb-tablewrap"><table class="sm-table"><thead><tr><th>Employee</th><th>Course</th><th>Renews</th><th>Status</th></tr></thead><tbody>' +
          due.slice(0, 8).map(({ c, s }) => '<tr><td><strong>' + esc(c.employeeName || '') + '</strong></td><td>' + esc(c.course || '') + '</td><td class="tnum">' + esc(fmtDate(c.renewalDate)) + '</td><td>' + certPill(s) + '</td></tr>').join('') +
          '</tbody></table></div></div>' : '') +
        '<div class="sm-card smb-panel"><h3 class="sm-h4">Quick actions</h3>' +
          '<div class="smb-actions" style="margin-top:0">' +
            '<button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-smb-go="register">Register training</button>' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-smb-go="employees">Manage employees</button>' +
            '<button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-smb-go="certs">View certifications</button>' +
          '</div><p class="sm-field__help" style="margin-top:10px">Your negotiated rates apply automatically at checkout.</p></div>';
      el.querySelectorAll('[data-smb-go]').forEach((b) => b.addEventListener('click', () => go(b.dataset.smbGo)));
    }

    function renderTrainings() {
      const el = root.querySelector('[data-smb-view="trainings"]');
      const up = upcomingRegs();
      const upIds = new Set(up.map((r) => r.id));
      const past = data.regs.filter((r) => !upIds.has(r.id)).sort((a, b) => regDate(b).localeCompare(regDate(a)));
      const unpaid = up.filter((r) => regStatus(r) === 'invoiced').length;
      el.innerHTML = head('Your orders', 'Trainings') + (data.failed.regs ? loadErr('your trainings') : '') +
        (unpaid ? '<div class="sm-card smb-panel" style="background:rgba(193,132,26,.08)"><strong>' + esc(unpaid) + ' training' + (unpaid === 1 ? '' : 's') + ' awaiting payment.</strong> <span class="sm-field__help">Pay the invoice to confirm your seats.</span></div>' : '') +
        '<h3 class="sm-h4" style="margin:0 0 10px">Upcoming</h3>' +
        (up.length ? up.map((r) => trainingCard(r)).join('') : '<div class="sm-card smb-empty">No upcoming training. <button type="button" class="smb-link" data-smb-go="register">Register training</button></div>') +
        '<h3 class="sm-h4" style="margin:22px 0 10px">Past &amp; cancelled</h3>' +
        (past.length ? past.map((r) => trainingCard(r)).join('') : '<p class="sm-field__help">Nothing here yet.</p>');
      el.querySelectorAll('[data-smb-go]').forEach((b) => b.addEventListener('click', () => go(b.dataset.smbGo)));
    }

    function renderEmployees() {
      const el = root.querySelector('[data-smb-view="employees"]');
      const emps = data.emps;
      let editing = '';
      const rows = emps.length ? emps.map((e) =>
        '<tr><td><strong>' + esc(empName(e)) + '</strong></td>' +
        '<td>' + esc(e.position || '—') + '</td><td class="tnum">' + esc(e.employeeId || '—') + '</td>' +
        '<td class="sm-field__help">' + esc(e.email || '—') + (e.phone ? '<br>' + esc(e.phone) : '') + '</td>' +
        '<td style="text-align:right;white-space:nowrap"><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-emp-edit="' + esc(e.id) + '">Edit</button> ' +
          '<button type="button" class="sm-btn sm-btn--light sm-btn--sm smb-danger" data-emp-del="' + esc(e.id) + '" aria-label="Remove ' + esc(empName(e)) + '">Remove</button></td></tr>'
      ).join('') : '<tr><td colspan="5" class="smb-empty">No employees yet. Add your first below.</td></tr>';
      const ef = (ph, id, type) => '<input type="' + (type || 'text') + '" data-ef="' + id + '" placeholder="' + esc(ph) + '" aria-label="' + esc(ph) + '">';
      el.innerHTML = head('Your team', 'Employees (' + emps.length + ')') + (data.failed.emps ? loadErr('your employees') : '') +
        '<div class="sm-card"><div class="smb-tablewrap"><table class="sm-table"><thead><tr><th>Name</th><th>Position</th><th>Employee ID</th><th>Contact</th><th></th></tr></thead><tbody>' + rows + '</tbody></table></div></div>' +
        '<div class="sm-card smb-panel" style="margin-top:14px" data-emp-form><strong style="font-size:14px" data-emp-title>Add employee</strong>' +
          '<div class="smb-grid3">' + ef('First name', 'firstName') + ef('Last name', 'lastName') + ef('Position (RN, CNA…)', 'position') + '</div>' +
          '<div class="smb-grid3">' + ef('Employee ID', 'employeeId') + ef('Email', 'email', 'email') + ef('Phone', 'phone', 'tel') + '</div>' +
          '<div class="smb-actions"><button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-emp-save>Add employee</button><button type="button" class="sm-btn sm-btn--light sm-btn--sm" data-emp-cancel hidden>Cancel</button><span class="sm-field__help" data-emp-msg role="status"></span></div></div>' +
        '<details class="sm-card smb-panel smb-paste"><summary style="cursor:pointer;font-weight:700;font-size:14px">Add many at once (paste a list)</summary>' +
          '<p class="sm-field__help" style="margin:8px 0">One person per line: <em>First Last, Position, Employee ID, Email, Phone</em>. Only the name is required. You can paste straight from a spreadsheet.</p>' +
          '<textarea data-emp-paste aria-label="Employee list" placeholder="Jane Doe, RN, 1042, jane@clinic.com, (352) 555-0100"></textarea>' +
          '<div class="smb-actions"><button type="button" class="sm-btn sm-btn--primary sm-btn--sm" data-emp-import>Add these employees</button><span class="sm-field__help" data-emp-imsg role="status"></span></div></details>';

      const form = el.querySelector('[data-emp-form]');
      const get = (id) => { const x = form.querySelector('[data-ef="' + id + '"]'); return x ? x.value.trim() : ''; };
      const emsg = form.querySelector('[data-emp-msg]');
      const say = (node, m, kind) => { node.textContent = m; node.className = 'sm-field__help' + (kind ? ' smb-msg-' + kind : ''); };
      const reset = () => { form.querySelectorAll('[data-ef]').forEach((x) => { x.value = ''; }); editing = ''; form.querySelector('[data-emp-title]').textContent = 'Add employee'; form.querySelector('[data-emp-save]').textContent = 'Add employee'; form.querySelector('[data-emp-cancel]').hidden = true; };
      form.querySelector('[data-emp-save]').addEventListener('click', async (ev) => {
        const d = { firstName: get('firstName'), lastName: get('lastName'), position: get('position'), employeeId: get('employeeId'), email: get('email'), phone: get('phone') };
        if (!d.firstName && !d.lastName) { say(emsg, 'Enter a name.', 'err'); return; }
        ev.currentTarget.disabled = true; say(emsg, 'Saving…');
        try { await ns.fb.adminSaveBizEmployee(biz.id, editing || null, d); await refreshAll(); }
        catch (e) { ev.currentTarget.disabled = false; say(emsg, 'Couldn’t save. Please try again.', 'err'); console.warn(e); }
      });
      form.querySelector('[data-emp-cancel]').addEventListener('click', reset);
      el.querySelectorAll('[data-emp-del]').forEach((b) => b.addEventListener('click', async () => {
        // Two-tap confirm (no browser pop-ups).
        if (b.dataset.armed !== '1') { b.dataset.armed = '1'; b.textContent = 'Tap again to remove'; setTimeout(() => { if (b.isConnected) { b.dataset.armed = ''; b.textContent = 'Remove'; } }, 4000); return; }
        b.disabled = true; b.textContent = 'Removing…';
        try { await ns.fb.adminDeleteBizEmployee(biz.id, b.dataset.empDel); await refreshAll(); }
        catch (e) { b.disabled = false; b.dataset.armed = ''; b.textContent = 'Remove'; say(emsg, 'Couldn’t remove that employee. Please try again.', 'err'); console.warn(e); }
      }));
      el.querySelectorAll('[data-emp-edit]').forEach((b) => b.addEventListener('click', () => {
        const e = emps.find((x) => x.id === b.dataset.empEdit); if (!e) return; editing = e.id;
        ['firstName', 'lastName', 'position', 'employeeId', 'email', 'phone'].forEach((k) => { const x = form.querySelector('[data-ef="' + k + '"]'); if (x) x.value = e[k] || ''; });
        form.querySelector('[data-emp-title]').textContent = 'Edit ' + empName(e); form.querySelector('[data-emp-save]').textContent = 'Save changes'; form.querySelector('[data-emp-cancel]').hidden = false; form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }));
      const imsg = el.querySelector('[data-emp-imsg]');
      el.querySelector('[data-emp-import]').addEventListener('click', async (ev) => {
        const lines = el.querySelector('[data-emp-paste]').value.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
        const people = lines.map((l) => {
          const parts = l.split(/\t|,/).map((x) => x.trim());
          const nm = (parts[0] || '').split(/\s+/);
          return { firstName: nm.shift() || '', lastName: nm.join(' '), position: parts[1] || '', employeeId: parts[2] || '', email: parts[3] || '', phone: parts[4] || '' };
        }).filter((p) => p.firstName && !/^first/i.test(p.firstName + ' ' + p.lastName));
        if (!people.length) { say(imsg, 'Paste at least one name.', 'err'); return; }
        if (people.length > 200) { say(imsg, 'Please add 200 or fewer at a time.', 'err'); return; }
        ev.currentTarget.disabled = true; let done = 0;
        try { for (const p of people) { await ns.fb.adminSaveBizEmployee(biz.id, null, p); done++; say(imsg, 'Added ' + done + ' of ' + people.length + '…'); } await refreshAll(); }
        catch (e) { ev.currentTarget.disabled = false; say(imsg, 'Added ' + done + ' of ' + people.length + ', then something went wrong. Please try the rest again.', 'err'); await refreshAll(); }
      });
    }

    let availToken = 0;
    const avail = { key: '', loading: false, data: null };
    async function renderRegister() {
      const el = root.querySelector('[data-smb-view="register"]');
      let locations = [];
      try { locations = ((await ns.fb.getLocations()) || []).filter((l) => l && l.active !== false); } catch (e) {}
      const emps = data.emps;
      const office = biz.officeRates || {}, onsite = biz.onsiteRates || {};
      const hasOffice = COURSES.some((c) => Number(office[c.key]) > 0);
      const hasOnsite = COURSES.some((c) => onsiteUnit(onsite[c.key], 999));
      if (!reg.mode || (reg.mode === 'office' && !hasOffice) || (reg.mode === 'onsite' && !hasOnsite)) reg.mode = hasOffice ? 'office' : 'onsite';
      if (!reg.location || !locations.some((l) => l.id === reg.location)) reg.location = (locations[0] && locations[0].id) || '';
      const minDate = addDays(todayNY(), 1);
      const locName = (id) => { const l = locations.find((x) => x.id === id); return l ? (l.name || id) : id; };
      const availableCourses = () => COURSES.filter((c) => reg.mode === 'office' ? Number(office[c.key]) > 0 : !!onsiteUnit(onsite[c.key], 999));
      if (!hasOffice && !hasOnsite) {
        el.innerHTML = head('New session', 'Register training') + '<div class="sm-card smb-panel"><p style="margin:0">Your pricing hasn’t been set up yet. Please contact STAT Med and we’ll get your group rates in place.</p></div>';
        return;
      }
      el.innerHTML = head('New session', 'Register training') +
        '<div class="smb-reg-grid"><div class="smb-reg-col">' +
          '<div class="sm-card" style="padding:16px"><h3 class="sm-h4" style="margin:0 0 12px">1 · Where</h3><div class="smb-modes">' +
            (hasOffice ? '<button type="button" class="sm-btn sm-btn--sm" data-mode="office">At a STAT Med office</button>' : '') +
            (hasOnsite ? '<button type="button" class="sm-btn sm-btn--sm" data-mode="onsite">At your location</button>' : '') +
          '</div><p class="sm-field__help" style="margin:0" data-mode-help></p></div>' +
          '<div class="sm-card" style="padding:16px"><h3 class="sm-h4" style="margin:0 0 4px">2 · Choose a course</h3><p class="sm-field__help" style="margin:0 0 12px">Your negotiated rate is shown.</p><div class="smb-courses" data-rg-courses></div></div>' +
          '<div class="sm-card" style="padding:16px"><div class="sm-flex" style="justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:12px"><h3 class="sm-h4" style="margin:0">3 · Select employees <span class="sm-field__help" data-rg-count></span></h3>' +
            (emps.length ? '<span><button type="button" class="smb-link" data-rg-all>Select all</button> · <button type="button" class="smb-link" data-rg-none>Clear</button></span>' : '') + '</div><div class="smb-emps" data-rg-emps></div></div>' +
          '<div class="sm-card" style="padding:16px"><h3 class="sm-h4" style="margin:0 0 12px">4 · Date &amp; time</h3><div data-rg-where></div></div>' +
        '</div><div class="sm-card smb-summary"><h3 class="sm-h4" style="margin:0 0 12px">Summary</h3><div data-rg-summary></div></div></div>';
      const q = (s) => el.querySelector(s);

      function renderModes() {
        el.querySelectorAll('[data-mode]').forEach((b) => { const on = b.dataset.mode === reg.mode; b.classList.toggle('sm-btn--primary', on); b.classList.toggle('sm-btn--light', !on); b.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        q('[data-mode-help]').textContent = reg.mode === 'office' ? 'Your team trains at one of our offices, in a scheduled session.' : 'Our instructor comes to you. Minimum 5 people; your group-size rate applies.';
      }
      function renderCourses() {
        const list = availableCourses();
        if (!reg.course || !list.some((c) => c.key === reg.course)) reg.course = list[0] ? list[0].key : null;
        q('[data-rg-courses]').innerHTML = list.length ? list.map((c) => {
          let price;
          if (reg.mode === 'office') price = esc(money(office[c.key])) + ' <span class="sm-field__help">/seat</span>';
          else { const vals = TIERS.map((t) => Number((onsite[c.key] || {})[t])).filter((v) => v > 0); price = 'from ' + esc(money(Math.min.apply(null, vals))) + ' <span class="sm-field__help">/person</span>'; }
          return '<button type="button" class="smb-course' + (reg.course === c.key ? ' is-sel' : '') + '" data-course="' + esc(c.key) + '" aria-pressed="' + (reg.course === c.key) + '"><b>' + esc(c.name) + '</b><div style="margin-top:6px;font-weight:800">' + price + '</div></button>';
        }).join('') : '<p class="sm-field__help">No courses are priced for this option yet. Contact STAT Med.</p>';
        q('[data-rg-courses]').querySelectorAll('[data-course]').forEach((d) => d.addEventListener('click', () => { reg.course = d.dataset.course; renderCourses(); renderWhere(); renderSummary(); }));
      }
      function renderEmps() {
        q('[data-rg-emps]').innerHTML = emps.length ? emps.map((e) =>
          '<label class="smb-emp"><input type="checkbox" ' + (reg.empIds.has(e.id) ? 'checked' : '') + ' data-emp="' + esc(e.id) + '"><span><strong>' + esc(empName(e)) + '</strong>' + (e.position ? '<br><span class="sm-field__help">' + esc(e.position) + '</span>' : '') + '</span></label>'
        ).join('') : '<p class="sm-field__help">No employees yet. <button type="button" class="smb-link" data-smb-go="employees">Add them in the Employees tab</button> first.</p>';
        q('[data-rg-emps]').querySelectorAll('[data-emp]').forEach((c) => c.addEventListener('change', () => { if (c.checked) reg.empIds.add(c.dataset.emp); else reg.empIds.delete(c.dataset.emp); countChanged(); }));
        q('[data-rg-emps]').querySelectorAll('[data-smb-go]').forEach((b) => b.addEventListener('click', () => go(b.dataset.smbGo)));
        q('[data-rg-count]').textContent = '(' + reg.empIds.size + ' selected)';
      }
      function countChanged() { q('[data-rg-count]').textContent = '(' + reg.empIds.size + ' selected)'; renderWhere(); renderSummary(); }
      const allBtn = q('[data-rg-all]'); if (allBtn) allBtn.addEventListener('click', () => { emps.forEach((e) => reg.empIds.add(e.id)); renderEmps(); countChanged(); });
      const noneBtn = q('[data-rg-none]'); if (noneBtn) noneBtn.addEventListener('click', () => { reg.empIds.clear(); renderEmps(); countChanged(); });

      async function loadAvail() {
        const key = [reg.course, reg.location, reg.date].join('|');
        if (reg.mode !== 'office' || !reg.course || !reg.location || !reg.date) { avail.key = ''; avail.data = null; return; }
        if (avail.key === key && (avail.data || avail.loading)) return;
        avail.key = key; avail.loading = true; avail.data = null; const my = ++availToken;
        let res = null;
        try { res = typeof ns.fb.businessOfficeAvailability === 'function' ? await ns.fb.businessOfficeAvailability({ course: reg.course, locationId: reg.location, date: reg.date }) : null; }
        catch (e) { res = { ok: false, closedReason: 'Couldn’t check availability. Please try again.', slots: [] }; }
        if (my !== availToken) return;
        avail.loading = false; avail.data = res || { ok: false, closedReason: 'Couldn’t check availability.', slots: [] };
        if (!root.contains(el) || reg.mode !== 'office') return;
        renderWhere(); renderSummary();
      }
      function renderWhere() {
        const n = reg.empIds.size;
        if (reg.mode === 'office') {
          const a = avail.data;
          let timeHtml;
          if (!reg.date) timeHtml = '<p class="sm-field__help" style="margin:8px 0 0">Pick a date to see open times.</p>';
          else if (avail.loading || !a) timeHtml = '<p class="sm-field__help" style="margin:8px 0 0">Checking open times…</p>';
          else if (!a.ok) timeHtml = '<p class="sm-field__help smb-msg-warn" style="margin:8px 0 0">' + esc(a.closedReason || 'No times are available that day.') + '</p>';
          else {
            const usable = a.slots.filter((s) => !s.blocked && s.left >= Math.max(1, n));
            if (reg.officeTime && !usable.some((s) => s.time === reg.officeTime)) reg.officeTime = '';
            timeHtml = '<div class="sm-field" style="margin:12px 0 0"><label for="smb-rg-time">Time</label><select id="smb-rg-time" data-w="officeTime"><option value="">Choose a time…</option>' +
              a.slots.map((s) => { const off = s.blocked || s.left < Math.max(1, n); const label = s.time + ' — ' + (s.blocked ? 'unavailable' : s.left === 0 ? 'full' : s.left + ' seat' + (s.left === 1 ? '' : 's') + ' left'); return '<option value="' + esc(s.time) + '"' + (off ? ' disabled' : '') + (reg.officeTime === s.time ? ' selected' : '') + '>' + esc(label) + '</option>'; }).join('') +
              '</select></div>' + (!usable.length ? '<p class="sm-field__help smb-msg-warn" style="margin:8px 0 0">No time that day has ' + esc(n) + ' open seats. Try another date or office' + (hasOnsite ? ', or book on-site training' : '') + '.</p>' : '');
          }
          q('[data-rg-where]').innerHTML =
            '<div class="sm-form__row">' +
              '<div class="sm-field" style="margin:0"><label for="smb-rg-loc">Office</label><select id="smb-rg-loc" data-w="location">' + locations.map((l) => '<option value="' + esc(l.id) + '"' + (reg.location === l.id ? ' selected' : '') + '>' + esc(l.name || l.id) + '</option>').join('') + '</select></div>' +
              '<div class="sm-field" style="margin:0"><label for="smb-rg-date">Date</label><input id="smb-rg-date" type="date" data-w="date" min="' + esc(minDate) + '" value="' + esc(reg.date) + '"></div>' +
            '</div>' + timeHtml;
        } else {
          const rates = onsite[reg.course] || {};
          const pick = onsiteUnit(rates, n);
          q('[data-rg-where]').innerHTML =
            '<span class="sm-field__help">Your group rates (per person)</span>' +
            '<div class="smb-tiers">' + TIERS.map((t) => '<div class="smb-tier' + (pick && pick.tier === t ? ' is-active' : '') + '"><div class="sm-field__help">' + t + '+ people</div><div style="font-weight:800">' + esc(money(rates[t])) + '</div></div>').join('') + '</div>' +
            '<div class="sm-field" style="margin:0"><label for="smb-rg-addr">Training address</label><input id="smb-rg-addr" type="text" data-w="address" value="' + esc(reg.address) + '" placeholder="123 Care Dr, Ocala FL" autocomplete="street-address"></div>' +
            '<div class="sm-form__row" style="margin-top:12px"><div class="sm-field" style="margin:0"><label for="smb-rg-odate">Date</label><input id="smb-rg-odate" type="date" data-w="date" min="' + esc(minDate) + '" value="' + esc(reg.date) + '"></div>' +
            '<div class="sm-field" style="margin:0"><label for="smb-rg-otime">Start time</label><input id="smb-rg-otime" type="time" data-w="onsiteTime" value="' + esc(reg.onsiteTime) + '"></div></div>' +
            '<p class="sm-field__help" style="margin:8px 0 0">We’ll confirm the instructor for your date after you check out.</p>';
        }
        q('[data-rg-where]').querySelectorAll('[data-w]').forEach((inp) => inp.addEventListener(inp.tagName === 'INPUT' && inp.type === 'text' ? 'input' : 'change', () => {
          reg[inp.dataset.w] = inp.value.trim();
          if (inp.dataset.w === 'location' || inp.dataset.w === 'date') { reg.officeTime = ''; if (reg.mode === 'office') { renderWhere(); loadAvail(); } }
          renderSummary();
        }));
        if (reg.mode === 'office') loadAvail();
      }
      function compute() {
        const n = reg.empIds.size; const c = COURSES.find((x) => x.key === reg.course);
        const out = { n, c, unit: 0, label: '', ok: true, warn: '' };
        if (!c) { out.ok = false; out.warn = 'Pick a course.'; return out; }
        if (n < 1) { out.ok = false; out.warn = 'Select at least one employee.'; }
        if (reg.mode === 'office') {
          out.unit = Number(office[c.key] || 0); out.label = c.name + ' · office · ' + n + ' seat' + (n !== 1 ? 's' : '');
          if (out.ok && !reg.date) { out.ok = false; out.warn = 'Pick a date.'; }
          else if (out.ok && reg.date < minDate) { out.ok = false; out.warn = 'Pick a date from tomorrow onward.'; }
          else if (out.ok && (!avail.data || avail.loading)) { out.ok = false; out.warn = 'Checking open times…'; }
          else if (out.ok && !avail.data.ok) { out.ok = false; out.warn = avail.data.closedReason || 'That date isn’t available.'; }
          else if (out.ok && !reg.officeTime) { out.ok = false; out.warn = 'Pick a time.'; }
          else if (out.ok) { const s = avail.data.slots.find((x) => x.time === reg.officeTime); if (!s || s.blocked || s.left < n) { out.ok = false; out.warn = 'That time no longer has ' + n + ' open seats.'; } }
        } else {
          const pick = onsiteUnit(onsite[c.key], n);
          if (n < 5) { out.ok = false; out.warn = out.warn || 'On-site training needs at least 5 attendees.'; }
          else if (!pick) { out.ok = false; out.warn = 'No group rate is set for ' + n + ' people. Contact STAT Med.'; }
          else { out.unit = pick.unit; out.label = c.name + ' · on-site · ' + pick.tier + '+ rate · ' + n + ' people'; }
          if (out.ok && !reg.address) { out.ok = false; out.warn = 'Enter the training address.'; }
          else if (out.ok && !reg.date) { out.ok = false; out.warn = 'Pick a date.'; }
          else if (out.ok && reg.date < minDate) { out.ok = false; out.warn = 'Pick a date from tomorrow onward.'; }
          else if (out.ok && !reg.onsiteTime) { out.ok = false; out.warn = 'Pick a start time.'; }
        }
        return out;
      }
      function renderSummary() {
        const x = compute();
        if (!x.c) { q('[data-rg-summary]').innerHTML = '<p class="sm-field__help">Pick a course to see pricing.</p>'; return; }
        const total = x.unit * x.n;
        q('[data-rg-summary]').innerHTML =
          '<div class="smb-sumrow"><span>' + esc(x.label || x.c.name) + '</span><span>' + (x.n && x.unit ? esc(money(total)) : '—') + '</span></div>' +
          (x.unit ? '<div class="smb-sumrow"><span class="sm-field__help">' + esc(money(x.unit)) + (reg.mode === 'office' ? ' per seat' : ' per person') + '</span><span></span></div>' : '') +
          (reg.date ? '<div class="smb-sumrow"><span class="sm-field__help">When</span><span class="sm-field__help" style="text-align:right">' + esc(fmtDate(reg.date)) + ((reg.mode === 'office' ? reg.officeTime : reg.onsiteTime) ? ' · ' + esc(reg.mode === 'office' ? reg.officeTime : fmtTime(reg.onsiteTime)) : '') + '</span></div>' : '') +
          '<div class="smb-sumrow total"><span>Total</span><span>' + (x.n && x.unit ? esc(money(total)) : '—') + '</span></div>' +
          (x.warn ? '<p class="sm-field__help smb-msg-warn" style="margin-top:6px">' + esc(x.warn) + '</p>' : '') +
          '<button type="button" class="sm-btn sm-btn--primary smb-btn-block" data-rg-checkout ' + (x.ok && total > 0 ? '' : 'disabled') + '>Continue to payment</button>' +
          '<p class="sm-field__help" data-rg-msg role="status" style="margin-top:8px"></p>' +
          '<p class="sm-policy-link">By ' + 'continuing' + ', you agree to our <a href="' + esc(((window.StatMedConfig && window.StatMedConfig.refundPolicyUrl) || '/pages/refund-policy') + '#other-situations') + '" target="_blank" rel="noopener">Refund Policy</a>.</p>' +
          '<p class="sm-field__help" style="margin-top:4px">You’ll pay securely through Shopify at your negotiated rate. ' + (reg.mode === 'office' ? 'Seats are held for you while the invoice is open.' : '') + '</p>';
        const btn = q('[data-rg-checkout]'); if (btn) btn.addEventListener('click', doCheckout);
      }
      async function doCheckout() {
        const msg = q('[data-rg-msg]'); const btn = q('[data-rg-checkout]');
        const chosen = emps.filter((e) => reg.empIds.has(e.id));
        btn.disabled = true; msg.className = 'sm-field__help'; msg.textContent = 'Creating your invoice…';
        try {
          const res = await ns.fb.businessCheckout({
            course: reg.course, mode: reg.mode,
            employees: chosen.map((e) => ({ id: e.id, name: empName(e), employeeId: e.employeeId || '' })),
            schedule: reg.mode === 'office' ? { location: locName(reg.location), locationId: reg.location, date: reg.date, time: reg.officeTime } : { address: reg.address, date: reg.date, time: reg.onsiteTime }
          });
          if (res && /^https:\/\//.test(res.invoiceUrl || '')) { msg.textContent = 'Taking you to secure checkout…'; window.location.href = res.invoiceUrl; refreshAll(); }
          else if (res && res.mock) { msg.className = 'sm-field__help smb-msg-ok'; msg.textContent = 'Preview mode: no order was created.'; btn.disabled = false; }
          else { msg.className = 'sm-field__help smb-msg-err'; msg.textContent = 'Your order was created, but we couldn’t open checkout. Find it under Trainings, or contact STAT Med.'; refreshAll(); }
        } catch (e) {
          msg.className = 'sm-field__help smb-msg-err'; msg.textContent = (e && e.message) || 'Checkout failed. Please try again.'; btn.disabled = false;
          if (reg.mode === 'office') { avail.key = ''; loadAvail(); }
          if (/removed from your roster/i.test((e && e.message) || '')) refreshAll();
        }
      }

      el.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => { reg.mode = b.dataset.mode; renderModes(); renderCourses(); renderWhere(); renderSummary(); }));
      renderModes(); renderCourses(); renderEmps(); renderWhere(); renderSummary();
    }

    function renderCerts() {
      const el = root.querySelector('[data-smb-view="certs"]');
      const cs = certStats();
      const certs = data.certs.slice().sort((a, b) => String(a.renewalDate || '').localeCompare(String(b.renewalDate || '')));
      const rows = certs.length ? certs.map((c) => { const s = smCertStatus(c.renewalDate);
        return '<tr><td><strong>' + esc(c.employeeName || '') + '</strong></td><td>' + esc(c.course || '') + '</td><td class="tnum">' + esc(fmtDate(c.completedDate)) + '</td><td class="tnum">' + esc(fmtDate(c.renewalDate)) + '</td><td>' + certPill(s) + '</td></tr>';
      }).join('') : '<tr><td colspan="5" class="smb-empty">No certifications on file yet. They appear here after your team completes training with STAT Med.</td></tr>';
      el.innerHTML = head('Compliance', 'Certifications') + (data.failed.certs ? loadErr('certifications') : '') +
        '<div class="smb-kpis smb-kpis--3">' +
          '<div class="sm-card smb-kpi"><span class="sm-field__help">Valid</span><div class="v" style="color:var(--sm-success,#0f9d6b)">' + esc(cs.ok) + '</div></div>' +
          '<div class="sm-card smb-kpi"><span class="sm-field__help">Expiring ≤60 days</span><div class="v" style="color:#b07d12">' + esc(cs.warn) + '</div></div>' +
          '<div class="sm-card smb-kpi"><span class="sm-field__help">Expired</span><div class="v" style="color:var(--sm-danger,#d6453f)">' + esc(cs.exp) + '</div></div>' +
        '</div>' +
        '<div class="sm-card"><div class="smb-tablewrap"><table class="sm-table"><thead><tr><th>Employee</th><th>Course</th><th>Completed</th><th>Renews</th><th>Status</th></tr></thead><tbody>' + rows + '</tbody></table></div></div>' +
        '<p class="sm-field__help" style="margin-top:12px">STAT Med records certifications when your team completes training. Someone trained elsewhere? Send us a copy of their card and we’ll add it.' +
        (cs.warn + cs.exp ? ' <button type="button" class="smb-link" data-smb-go="register">Book renewals</button>' : '') + '</p>';
      el.querySelectorAll('[data-smb-go]').forEach((b) => b.addEventListener('click', () => go(b.dataset.smbGo)));
    }

    // Auto-restore an existing session.
    ns.fb.onAuth((u) => { if (u) afterAuth(); });
  }

  /* ---------- Instructor materials portal ---------- */
  async function initInstructorPortal() {
    const root = document.querySelector('[data-sm-instructor]');
    if (!root || !ns.fb) return;
    const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const money = (n) => '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const loginPanel = root.querySelector('[data-si-login]'), appPanel = root.querySelector('[data-si-app]');
    const statusEl = root.querySelector('[data-si-status]'), emailIn = root.querySelector('[data-si-email]'), passIn = root.querySelector('[data-si-pass]');
    let inst = null, myMaterials = [], cart = {};
    const setStatus = (m, err) => { if (statusEl) { statusEl.textContent = m; statusEl.style.color = err ? 'var(--sm-danger)' : 'var(--sm-ink-muted)'; } };
    const authMsg = (e) => { const c = (e && e.code) || ''; if (c.indexOf('email-already-in-use') > -1) return 'That email already has a login — just Sign in.'; if (c.indexOf('wrong-password') > -1 || c.indexOf('invalid-credential') > -1) return 'Incorrect email or password.'; if (c.indexOf('user-not-found') > -1) return 'No login yet — click “Set your password”.'; if (c.indexOf('weak-password') > -1) return 'Password must be at least 6 characters.'; return (e && e.message) || 'Sign-in failed.'; };

    async function afterAuth() {
      setStatus('Loading your account…');
      try { inst = await ns.fb.getMyInstructor(); } catch (e) { setStatus('Could not load your account: ' + (e.message || e), true); return; }
      if (!inst) { setStatus('No instructor account is set up for this email. Contact StatMed for access.', true); try { await ns.fb.signOut(); } catch (e) {} return; }
      let all = []; try { all = await ns.fb.adminListMaterials(); } catch (e) {}
      const pricing = inst.pricing || {};
      myMaterials = all.filter((m) => (m.id in pricing) && m.active !== false).map((m) => ({ id: m.id, name: m.name, price: Number(pricing[m.id]) || 0 }));
      showApp();
    }
    async function doSignIn() { const email = (emailIn.value || '').trim().toLowerCase(), pass = passIn.value || ''; if (!email || !pass) { setStatus('Enter your email and password.', true); return; } setStatus('Signing in…'); try { await ns.fb.signIn(email, pass); await afterAuth(); } catch (e) { setStatus(authMsg(e), true); } }
    async function doSignUp() { const email = (emailIn.value || '').trim().toLowerCase(), pass = passIn.value || ''; if (!email || pass.length < 6) { setStatus('Enter your email and a 6+ character password, then click “Set your password”.', true); return; } setStatus('Setting up your login…'); try { await ns.fb.businessSignUp(email, pass); await afterAuth(); } catch (e) { setStatus(authMsg(e), true); } }
    root.querySelector('[data-si-signin]').addEventListener('click', doSignIn);
    root.querySelector('[data-si-signup]').addEventListener('click', (e) => { e.preventDefault(); doSignUp(); });
    passIn.addEventListener('keydown', (e) => { if (e.key === 'Enter') doSignIn(); });
    root.querySelector('[data-si-signout]').addEventListener('click', (e) => { e.preventDefault(); ns.fb.signOut(); inst = null; cart = {}; appPanel.hidden = true; loginPanel.hidden = false; });

    function showApp() {
      loginPanel.hidden = true; appPanel.hidden = false;
      root.querySelector('[data-si-name]').textContent = inst.name || 'Materials';
      const u = ns.fb.currentUser && ns.fb.currentUser();
      root.querySelector('[data-si-whoami]').textContent = (u && u.email) || '';
      renderMaterials(); renderSummary();
    }
    function renderMaterials() {
      const el = root.querySelector('[data-si-materials]');
      el.innerHTML = myMaterials.length ? myMaterials.map((m) =>
        '<div class="si-item"><div style="flex:1"><b>' + esc(m.name) + '</b><br><span class="sm-field__help">Your price</span></div>' +
        '<span class="si-price">' + money(m.price) + '</span>' +
        '<input type="number" min="0" step="1" class="si-qty" data-si-qty="' + m.id + '" value="' + (cart[m.id] || 0) + '"></div>'
      ).join('') : '<div class="sm-card" style="padding:18px"><p class="sm-field__help">No materials are assigned to your account yet. Contact StatMed.</p></div>';
      el.querySelectorAll('[data-si-qty]').forEach((i) => i.addEventListener('input', () => { cart[i.dataset.siQty] = Math.max(0, parseInt(i.value, 10) || 0); renderSummary(); }));
    }
    function renderSummary() {
      const el = root.querySelector('[data-si-summary]');
      const lines = myMaterials.filter((m) => cart[m.id] > 0);
      const total = lines.reduce((s, m) => s + m.price * cart[m.id], 0);
      el.innerHTML = '<h3 class="sm-h4" style="margin:0 0 12px">Your order</h3>' +
        (lines.length ? lines.map((m) => '<div class="si-sumrow"><span>' + esc(m.name) + ' × ' + cart[m.id] + '</span><span>' + money(m.price * cart[m.id]) + '</span></div>').join('') : '<p class="sm-field__help">Add quantities to build your order.</p>') +
        '<div class="si-sumrow total"><span>Total</span><span>' + money(total) + '</span></div>' +
        '<button type="button" class="sm-btn sm-btn--primary" style="width:100%;justify-content:center;margin-top:12px" data-si-checkout' + (total > 0 ? '' : ' disabled') + '>Proceed to checkout →</button>' +
        '<p class="sm-field__help" data-si-msg style="margin-top:8px"></p>' +
        '<p class="sm-field__help" style="margin-top:4px">You pay securely via Shopify at your rates.</p>';
      const btn = el.querySelector('[data-si-checkout]'); if (btn) btn.addEventListener('click', doCheckout);
    }
    async function doCheckout() {
      const msg = root.querySelector('[data-si-msg]'), btn = root.querySelector('[data-si-checkout]');
      const items = myMaterials.filter((m) => cart[m.id] > 0).map((m) => ({ materialId: m.id, qty: cart[m.id] }));
      if (!items.length) return;
      btn.disabled = true; msg.style.color = 'var(--sm-ink-muted)'; msg.textContent = 'Creating your checkout…';
      try { const res = await ns.fb.instructorCheckout({ items: items }); if (res.invoiceUrl) { msg.textContent = 'Redirecting to secure checkout…'; window.location.href = res.invoiceUrl; } else { msg.style.color = 'var(--sm-danger)'; msg.textContent = 'Order created but no checkout link returned. Contact StatMed.'; btn.disabled = false; } }
      catch (e) { msg.style.color = 'var(--sm-danger)'; msg.textContent = e.message || 'Checkout failed.'; btn.disabled = false; }
    }
    ns.fb.onAuth((u) => { if (u) afterAuth(); });
  }

  /* ---------- Class portal (register → content + quiz) ---------- */
  function initClassPortal() {
    // A page can hold several class-portal sections (e.g. a check-in section
    // plus one or more "content only" sections when 50 blocks isn't enough).
    // They share one check-in gate and one quiz across all sections.
    const roots = Array.from(document.querySelectorAll('[data-sm-class-portal]'));
    if (!roots.length) return;
    const primary = roots.find((r) => r.querySelector('[data-cp-reg]')) || roots[0];
    const className = primary.dataset.classTitle || 'Class';
    // The check-in is remembered under this key. Prefer an explicit shared
    // Class ID (so course + quiz pages match even if titles differ); fall
    // back to the class title.
    const keyBase = (primary.dataset.classKey && primary.dataset.classKey.trim()) || className;
    const storeKey = 'sm_class_' + keyBase.replace(/\W+/g, '_');

    // Read the saved check-in. Try this page's key first; if that's missing
    // (e.g. the student checked in under a slightly different title/key on
    // another page), fall back to the most recent check-in in this browser so
    // the quiz is still attributed to the right person.
    function readCheckin() {
      try { const s = JSON.parse(localStorage.getItem(storeKey) || 'null'); if (s && s.firstName) return s; } catch (e) {}
      let best = null;
      try {
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i); if (!k || k.indexOf('sm_class_') !== 0) continue;
          let v = null; try { v = JSON.parse(localStorage.getItem(k) || 'null'); } catch (e) { continue; }
          if (v && v.firstName && (!best || String(v.createdAt || '') > String(best.createdAt || ''))) best = v;
        }
      } catch (e) {}
      return best;
    }

    // Turn a YouTube/Vimeo/mp4 link into an embedded player.
    function embed(url) {
      url = (url || '').trim(); if (!url) return '';
      let m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
      if (m) return '<iframe src="https://www.youtube.com/embed/' + m[1] + '" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
      m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (m) return '<iframe src="https://player.vimeo.com/video/' + m[1] + '" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>';
      if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) return '<video src="' + url + '" controls playsinline></video>';
      if (/^https?:\/\//.test(url)) return '<iframe src="' + url + '" allowfullscreen></iframe>';
      return '<p class="sm-field__help">Add a video link in the editor.</p>';
    }
    roots.forEach((root) => root.querySelectorAll('[data-cp-video]').forEach((el) => { el.innerHTML = embed(el.dataset.cpVideo); }));

    // Reveal (or, in the editor, just show) the content in EVERY portal section.
    function revealAll(name) {
      roots.forEach((root) => {
        const reg = root.querySelector('[data-cp-reg]'); if (reg) reg.hidden = true;
        const gate = root.querySelector('[data-cp-gate]'); if (gate) gate.hidden = true;
        const content = root.querySelector('[data-cp-content]'); if (content) content.hidden = false;
        const w = root.querySelector('[data-cp-welcome]'); if (w && name) w.textContent = 'Welcome, ' + name + '!';
      });
      window.scrollTo(0, 0);
    }
    // In the Shopify theme editor, always show content so blocks stay editable.
    if (window.Shopify && window.Shopify.designMode) {
      roots.forEach((root) => {
        const c = root.querySelector('[data-cp-content]'); if (c) c.hidden = false;
        const gate = root.querySelector('[data-cp-gate]'); if (gate) gate.hidden = true;
      });
    } else {
      // Already checked in this browser?
      const saved = readCheckin(); if (saved && saved.firstName) revealAll(saved.firstName);
    }

    // Check-in form (lives on the primary section).
    const g = (k) => { const el = primary.querySelector('[data-cp="' + k + '"]'); return el ? el.value.trim() : ''; };
    const msg = primary.querySelector('[data-cp-msg]');
    const submitBtn = primary.querySelector('[data-cp-submit]');
    submitBtn && submitBtn.addEventListener('click', async () => {
      const data = { firstName: g('firstName'), lastName: g('lastName'), address: g('address'), phone: g('phone'), email: g('email'), className: className };
      if (!data.firstName || !data.lastName) { msg.textContent = 'Please enter your first and last name.'; msg.style.color = 'var(--sm-danger)'; return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) { msg.textContent = 'Please enter a valid email.'; msg.style.color = 'var(--sm-danger)'; return; }
      if (!data.address || !data.phone) { msg.textContent = 'Please fill in your address and phone.'; msg.style.color = 'var(--sm-danger)'; return; }
      msg.textContent = 'Checking you in…'; msg.style.color = 'var(--sm-ink-muted)';
      submitBtn.disabled = true;
      try { if (ns.fb && ns.fb.submitClassRegistration) await ns.fb.submitClassRegistration(data); } catch (e) { console.warn('[class] reg save failed', e); }
      try { localStorage.setItem(storeKey, JSON.stringify(data)); } catch (e) {}
      revealAll(data.firstName);
    });

    // Quiz — the submit button lives on one section, but questions may be
    // spread across every portal section on the page.
    let quizBtn = null, quizRoot = null;
    roots.forEach((root) => { const b = root.querySelector('[data-cp-quiz-submit]'); if (b) { quizBtn = b; quizRoot = root; } });
    quizBtn && quizBtn.addEventListener('click', async () => {
      const qs = roots.reduce((acc, root) => acc.concat(Array.from(root.querySelectorAll('[data-cp-q]'))), []);
      let unanswered = 0, correct = 0; const answers = [];
      qs.forEach((q) => {
        const picked = q.querySelector('input[type="radio"]:checked');
        const right = String(q.dataset.correct);
        const fb = q.querySelector('.sm-cp-qfb');
        q.classList.remove('is-correct', 'is-wrong');
        if (!picked) { unanswered++; fb.textContent = ''; return; }
        const ok = picked.value === right;
        if (ok) { correct++; q.classList.add('is-correct'); fb.style.color = '#0f7a51'; fb.textContent = '✓ Correct'; }
        else { q.classList.add('is-wrong'); fb.style.color = '#b91c1c'; fb.textContent = '✗ Not quite'; }
        answers.push({ q: (q.querySelector('p') || {}).textContent || '', picked: picked.value, correct: right, ok: ok });
      });
      const result = quizRoot.querySelector('[data-cp-quiz-result]');
      if (unanswered) { result.style.color = 'var(--sm-danger)'; result.textContent = 'Please answer all ' + qs.length + ' questions (' + unanswered + ' left).'; return; }
      const pct = qs.length ? Math.round(correct / qs.length * 100) : 0;
      result.style.color = pct >= 80 ? '#0f7a51' : 'var(--sm-ink)';
      result.textContent = 'You scored ' + correct + ' / ' + qs.length + ' (' + pct + '%).' + (pct >= 80 ? ' 🎉 Passed!' : '');
      const student = readCheckin() || {};
      try { if (ns.fb && ns.fb.submitClassQuiz) await ns.fb.submitClassQuiz({ className: student.className || className, firstName: student.firstName || '', lastName: student.lastName || '', email: student.email || '', phone: student.phone || '', address: student.address || '', score: correct, total: qs.length, percent: pct, answers: answers }); } catch (e) { console.warn('[class] quiz save failed', e); }
      quizBtn.textContent = 'Submitted ✓';
    });
  }

  /* ---------- Self-service reschedule page ---------- */
  async function initReschedule() {
    const root = document.querySelector('[data-sm-reschedule]');
    if (!root) return;
    const rescheduleHandle = root.dataset.rescheduleHandle || 'rescheduling-fee';
    const daysAhead = parseInt(root.dataset.daysAhead || '60', 10) || 60;
    const statusEl = root.querySelector('[data-rs-status]');
    const bodyEl = root.querySelector('[data-rs-body]');
    const doneEl = root.querySelector('[data-rs-done]');
    const params = new URLSearchParams(window.location.search);
    const bookingId = params.get('b') || params.get('booking') || '';
    if (!bookingId) { statusEl.textContent = 'This link is missing its appointment reference. Please open the link from your reminder email, or contact us.'; return; }
    if (!ns.fb || typeof ns.fb.rescheduleLookup !== 'function') { statusEl.textContent = 'Rescheduling isn’t available right now. Please contact us.'; return; }

    let data;
    try { data = await ns.fb.rescheduleLookup(bookingId); }
    catch (e) { statusEl.textContent = 'We could not load your appointment. ' + (e.message || ''); return; }
    if (data.cancelled) { statusEl.textContent = 'This appointment has been cancelled. Please contact us to book again.'; return; }
    if (!data || !data.ok) { statusEl.textContent = 'We could not load your appointment.'; return; }

    const locSel = root.querySelector('[data-rs-loc]');
    const locWrap = root.querySelector('[data-rs-loc-wrap]');
    const dateInp = root.querySelector('[data-rs-date]');
    const timesEl = root.querySelector('[data-rs-times]');
    const confirmBtn = root.querySelector('[data-rs-confirm]');
    const msgEl = root.querySelector('[data-rs-msg]');
    let chosenTime = '';

    // Header + fee line.
    const fmtNiceDate = (ds) => { try { return new Date(ds + 'T12:00:00').toLocaleDateString(undefined, { weekday:'short', month:'short', day:'numeric' }); } catch (e) { return ds; } };
    root.querySelector('[data-rs-current]').textContent =
      (data.service.name || 'Appointment') + ' — ' + (data.current.date ? fmtNiceDate(data.current.date) : '') + (data.current.time ? ' at ' + data.current.time : '') + (data.current.locationName ? ' · ' + data.current.locationName : '');
    root.querySelector('[data-rs-fee]').innerHTML = data.hasProtection
      ? '✅ You have <strong>Cancellation Protection</strong> — rescheduling is <strong>free</strong>.'
      : 'A <strong>$' + (data.fee || 25) + ' rescheduling fee</strong> applies (you didn’t add Cancellation Protection). You’ll pay it at checkout after choosing your new time.';

    // Locations.
    const locs = (data.locations && data.locations.length) ? data.locations : (data.current.locationId ? [{ id: data.current.locationId, name: data.current.locationName || data.current.locationId, slots: null }] : []);
    locSel.innerHTML = locs.map((l) => '<option value="' + l.id + '"' + (l.id === data.current.locationId ? ' selected' : '') + '>' + (l.name || l.id) + '</option>').join('');
    if (locs.length <= 1 && locWrap) locWrap.style.display = 'none';

    // Date bounds.
    const todayStr = new Date().toISOString().slice(0, 10);
    const maxD = new Date(Date.now() + daysAhead * 86400000).toISOString().slice(0, 10);
    dateInp.min = todayStr; dateInp.max = maxD;

    function slotTimesFor(locId) {
      const sc = data.slotConfig || {};
      const byLoc = sc.slotsByLocation && sc.slotsByLocation[locId];
      if (Array.isArray(byLoc) && byLoc.length) return byLoc;
      const loc = locs.find((l) => l.id === locId);
      if (loc && Array.isArray(loc.slots) && loc.slots.length) return loc.slots;
      if (Array.isArray(data.scheduleDefaults) && data.scheduleDefaults.length) return data.scheduleDefaults;
      return ['9:00 AM', '11:00 AM', '1:00 PM', '3:00 PM'];
    }
    function timeToMin(t) { const m = String(t).match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i); if (!m) return null; let h = +m[1] % 12; if (m[3] && /pm/i.test(m[3])) h += 12; return h * 60 + (+m[2]); }

    async function loadTimes() {
      chosenTime = ''; confirmBtn.disabled = true; confirmBtn.textContent = 'Choose a time above';
      const locId = locSel.value; const date = dateInp.value;
      if (!date) { timesEl.innerHTML = '<span class="sm-field__help">Pick a date first.</span>'; return; }
      timesEl.innerHTML = '<span class="sm-field__help">Loading…</span>';
      let booked = {};
      try { booked = await ns.fb.getBookedSlots({ locationId: locId, serviceId: data.service.id, resourceGroup: data.slotConfig.resourceGroup, crossLocations: data.slotConfig.crossLocations, date: date }) || {}; } catch (e) {}
      const max = data.slotConfig.maxPerSlot || 1;
      const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
      const isToday = date === todayStr;
      const times = slotTimesFor(locId);
      timesEl.innerHTML = times.map((t) => {
        const taken = booked[t] || 0;
        const past = isToday && timeToMin(t) != null && timeToMin(t) <= nowMin;
        const full = taken >= max;
        const isCurrent = (date === data.current.date && t === data.current.time && locId === data.current.locationId);
        const disabled = full || past || isCurrent;
        return '<button type="button" class="sm-rs-slot" data-rs-time="' + t + '"' + (disabled ? ' disabled' : '') + '>' + t + (full ? ' (full)' : (isCurrent ? ' (current)' : '')) + '</button>';
      }).join('') || '<span class="sm-field__help">No times available that day — try another date.</span>';
    }

    timesEl.addEventListener('click', (e) => {
      const b = e.target.closest('[data-rs-time]'); if (!b || b.disabled) return;
      timesEl.querySelectorAll('.sm-rs-slot').forEach((x) => x.classList.remove('is-selected'));
      b.classList.add('is-selected');
      chosenTime = b.dataset.rsTime;
      confirmBtn.disabled = false;
      confirmBtn.textContent = data.hasProtection ? 'Confirm new time (free)' : 'Continue to pay the $' + (data.fee || 25) + ' fee';
    });
    locSel.addEventListener('change', loadTimes);
    dateInp.addEventListener('change', loadTimes);

    confirmBtn.addEventListener('click', async () => {
      if (!chosenTime) return;
      const locId = locSel.value; const date = dateInp.value;
      const locName = (locs.find((l) => l.id === locId) || {}).name || '';
      confirmBtn.disabled = true; msgEl.textContent = 'Working…';
      const target = { bookingId: bookingId, locationId: locId, locationName: locName, date: date, time: chosenTime };
      if (data.hasProtection) {
        try {
          const res = await ns.fb.rescheduleApply(target);
          bodyEl.hidden = true; statusEl.hidden = true;
          root.querySelector('[data-rs-done-msg]').textContent = 'Your ' + data.service.name + ' is moved to ' + fmtNiceDate(res.date) + ' at ' + res.time + (res.locationName ? ' in ' + res.locationName : '') + '. A confirmation reminder will follow.';
          doneEl.hidden = false; window.scrollTo(0, 0);
        } catch (e) {
          confirmBtn.disabled = false; msgEl.style.color = 'var(--sm-danger)';
          msgEl.textContent = e.message || 'Could not reschedule. Please try another time.';
        }
      } else {
        // Unprotected — add the reschedule fee to cart with the chosen slot,
        // then send them to checkout. The paid-order webhook applies the move.
        try {
          let product = await fetchProduct(rescheduleHandle);
          const vid = product && product.variants && product.variants[0] && product.variants[0].id;
          if (!vid) throw new Error('Reschedule product not found.');
          await fetch('/cart/add.js', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: vid, quantity: 1, properties: {
              'Reschedule Booking ID': bookingId, 'New Date': date, 'New Time': chosenTime,
              'New Location': locId, 'New Location Name': locName,
              'Moving from': (data.current.date || '') + ' ' + (data.current.time || '')
            } })
          });
          window.location.href = '/cart';
        } catch (e) {
          confirmBtn.disabled = false; msgEl.style.color = 'var(--sm-danger)';
          msgEl.textContent = e.message || 'Could not start checkout. Please try again or contact us.';
        }
      }
    });

    // Default the date to tomorrow and show it.
    dateInp.value = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    statusEl.hidden = true; bodyEl.hidden = false;
    loadTimes();
  }

  /* ---------- Site variables: replace [[key]] tokens site-wide ---------- */
  // Built-in defaults come from statmed-firebase.js (StatMed.SITE_VAR_DEFAULTS,
  // shared with the staff admin "Site text" screen). The few below are a
  // safety net in case that file failed to load, so core tokens never show raw.
  // A value saved in the admin (Firestore settings/site_vars) overrides them.
  const SM_VAR_DEFAULTS = Object.assign({
    locations: '4 North Florida locations',
    phone: '(386) 288-8332',
    phone_tel: '+13862888332',
    email: 'support@statmedtraining.com'
  }, (ns.SITE_VAR_DEFAULTS && typeof ns.SITE_VAR_DEFAULTS === 'object') ? ns.SITE_VAR_DEFAULTS : {});
  const SM_VAR_CACHE_KEY = 'sm_site_vars_v2';
  const SM_VAR_CACHE_MAX_AGE = 60 * 60 * 1000; // first-paint cache is only trusted for an hour
  function initSiteVars() {
    const varEls = Array.from(document.querySelectorAll('[data-sm-var]'));
    const hrefEls = Array.from(document.querySelectorAll('[href*="[["]'));
    const hasTextTokens = !!(document.body && document.body.textContent && document.body.textContent.indexOf('[[') !== -1);
    if (!varEls.length && !hrefEls.length && !hasTextTokens) return;
    const rx = /\[\[([a-zA-Z0-9_.\-]+)\]\]/g;
    const sub = (str, vars) => str.replace(rx, (m, key) => (key in vars ? String(vars[key]) : m));

    // Remember each token's ORIGINAL text once, so values can be applied more
    // than once: first from defaults/cache (instant, no raw [[tokens]]), then
    // again with the fresh Firestore values as soon as they arrive.
    const hrefTpl = hrefEls.map((el) => ({ el, tpl: el.getAttribute('href') || '' }));
    const textTpl = [];
    if (hasTextTokens) {
      const skip = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1 };
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) => (n.parentNode && skip[n.parentNode.nodeName]) ? NodeFilter.FILTER_REJECT
          : (n.nodeValue && n.nodeValue.indexOf('[[') !== -1) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
      });
      let n; while ((n = walker.nextNode())) textTpl.push({ node: n, tpl: n.nodeValue });
    }

    function apply(vars) {
      varEls.forEach((el) => { const k = el.dataset.smVar; if (k && (k in vars)) el.textContent = vars[k]; });
      // Tokens inside href attributes (tel:/mailto:/links).
      hrefTpl.forEach((x) => { const v = sub(x.tpl, vars); if (x.el.getAttribute('href') !== v) x.el.setAttribute('href', v); });
      textTpl.forEach((x) => { const v = sub(x.tpl, vars); if (x.node.nodeValue !== v) x.node.nodeValue = v; });
    }

    // 1) Instant paint: defaults + a recent cached copy of the admin values.
    let cached = null;
    try {
      localStorage.removeItem('sm_site_vars'); // old unversioned, never-expiring cache
      const c = JSON.parse(localStorage.getItem(SM_VAR_CACHE_KEY) || 'null');
      if (c && c.vars && typeof c.t === 'number' && (Date.now() - c.t) < SM_VAR_CACHE_MAX_AGE) cached = c.vars;
    } catch (e) {}
    apply(Object.assign({}, SM_VAR_DEFAULTS, cached || {}));

    // 2) Fresh values: re-apply as soon as Firestore answers (also on a
    //    visitor's very first page view), and refresh the cache. An empty
    //    result clears the cached overrides so removed values fall back to
    //    the defaults.
    if (ns.fb && typeof ns.fb.getSiteVariables === 'function') {
      ns.fb.getSiteVariables().then((vars) => {
        vars = (vars && typeof vars === 'object') ? vars : {};
        apply(Object.assign({}, SM_VAR_DEFAULTS, vars));
        try { localStorage.setItem(SM_VAR_CACHE_KEY, JSON.stringify({ t: Date.now(), vars })); } catch (e) {}
      }).catch(() => {});
    }
  }

  /* ---------- Live product prices from Shopify ---------- */
  // Any [data-sm-price="<spec>"] element is filled with a live price fetched
  // from Shopify's public /products/<handle>.js, so bundle-compare tables,
  // FAQ answers and savings always match the store. The element's existing
  // text is the fallback and is kept if the fetch fails or a value is unset.
  //   spec forms:
  //     "<handle>"           → that product's current price
  //     "<h1>+<h2>[+..]"     → sum of those products' prices
  //     "was:<handle>"       → the product's compare-at ("separately") price
  //     "save:<handle>"      → compare-at minus price (the amount saved)
  function initLivePrices() {
    const els = document.querySelectorAll('[data-sm-price]');
    if (!els.length || typeof fetch !== 'function') return;
    const handles = new Set();
    els.forEach((el) => {
      String(el.getAttribute('data-sm-price') || '')
        .replace(/^(?:was|save):/, '').split('+')
        .forEach((h) => { h = h.trim(); if (h) handles.add(h); });
    });
    const store = {}; // handle -> { price, compare } in cents (or null)
    const fetchOne = (h) => {
      const ck = 'sm_prod_' + h;
      try { const c = sessionStorage.getItem(ck); if (c) return Promise.resolve(JSON.parse(c)); } catch (e) {}
      return fetch('/products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((p) => {
          const v = {
            price: (p && typeof p.price === 'number') ? p.price : null,
            compare: (p && typeof p.compare_at_price === 'number') ? p.compare_at_price : null
          };
          try { sessionStorage.setItem(ck, JSON.stringify(v)); } catch (e) {}
          return v;
        });
    };
    const fmt = (cents) => (cents == null ? null : '$' + (cents % 100 === 0 ? (cents / 100) : (cents / 100).toFixed(2)));
    Promise.all([...handles].map((h) => fetchOne(h).then((v) => { store[h] = v; }).catch(() => { store[h] = null; })))
      .then(() => {
        els.forEach((el) => {
          const spec = String(el.getAttribute('data-sm-price') || '').trim();
          let cents = null;
          if (spec.indexOf('was:') === 0) {
            const v = store[spec.slice(4).trim()]; cents = v ? v.compare : null;
          } else if (spec.indexOf('save:') === 0) {
            const v = store[spec.slice(5).trim()];
            if (v && v.compare != null && v.price != null && v.compare > v.price) cents = v.compare - v.price;
          } else {
            let sum = 0, ok = true;
            spec.split('+').forEach((h) => { const v = store[h.trim()]; if (!v || v.price == null) ok = false; else sum += v.price; });
            if (ok) cents = sum;
          }
          const out = fmt(cents);
          if (out) el.textContent = out; // else keep the static fallback text
        });
      })
      .catch(() => {});
  }

  /* ---------- Location addresses from the admin (Firestore locations) ---------- */
  // Fills any [data-sm-address="<city/heading>"] element with that location's
  // address from the Locations admin, so pages match the dashboard. The
  // hardcoded address stays as a fallback until (and unless) a real admin
  // address is found. Cached in localStorage to avoid a flash on repeat views.
  function initLocationAddresses() {
    const els = document.querySelectorAll('[data-sm-address]');
    if (!els.length || !ns.fb || typeof ns.fb.getLocations !== 'function') return;
    const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const escHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    function fill(locations) {
      const items = (locations || [])
        .map((l) => ({ name: norm(l.name || l.city || l.id), id: norm(l.id), addr: l.address || '' }))
        .filter((x) => /\d/.test(x.addr)); // only real street addresses override the fallback
      els.forEach((el) => {
        const k = norm(el.getAttribute('data-sm-address'));
        if (!k) return;
        const hit = items.find((x) =>
          (x.name && (k === x.name || k.indexOf(x.name) !== -1 || x.name.indexOf(k) !== -1)) ||
          (x.id && (k === x.id || k.indexOf(x.id) !== -1)));
        if (hit && hit.addr) el.innerHTML = escHtml(hit.addr).replace(/\r?\n/g, '<br>');
      });
    }
    let cached = null;
    try { cached = JSON.parse(localStorage.getItem('sm_locations') || 'null'); } catch (e) {}
    if (cached && cached.length) fill(cached);
    ns.fb.getLocations().then((fresh) => {
      if (fresh && fresh.length) { try { localStorage.setItem('sm_locations', JSON.stringify(fresh)); } catch (e) {} fill(fresh); }
    }).catch(() => {});
  }

  /* ---------- Init ---------- */
  function init() {
    initReveal();
    initSiteVars();
    initLivePrices();
    initLocationAddresses();
    initHeader();
    initLocationPickers();
    initBookingWidgets();
    initCnaBookings();
    initSkillsUploads();
    initAdmin();
    initBookingV2();
    initCnaExamForms();
    initSearch();
    initFingerprintForm();
    initCnaClassForms();
    initIvClassForms();
    initExamRegForms();
    initElksForms();
    initAhaCoursePicker();
    initBusinessPortal();
    initInstructorPortal();
    initClassPortal();
    initReschedule();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-initialize the class portal after Shopify re-renders the
  // section on each edit, so the content stays visible without a page refresh.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('[data-sm-class-portal]')) {
      try { initClassPortal(); } catch (err) { console.warn('[class] re-init failed', err); }
    }
  });
})();